// ArcStallion Pro v5 — Multi-Tenant Production Build
// Supabase Auth + Live Database (replaces all mock data & usePersistedState)
// ArcStallion LLC
// Role-based access control added 2026-04-22 — Admin/Staff/Viewer, Team management tab

import { useState, useEffect, useCallback, useMemo, useRef, Component, lazy, Suspense } from "react";
import { supabase, INITIAL_AUTH_TYPE } from "./supabaseClient";
import CHANGELOG from "./changelog.json";

// E-signing (src/esign.jsx) is its own chunk, pdf.js is fetched on first use,
// so neither weighs on the dashboard. See ESIGN_PLAN.md.
const SignPage = lazy(() => import("./esign").then(m => ({ default: m.SignPage })));
const FieldPlacer = lazy(() => import("./esign").then(m => ({ default: m.FieldPlacer })));
const SendForSignatureModal = lazy(() => import("./esign").then(m => ({ default: m.SendForSignatureModal })));
const PdfPreviewModal = lazy(() => import("./esign").then(m => ({ default: m.PdfPreviewModal })));

// ─── STYLES ───────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Manrope:wght@300;800&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  :root {
    --forest:#1a2e1a; --forest-mid:#243824; --forest-light:#2f4a2f;
    --gold:#c9a84c; --gold-light:#e2c97e; --gold-dim:#8a6e2f;
    --parchment:#f5f0e8; --parchment-dark:#ede5d0; --cream:#faf7f0;
    --text-dark:#1a1a14; --text-mid:#4a4a3a; --text-light:#7a7a6a;
    --red:#c0392b; --teal:#2e7d6e; --blue:#2c5f8a; --purple:#6b3fa0;
    --fedex-purple:#4d148c; --fedex-orange:#ff6600;
    --ups-brown:#351c15; --ups-gold:#ffb500;
    --sidebar-w:252px;
  }
  body { font-family:'Crimson Pro',Georgia,serif; background:var(--cream); color:var(--text-dark); }
  .app { display:flex; min-height:100vh; }

  /* ── SIDEBAR ── */
  .sidebar { width:var(--sidebar-w); background:var(--forest); display:flex; flex-direction:column; position:fixed; top:0;left:0;bottom:0; z-index:100; border-right:1px solid rgba(201,168,76,.2); overflow-y:auto; }
  .sidebar-brand { padding:22px 20px 16px; border-bottom:1px solid rgba(201,168,76,.2); }
  .sidebar-nav { padding:10px 0; flex:1; }
  .nav-section-label { font-size:9px; letter-spacing:.14em; text-transform:uppercase; color:rgba(201,168,76,.35); padding:10px 20px 4px; font-weight:600; }
  .nav-item { display:flex; align-items:center; gap:10px; padding:8px 20px; cursor:pointer; transition:all .15s; color:rgba(245,240,232,.62); font-size:13px; font-family:'Crimson Pro',serif; font-weight:400; border-left:3px solid transparent; }
  .nav-item:hover { color:var(--parchment); background:rgba(201,168,76,.08); }
  .nav-item.active { color:var(--gold-light); background:rgba(201,168,76,.12); border-left-color:var(--gold); font-weight:600; }
  .nav-icon { font-size:14px; width:18px; text-align:center; flex-shrink:0; }
  .nav-badge { margin-left:auto; background:var(--gold); color:var(--forest); font-size:9.5px; font-weight:700; padding:1px 6px; border-radius:10px; }
  .nav-badge.red { background:var(--red); color:white; }
  .sidebar-footer { padding:14px 20px; border-top:1px solid rgba(201,168,76,.2); font-size:11px; color:rgba(201,168,76,.4); }
  .sidebar-footer .farm-name { font-family:'Playfair Display',serif; color:rgba(201,168,76,.7); font-size:12.5px; margin-bottom:2px; }

  /* ── MAIN ── */
  .main { margin-left:var(--sidebar-w); flex:1; display:flex; flex-direction:column; min-height:100vh; }
  .topbar { background:var(--parchment); border-bottom:1px solid var(--parchment-dark); padding:12px 26px; display:flex; align-items:center; justify-content:space-between; position:sticky; top:0; z-index:50; }
  /* The page's only title (see CLAUDE.md "Page titles"). 26px: page titles in
     product design systems sit at 24-28px; Playfair's small x-height reads small, so
     the upper middle of that range. 20px is card/section size and read as one. */
  .topbar-title { font-family:'Playfair Display',serif; font-size:26px; line-height:1.2; color:var(--forest); font-weight:700; }
  .topbar-subtitle { font-size:13.5px; color:var(--text-light); font-style:italic; margin-top:2px; }
  .topbar-actions { display:flex; gap:8px; align-items:center; }
  .content { padding:24px 26px; flex:1; }

  /* ── BUTTONS ── */
  .btn { padding:7px 15px; border-radius:4px; font-family:'Crimson Pro',serif; font-size:13px; font-weight:600; cursor:pointer; border:none; transition:all .15s; }
  .btn-primary { background:var(--forest); color:var(--gold-light); border:1px solid var(--forest-light); }
  .btn-primary:hover { background:var(--forest-light); }
  .btn-outline { background:transparent; color:var(--forest); border:1px solid var(--forest-light); }
  .btn-outline:hover { background:var(--parchment-dark); }
  .btn-gold { background:var(--gold); color:var(--forest); border:1px solid var(--gold-dim); }
  .btn-gold:hover { background:var(--gold-light); }
  .btn-ai { background:linear-gradient(135deg,#1a2e1a,#2e5c8a); color:var(--gold-light); border:1px solid #2e5c8a; }
  .btn-fedex { background:var(--fedex-purple); color:white; border:1px solid #3a0f6e; }
  .btn-teal { background:var(--teal); color:white; border:1px solid #1f5c4e; }
  .btn-red { background:var(--red); color:white; border:1px solid #a93226; }
  .btn-sm { padding:4px 10px; font-size:11.5px; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }

  /* ── CARDS / PANELS ── */
  .stats-row { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:22px; }
  .stat-card { background:white; border:1px solid var(--parchment-dark); border-radius:8px; padding:17px 19px; position:relative; overflow:hidden; }
  .stat-card::before { content:''; position:absolute; top:0;left:0;right:0; height:3px; background:var(--gold); }
  .stat-card.green::before { background:var(--teal); }
  .stat-card.blue::before { background:var(--blue); }
  .stat-card.red::before { background:var(--red); }
  .stat-label { font-size:10px; letter-spacing:.1em; text-transform:uppercase; color:var(--text-light); font-weight:600; margin-bottom:6px; }
  .stat-value { font-family:'Playfair Display',serif; font-size:32px; color:var(--forest); font-weight:700; line-height:1; }
  .stat-meta { font-size:11px; color:var(--text-light); margin-top:5px; font-style:italic; }
  .stat-icon { position:absolute; right:14px; top:50%; transform:translateY(-50%); font-size:28px; opacity:.1; }
  .panel { background:white; border:1px solid var(--parchment-dark); border-radius:8px; overflow:hidden; margin-bottom:18px; }
  .panel-header { background:var(--forest); padding:11px 17px; display:flex; align-items:center; justify-content:space-between; }
  .panel-title { font-family:'Playfair Display',serif; color:var(--gold-light); font-size:14px; font-weight:600; }
  .full-panel { background:white; border:1px solid var(--parchment-dark); border-radius:8px; overflow:hidden; margin-bottom:20px; }

  /* ── TABLE ── */
  .data-table { width:100%; border-collapse:collapse; }
  .data-table th { background:var(--parchment); padding:8px 14px; font-size:10px; letter-spacing:.08em; text-transform:uppercase; color:var(--text-mid); font-weight:600; text-align:left; border-bottom:1px solid var(--parchment-dark); white-space:nowrap; }
  .data-table td { padding:10px 14px; font-size:13px; border-bottom:1px solid var(--parchment-dark); color:var(--text-dark); vertical-align:middle; }
  .data-table tr:last-child td { border-bottom:none; }
  .data-table tr:hover td { background:rgba(245,240,232,.5); }

  /* ── BADGES ── */
  .badge { display:inline-block; padding:2px 8px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
  .badge-paid { background:#e8f5e9; color:#1b5e20; }
  .badge-pending { background:#fff3cd; color:#7a5000; }
  .badge-overdue { background:#fde8e8; color:#8a1f1f; }
  .badge-partial { background:#e8eaf6; color:#1a237e; }
  .badge-shipped { background:#e3f2fd; color:#0d47a1; }
  .badge-delivered { background:#e8f5e9; color:#1b5e20; }
  .badge-intransit { background:#e8eaf6; color:#1a237e; }
  .badge-labelcreated { background:#fff3cd; color:#7a5000; }
  .badge-active { background:#e8f5e9; color:#1b5e20; }
  .badge-inactive { background:#f5f5f5; color:#666; }
  .badge-vip { background:#fff8e1; color:#f57f17; }
  .badge-pregnant { background:#fff3cd; color:#7a5000; }
  .badge-open { background:#fde8e8; color:#8a1f1f; }
  .badge-cycle { background:#e8f4f0; color:#1a5c4e; }
  .badge-flush { background:#e8eaf6; color:#1a237e; }
  .badge-frozen { background:#e3f2fd; color:#0d47a1; }
  .badge-available { background:#e3f2fd; color:#0d47a1; }
  .badge-lowstock { background:#fff3cd; color:#7a5000; }
  .badge-depleted { background:#f5f5f5; color:#888; }

  /* ── FORMS ── */
  .filter-bar { padding:11px 17px; background:var(--parchment); border-bottom:1px solid var(--parchment-dark); display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
  .search-input { flex:1; min-width:130px; padding:6px 11px; border:1px solid var(--parchment-dark); border-radius:4px; font-family:'Crimson Pro',serif; font-size:13px; background:white; color:var(--text-dark); }
  .search-input:focus { outline:2px solid var(--gold); outline-offset:-1px; }
  .filter-select { padding:6px 10px; border:1px solid var(--parchment-dark); border-radius:4px; font-family:'Crimson Pro',serif; font-size:13px; background:white; color:var(--text-dark); }
  .form-row { margin-bottom:13px; }
  .form-label { display:block; font-size:10.5px; letter-spacing:.08em; text-transform:uppercase; color:var(--text-light); font-weight:600; margin-bottom:5px; }
  .form-input { width:100%; padding:8px 11px; border:1px solid var(--parchment-dark); border-radius:4px; font-family:'Crimson Pro',serif; font-size:13px; color:var(--text-dark); background:white; }
  .form-input:focus { outline:2px solid var(--gold); outline-offset:-1px; }
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .form-grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; }

  /* ── MODAL ── */
  .modal-overlay { position:fixed; inset:0; background:rgba(26,46,26,.55); z-index:300; display:flex; align-items:center; justify-content:center; padding:16px; }
  .modal { background:white; border-radius:10px; width:100%; max-width:560px; max-height:92vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.25); }
  .modal-wide { max-width:720px; }
  .modal-header { background:var(--forest); padding:17px 22px; display:flex; align-items:center; justify-content:space-between; border-radius:10px 10px 0 0; position:sticky; top:0; z-index:2; }
  .modal-title { font-family:'Playfair Display',serif; color:var(--gold-light); font-size:17px; font-weight:700; }
  .modal-close { background:none; border:none; color:rgba(201,168,76,.6); font-size:20px; cursor:pointer; }
  .modal-close:hover { color:var(--gold-light); }
  .modal-body { padding:20px 22px; }
  .modal-footer { padding:14px 22px; border-top:1px solid var(--parchment-dark); display:flex; justify-content:flex-end; gap:9px; background:var(--parchment); border-radius:0 0 10px 10px; }

  /* ── DRAWER ── */
  .overlay { position:fixed; inset:0; background:rgba(26,46,26,.45); z-index:200; display:flex; justify-content:flex-end; }
  .drawer { width:460px; background:white; height:100%; overflow-y:auto; box-shadow:-8px 0 32px rgba(0,0,0,.18); }
  .drawer-header { background:var(--forest); padding:19px 22px; position:sticky; top:0; display:flex; align-items:flex-start; justify-content:space-between; z-index:2; }
  .drawer-title { font-family:'Playfair Display',serif; color:var(--gold-light); font-size:18px; font-weight:700; }
  .drawer-subtitle { font-size:12px; color:rgba(201,168,76,.6); font-style:italic; margin-top:3px; }
  .drawer-close { background:none; border:none; color:rgba(201,168,76,.6); font-size:20px; cursor:pointer; }
  .drawer-body { padding:18px 22px; }
  .drawer-section { margin-bottom:20px; }
  .drawer-section-title { font-size:10px; letter-spacing:.1em; text-transform:uppercase; color:var(--text-light); font-weight:600; margin-bottom:9px; padding-bottom:5px; border-bottom:1px solid var(--parchment-dark); }
  .detail-row { display:flex; justify-content:space-between; margin-bottom:7px; font-size:13px; }
  .detail-key { color:var(--text-light); }
  .detail-val { color:var(--text-dark); font-weight:600; }
  .tab-row { display:flex; border-bottom:2px solid var(--parchment-dark); padding:0 18px; background:var(--parchment); overflow-x:auto; }
  .tab { padding:10px 14px; font-family:'Crimson Pro',serif; font-size:13px; cursor:pointer; color:var(--text-light); border-bottom:2px solid transparent; margin-bottom:-2px; transition:all .15s; font-weight:600; white-space:nowrap; }
  .tab:hover { color:var(--forest); }
  .tab.active { color:var(--forest); border-bottom-color:var(--gold); }

  /* ── PAGE HEADER ── */
  /* The page title lives once, in the sticky top bar. This row only carries a
     page's action buttons, right-aligned, and disappears when there are none. */
  .page-header-row { display:flex; align-items:center; justify-content:flex-end; margin-bottom:20px; flex-wrap:wrap; gap:10px; }
  .page-header-row:empty { display:none; }
  .page-h1 { font-family:'Playfair Display',serif; font-size:24px; color:var(--forest); font-weight:700; }
  .page-desc { font-size:13px; color:var(--text-light); font-style:italic; margin-top:2px; }

  /* ── MISC ── */
  .empty-state { padding:36px; text-align:center; color:var(--text-light); font-style:italic; }
  .divider { height:1px; background:var(--parchment-dark); margin:14px 0; }
  .save-toast { position:fixed; bottom:20px; right:20px; background:var(--forest); color:var(--gold-light); padding:9px 18px; border-radius:6px; font-size:12.5px; z-index:500; box-shadow:0 4px 16px rgba(0,0,0,.2); animation:fadeUp .3s ease; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin { to { transform:rotate(360deg); } }
  .spinner-inline { display:inline-block; width:14px; height:14px; border:2px solid rgba(255,255,255,.3); border-top-color:white; border-radius:50%; animation:spin .7s linear infinite; vertical-align:middle; }

  /* ── LOADING SCREEN ── */
  .loading-screen { min-height:100vh; display:flex; align-items:center; justify-content:center; background:var(--forest); flex-direction:column; gap:16px; }
  .loading-spinner { width:40px; height:40px; border:3px solid rgba(201,168,76,.2); border-top-color:var(--gold); border-radius:50%; animation:spin .9s linear infinite; }

  /* ── ARCSTALLION LOGO ── */
  .arcstallion-logo { display:inline-flex; flex-direction:column; align-items:center; font-family:'Manrope',sans-serif; cursor:default; }
  .arcstallion-logo .wordmark { display:flex; align-items:baseline; letter-spacing:-1px; line-height:1; }
  .arcstallion-logo .arc { font-weight:800; color:#C65217; }
  .arcstallion-logo .stallion { font-weight:300; color:#111; }
  .arcstallion-logo.dark .stallion { color:rgba(245,240,232,.75); }

  /* ── LOGIN ── */
  .login-page { min-height:100vh; width:100%; background:linear-gradient(160deg,#0d1f0d 0%,#1a2e1a 45%,#0a1a0a 100%); display:flex; align-items:center; justify-content:center; position:relative; overflow:hidden; }
  .login-bg-pattern { position:absolute; inset:0; pointer-events:none; background-image:radial-gradient(rgba(201,168,76,.06) 1px,transparent 1px); background-size:32px 32px; }
  .login-card { position:relative; z-index:1; width:100%; max-width:420px; background:rgba(255,255,255,.04); border:1px solid rgba(201,168,76,.18); border-radius:14px; overflow:hidden; box-shadow:0 32px 80px rgba(0,0,0,.6); }
  .login-header { padding:32px 32px 24px; text-align:center; border-bottom:1px solid rgba(201,168,76,.12); background:linear-gradient(180deg,rgba(201,168,76,.06) 0%,transparent 100%); }
  .login-sub { font-size:12px; color:rgba(226,201,126,.5); letter-spacing:.1em; text-transform:uppercase; margin-top:12px; }
  .login-body { padding:26px 32px 28px; }
  .login-welcome { font-family:'Playfair Display',serif; font-size:16px; color:rgba(245,240,232,.7); margin-bottom:20px; font-style:italic; text-align:center; }
  .login-field { margin-bottom:15px; }
  .login-label { display:block; font-size:10.5px; letter-spacing:.1em; text-transform:uppercase; color:rgba(201,168,76,.6); font-weight:600; margin-bottom:6px; }
  .login-input { width:100%; padding:10px 13px; background:rgba(255,255,255,.07); border:1px solid rgba(201,168,76,.2); border-radius:6px; color:var(--parchment); font-family:'Crimson Pro',serif; font-size:15px; transition:all .2s; }
  .login-input::placeholder { color:rgba(245,240,232,.25); }
  .login-input:focus { outline:none; border-color:var(--gold); background:rgba(255,255,255,.1); }
  .login-input.error { border-color:var(--red); }
  .login-error { margin-top:-10px; margin-bottom:13px; padding:8px 11px; background:rgba(192,57,43,.15); border:1px solid rgba(192,57,43,.35); border-radius:5px; font-size:13px; color:#ff8a80; }
  .login-btn { width:100%; padding:12px; background:linear-gradient(135deg,var(--gold-dim),var(--gold)); color:var(--forest); border:none; border-radius:6px; font-family:'Playfair Display',serif; font-size:16px; font-weight:700; cursor:pointer; transition:all .2s; margin-top:4px; }
  .login-btn:hover { background:linear-gradient(135deg,var(--gold),var(--gold-light)); transform:translateY(-1px); }
  .login-btn:disabled { opacity:.5; cursor:not-allowed; transform:none; }
  .login-footer { padding:14px 32px 18px; text-align:center; font-size:11.5px; color:rgba(201,168,76,.35); border-top:1px solid rgba(201,168,76,.1); }

  /* ── USER MENU ── */
  .user-menu-btn { display:flex; align-items:center; gap:8px; padding:5px 12px 5px 8px; background:rgba(201,168,76,.1); border:1px solid rgba(201,168,76,.2); border-radius:20px; cursor:pointer; transition:all .15s; font-family:'Crimson Pro',serif; }
  .user-menu-btn:hover { background:rgba(201,168,76,.18); }
  .user-avatar { width:28px; height:28px; border-radius:50%; background:var(--forest); border:1px solid rgba(201,168,76,.4); display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:12px; color:var(--gold-light); font-weight:700; flex-shrink:0; }
  .user-menu-name { font-size:13px; color:var(--gold-light); font-weight:600; }
  .user-menu-role { font-size:10.5px; color:rgba(201,168,76,.55); }
  .user-dropdown { position:absolute; top:calc(100% + 8px); right:0; width:230px; background:var(--forest); border:1px solid rgba(201,168,76,.2); border-radius:8px; box-shadow:0 12px 40px rgba(0,0,0,.4); z-index:200; overflow:hidden; }
  .user-dropdown-header { padding:14px 16px; border-bottom:1px solid rgba(201,168,76,.15); background:rgba(201,168,76,.06); }
  .user-dropdown-name { font-family:'Playfair Display',serif; color:var(--gold-light); font-size:14px; font-weight:700; }
  .user-dropdown-email { font-size:11.5px; color:rgba(201,168,76,.5); margin-top:2px; }
  .user-dropdown-item { padding:10px 16px; font-size:13px; color:rgba(245,240,232,.7); cursor:pointer; display:flex; align-items:center; gap:9px; font-family:'Crimson Pro',serif; transition:background .15s; border-bottom:1px solid rgba(201,168,76,.08); }
  .user-dropdown-item:last-child { border-bottom:none; }
  .user-dropdown-item:hover { background:rgba(201,168,76,.1); color:var(--parchment); }
  .user-dropdown-item.logout { color:rgba(255,100,100,.75); }
  .user-dropdown-item.logout:hover { background:rgba(192,57,43,.15); }
  .role-badge-admin { background:rgba(201,168,76,.2); color:var(--gold-light); padding:2px 7px; border-radius:10px; font-size:10px; font-weight:600; }
  .role-badge-staff { background:rgba(46,125,110,.2); color:#80cbc4; padding:2px 7px; border-radius:10px; font-size:10px; font-weight:600; }
  .role-badge-viewer { background:rgba(107,63,160,.2); color:#ce93d8; padding:2px 7px; border-radius:10px; font-size:10px; font-weight:600; }

  /* ── CYCLE TIMELINE ── */
  .cycle-row { display:flex; align-items:center; gap:9px; margin-bottom:8px; }
  .cycle-mare-name { width:100px; font-size:12px; font-weight:600; color:var(--forest); flex-shrink:0; }
  .cycle-bar-track { flex:1; height:14px; background:var(--parchment-dark); border-radius:3px; overflow:visible; position:relative; }
  .cycle-segment { position:absolute; height:100%; border-radius:3px; }
  .seg-anestrus{background:#aaa;} .seg-diestrus{background:#7fb3d3;} .seg-proestrus{background:#f0a500;} .seg-estrus{background:#c0392b;} .seg-ovulation{background:#1a5c4e;}
  .cycle-day-marker { position:absolute; top:-3px; width:2px; height:20px; background:var(--forest); border-radius:2px; z-index:2; }
  .cycle-day-label { position:absolute; top:-16px; font-size:9px; color:var(--forest); font-weight:700; transform:translateX(-50%); }

  /* ── ALERT LIST ── */
  .alert-list { list-style:none; }
  .alert-item { padding:10px 17px; border-bottom:1px solid var(--parchment-dark); display:flex; align-items:flex-start; gap:9px; }
  .alert-item:last-child { border-bottom:none; }
  .alert-dot { width:7px; height:7px; border-radius:50%; margin-top:5px; flex-shrink:0; }
  .dot-urgent{background:var(--red);} .dot-warn{background:#e67e22;} .dot-info{background:var(--teal);}
  .alert-text { font-size:13px; color:var(--text-dark); line-height:1.4; }
  .alert-sub { font-size:11px; color:var(--text-light); font-style:italic; }

  /* ── CUSTOMER CARDS ── */
  .customer-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:14px; margin-bottom:20px; }
  .customer-card { background:white; border:1px solid var(--parchment-dark); border-radius:8px; overflow:hidden; cursor:pointer; transition:box-shadow .2s; }
  .customer-card:hover { box-shadow:0 4px 14px rgba(0,0,0,.08); border-color:var(--gold-dim); }
  .customer-card-head { padding:14px 16px 12px; display:flex; align-items:flex-start; gap:11px; border-bottom:1px solid var(--parchment-dark); }
  .customer-avatar { width:40px; height:40px; border-radius:50%; background:var(--forest); display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:16px; color:var(--gold-light); font-weight:700; flex-shrink:0; }
  .customer-name { font-family:'Playfair Display',serif; font-size:14px; font-weight:700; color:var(--forest); margin-bottom:2px; }
  .customer-body { padding:12px 16px; }
  .customer-row { display:flex; justify-content:space-between; font-size:12px; margin-bottom:5px; }

  /* ── HORSE TILES ── */
  .horse-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(195px,1fr)); gap:14px; }
  .horse-card { background:white; border:1px solid var(--parchment-dark); border-radius:8px; overflow:hidden; cursor:pointer; transition:box-shadow .2s,border-color .2s; }
  .horse-card:hover { box-shadow:0 6px 18px rgba(0,0,0,.1); border-color:var(--gold-dim); }
  .horse-photo { width:100%; height:145px; object-fit:cover; display:block; }
  .horse-photo-placeholder { width:100%; height:145px; background:linear-gradient(160deg,var(--forest) 0%,#1e3d1e 100%); display:flex; align-items:center; justify-content:center; font-size:54px; }
  .horse-card-body { padding:11px 13px 13px; }
  .horse-name { font-family:'Playfair Display',serif; font-size:14px; font-weight:700; color:var(--forest); margin-bottom:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .horse-detail { font-size:11.5px; color:var(--text-light); margin-bottom:5px; }
  .upload-photo-btn { display:inline-flex; align-items:center; gap:5px; font-size:11.5px; color:var(--forest); background:#e8f0e8; border:1px dashed #a0bfa0; border-radius:5px; padding:6px 10px; cursor:pointer; width:100%; justify-content:center; }
  .upload-photo-btn:hover { background:#d4e8d4; }

  /* ── SHIPMENT CALENDAR ── */
  .cal-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:3px; }
  .cal-dow { text-align:center; font-size:10px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:var(--text-light); padding:5px 0 8px; }
  .cal-day { min-height:52px; border-radius:6px; padding:5px 6px 4px; border:1px solid transparent; cursor:pointer; transition:background .15s,border-color .15s; position:relative; }
  .cal-day:hover { background:var(--parchment); border-color:var(--parchment-dark); }
  .cal-day.has-events { background:var(--parchment); }
  .cal-day.selected { background:#e8f0e8; border-color:var(--forest) !important; }
  .cal-day.today .cal-day-num { background:var(--forest); color:white; border-radius:50%; width:20px; height:20px; display:flex; align-items:center; justify-content:center; }
  .cal-day.other-month { opacity:.3; cursor:default; }
  .cal-day-num { font-size:12px; font-weight:600; color:var(--text-dark); width:20px; height:20px; display:flex; align-items:center; justify-content:center; }
  .cal-dots { display:flex; gap:2px; flex-wrap:wrap; margin-top:3px; }
  .cal-dot { width:6px; height:6px; border-radius:50%; flex-shrink:0; }
  .cal-dot.order { background:var(--gold); }
  .cal-dot.shipped { background:var(--blue); }
  .cal-dot.delivery { background:#1a7f50; }
  .cal-dot.delivered { background:#9b9b9b; }

  /* ── GLOBAL SEARCH ── */
  .global-search { position:relative; padding:10px 14px; border-bottom:1px solid rgba(201,168,76,.15); }
  .global-search input { background:rgba(255,255,255,.07); border:1px solid rgba(201,168,76,.2); border-radius:6px; padding:6px 10px 6px 30px; width:100%; font-size:12px; color:var(--parchment); font-family:inherit; outline:none; }
  .global-search input::placeholder { color:rgba(201,168,76,.35); }
  .global-search input:focus { border-color:rgba(201,168,76,.45); background:rgba(255,255,255,.1); }
  .search-icon { position:absolute; left:23px; top:50%; transform:translateY(-50%); font-size:12px; pointer-events:none; }
  .search-dropdown { position:absolute; left:14px; right:14px; top:calc(100% - 4px); background:#1a2e1a; border:1px solid rgba(201,168,76,.25); border-radius:6px; z-index:500; box-shadow:0 8px 24px rgba(0,0,0,.45); overflow:hidden; }
  .search-group-label { padding:5px 12px; font-size:9px; letter-spacing:.1em; text-transform:uppercase; color:rgba(201,168,76,.4); border-bottom:1px solid rgba(201,168,76,.1); }
  .search-result { padding:8px 12px; cursor:pointer; font-size:12.5px; color:var(--parchment); display:flex; justify-content:space-between; align-items:center; }
  .search-result:hover { background:rgba(201,168,76,.1); }

  /* ── BREEDING LOG ── */
  .breed-tab-bar { display:flex; gap:0; border-bottom:2px solid var(--parchment-dark); margin-bottom:14px; }
  .breed-tab { padding:7px 16px; font-size:12px; font-weight:600; letter-spacing:.04em; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-2px; color:var(--text-light); background:none; border-top:none; border-left:none; border-right:none; }
  .breed-tab.active { color:var(--forest); border-bottom-color:var(--forest); }
  .breed-quick-actions { display:flex; flex-wrap:wrap; gap:7px; margin-bottom:14px; }
  .breed-quick-btn { padding:6px 12px; font-size:11.5px; border-radius:6px; border:1px solid var(--parchment-dark); background:white; cursor:pointer; font-family:'Crimson Pro',serif; display:flex; align-items:center; gap:5px; color:var(--text-mid); transition:all .15s; }
  .breed-quick-btn:hover { background:var(--parchment); border-color:var(--gold); color:var(--forest); }
  .breed-event { padding:10px 12px; border-left:3px solid var(--parchment-dark); margin-bottom:8px; background:var(--parchment); border-radius:0 6px 6px 0; }
  .breed-event.teasing { border-left-color:#e6a817; }
  .breed-event.palpation { border-left-color:var(--blue); }
  .breed-event.icsi { border-left-color:#9c27b0; }
  .breed-event.breeding { border-left-color:#1a7f50; }
  .breed-event.medication { border-left-color:#9333ea; }
  .breed-event.pregnancy_check { border-left-color:#e44; }
  .breed-event.note { border-left-color:var(--text-light); }
  .breed-event-date { font-size:10px; color:var(--text-light); letter-spacing:.05em; text-transform:uppercase; }
  .breed-event-title { font-size:13px; font-weight:700; color:var(--forest); margin-top:2px; }
  .breed-event-detail { font-size:12px; color:var(--text-mid); margin-top:2px; }
  .breed-protocol-card { border:1px solid var(--parchment-dark); border-radius:8px; padding:14px; margin-bottom:10px; cursor:pointer; transition:all .15s; }
  .breed-protocol-card:hover { border-color:var(--gold); background:rgba(201,168,76,.04); }
  .breed-protocol-card.active { border-color:var(--forest); background:rgba(26,60,26,.04); }
  .breed-protocol-step { display:flex; align-items:flex-start; gap:10px; padding:8px 0; border-bottom:1px solid var(--parchment-dark); }
  .breed-protocol-step:last-child { border-bottom:none; }
  .breed-step-check { width:20px; height:20px; border-radius:50%; border:2px solid var(--parchment-dark); display:flex; align-items:center; justify-content:center; font-size:11px; flex-shrink:0; margin-top:1px; }
  .breed-step-check.done { background:var(--forest); border-color:var(--forest); color:white; }
  .breed-mgmt-grid { display:grid; grid-template-columns:300px 1fr; gap:18px; }
  @media(max-width:900px) { .breed-mgmt-grid { grid-template-columns:1fr; } }

  /* ── SHIPMENT ── */
  .shipment-row { display:flex; align-items:center; gap:12px; padding:12px 15px; border-bottom:1px solid var(--parchment-dark); }
  .shipment-row:last-child { border-bottom:none; }
  .shipment-info { flex:1; }
  .shipment-name { font-size:13.5px; font-weight:700; color:var(--forest); }
  .shipment-detail { font-size:11.5px; color:var(--text-light); font-style:italic; }
  .shipment-tracking { font-family:'Courier New',monospace; font-size:11px; color:var(--blue); }
  .progress-bar-wrap { height:6px; background:var(--parchment-dark); border-radius:3px; overflow:hidden; margin-top:5px; }
  .progress-bar { height:100%; border-radius:3px; }
`;

// ─── PHOTO UPLOAD ─────────────────────────────────────────────────────────────
//
// Photos go into `<org_id>/<uuid>.<ext>` and this returns the PATH, not a URL.
//
// Both halves of that matter. Until 2026-09-24 photos were bare UUIDs at the
// bucket root, which meant the storage policies had no org in the key to scope
// on -- so any farm's Admin could delete any other farm's photos, and an
// unauthenticated caller holding the publishable key could list every filename
// in the bucket. The folder IS the tenant boundary now.
//
// And the bucket is private, so the only way to display a photo is a signed URL
// that expires. An expiring URL must never be written to the database, which is
// why photo_url holds a path and HorsePhoto resolves it at render time.
async function uploadHorsePhoto(file, orgId) {
  if (!orgId) throw new Error("Cannot upload a photo without a farm.");
  assertWritable();
  const ext = file.name.split(".").pop().toLowerCase();
  const path = `${orgId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from("horse-photos").upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  return path;
}

// Legacy public URLs written before the bucket was locked down, plus photos
// hosted on a farm's own website (two Capital Quarter Horses stallions use
// these). Anything already a URL is rendered as-is; anything else is a storage
// path that needs signing.
const LEGACY_PUBLIC_PREFIX = "/storage/v1/object/public/horse-photos/";

function horsePhotoPath(value) {
  if (!value) return null;
  if (value.startsWith("http")) {
    const at = value.indexOf(LEGACY_PUBLIC_PREFIX);
    return at === -1 ? null : decodeURIComponent(value.slice(at + LEGACY_PUBLIC_PREFIX.length));
  }
  return value;
}

/**
 * Renders a horse photo from either a storage path or an external URL.
 *
 * Signed URLs are minted per render and live one hour. They are deliberately
 * not cached in state beyond the component: a stale signed URL renders as a
 * broken image, which looks like data loss to a customer.
 */
function HorsePhoto({ value, alt, className, style, onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    const path = horsePhotoPath(value);
    if (!value) { setSrc(null); return; }
    if (value.startsWith("http") && !path) { setSrc(value); return; }   // external host
    if (!path) { setSrc(null); return; }
    (async () => {
      const { data, error } = await supabase.storage.from("horse-photos").createSignedUrl(path, 3600);
      if (!alive) return;
      if (error) { console.error("Could not sign photo", path, error.message); setSrc(null); return; }
      setSrc(data.signedUrl);
    })();
    return () => { alive = false; };
  }, [value]);
  if (!src) return null;
  return <img src={src} alt={alt} className={className} style={style} onClick={onClick} />;
}

// ─── FARRIER PHOTO DROP ZONE ─────────────────────────────────────────────────
function FarrierPhotoZone({ record, onUpdate, orgId }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const photos = record.photo_urls || [];

  const handleFiles = async (files) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith("image/"));
    if (imageFiles.length === 0) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of imageFiles) {
        const url = await uploadHorsePhoto(file, orgId);
        newUrls.push(url);
      }
      const updated = [...photos, ...newUrls];
      await dbUpdate("farrier_records", record.id, { photo_urls: updated });
      onUpdate(record.id, updated);
    } catch (e) { alert("Upload failed: " + e.message); }
    setUploading(false);
  };

  const removePhoto = async (url) => {
    if (!window.confirm("Remove this photo?")) return;
    const updated = photos.filter(p => p !== url);
    await dbUpdate("farrier_records", record.id, { photo_urls: updated });
    onUpdate(record.id, updated);
  };

  return (
    <div style={{ marginTop: 8 }}>
      {photos.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {photos.map((url, i) => (
            <div key={i} style={{ position: "relative", width: 72, height: 72, borderRadius: 6, overflow: "hidden", border: "1px solid rgba(0,0,0,.1)", cursor: "pointer" }}>
              <HorsePhoto value={url} alt={`Hoof ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} onClick={() => setViewingPhoto(url)} />
              <button onClick={(e) => { e.stopPropagation(); removePhoto(url); }} style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,.6)", color: "white", border: "none", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>✕</button>
            </div>
          ))}
        </div>
      )}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => { const input = document.createElement("input"); input.type = "file"; input.accept = "image/*"; input.multiple = true; input.onchange = e => handleFiles(e.target.files); input.click(); }}
        style={{ border: `2px dashed ${dragging ? "var(--forest)" : "rgba(0,0,0,.15)"}`, borderRadius: 8, padding: "10px 14px", textAlign: "center", cursor: "pointer", background: dragging ? "rgba(45,90,61,.06)" : "transparent", transition: "all .2s" }}
      >
        {uploading
          ? <div style={{ fontSize: 12, color: "var(--forest)" }}>Uploading...</div>
          : <div style={{ fontSize: 11, color: "var(--text-light)" }}>📷 Drop hoof photos here or click to upload</div>
        }
      </div>
      {viewingPhoto && (
        <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={() => setViewingPhoto(null)}>
          <div style={{ maxWidth: "90vw", maxHeight: "90vh", position: "relative" }} onClick={e => e.stopPropagation()}>
            <HorsePhoto value={viewingPhoto} alt="Hoof photo" style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 8, boxShadow: "0 4px 30px rgba(0,0,0,.4)" }} />
            <button onClick={() => setViewingPhoto(null)} style={{ position: "absolute", top: -12, right: -12, width: 30, height: 30, borderRadius: "50%", background: "rgba(0,0,0,.7)", color: "white", border: "none", fontSize: 16, cursor: "pointer" }}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── GLOBAL SEARCH ────────────────────────────────────────────────────────────
function GlobalSearch({ orgId, setPage }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ mares: [], stallions: [], customers: [] });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q.trim() || q.length < 2) { setResults({ mares: [], stallions: [], customers: [] }); setOpen(false); return; }
    const timer = setTimeout(async () => {
      const [m, s, c] = await Promise.all([
        supabase.from("mares").select("id,name,breed,status").ilike("name", `%${q}%`).eq("org_id", orgId).limit(4),
        supabase.from("stallions").select("id,name,breed,status").ilike("name", `%${q}%`).eq("org_id", orgId).limit(4),
        supabase.from("customers").select("id,name,company,type").ilike("name", `%${q}%`).eq("org_id", orgId).limit(4),
      ]);
      setResults({ mares: m.data || [], stallions: s.data || [], customers: c.data || [] });
      setOpen(true);
    }, 280);
    return () => clearTimeout(timer);
  }, [q, orgId]);

  const total = results.mares.length + results.stallions.length + results.customers.length;
  const go = (pg) => { setPage(pg); setQ(""); setOpen(false); };

  return (
    <div className="global-search">
      <span className="search-icon" style={{ color: "rgba(201,168,76,.45)" }}>🔍</span>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search mares, stallions, customers…"
        onBlur={() => setTimeout(() => setOpen(false), 180)} onFocus={() => q.length >= 2 && total > 0 && setOpen(true)} />
      {open && total > 0 && (
        <div className="search-dropdown">
          {results.mares.length > 0 && <>
            <div className="search-group-label">Mares</div>
            {results.mares.map(m => <div key={m.id} className="search-result" onMouseDown={() => go("mares")}><span>🐴 {m.name}</span><span style={{ fontSize: 11, color: "rgba(201,168,76,.5)" }}>{m.status}</span></div>)}
          </>}
          {results.stallions.length > 0 && <>
            <div className="search-group-label">Stallions</div>
            {results.stallions.map(s => <div key={s.id} className="search-result" onMouseDown={() => go("stallions")}><span>🐎 {s.name}</span><span style={{ fontSize: 11, color: "rgba(201,168,76,.5)" }}>{s.status}</span></div>)}
          </>}
          {results.customers.length > 0 && <>
            <div className="search-group-label">Customers</div>
            {results.customers.map(c => <div key={c.id} className="search-result" onMouseDown={() => go("customers")}><span>👤 {c.name}</span><span style={{ fontSize: 11, color: "rgba(201,168,76,.5)" }}>{c.type}</span></div>)}
          </>}
        </div>
      )}
    </div>
  );
}

// ─── ARCSTALLION LOGO ─────────────────────────────────────────────────────────
function ArcStallionLogo({ size = "sm", dark = false }) {
  // The wordmark alone. The "Solutions" tagline and its flanking rules were
  // removed 2026-09-25 along with the name itself: the company is ArcStallion LLC.
  const sizes = { xs: { wordmark:11 }, sm: { wordmark:14 }, md: { wordmark:20 }, lg: { wordmark:28 } };
  const s = sizes[size] || sizes.sm;
  return (
    <div className={`arcstallion-logo${dark ? " dark" : ""}`}>
      <div className="wordmark" style={{ fontSize: s.wordmark }}>
        <span className="arc">Arc</span>
        <span className="stallion">Stallion</span>
      </div>
    </div>
  );
}

// ─── SUPABASE DATA HOOKS ──────────────────────────────────────────────────────
// Generic hook for fetching any table filtered by org_id
// `columns` defaults to "*" so the 60-odd other call sites are unaffected. It
// exists so org_integrations can be read without its `credentials` column,
// which the browser has no business holding.
const INTEGRATION_COLUMNS = "id, org_id, provider, provider_type, config, is_active, connected_at, connected_by, last_sync, sync_status, created_at, updated_at";

function useSupabaseTable(table, orgId, deps = [], columns = "*") {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const { data: rows, error: fetchError } = await supabase
      .from(table)
      .select(columns)
      .eq("org_id", orgId)
      .order("created_at", { ascending: false });

    // This used to be `if (!error) setData(...)` and nothing else, so a failed
    // query rendered an empty page with no message and nothing in the console.
    // Every support call started as "it's just blank".
    if (fetchError) {
      console.error(`[useSupabaseTable] ${table} failed:`, fetchError);
      setError(fetchError);
    } else {
      setError(null);
      setData(rows || []);
    }
    setLoading(false);
  }, [table, orgId, columns]);

  useEffect(() => { fetch(); }, [fetch, ...deps]);

  return { data, setData, loading, error, refetch: fetch };
}

// Shown in place of a list that failed to load, so an empty screen is never
// ambiguous between "no records yet" and "something broke".
function LoadError({ error, onRetry, what = "this" }) {
  if (!error) return null;
  return (
    <div style={{ background: "rgba(180,60,60,.08)", border: "1px solid rgba(180,60,60,.3)", borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
      <div style={{ fontWeight: 600, color: "#c0504d", marginBottom: 4 }}>Couldn't load {what}</div>
      <div style={{ fontSize: 13, color: "rgba(80,40,40,.8)", marginBottom: 10 }}>
        {error.message || "Something went wrong talking to the server."}
      </div>
      {onRetry && <button className="btn btn-secondary" onClick={onRetry}>Try again</button>}
    </div>
  );
}

// ─── SUPABASE CRUD HELPERS ────────────────────────────────────────────────────
// The farm's date, not UTC's. toISOString() is UTC, so from 7pm Central every
// "today" default in the app -- collection dates, foaling records, farrier
// visits, signed contracts -- was already tomorrow. (2026-09-25)
function localToday(d = new Date()) {
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ─── Pregnancy helpers ───────────────────────────────────────────────────────
// A pregnancy is a foaling_records row that is still Expected/Overdue. Mare
// gestation averages about 340 days; the farm overrides the due date when the
// vet gives a different one.
const GESTATION_DAYS = 340;
function addDaysISO(iso, n) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return localToday(new Date(y, m - 1, d + n));
}
function fmtShortDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
}
function daysFromToday(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date();
  return Math.round((new Date(y, m - 1, d) - new Date(t.getFullYear(), t.getMonth(), t.getDate())) / 86400000);
}
function isActivePregnancy(f) {
  if (!f || f.actual_foaling_date) return false;
  const s = (f.status || "").toLowerCase();
  return s === "expected" || s === "overdue";
}
// "Sire x Dam". dam_name is the genetic dam (a donor, for an embryo transfer);
// empty means the mare carrying the foal is also its dam.
function foalCross(f, carrierName) {
  return [f.stallion_name, f.dam_name || carrierName || f.mare_name].filter(Boolean).join(" x ");
}
// An embryo transfer sets the due date from the transfer, not a breeding: the
// embryo is already `age` days along when it goes in (fresh flushes are
// usually day 8, frozen embryos day 6 or 7). Own foal: breeding date + 340.
const DEFAULT_EMBRYO_AGE = 8;
const EMBRYO_AGES = [6, 7, 8, 9];
function dueFromDates({ transfer_date, embryo_age, breeding_date }) {
  if (transfer_date) return addDaysISO(transfer_date, GESTATION_DAYS - (parseInt(embryo_age) || DEFAULT_EMBRYO_AGE));
  if (breeding_date) return addDaysISO(breeding_date, GESTATION_DAYS);
  return "";
}
function recordDue(f) {
  return dueFromDates({ transfer_date: f.transfer_date, embryo_age: f.embryo_age_days, breeding_date: f.breeding_date });
}
const emptyPreg = { id: null, stallion_name: "", dam_mode: "donor", dam_name: "", breeding_date: "", transfer_date: "", embryo_age: String(DEFAULT_EMBRYO_AGE), embryo_id: null, due_date: "", due_auto: true, foal_name: "" };
function pregFromRecord(f) {
  if (!f) return { ...emptyPreg };
  return {
    id: f.id, stallion_name: f.stallion_name || "", dam_mode: f.dam_name ? "donor" : "self", dam_name: f.dam_name || "",
    breeding_date: f.breeding_date || "", transfer_date: f.transfer_date || "",
    embryo_age: String(f.embryo_age_days || DEFAULT_EMBRYO_AGE), embryo_id: f.embryo_id || null,
    due_date: f.due_date || "",
    due_auto: !f.due_date || f.due_date === recordDue(f),
    foal_name: f.foal_name || "",
  };
}
// Fill a pregnancy from an embryo logged on Embryo Transfer.
function pregFromEmbryo(p, e) {
  const next = { ...p, dam_mode: "donor", dam_name: e.mare || p.dam_name, stallion_name: e.stallion || p.stallion_name,
    transfer_date: e.transfer_date || p.transfer_date, embryo_age: String(e.age_days || DEFAULT_EMBRYO_AGE), embryo_id: e.id };
  if (next.due_auto || !next.due_date) { const d = dueFromDates(next); if (d) { next.due_date = d; next.due_auto = true; } }
  return next;
}
function pregnancyRow(p) {
  const t = v => (v || "").trim() || null;
  const et = p.dam_mode === "donor";
  return {
    stallion_name: t(p.stallion_name), dam_name: et ? t(p.dam_name) : null,
    breeding_date: p.breeding_date || null, due_date: p.due_date || null, foal_name: t(p.foal_name),
    transfer_date: et ? (p.transfer_date || null) : null,
    embryo_age_days: et && p.transfer_date ? (parseInt(p.embryo_age) || DEFAULT_EMBRYO_AGE) : null,
    embryo_id: et ? (p.embryo_id || null) : null,
  };
}
// "Transferred 5/27/26 (day 8 embryo)" / "Bred 5/19/26"
function pregDatesLine(f) {
  const parts = [];
  if (f.transfer_date) parts.push(`Transferred ${fmtShortDate(f.transfer_date)} (day ${f.embryo_age_days || DEFAULT_EMBRYO_AGE} embryo)`);
  if (f.breeding_date) parts.push(`Bred ${fmtShortDate(f.breeding_date)}`);
  return parts.join(" · ");
}

// ─── Read-only after a subscription ends (stage 2 billing, 2026-09-26) ───────
// The DATABASE is the guard: auth_role() answers "Viewer" for everyone on a
// read-only farm, so RLS refuses every write whatever this file does. This
// flag only turns that refusal into a sentence a breeder understands. It
// matters most for deletes: RLS refuses a delete SILENTLY (0 rows, no error),
// so without this a deleted row would vanish from the screen and come back.
// App sets it from my_farm_access(); see farm_access() in the migration.
let FARM_READ_ONLY = false;
const READ_ONLY_TEXT = "Your farm is read-only because its subscription has ended. You can still view and download everything. Subscribe again from Settings > Billing to make changes.";
function assertWritable() {
  if (FARM_READ_ONLY) throw new Error(READ_ONLY_TEXT);
}

async function dbInsert(table, row) {
  assertWritable();
  const { data, error } = await supabase.from(table).insert(row).select().single();
  if (error) throw error;
  return data;
}
async function dbUpdate(table, id, updates) {
  assertWritable();
  const { error } = await supabase.from(table).update(updates).eq("id", id);
  if (error) throw error;
}
// Deletes are Admin-only in RLS (every table's _delete_admin_only policy), and
// RLS refuses a Staff delete SILENTLY: 0 rows, no error. Before 2026-09-29 the
// screen dropped the row anyway and it came back on the next load (Capital
// Quarter Horses ticket). .select("id") makes the refusal visible.
// CAN_DELETE is set by App from the signed-in role; it hides Delete buttons for
// non-Admins. Not security, the policy is.
let CAN_DELETE = false;
function canDelete() { return CAN_DELETE; }
const ADMIN_DELETE_TEXT = "Only your farm's Admin can delete records. Ask your Admin to delete it, or to make you an Admin in Settings > Team.";
async function dbDelete(table, id) {
  assertWritable();
  const { data, error } = await supabase.from(table).delete().eq("id", id).select("id");
  if (error) {
    if (error.code === "23503") throw new Error("This can't be deleted yet because other records still use it (for example orders or shipments). Delete those first.");
    throw error;
  }
  // An Admin getting 0 rows means it was already gone; anyone else was refused.
  if ((!data || data.length === 0) && !CAN_DELETE) throw new Error(ADMIN_DELETE_TEXT);
}
// Every delete button: ask first, run, and say why if it was refused.
async function confirmDelete(message, run) {
  if (!window.confirm(message)) return false;
  try { await run(); return true; }
  catch (e) { alert(e.message); return false; }
}

// ─── EDGE FUNCTION HELPER ─────────────────────────────────────────────────────
// The ONLY way the app should call an edge function.
//
// Sends the signed-in user's access token, never an API key. Functions derive
// org_id from this token, so callers must not pass org_id in the body — doing
// so is what let anyone on the internet send mail as any tenant
// (AUDIT_REPORT.md S3).
const FUNCTIONS_URL = "https://cshfvhzmvkzrvlyrnfap.supabase.co/functions/v1";

async function callFunction(name, body) {
  const { data: { session } } = await supabase.auth.getSession();
  console.log("[callFunction]", name, "session?", !!session, "token prefix:", session?.access_token?.slice(0, 20));
  if (!session) throw new Error("Not signed in");
  const resp = await fetch(`${FUNCTIONS_URL}/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(data.error || resp.statusText);
    err.code = data.code;   // lets callers distinguish causes, not just messages
    throw err;
  }
  return data;
}

// ─── HELP & FEEDBACK ──────────────────────────────────────────────────────────
// One form behind every "tell us" door: the sidebar row, the user menu's
// Support item and the crash screen. It posts to the send-feedback function,
// which mails support@arcstallion.com with Reply-To set to the sender, so an
// answer is just a reply.
//
// Not a mailto: link. For anyone on Gmail in a browser -- most farm staff --
// mailto opens the wrong app or nothing at all, and the message never gets
// written. Who is writing comes from their session on the server; the browser
// only sends what they typed plus the page, browser and build.
const FEEDBACK_KINDS = [
  { id: "problem",  label: "Something's not working", prompt: "Tell us what happened" },
  { id: "idea",     label: "I have an idea",          prompt: "What would make ArcStallion better for you?" },
  { id: "question", label: "A question",              prompt: "What would you like to know?" },
];
// Vercel exposes the commit to CRA builds as REACT_APP_VERCEL_GIT_COMMIT_SHA.
const APP_VERSION = (process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || "").slice(0, 7) || "local";

function FeedbackModal({ onClose, page = "", initialKind = "problem", errorText = "" }) {
  const [kind, setKind] = useState(initialKind);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [err, setErr] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setEmail(data?.session?.user?.email || ""));
  }, []);

  const current = FEEDBACK_KINDS.find(k => k.id === kind) || FEEDBACK_KINDS[0];
  const busy = status === "sending";

  const send = async () => {
    if (!message.trim() || busy) return;
    setStatus("sending"); setErr("");
    try {
      await callFunction("send-feedback", {
        kind, message: message.trim(), page,
        user_agent: navigator.userAgent, app_version: APP_VERSION, error: errorText,
      });
      setStatus("sent");
    } catch (e) {
      setErr(e.message === "Not signed in"
        ? "You're signed out, so this can't send from here. Please email support@arcstallion.com."
        : (e.message || "Couldn't send just now. Please email support@arcstallion.com."));
      setStatus("error");
    }
  };

  const chip = (active) => ({
    padding: "6px 12px", borderRadius: 16, fontFamily: "'Crimson Pro',serif", fontSize: 13, cursor: "pointer",
    border: `1px solid ${active ? "var(--forest)" : "var(--parchment-dark)"}`,
    background: active ? "var(--forest)" : "white", color: active ? "var(--gold-light)" : "var(--text-mid)",
  });

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="feedback-title">
        <div className="modal-header">
          <div className="modal-title" id="feedback-title">Help &amp; feedback</div>
          <button className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>
        {status === "sent" ? (
          <div className="modal-body" style={{ textAlign: "center", padding: "34px 26px" }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: "var(--forest)", marginBottom: 8 }}>Thanks, it's on its way.</div>
            <div style={{ fontSize: 14, color: "var(--text-mid)", lineHeight: 1.6 }}>
              A real person reads every message.{email && <> We'll reply to you at <b>{email}</b>.</>}
            </div>
          </div>
        ) : (
          <div className="modal-body">
            <div style={{ fontSize: 14, color: "var(--text-mid)", marginBottom: 10 }}>What's this about?</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
              {FEEDBACK_KINDS.map(k => (
                <button key={k.id} type="button" style={chip(kind === k.id)} aria-pressed={kind === k.id}
                  onClick={() => setKind(k.id)} disabled={busy}>{k.label}</button>
              ))}
            </div>
            <label className="form-label" htmlFor="feedback-message">{current.prompt}</label>
            <textarea id="feedback-message" className="form-input" rows={6} maxLength={5000} autoFocus
              value={message} onChange={e => setMessage(e.target.value)} disabled={busy} style={{ resize: "vertical" }} />
            <div style={{ fontSize: 12, color: "var(--text-light)", marginTop: 10, lineHeight: 1.5 }}>
              We'll include the page you're on and your browser{errorText ? ", plus the error details," : ""} so you don't have to explain it.
              {email && <> We'll reply to you at <b>{email}</b>.</>}
            </div>
            {status === "error" && <div role="alert" style={{ marginTop: 12, fontSize: 13, color: "var(--red)" }}>{err}</div>}
          </div>
        )}
        <div className="modal-footer">
          {status === "sent"
            ? <button className="btn btn-primary" onClick={onClose}>Close</button>
            : <>
                <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
                <button className="btn btn-primary" onClick={send} disabled={!message.trim() || busy}>{busy ? "Sending…" : "Send"}</button>
              </>}
        </div>
      </div>
    </div>
  );
}

// ─── WHAT'S NEW ───────────────────────────────────────────────────────────────
// Customer-facing changelog. src/changelog.json is the single source: this
// panel, and the monthly "What's new in ArcStallion" email built by
// scripts/build-update-email.mjs. Newest entry first. Write entries in the
// farm's language (what they can now do), not ours (what we changed). Add one
// in the same commit as any change a customer would notice.
//
// "Seen" is per browser in localStorage: a convenience, not a record. If
// storage is blocked we treat everything as seen rather than nag forever.
const WHATS_NEW_KEY = "arcstallion.whatsNewSeen";
const latestChangeId = CHANGELOG[0]?.id || "";
function hasUnseenChanges() {
  if (!latestChangeId) return false;
  try { return localStorage.getItem(WHATS_NEW_KEY) !== latestChangeId; } catch { return false; }
}
function markChangesSeen() {
  try { localStorage.setItem(WHATS_NEW_KEY, latestChangeId); } catch { /* private window */ }
}
const CHANGE_TYPES = {
  new:      { label: "New",      bg: "#e8f5e9", fg: "#166534" },
  improved: { label: "Improved", bg: "#e3f2fd", fg: "#1e40af" },
  fixed:    { label: "Fixed",    bg: "#fdf0e8", fg: "#9a3a0c" },
};
function formatChangeDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function WhatsNewModal({ onClose, onFeedback }) {
  const byDate = CHANGELOG.reduce((acc, e) => { (acc[e.date] = acc[e.date] || []).push(e); return acc; }, {});
  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="whats-new-title" style={{ maxWidth: 620 }}>
        <div className="modal-header">
          <div className="modal-title" id="whats-new-title">What's new</div>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
          {Object.entries(byDate).map(([date, entries]) => (
            <div key={date} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 10 }}>{formatChangeDate(date)}</div>
              {entries.map(e => {
                const t = CHANGE_TYPES[e.type] || CHANGE_TYPES.improved;
                return (
                  <div key={e.id} style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                    <span style={{ flex: "0 0 auto", alignSelf: "flex-start", fontSize: 10.5, fontWeight: 700, letterSpacing: ".04em", padding: "3px 8px", borderRadius: 10, background: t.bg, color: t.fg, minWidth: 64, textAlign: "center" }}>{t.label}</span>
                    <div>
                      <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 600, color: "var(--forest)", marginBottom: 3 }}>{e.title}</div>
                      <div style={{ fontSize: 13.5, color: "var(--text-mid)", lineHeight: 1.55 }}>{e.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="modal-footer" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <button className="btn" style={{ background: "none", color: "var(--text-mid)", textDecoration: "underline" }}
            onClick={() => { onClose(); onFeedback && onFeedback(); }}>Have an idea? Tell us</button>
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── BILLING ──────────────────────────────────────────────────────────────────
// Stripe billing, stage 1 (2026-09-26). The `billing` function decides whether
// billing is on for this farm at all: while the Stripe key is a TEST key it is
// on only for farms in STRIPE_TEST_ORGS, so real farms see none of this until
// the live key goes in. Farms billed outside Stripe (Capital Quarter Horses,
// internal) come back `managedExternally` and are never offered checkout.
//
// Card details never touch ArcStallion: checkout and "Manage billing" are
// redirects to Stripe-hosted pages. Nothing here blocks access; the read-only
// rule after the grace period is stage 2, in the database, not in this file.
const BILLING_PROMPT_KEY = "arcstallion.cardPromptSkipped";
const PLAN_NAMES = {
  arcstallion_founding_monthly: "Founding farm, monthly",
  arcstallion_founding_yearly: "Founding farm, yearly",
  arcstallion_list_monthly: "Monthly",
  arcstallion_list_yearly: "Yearly",
};
const formatMoney = cents => "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: cents % 100 ? 2 : 0 });
const formatLongDate = iso => iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

async function goToBilling(action, interval) {
  const { url } = await callFunction("billing", { action, interval });
  if (!url) throw new Error("Billing didn't return a page to open.");
  window.location.assign(url);
}

function useBillingStatus(active) {
  const [billing, setBilling] = useState(null);
  const refresh = useCallback(async () => {
    if (!active) { setBilling(null); return null; }
    try { const b = await callFunction("billing", { action: "status" }); setBilling(b); return b; }
    catch (e) { console.error("[billing] status failed", e); setBilling(null); return null; }
  }, [active]);
  useEffect(() => { refresh(); }, [refresh]);
  return [billing, refresh];
}

function PlanChooser({ prices, interval, setInterval, disabled }) {
  if (!prices?.monthly || !prices?.yearly) return null;
  const saved = prices.monthly.amount * 12 - prices.yearly.amount;
  const opt = (value, title, price, note) => {
    const on = interval === value;
    return (
      <button type="button" disabled={disabled} onClick={() => setInterval(value)} aria-pressed={on}
        style={{ flex: 1, textAlign: "left", padding: "12px 14px", borderRadius: 8, cursor: "pointer", background: on ? "#fbf7ef" : "white",
          border: `2px solid ${on ? "var(--forest)" : "var(--parchment-dark)"}`, fontFamily: "'Crimson Pro',serif" }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-light)" }}>{title}</div>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, color: "var(--forest)", margin: "2px 0" }}>{price}</div>
        <div style={{ fontSize: 12.5, color: "var(--text-mid)" }}>{note}</div>
      </button>
    );
  };
  return (
    <div style={{ display: "flex", gap: 10, margin: "14px 0" }}>
      {opt("month", "Monthly", `${formatMoney(prices.monthly.amount)}/mo`, "Billed every month")}
      {opt("year", "Yearly", `${formatMoney(prices.yearly.amount)}/yr`, saved > 0 ? `Two months free, save ${formatMoney(saved)}` : "Billed once a year")}
    </div>
  );
}

function AddCardModal({ billing, onClose, onSkip }) {
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const trialFuture = billing?.status === "trial" && billing?.trial_ends_at && new Date(billing.trial_ends_at) > new Date();
  const founding = billing?.prices?.tier === "founding";
  const go = async () => {
    setBusy(true); setErr("");
    try { await goToBilling("checkout", interval); }
    catch (e) { setErr(e.message || "Couldn't open checkout. Please try again."); setBusy(false); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="add-card-title">
        <div className="modal-header">
          <div className="modal-title" id="add-card-title">{trialFuture ? "Keep your farm running after the trial" : "Subscribe to ArcStallion"}</div>
          <button className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <div className="modal-body" style={{ fontSize: 14, color: "var(--text-mid)", lineHeight: 1.6 }}>
          {trialFuture
            ? <>Add a card now and your free trial carries on as normal. <b>You won't be charged until {formatLongDate(billing.trial_ends_at)}</b>, and you can cancel any time before then from Settings, Billing.</>
            : <>Choose monthly or yearly. You can cancel any time from Settings, Billing.</>}
          {founding && <div style={{ marginTop: 10, padding: "8px 12px", background: "#fdf0e8", color: "#9a3a0c", borderRadius: 6, fontSize: 13 }}>
            You're getting our <b>founding farm price</b>, locked in for as long as you stay subscribed.</div>}
          <PlanChooser prices={billing?.prices} interval={interval} setInterval={setInterval} disabled={busy} />
          <div style={{ fontSize: 12, color: "var(--text-light)" }}>Payments are handled securely by Stripe. ArcStallion never sees your card number.</div>
          {err && <div role="alert" style={{ marginTop: 10, color: "var(--red)", fontSize: 13 }}>{err}</div>}
        </div>
        <div className="modal-footer" style={{ justifyContent: "space-between" }}>
          {onSkip ? <button className="btn" onClick={onSkip} disabled={busy} style={{ background: "none", textDecoration: "underline", color: "var(--text-mid)" }}>Skip for now</button> : <span />}
          <button className="btn btn-primary" onClick={go} disabled={busy || !billing?.prices?.monthly}>{busy ? "Opening checkout…" : "Continue to secure checkout"}</button>
        </div>
      </div>
    </div>
  );
}

// One line under the top bar, Admins only, and only when there is something
// the farm needs to do. A farm with a card on file during its trial sees nothing.
function BillingBanner({ billing, onAddCard }) {
  const [busy, setBusy] = useState(false);
  if (!billing?.enabled) return null;
  const portal = async () => { setBusy(true); try { await goToBilling("update_card"); } catch (e) { alert(e.message); setBusy(false); } };
  let tone = "info", text = null, action = null;
  if (billing.status === "trial" && !billing.has_subscription) {
    const end = billing.trial_ends_at ? new Date(billing.trial_ends_at) : null;
    const days = end ? Math.ceil((end - new Date()) / 86400000) : null;
    if (days !== null && days < 0) { tone = "warn"; text = <>Your free trial ended {formatLongDate(billing.trial_ends_at)}. Add a card to keep full access.</>; }
    else if (days !== null && days <= 7) { tone = "warn"; text = <>Your free trial ends in {days <= 1 ? "1 day" : `${days} days`}. Add a card to keep everything running.</>; }
    else text = <>You're on a free trial until {formatLongDate(billing.trial_ends_at)}.</>;
    action = <button className="btn btn-sm" onClick={onAddCard}>Add card</button>;
  } else if (billing.status === "past_due") {
    tone = "warn"; text = <>Your last payment didn't go through. Update your card to keep full access.</>;
    action = <button className="btn btn-sm" onClick={portal} disabled={busy}>{busy ? "Opening…" : "Update card"}</button>;
  } else if (billing.status === "cancelled") {
    tone = "warn"; text = <>Your subscription has ended. Subscribe again to keep full access.</>;
    action = <button className="btn btn-sm" onClick={onAddCard}>Subscribe</button>;
  }
  if (!text) return null;
  const warn = tone === "warn";
  return (
    <div role="status" style={{ background: warn ? "#fdf0e8" : "#f5f1e8", borderBottom: `1px solid ${warn ? "#f3cfb5" : "#e6dcc6"}`,
      color: warn ? "#7a2e08" : "var(--text-mid)", padding: "7px 26px", fontSize: 13, fontFamily: "'Crimson Pro', serif",
      display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ flex: 1 }}>{text}</span>{action}
    </div>
  );
}

// Shown to EVERYONE on a farm whose access has ended, not just Admins (unlike
// BillingBanner): Staff and Viewers are the people who would otherwise hit a
// refused save with no idea why. Every date comes from farm_access() in the
// database, never recomputed here, so this banner, the reminder emails and
// the deletion rule cannot drift apart.
function AccessEndedBanner({ access, isAdmin, canSubscribe, onSubscribe, onDownload }) {
  if (!access?.ended_at) return null;
  const what = access.ever_paid ? "subscription" : "free trial";
  const ended = formatLongDate(access.ended_at);
  const del = formatLongDate(access.delete_after);
  const text = access.read_only
    ? <>Your farm is <b>read-only</b>. Your {what} ended on {ended}. Everything is still here to look up and download
        until <b>{del}</b>, when it will be permanently deleted.
        {isAdmin ? " Subscribe to pick up right where you left off." : " Ask your farm's Admin to subscribe to make changes again."}</>
    : <>Your {what} ended on {ended}. You can keep making changes until <b>{formatLongDate(access.read_only_from)}</b>,
        then your farm becomes read-only.{isAdmin ? "" : " Your farm's Admin can subscribe to keep going."}</>;
  return (
    <div role="status" style={{ background: "#fdf0e8", borderBottom: "1px solid #f3cfb5", color: "#7a2e08", padding: "9px 26px",
      fontSize: 13.5, lineHeight: 1.5, fontFamily: "'Crimson Pro', serif", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <span style={{ flex: 1, minWidth: 260 }}>{text}</span>
      {isAdmin && <button className="btn btn-sm btn-outline" onClick={onDownload}>Download my data</button>}
      {isAdmin && canSubscribe && <button className="btn btn-sm" onClick={onSubscribe}>Subscribe</button>}
    </div>
  );
}

// Cancelling: a plain "Cancel subscription" link at the foot of Settings >
// Billing -- the one place anyone looks for it, findable without hunting, and
// not a big red button. It opens one honest screen (what happens, when, your
// records stay), then Stripe's portal cancel step, then straight back here.
// A scheduled cancellation shows "Ends on <date>" with a one-click "Keep my
// subscription". No guilt trips, no maze: a B2B customer who has to fight to
// leave tells the next breeder about it.
// ─── Data retention and export ───────────────────────────────────────────────
// After a subscription ends, a farm's data is kept this many days so it can be
// downloaded, then permanently deleted (John approves each deletion). A trial
// that never became a paid subscription gets the shorter window. The same
// numbers are in the Terms, the Privacy Policy and CLAUDE.md "Data retention".
const RETENTION_DAYS_PAID = 90;
const RETENTION_DAYS_TRIAL = 30;
const addDays = (iso, days) => iso ? new Date(new Date(iso).getTime() + days * 86400000).toISOString() : null;

// "Download all my data": everything the farm entered, as one zip (Excel
// workbook, CSVs, photos, logo, contracts). Built in the browser from queries
// the signed-in Admin is already allowed to make; see src/dataExport.js.
function DownloadDataButton({ orgId, farmName, label = "Download all my data", className = "btn btn-primary", style }) {
  const [state, setState] = useState({ busy: false, text: "", err: "", done: "" });
  const run = async () => {
    setState({ busy: true, text: "Starting", err: "", done: "" });
    try {
      const { exportFarmData } = await import("./dataExport");
      const { blob, fileName, missing } = await exportFarmData({ orgId, farmName, onProgress: text => setState(s => ({ ...s, text })) });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      const mb = blob.size / 1048576;
      setState({ busy: false, text: "", err: "", done: `Downloaded ${fileName} (${mb < 1 ? "under 1" : mb.toFixed(1)} MB).${missing.length ? ` ${missing.length} file${missing.length === 1 ? "" : "s"} couldn't be included; the README inside lists them.` : ""}` });
    } catch (e) {
      console.error("[export] failed", e);
      setState({ busy: false, text: "", err: `The download didn't finish: ${e.message || e}. Please try again, or use Help & feedback and we'll send it to you.`, done: "" });
    }
  };
  return (
    <div>
      <button type="button" className={className} style={style} onClick={run} disabled={state.busy || !orgId}>
        {state.busy ? "Preparing download…" : label}</button>
      {state.busy && <div role="status" style={{ marginTop: 8, fontSize: 12.5, color: "var(--text-light)" }}>{state.text}…</div>}
      {state.done && <div role="status" style={{ marginTop: 8, fontSize: 12.5, color: "#166534" }}>{state.done}</div>}
      {state.err && <div role="alert" style={{ marginTop: 8, fontSize: 12.5, color: "var(--red)" }}>{state.err}</div>}
    </div>
  );
}

function CancelSubscriptionModal({ billing, onClose, onFeedback, orgId, farmName }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const sub = billing?.subscription || {};
  const inTrial = sub.status === "trialing";
  const endIso = inTrial ? sub.trial_end : sub.next_charge_at;
  const until = formatLongDate(endIso);
  const deleteOn = formatLongDate(addDays(endIso, inTrial ? RETENTION_DAYS_TRIAL : RETENTION_DAYS_PAID));
  const go = async () => {
    setBusy(true); setErr("");
    try { await goToBilling("cancel"); } catch (e) { setErr(e.message || "Couldn't open cancellation. Please try again."); setBusy(false); }
  };
  const li = { margin: "0 0 8px 0" };
  return (
    <div className="modal-overlay">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
        <div className="modal-header">
          <div className="modal-title" id="cancel-title">{inTrial ? "Cancel your trial?" : "Cancel your subscription?"}</div>
          <button className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <div className="modal-body" style={{ fontSize: 14, color: "var(--text-mid)", lineHeight: 1.6 }}>
          <ul style={{ margin: "0 0 12px 18px", padding: 0 }}>
            {inTrial
              ? <li style={li}>You won't be charged. Your trial carries on until <b>{until}</b>.</li>
              : <li style={li}>You won't be charged again. You keep full access until <b>{until}</b>, the end of the period you've paid for.</li>}
            <li style={li}>A week after that your farm becomes read-only. You can still sign in, look everything up and download it until <b>{deleteOn}</b>.</li>
            <li style={li}>On <b>{deleteOn}</b> we permanently delete your farm's records, photos and files. This can't be undone.</li>
            <li style={li}>Change your mind any time before <b>{until}</b> with one click.</li>
          </ul>
          <div style={{ margin: "0 0 14px", padding: "12px 14px", background: "#fbf7ef", border: "1px solid var(--parchment-dark)", borderRadius: 8 }}>
            <div style={{ fontSize: 13.5, marginBottom: 8 }}>Keep your own copy: every record as an Excel workbook, plus your photos and contracts.</div>
            <DownloadDataButton orgId={orgId} farmName={farmName} className="btn btn-outline" />
          </div>
          <div style={{ fontSize: 13, color: "var(--text-light)" }}>
            Leaving because something isn't working? <button type="button" onClick={() => { onClose(); onFeedback && onFeedback(); }}
              style={{ background: "none", border: "none", padding: 0, color: "var(--forest)", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>Tell us</button>. A real person reads it.
          </div>
          {err && <div role="alert" style={{ marginTop: 10, color: "var(--red)", fontSize: 13 }}>{err}</div>}
        </div>
        <div className="modal-footer" style={{ justifyContent: "space-between" }}>
          <button className="btn btn-primary" onClick={onClose} disabled={busy}>Keep my subscription</button>
          <button className="btn" onClick={go} disabled={busy} style={{ background: "white", border: "1px solid var(--parchment-dark)", color: "var(--text-dark)" }}>
            {busy ? "Opening…" : "Continue to cancel"}</button>
        </div>
      </div>
    </div>
  );
}

function BillingPanel({ billing, onAddCard, onRefresh, onFeedback, orgId, farmName }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  if (!billing) return <div className="full-panel" style={{ padding: 20 }}>Loading billing…</div>;
  const row = (k, v) => <div style={{ display: "flex", gap: 16, padding: "6px 0", fontSize: 14 }}><div style={{ width: 130, color: "var(--text-light)" }}>{k}</div><div style={{ color: "var(--text-dark)", fontWeight: 600 }}>{v}</div></div>;
  if (billing.managedExternally) {
    return (
      <div className="full-panel" style={{ padding: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "var(--forest)", marginBottom: 6 }}>Billing</div>
        <div style={{ fontSize: 14, color: "var(--text-mid)", lineHeight: 1.6 }}>Billing for your farm is handled directly with ArcStallion. Questions about it, or want to cancel? Use Help &amp; feedback at the bottom of the menu.</div>
      </div>
    );
  }
  const sub = billing.subscription;
  const subscribed = billing.has_subscription && billing.status !== "cancelled";
  const ending = !!sub?.ending;
  const statusText = ending
    ? `Ends ${formatLongDate(sub.ends_at)}`
    : ({
        trial: subscribed ? `Free trial until ${formatLongDate(sub?.trial_end || billing.trial_ends_at)}` : `Free trial until ${formatLongDate(billing.trial_ends_at)}`,
        active: "Active",
        past_due: "Payment failed",
        cancelled: "Cancelled",
      }[billing.status] || billing.status || "—");
  const act = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(e.message); } setBusy(false); };
  const portal = () => act(() => goToBilling("portal"));
  const resume = () => act(async () => { await callFunction("billing", { action: "resume" }); await (onRefresh && onRefresh()); });
  const nextCharge = !ending && sub?.amount != null && (sub.status === "trialing" ? sub.trial_end : sub.next_charge_at);
  return (
    <div className="full-panel" style={{ padding: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: "var(--forest)", marginBottom: 10 }}>Billing</div>
      {row("Status", statusText)}
      {billing.plan && row("Plan", PLAN_NAMES[billing.plan] || billing.plan)}
      {nextCharge && row(sub.status === "trialing" ? "First charge" : "Next charge", `${formatMoney(sub.amount)} on ${formatLongDate(nextCharge)}`)}
      {subscribed && row("Card", <>
        {sub?.card ? `${sub.card.brand.charAt(0).toUpperCase() + sub.card.brand.slice(1)} ending ${sub.card.last4}` : "On file"}
        <button type="button" onClick={() => act(() => goToBilling("update_card"))} disabled={busy}
          style={{ marginLeft: 12, background: "none", border: "none", padding: 0, fontSize: 13, fontWeight: 600, color: "var(--forest)", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>
          Change card</button></>)}

      {ending && (
        <div style={{ marginTop: 14, padding: "12px 14px", background: "#fdf0e8", border: "1px solid #f3cfb5", borderRadius: 8, fontSize: 14, color: "#7a2e08", lineHeight: 1.55 }}>
          Your subscription is cancelled and ends on <b>{formatLongDate(sub.ends_at)}</b>. You won't be charged again.
          A week after that your farm becomes read-only, and your data is kept until <b>{formatLongDate(addDays(sub.ends_at, sub.status === "trialing" ? RETENTION_DAYS_TRIAL : RETENTION_DAYS_PAID))}</b> so you can download it, then permanently deleted. Changed your mind?
          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
            <button className="btn btn-primary" onClick={resume} disabled={busy}>{busy ? "Working…" : "Keep my subscription"}</button>
            <DownloadDataButton orgId={orgId} farmName={farmName} className="btn btn-outline" />
          </div>
        </div>
      )}

      {subscribed ? (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13.5, color: "var(--text-mid)", marginBottom: 10, lineHeight: 1.55 }}>Download invoices, change your billing email or address, or see past payments. This opens Stripe's secure billing page.</div>
          <button className="btn btn-primary" onClick={portal} disabled={busy}>{busy && !ending ? "Opening…" : "Manage billing"}</button>
        </div>
      ) : (
        <div style={{ marginTop: 14 }}>
          <button className="btn btn-primary" onClick={onAddCard}>{billing.status === "trial" ? "Add card" : "Subscribe"}</button>
        </div>
      )}

      {subscribed && !ending && (
        <div style={{ marginTop: 18, paddingTop: 12, borderTop: "1px solid var(--parchment-dark)" }}>
          <button type="button" onClick={() => setCancelOpen(true)}
            style={{ background: "none", border: "none", padding: 0, fontSize: 13, color: "var(--text-mid)", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>
            {sub?.status === "trialing" ? "Cancel trial" : "Cancel subscription"}</button>
        </div>
      )}
      {billing.mode === "test" && <div style={{ marginTop: 14, fontSize: 12, color: "#9a3a0c" }}>Test mode: use card 4242 4242 4242 4242, any future date, any CVC. No real charges.</div>}
      {err && <div role="alert" style={{ marginTop: 10, color: "var(--red)", fontSize: 13 }}>{err}</div>}
      {cancelOpen && <CancelSubscriptionModal billing={billing} onClose={() => setCancelOpen(false)} onFeedback={onFeedback} orgId={orgId} farmName={farmName} />}
    </div>
  );
}

// ─── AI HELPER ────────────────────────────────────────────────────────────────
// AI Advisor is switched off (2026-09-25). askClaude() below calls Anthropic
// straight from the browser with no API key, so every question failed with
// "Could not reach AI." -- and the only context it sent was a few herd counts,
// so even working it could only give generic advice. With this false, the
// sidebar item, the top-bar button and the page are all removed from the
// bundle. To bring it back, build it properly first:
//   - an authenticated `ai-advisor` edge function holding ANTHROPIC_API_KEY as
//     a function secret, org from the JWT, pulling the farm's actual horse
//     records server-side (never a key or farm data assembled in the browser)
//   - a daily per-farm cap, because every question costs money
//   - privacy policy wording that farm data is sent to Anthropic
//   - a visible "not a substitute for your veterinarian" notice
// then point AIAdvisor at callFunction("ai-advisor", ...) and flip this.
const AI_ADVISOR_ENABLED = false;

async function askClaude(prompt, sys) {
  const r = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514", max_tokens: 1000,
      system: sys || "You are an expert equine reproductive veterinarian and breeding manager. Be concise and practical.",
      messages: [{ role: "user", content: prompt }]
    })
  });
  const d = await r.json();
  return d.content?.find(b => b.type === "text")?.text || "No response.";
}

// ─── BREEDING PROTOCOL TEMPLATES ──────────────────────────────────────────────
const PROTOCOL_TEMPLATES = {
  "AI Fresh": {
    name: "AI — Fresh/Chilled Semen",
    steps: [
      { label: "Begin teasing / heat detection", day: "Day 1", type: "teasing" },
      { label: "Initial ultrasound — check follicle size", day: "Day 2-3", type: "palpation" },
      { label: "Monitor follicle growth (every 24-48h)", day: "Day 3-5", type: "palpation" },
      { label: "Administer hCG or Deslorelin when follicle ≥35mm", day: "When ready", type: "medication" },
      { label: "First breeding (AI with fresh/chilled semen)", day: "24-36h pre-ovulation", type: "breeding" },
      { label: "Confirm ovulation via ultrasound", day: "Post-breeding", type: "palpation" },
      { label: "Pregnancy check — 14 day ultrasound", day: "Day 14", type: "pregnancy_check" },
      { label: "Viability check — confirm heartbeat", day: "Day 25-30", type: "pregnancy_check" },
      { label: "Mid-pregnancy ultrasound", day: "Day 45-60", type: "pregnancy_check" },
    ]
  },
  "AI Frozen": {
    name: "AI — Frozen Semen",
    steps: [
      { label: "Begin teasing / heat detection", day: "Day 1", type: "teasing" },
      { label: "Initial ultrasound — check follicle size", day: "Day 2-3", type: "palpation" },
      { label: "Frequent monitoring (every 6-8h when follicle mature)", day: "Day 3-5", type: "palpation" },
      { label: "Administer Deslorelin when follicle ≥35mm", day: "When ready", type: "medication" },
      { label: "Thaw and inseminate (within 12h of expected ovulation)", day: "Timed", type: "breeding" },
      { label: "Confirm ovulation via ultrasound", day: "Post-breeding", type: "palpation" },
      { label: "Post-breeding uterine lavage (if needed)", day: "24h post", type: "note" },
      { label: "Pregnancy check — 14 day ultrasound", day: "Day 14", type: "pregnancy_check" },
      { label: "Viability check — confirm heartbeat", day: "Day 25-30", type: "pregnancy_check" },
      { label: "Mid-pregnancy ultrasound", day: "Day 45-60", type: "pregnancy_check" },
    ]
  },
  "Natural Cover": {
    name: "Natural Cover (Live Stallion)",
    steps: [
      { label: "Begin teasing / heat detection", day: "Day 1", type: "teasing" },
      { label: "Palpation — check follicle and cervical tone", day: "Day 2-3", type: "palpation" },
      { label: "Breed when mare shows strong heat (score 4-5)", day: "When ready", type: "breeding" },
      { label: "Repeat breeding every 48h until ovulation", day: "Ongoing", type: "breeding" },
      { label: "Confirm ovulation via ultrasound", day: "Post-breeding", type: "palpation" },
      { label: "Pregnancy check — 14 day ultrasound", day: "Day 14", type: "pregnancy_check" },
      { label: "Viability check — confirm heartbeat", day: "Day 25-30", type: "pregnancy_check" },
      { label: "Mid-pregnancy ultrasound", day: "Day 45-60", type: "pregnancy_check" },
    ]
  },
  "Embryo Transfer": {
    name: "Embryo Transfer (Donor Mare)",
    steps: [
      { label: "Begin Regumate on donor (10-14 days)", day: "Day 1", type: "medication" },
      { label: "Administer PGF2α on last day of Regumate", day: "Day 10-14", type: "medication" },
      { label: "Monitor donor heat and follicle growth", day: "Day 14-18", type: "palpation" },
      { label: "Induce ovulation (hCG or Deslorelin)", day: "When ready", type: "medication" },
      { label: "Breed donor mare (AI or natural)", day: "Timed", type: "breeding" },
      { label: "Confirm donor ovulation", day: "Post-breeding", type: "palpation" },
      { label: "Embryo flush — Day 7-8 post-ovulation", day: "Day 7-8", type: "note" },
      { label: "Transfer embryo to synchronized recipient", day: "Day 7-8", type: "note" },
      { label: "Recipient pregnancy check — Day 14", day: "Day 14", type: "pregnancy_check" },
      { label: "Recipient viability check — Day 25-30", day: "Day 25-30", type: "pregnancy_check" },
    ]
  },
  "Cycle Sync": {
    name: "Estrus Synchronization",
    steps: [
      { label: "Start Regumate (altrenogest) daily", day: "Day 1", type: "medication" },
      { label: "Continue Regumate (0.044 mg/kg)", day: "Day 2-13", type: "medication" },
      { label: "Last Regumate dose + PGF2α injection", day: "Day 14", type: "medication" },
      { label: "Watch for heat signs (3-5 days post PGF2α)", day: "Day 17-19", type: "teasing" },
      { label: "Ultrasound — confirm follicle development", day: "When in heat", type: "palpation" },
      { label: "Proceed with breeding protocol", day: "When ready", type: "breeding" },
    ]
  }
};

const EVENT_ICONS = { teasing: "🔍", palpation: "📡", breeding: "🧬", medication: "💉", pregnancy_check: "🤰", icsi: "🧫", note: "📝" };
const EVENT_LABELS = { teasing: "Teasing", palpation: "Palpation", breeding: "Breeding", medication: "Medication", pregnancy_check: "Pregnancy Check", icsi: "ICSI / Aspiration", note: "Note" };

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    "Paid":"badge-paid","Pending":"badge-pending","Partial":"badge-partial",
    "Overdue":"badge-overdue","Shipped":"badge-shipped","Delivered":"badge-delivered",
    "In Transit":"badge-intransit","Label Created":"badge-labelcreated",
    "Active":"badge-active","VIP":"badge-vip","Inactive":"badge-inactive",
    "Pregnant":"badge-pregnant","Open":"badge-open","In Cycle":"badge-cycle",
    "Flush Scheduled":"badge-flush","Frozen":"badge-frozen",
    "Available":"badge-available","Low Stock":"badge-lowstock","Depleted":"badge-depleted",
  };
  return <span className={`badge ${map[status] || "badge-active"}`}>{status}</span>;
}

function SaveToast({ show, text }) {
  return show ? <div className="save-toast">✓ {text || "Saved"}</div> : null;
}

// Phone number auto-formatter: (XXX) XXX-XXXX
function formatPhone(value) {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
function PhoneInput({ value, onChange, ...props }) {
  return <input {...props} className={props.className || "form-input"} type="tel" value={value || ""}
    onChange={e => onChange(formatPhone(e.target.value))} placeholder="(405) 555-1234" />;
}

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
// Password reset, part one: ask Supabase to email a recovery link.
//
// Note the deliberate lack of feedback about whether the address exists. A
// page that says "no account with that email" is a free account-enumeration
// tool for anyone who wants to know which farms use ArcStallion.
function ForgotPasswordPage({ onBack, prefillEmail }) {
  const [email, setEmail] = useState(prefillEmail || "");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!email.trim()) { setError("Enter the email address you sign in with."); return; }
    setLoading(true); setError("");
    const { error: sendErr } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/`,
    });
    if (sendErr) console.error("resetPasswordForEmail failed:", sendErr);
    setLoading(false);
    setSent(true);
  };

  return (
    <div className="login-page">
      <div className="login-bg-pattern" />
      <div className="login-card">
        <div className="login-header"><ArcStallionLogo size="lg" dark={true} /></div>
        <div className="login-body">
          {sent ? (
            <>
              <div className="login-welcome">Check your email</div>
              <div className="login-sub" style={{ marginBottom: 18 }}>
                If there is an account for {email.trim()}, a link to set a new password is on its way.
                It expires in an hour. Check your spam folder if it has not arrived in a few minutes.
              </div>
              <button className="login-btn" onClick={onBack}>Back to sign in</button>
            </>
          ) : (
            <>
              <div className="login-welcome">Reset your password</div>
              <div className="login-sub" style={{ marginBottom: 18 }}>
                We will email you a link to set a new one.
              </div>
              <div className="login-field">
                <label className="login-label">Email Address</label>
                <input className={`login-input${error ? " error" : ""}`} type="email"
                  placeholder="you@yourfarm.com" value={email} autoFocus
                  onChange={e => { setEmail(e.target.value); setError(""); }}
                  onKeyDown={e => e.key === "Enter" && handleSend()} />
              </div>
              {error && <div className="login-error">⚠ {error}</div>}
              <button className="login-btn" onClick={handleSend} disabled={loading}>
                {loading ? "Sending…" : "Send reset link"}
              </button>
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button onClick={onBack}
                  style={{ background: "none", border: "none", color: "rgba(201,168,76,.6)", cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>
                  Back to sign in
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Where BOTH emailed links land -- a password reset and a teammate invite.
// Supabase has already established a session by the time this renders, so
// updateUser is all that is needed. `invite` only changes the wording: someone
// who has never had an account should not be told to set a "new" password.
function ResetPasswordPage({ onDone, invite = false }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (password.length < 10) { setError("Use at least 10 characters."); return; }
    if (password !== confirm) { setError("Those two passwords do not match."); return; }
    setLoading(true); setError("");
    const { error: updErr } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updErr) {
      setError(updErr.message || "That link may have expired. Request a new one.");
      return;
    }
    onDone();
  };

  return (
    <div className="login-page">
      <div className="login-bg-pattern" />
      <div className="login-card">
        <div className="login-header"><ArcStallionLogo size="lg" dark={true} /></div>
        <div className="login-body">
          <div className="login-welcome">{invite ? "Choose a password" : "Set a new password"}</div>
          <div className="login-sub" style={{ marginBottom: 18 }}>
            {invite
              ? "Ten characters or more. This is the last step \u2014 you are in once it is saved."
              : "Ten characters or more. You will be signed in once it is saved."}
          </div>
          <div className="login-field">
            <label className="login-label">{invite ? "Password" : "New Password"}</label>
            <div style={{ position: "relative" }}>
              <input className={`login-input${error ? " error" : ""}`} type={showPw ? "text" : "password"}
                value={password} autoFocus
                onChange={e => { setPassword(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleSave()}
                style={{ paddingRight: 42 }} />
              <button onClick={() => setShowPw(p => !p)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(201,168,76,.5)", cursor: "pointer", fontSize: 16, padding: 0 }}>
                {showPw ? "🙈" : "👁"}
              </button>
            </div>
          </div>
          <div className="login-field">
            <label className="login-label">Confirm Password</label>
            <input className={`login-input${error ? " error" : ""}`} type={showPw ? "text" : "password"}
              value={confirm}
              onChange={e => { setConfirm(e.target.value); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handleSave()} />
          </div>
          {error && <div className="login-error">⚠ {error}</div>}
          <button className="login-btn" onClick={handleSave} disabled={loading}>
            {loading ? "Saving\u2026" : (invite ? "Save and continue" : "Save and sign in")}
          </button>
        </div>
      </div>
    </div>
  );
}

function LoginPage({ onLogin, onSwitchToSignup, onForgotPassword }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If the user came here via "Switch account", surface a hint banner so
  // one click drops their previous email back into the field.
  const [switchFrom, setSwitchFrom] = useState("");
  useEffect(() => {
    try {
      const prev = localStorage.getItem("et.switch_from_email");
      if (prev) setSwitchFrom(prev);
    } catch (_) {}
  }, []);
  const clearSwitchHint = () => {
    try { localStorage.removeItem("et.switch_from_email"); } catch (_) {}
    setSwitchFrom("");
  };

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) {
      setError("Invalid email or password. Please try again.");
      setLoading(false);
    } else {
      clearSwitchHint();
    }
    // onLogin is called automatically by the auth state listener in App
  };

  return (
    <div className="login-page">
      <div className="login-bg-pattern" />
      <div className="login-card">
        <div className="login-header">
          <ArcStallionLogo size="lg" dark={true} />
          <div className="login-sub">Equine Breeding Management System</div>
        </div>
        <div className="login-body">
          <div className="login-welcome">Sign in to your account</div>
          {switchFrom && (
            <div style={{
              background: "rgba(201,168,76,.12)", border: "1px solid rgba(201,168,76,.3)",
              padding: "10px 12px", borderRadius: 6, marginBottom: 14, fontSize: 13,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            }}>
              <span style={{ color: "var(--parchment)", fontFamily: "'Crimson Pro', serif" }}>
                Switched from <strong>{switchFrom}</strong>. Sign in with a different account, or{" "}
                <button type="button" onClick={() => setEmail(switchFrom)}
                  style={{ background: "none", border: "none", color: "var(--gold-light)", textDecoration: "underline", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: "inherit" }}>
                  use that email
                </button>.
              </span>
              <button type="button" onClick={clearSwitchHint}
                style={{ background: "none", border: "none", color: "rgba(245,240,232,.5)", cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1 }}
                title="Dismiss">×</button>
            </div>
          )}
          <div className="login-field">
            <label className="login-label">Email Address</label>
            <input className={`login-input${error ? " error" : ""}`} type="email"
              placeholder="you@yourfarm.com" value={email}
              onChange={e => { setEmail(e.target.value); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handleLogin()} />
          </div>
          <div className="login-field">
            <label className="login-label">Password</label>
            <div style={{ position: "relative" }}>
              <input className={`login-input${error ? " error" : ""}`}
                type={showPw ? "text" : "password"} placeholder="••••••••"
                value={password} onChange={e => { setPassword(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                style={{ paddingRight: 42 }} />
              <button onClick={() => setShowPw(p => !p)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(201,168,76,.5)", cursor: "pointer", fontSize: 16, padding: 0 }}>
                {showPw ? "🙈" : "👁"}
              </button>
            </div>
          </div>
          {error && <div className="login-error">⚠ {error}</div>}
          <button className="login-btn" onClick={handleLogin} disabled={loading}>
            {loading
              ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                  <span style={{ display: "inline-block", width: 16, height: 16, border: "2px solid rgba(26,46,26,.3)", borderTopColor: "var(--forest)", borderRadius: "50%", animation: "spin .7s linear infinite" }} />
                  Signing In…
                </span>
              : "Sign In →"}
          </button>
          <div style={{ textAlign: "center", marginTop: 14 }}>
            <button onClick={onForgotPassword}
              style={{ background: "none", border: "none", color: "rgba(201,168,76,.6)", cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>
              Forgot your password?
            </button>
          </div>
        </div>
        <div className="login-footer">
          {onSwitchToSignup && <div style={{ marginBottom: 10 }}>Don't have an account? <button onClick={onSwitchToSignup} style={{ background: "none", border: "none", color: "rgba(201,168,76,.6)", cursor: "pointer", fontSize: 11, fontWeight: 600, textDecoration: "underline" }}>Start Free Trial</button></div>}
          <div style={{ marginBottom: 8 }}>Fort Worth, Texas</div>
          <div style={{ marginBottom: 8 }}><a href="mailto:support@arcstallion.com" style={{ color: "rgba(201,168,76,.45)", textDecoration: "none" }}>support@arcstallion.com</a></div>
          <div style={{ borderTop: "1px solid rgba(201,168,76,.1)", paddingTop: 10, fontSize: 10.5 }}>
            © ArcStallion LLC · All rights reserved
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SIGNUP PAGE ─────────────────────────────────────────────────────────────
function SignupPage({ onSwitchToLogin }) {
  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState("");
  const [farmName, setFarmName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateStep1 = () => {
    if (!fullName.trim()) { setError("Please enter your full name."); return false; }
    if (!farmName.trim()) { setError("Please enter your farm or business name."); return false; }
    setError(""); return true;
  };

  const handleSignup = async () => {
    if (!email.trim()) { setError("Please enter your email address."); return; }
    // Phone is required: it is how we reach a farm when email bounces or an
    // account needs verifying, and it is the only channel that works when the
    // thing that is broken IS their email integration.
    if (phone.replace(/\D/g, "").length < 10) { setError("Please enter a valid phone number."); return; }
    // Supabase rejects anything under 10 server-side; catch it here so the
    // user gets a clear message instead of a raw auth error.
    if (password.length < 10) { setError("Password must be at least 10 characters."); return; }
    if (password !== confirmPw) { setError("Passwords don't match."); return; }
    setLoading(true); setError("");
    try {
      const { data, error: authErr } = await supabase.auth.signUp({ email, password, options: { data: { full_name: fullName, org_name: farmName, phone } } });
      if (authErr) { setError(authErr.message); setLoading(false); return; }
      if (data?.user) {
        // The farm and the Admin role are created server-side by
        // provision-tenant. The browser must never choose its own org_id or
        // role (AUDIT_REPORT.md A2) — and after the tenant-isolation migration
        // it cannot, because user_profiles has no client INSERT policy.
        //
        // With email confirmation enabled, signUp() returns no session, so
        // there is no access token to call the function with yet. In that case
        // provisioning happens on first sign-in instead; see loadUserData.
        // This is what the old code got wrong: it wrote the profile without a
        // session, the write was rejected, and nothing checked the result.
        if (data.session) {
          try {
            await callFunction("provision-tenant", {
              farm_name: farmName, full_name: fullName, phone,
            });
          } catch (e) {
            console.error("provision-tenant failed at signup:", e);
            setError("Your account was created, but setting up your farm did not finish. Sign in and it will complete automatically.");
            setLoading(false);
            return;
          }
        }
        setSuccess(true);
      }
    } catch (e) { setError("Something went wrong. Please try again."); }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="login-page">
        <div className="login-bg-pattern" />
        <div className="login-card" style={{ textAlign: "center" }}>
          <div className="login-header"><ArcStallionLogo size="lg" dark={true} /></div>
          <div className="login-body">
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "var(--gold)", marginBottom: 8 }}>Welcome to ArcStallion!</div>
            <div style={{ fontSize: 14, color: "rgba(201,168,76,.5)", marginBottom: 8 }}>Your free trial is active.</div>
            <div style={{ fontSize: 13, color: "rgba(201,168,76,.35)", marginBottom: 24, lineHeight: 1.6 }}>
              Check your email at <strong style={{ color: "rgba(201,168,76,.6)" }}>{email}</strong> to verify your account, then sign in to get started.
            </div>
            <button className="login-btn" onClick={onSwitchToLogin}>Sign In →</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-bg-pattern" />
      <div className="login-card">
        <div className="login-header">
          <ArcStallionLogo size="lg" dark={true} />
          <div className="login-sub">Start Your Free Trial</div>
        </div>
        <div className="login-body">
          {/* Progress dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 20 }}>
            <div style={{ width: 32, height: 4, borderRadius: 2, background: "var(--gold)", opacity: step >= 1 ? 1 : .2, transition: "opacity .3s" }} />
            <div style={{ width: 32, height: 4, borderRadius: 2, background: "var(--gold)", opacity: step >= 2 ? 1 : .2, transition: "opacity .3s" }} />
          </div>

          {step === 1 && (<>
            <div className="login-welcome">Tell us about your operation</div>
            <div className="login-field">
              <label className="login-label">Your Full Name</label>
              <input className={`login-input${error && !fullName.trim() ? " error" : ""}`} type="text" placeholder="John Doe" value={fullName}
                onChange={e => { setFullName(e.target.value); setError(""); }} />
            </div>
            <div className="login-field">
              <label className="login-label">Farm / Business Name</label>
              <input className={`login-input${error && !farmName.trim() ? " error" : ""}`} type="text" placeholder="e.g. Capital Quarter Horses" value={farmName}
                onChange={e => { setFarmName(e.target.value); setError(""); }} />
            </div>
            <div className="login-field">
              <label className="login-label">Phone Number</label>
              <input className="login-input" type="tel" placeholder="(214) 384-7522" value={phone}
                onChange={e => setPhone(formatPhone(e.target.value))} />
            </div>
            {error && <div className="login-error">⚠ {error}</div>}
            <button className="login-btn" onClick={() => { if (validateStep1()) setStep(2); }}>Continue →</button>
          </>)}

          {step === 2 && (<>
            <div className="login-welcome">Create your account</div>
            <div className="login-field">
              <label className="login-label">Email Address</label>
              <input className={`login-input${error ? " error" : ""}`} type="email" placeholder="you@yourfarm.com" value={email}
                onChange={e => { setEmail(e.target.value); setError(""); }} />
            </div>
            <div className="login-field">
              <label className="login-label">Password</label>
              <div style={{ position: "relative" }}>
                <input className={`login-input${error ? " error" : ""}`} type={showPw ? "text" : "password"} placeholder="At least 6 characters" value={password}
                  onChange={e => { setPassword(e.target.value); setError(""); }} style={{ paddingRight: 42 }} />
                <button onClick={() => setShowPw(p => !p)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(201,168,76,.5)", cursor: "pointer", fontSize: 16, padding: 0 }}>
                  {showPw ? "🙈" : "👁"}
                </button>
              </div>
            </div>
            <div className="login-field">
              <label className="login-label">Confirm Password</label>
              <input className={`login-input${error && password !== confirmPw ? " error" : ""}`} type="password" placeholder="Re-enter your password" value={confirmPw}
                onChange={e => { setConfirmPw(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleSignup()} />
            </div>
            {error && <div className="login-error">⚠ {error}</div>}
            <button className="login-btn" onClick={handleSignup} disabled={loading}>
              {loading
                ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                    <span style={{ display: "inline-block", width: 16, height: 16, border: "2px solid rgba(26,46,26,.3)", borderTopColor: "var(--forest)", borderRadius: "50%", animation: "spin .7s linear infinite" }} />
                    Creating Account…
                  </span>
                : "Start Free Trial →"}
            </button>
            <div style={{ textAlign: "center", marginTop: 10 }}>
              <button onClick={() => { setStep(1); setError(""); }} style={{ background: "none", border: "none", color: "rgba(201,168,76,.4)", cursor: "pointer", fontSize: 12 }}>← Back</button>
            </div>
          </>)}

          <div style={{ textAlign: "center", marginTop: 16, fontSize: 12, color: "rgba(201,168,76,.3)" }}>
            No credit card required · Cancel anytime
          </div>
        </div>
        <div className="login-footer">
          <div style={{ marginBottom: 8 }}>Already have an account? <button onClick={onSwitchToLogin} style={{ background: "none", border: "none", color: "rgba(201,168,76,.6)", cursor: "pointer", fontSize: 11, fontWeight: 600, textDecoration: "underline" }}>Sign In</button></div>
          <div style={{ borderTop: "1px solid rgba(201,168,76,.1)", paddingTop: 10, fontSize: 10.5 }}>
            © ArcStallion LLC · All rights reserved
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── USER MENU ────────────────────────────────────────────────────────────────
function UserMenu({ user, profile, onLogout, onFeedback, onSettings, onWhatsNew, hasNew }) {
  const [open, setOpen] = useState(false);
  const initials = (profile?.full_name || user.email || "?").split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
  const ROLE_MAP = { Admin: "role-badge-admin", Staff: "role-badge-staff", Viewer: "role-badge-viewer" };

  // "Switch account" = sign out + bounce to login, with a hint so the
  // login page can offer a one-click return to this email if desired.
  const handleSwitchAccount = async () => {
    setOpen(false);
    try { localStorage.setItem("et.switch_from_email", user.email || ""); } catch (_) {}
    await supabase.auth.signOut();
  };

  return (
    <div style={{ position: "relative" }}>
      <div className="user-menu-btn" onClick={() => setOpen(p => !p)}>
        <div className="user-avatar" style={{ position: "relative" }}>{initials}
          {hasNew && <span title="Something new in ArcStallion" style={{ position: "absolute", top: -2, right: -2, width: 9, height: 9, borderRadius: "50%", background: "#C65217", border: "2px solid #faf7f0" }} />}
        </div>
        <div>
          <div className="user-menu-name">{profile?.full_name || user.email}</div>
          <div className="user-menu-role">{profile?.role || "User"}</div>
        </div>
        <span style={{ fontSize: 10, color: "rgba(201,168,76,.4)", marginLeft: 2 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 199 }} onClick={() => setOpen(false)} />
          <div className="user-dropdown">
            <div className="user-dropdown-header">
              <div className="user-dropdown-name">{profile?.full_name || "User"}</div>
              <div className="user-dropdown-email">{user.email}</div>
              {profile?.role && <div style={{ marginTop: 5 }}><span className={ROLE_MAP[profile.role]}>{profile.role}</span></div>}
            </div>
            {/* These two only closed the menu until 2026-09-25. There is no
                per-person account page, so "Settings" opens the farm's Settings
                and Notifications opens its tab. Viewers cannot reach Settings,
                so they do not see either. */}
            {profile?.role !== "Viewer" && onSettings && <>
              <div className="user-dropdown-item" onClick={() => { setOpen(false); onSettings(null); }}>⚙️ Settings</div>
              <div className="user-dropdown-item" onClick={() => { setOpen(false); onSettings("notifications"); }}>🔔 Notifications</div>
            </>}
            {onWhatsNew && <div className="user-dropdown-item" onClick={() => { setOpen(false); onWhatsNew(); }}>
              ✨ What's new {hasNew && <span style={{ marginLeft: "auto" }}><span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: "#C65217", display: "inline-block" }} /></span>}
            </div>}
            <div className="user-dropdown-item" style={{ color: "rgba(201,168,76,.7)" }}
              onClick={() => { setOpen(false); onFeedback && onFeedback(); }}>💬 Help &amp; feedback</div>
            <div className="user-dropdown-item" onClick={handleSwitchAccount} style={{ color: "var(--gold-light)" }}>
              🔁 Switch account
            </div>
            <div className="user-dropdown-item logout" onClick={() => { setOpen(false); onLogout(); }}>← Sign Out</div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── MARE CYCLE TIMELINE ──────────────────────────────────────────────────────
function CycleTimeline({ mare }) {
  const phases = [
    { left: "0%", width: "15%", cls: "seg-anestrus" },
    { left: "15%", width: "30%", cls: "seg-diestrus" },
    { left: "45%", width: "20%", cls: "seg-proestrus" },
    { left: "65%", width: "25%", cls: "seg-estrus" },
    { left: "90%", width: "10%", cls: "seg-ovulation" },
  ];
  const pct = Math.min(((mare.days_in_cycle || 1) / 21) * 100, 100);
  return (
    <div className="cycle-row">
      <div className="cycle-mare-name">{mare.name}</div>
      <div className="cycle-bar-track">
        {phases.map((p, i) => <div key={i} className={`cycle-segment ${p.cls}`} style={{ left: p.left, width: p.width }} />)}
        <div className="cycle-day-marker" style={{ left: `${pct}%` }}>
          <div className="cycle-day-label">D{mare.days_in_cycle}</div>
        </div>
      </div>
    </div>
  );
}

// ─── SHIPMENT CALENDAR ────────────────────────────────────────────────────────
function ShipmentCalendar({ shipments, orders = [], onViewAll }) {
  const today = new Date();
  const [cur, setCur] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(null);

  const year = cur.getFullYear();
  const month = cur.getMonth();
  const monthLabel = cur.toLocaleString("default", { month: "long", year: "numeric" });
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonth = () => setCur(new Date(year, month - 1, 1));
  const nextMonth = () => setCur(new Date(year, month + 1, 1));

  // Build map: "YYYY-MM-DD" → [{shipment, type}]
  const dayMap = {};
  const addToMap = (dateStr, s, type) => {
    if (!dateStr) return;
    const key = dateStr.slice(0, 10);
    const [y, m, d] = key.split("-").map(Number);
    if (y === year && m - 1 === month) {
      if (!dayMap[d]) dayMap[d] = [];
      dayMap[d].push({ s, type });
    }
  };
  shipments.forEach(s => {
    addToMap(s.ship_date, s, "shipped");
    if (s.status === "Delivered") addToMap(s.estimated_delivery, s, "delivered");
    else addToMap(s.estimated_delivery, s, "delivery");
  });
  orders.forEach(o => {
    addToMap(o.date, { ...o, _isOrder: true, recipient_name: o.customer_name, carrier: null, tracking_number: null, contents: o.description }, "order");
  });

  // Build calendar cells (leading blanks + days)
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const CARRIER_ICON = { FedEx: "🟣", UPS: "🟤", USPS: "🔵", DHL: "🟡" };
  const selectedEvents = selected ? (dayMap[selected] || []) : [];

  return (
    <div className="panel" style={{ marginBottom: 22 }}>
      <div className="panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span className="panel-title">📦 Shipment Calendar</span>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Legend */}
          <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 11, color: "var(--text-light)" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--gold)", display: "inline-block" }} /> Order</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--blue)", display: "inline-block" }} /> Ship date</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#1a7f50", display: "inline-block" }} /> Est. delivery</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#9b9b9b", display: "inline-block" }} /> Delivered</span>
          </div>
          <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={onViewAll}>View All →</button>
        </div>
      </div>
      <div style={{ padding: "14px 18px 18px", display: "grid", gridTemplateColumns: "1fr 280px", gap: 20 }}>
        {/* Calendar */}
        <div>
          {/* Month nav */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <button onClick={prevMonth} style={{ background: "none", border: "1px solid var(--parchment-dark)", borderRadius: 5, padding: "3px 10px", cursor: "pointer", fontSize: 14, color: "var(--text-mid)" }}>‹</button>
            <div style={{ fontFamily: "Playfair Display,serif", fontSize: 15, fontWeight: 700, color: "var(--forest)" }}>{monthLabel}</div>
            <button onClick={nextMonth} style={{ background: "none", border: "1px solid var(--parchment-dark)", borderRadius: 5, padding: "3px 10px", cursor: "pointer", fontSize: 14, color: "var(--text-mid)" }}>›</button>
          </div>
          {/* Day-of-week headers */}
          <div className="cal-grid" style={{ marginBottom: 2 }}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <div key={d} className="cal-dow">{d}</div>)}
          </div>
          {/* Day cells */}
          <div className="cal-grid">
            {cells.map((day, i) => {
              if (!day) return <div key={`blank-${i}`} />;
              const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
              const events = dayMap[day] || [];
              const hasEvents = events.length > 0;
              return (
                <div key={day}
                  className={`cal-day ${hasEvents ? "has-events" : ""} ${selected === day ? "selected" : ""} ${isToday ? "today" : ""}`}
                  onClick={() => setSelected(selected === day ? null : day)}>
                  <div className="cal-day-num">{day}</div>
                  <div className="cal-dots">
                    {events.slice(0, 4).map((e, idx) => <div key={idx} className={`cal-dot ${e.type}`} title={`${e.type === "order" ? "Order" : e.type === "shipped" ? "Shipped" : e.type === "delivery" ? "Est. Delivery" : "Delivered"}: ${e.s.recipient_name}`} />)}
                    {events.length > 4 && <div style={{ fontSize: 9, color: "var(--text-light)", lineHeight: "6px" }}>+{events.length - 4}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel: selected day or upcoming */}
        <div style={{ borderLeft: "1px solid var(--parchment-dark)", paddingLeft: 18 }}>
          {selected && selectedEvents.length > 0 ? (<>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 10 }}>
              {new Date(year, month, selected).toLocaleDateString("default", { weekday: "short", month: "short", day: "numeric" })}
            </div>
            {selectedEvents.map((e, idx) => (
              <div key={idx} style={{ padding: "9px 10px", background: "var(--parchment)", borderRadius: 7, marginBottom: 8, borderLeft: `3px solid ${e.type === "order" ? "var(--gold)" : e.type === "shipped" ? "var(--blue)" : e.type === "delivery" ? "#1a7f50" : "#9b9b9b"}` }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--forest)", marginBottom: 2 }}>{e.s.recipient_name}</div>
                {e.type === "order" ? (
                  <><div style={{ fontSize: 11.5, color: "var(--text-mid)" }}>💳 Order · <span style={{ fontWeight: 700, color: e.s.status === "Paid" ? "#1a7f50" : e.s.status === "Processing" ? "#2563eb" : e.s.status === "Partial" ? "#b8860b" : "var(--red)" }}>{e.s.status}</span> · ${(e.s.total || 0).toLocaleString()}</div>
                  {e.s.contents && <div style={{ fontSize: 11, color: "var(--text-light)", marginTop: 2, fontStyle: "italic" }}>{e.s.contents}</div>}</>
                ) : (
                  <><div style={{ fontSize: 11.5, color: "var(--text-mid)" }}>{CARRIER_ICON[e.s.carrier] || "📦"} {e.s.carrier} · {e.type === "shipped" ? "Ship date" : e.type === "delivery" ? "Est. delivery" : "Delivered"}</div>
                  {e.s.tracking_number && <div style={{ fontSize: 10.5, color: "var(--blue)", fontFamily: "monospace", marginTop: 2 }}>{e.s.tracking_number}</div>}
                  {e.s.contents && <div style={{ fontSize: 11, color: "var(--text-light)", marginTop: 2, fontStyle: "italic" }}>{e.s.contents}</div>}</>
                )}
              </div>
            ))}
          </>) : (<>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 10 }}>Upcoming Activity</div>
            {(() => {
              const upcomingShipments = shipments
                .filter(s => s.estimated_delivery && s.status !== "Delivered")
                .map(s => ({ ...s, _type: "shipment", _sortDate: s.estimated_delivery }));
              const pendingOrders = orders
                .filter(o => o.date && o.status !== "Paid" && o.status !== "Cancelled")
                .map(o => ({ ...o, _type: "order", _sortDate: o.date, recipient_name: o.customer_name }));
              const upcoming = [...upcomingShipments, ...pendingOrders]
                .sort((a, b) => a._sortDate > b._sortDate ? 1 : -1)
                .slice(0, 5);
              return upcoming.length === 0
                ? <div style={{ fontSize: 12.5, color: "var(--text-light)", fontStyle: "italic" }}>No upcoming activity. Click a day to see details.</div>
                : upcoming.map(item => (
                    <div key={item.id} style={{ padding: "9px 10px", background: "var(--parchment)", borderRadius: 7, marginBottom: 8, borderLeft: `3px solid ${item._type === "order" ? "var(--gold)" : "#1a7f50"}` }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--forest)", marginBottom: 2 }}>{item.recipient_name}</div>
                      {item._type === "order" ? (
                        <div style={{ fontSize: 11.5, color: "var(--text-mid)" }}>💳 {item.status} · ${(item.total || 0).toLocaleString()} · {item.date}</div>
                      ) : (
                        <div style={{ fontSize: 11.5, color: "var(--text-mid)" }}>{CARRIER_ICON[item.carrier] || "📦"} Est. {item.estimated_delivery}</div>
                      )}
                      {item.description && <div style={{ fontSize: 11, color: "var(--text-light)", marginTop: 2, fontStyle: "italic" }}>{item.description}</div>}
                      {item.contents && <div style={{ fontSize: 11, color: "var(--text-light)", marginTop: 2, fontStyle: "italic" }}>{item.contents}</div>}
                    </div>
                  ));
            })()}
          </>)}
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
// ─── ONBOARDING CHECKLIST ─────────────────────────────────────────────────────
// Admin-only welcome panel that shows 5 setup steps with live completion
// detected from the DB. Auto-hides when all steps are done or the user
// dismisses it. Invited teammates (Staff/Viewer) never see this.
function OnboardingChecklist({ orgId, profile, authUser, setPage }) {
  const lsKey = `et.onboarding_dismissed.${authUser?.id || "anon"}`;
  const [checks, setChecks] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(lsKey) === "1"; } catch (_) { return false; }
  });

  const isAdmin = profile?.role === "Admin";

  useEffect(() => {
    if (!orgId || !isAdmin || dismissed) return;
    let cancelled = false;
    (async () => {
      const [mares, stallions, customers, members, integrations, prices] = await Promise.all([
        supabase.from("mares").select("id", { count: "exact", head: true }).eq("org_id", orgId),
        supabase.from("stallions").select("id", { count: "exact", head: true }).eq("org_id", orgId),
        supabase.from("customers").select("id", { count: "exact", head: true }).eq("org_id", orgId),
        supabase.from("user_profiles").select("id", { count: "exact", head: true }).eq("org_id", orgId),
        supabase.from("org_integrations").select("id", { count: "exact", head: true }).eq("org_id", orgId).eq("provider_type", "email").eq("is_active", true),
        supabase.from("price_book").select("id", { count: "exact", head: true }).eq("org_id", orgId),
      ]);
      if (cancelled) return;
      setChecks({
        horses:   ((mares.count || 0) + (stallions.count || 0)) > 0,
        team:     (members.count || 0) > 1,
        email:    (integrations.count || 0) > 0,
        customer: (customers.count || 0) > 0,
        pricing:  (prices.count || 0) > 0,
      });
    })();
    return () => { cancelled = true; };
  }, [orgId, isAdmin, dismissed]);

  const steps = [
    { key: "horses",   icon: "🐎", title: "Add your first horse",      desc: "Start tracking mares or stallions in your breeding program.", cta: "Add a horse",    page: "herd-mgmt" },
    { key: "team",     icon: "👥", title: "Invite your team",          desc: "Give staff and viewers their own logins with the right permissions.", cta: "Open Team tab", page: "settings"  },
    { key: "email",    icon: "✉️", title: "Connect your email",        desc: "So shipment notices and customer emails go out from your own address.", cta: "Connect email", page: "settings" },
    { key: "customer", icon: "👤", title: "Add your first customer",   desc: "Track mare owners, stud partners, and clients for billing and contracts.", cta: "Add customer", page: "customers" },
    { key: "pricing",  icon: "💲", title: "Review your price book",    desc: "Set services and markups so invoices and quotes calculate correctly.", cta: "Review pricing", page: "settings" },
  ];

  const done = checks ? steps.filter(s => checks[s.key]).length : 0;
  const allDone = checks && done === steps.length;

  useEffect(() => {
    if (!allDone) return;
    try { localStorage.setItem(lsKey, "1"); } catch (_) {}
    const t = setTimeout(() => setDismissed(true), 3500);
    return () => clearTimeout(t);
  }, [allDone, lsKey]);

  if (!isAdmin || dismissed || !checks) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(lsKey, "1"); } catch (_) {}
    setDismissed(true);
  };

  return (
    <div style={{
      background: "white", border: "1px solid var(--parchment-dark)", borderRadius: 10,
      marginBottom: 22, overflow: "hidden", boxShadow: "0 2px 6px rgba(26,46,26,.04)",
    }}>
      <div style={{
        background: "linear-gradient(135deg, var(--forest) 0%, #0d2010 100%)",
        padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, color: "var(--gold-light)", fontWeight: 700 }}>
            {allDone ? "🎉 You're all set" : "Welcome — let's finish setup"}
          </div>
          <div style={{ fontSize: 12.5, color: "rgba(226,201,126,.55)", marginTop: 2, fontStyle: "italic" }}>
            {allDone
              ? "Nice work. This panel will disappear in a moment."
              : `${steps.length} quick steps to get ArcStallion working for your farm`}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <div style={{
            background: "rgba(201,168,76,.15)", color: "var(--gold-light)",
            padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            whiteSpace: "nowrap",
          }}>{done} of {steps.length} done</div>
          <button onClick={handleDismiss} aria-label="Dismiss setup panel"
            title="Dismiss — you can always set these up later from Settings"
            style={{ background: "none", border: "none", color: "rgba(245,240,232,.5)", cursor: "pointer", fontSize: 20, padding: 0, lineHeight: 1 }}>
            ×
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 4, background: "var(--parchment-dark)" }}>
        <div style={{ height: "100%", width: `${(done / steps.length) * 100}%`, background: "var(--gold)", transition: "width .3s" }} />
      </div>

      {/* Steps */}
      <div>
        {steps.map((s, i) => {
          const isDone = checks[s.key];
          return (
            <div key={s.key} style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "14px 20px",
              borderBottom: i === steps.length - 1 ? "none" : "1px solid var(--parchment-dark)",
              background: isDone ? "rgba(46,125,110,.04)" : "transparent",
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: isDone ? "var(--teal)" : "transparent",
                border: isDone ? "none" : "2px solid var(--parchment-dark)",
                color: "white", fontSize: 13, fontWeight: 700, flexShrink: 0,
              }}>{isDone ? "✓" : ""}</div>
              <div style={{ fontSize: 20, flexShrink: 0, opacity: isDone ? 0.5 : 1 }}>{s.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 14, fontWeight: 600, color: "var(--forest)",
                  textDecoration: isDone ? "line-through" : "none",
                  opacity: isDone ? 0.55 : 1,
                }}>{s.title}</div>
                <div style={{ fontSize: 12.5, color: "var(--text-light)", marginTop: 2, opacity: isDone ? 0.55 : 1 }}>
                  {s.desc}
                </div>
              </div>
              {!isDone && (
                <button onClick={() => setPage(s.page)} className="btn btn-outline btn-sm" style={{ flexShrink: 0 }}>
                  {s.cta} →
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Dashboard({ orgId, orgName, orgLogo, setPage, profile, authUser, openMare }) {
  const { data: mares } = useSupabaseTable("mares", orgId);
  const { data: breedingEvents } = useSupabaseTable("breeding_events", orgId);
  const { data: embryos } = useSupabaseTable("embryos", orgId);
  const { data: orders } = useSupabaseTable("orders", orgId);
  const { data: shipments } = useShipments(orgId);
  const { data: frozenSemen } = useSupabaseTable("frozen_semen", orgId);

  const pregnant = mares.filter(m => m.status === "Pregnant").length;
  const stored = embryos.filter(e => e.status === "Frozen" || e.status === "Stored").length;
  const pendingRev = orders.filter(o => o.status === "Pending" || o.status === "Partial").reduce((a, o) => a + (o.total || 0), 0);
  const totalStraws = frozenSemen.reduce((a, s) => a + (s.straws_available || 0), 0);
  const inTransit = shipments.filter(s => s.status === "In Transit" || s.status === "Label Created").length;

  return (<>
    {/* Onboarding checklist (Admin only, auto-hides when complete) */}
    <OnboardingChecklist orgId={orgId} profile={profile} authUser={authUser} setPage={setPage} />

    {/* Org welcome banner */}
    <div style={{ background: "linear-gradient(135deg,var(--forest) 0%,#0d2010 100%)", borderRadius: 10, padding: "18px 24px", marginBottom: 22, border: "1px solid rgba(201,168,76,.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        {orgLogo && (
          <img src={orgLogo} alt={orgName} style={{ height: 60, width: "auto", objectFit: "contain" }}
            onError={e => e.target.style.display = "none"} />
        )}
        <div>
          <div style={{ fontFamily: "Playfair Display,serif", fontSize: 20, color: "var(--gold-light)", fontWeight: 700 }}>{orgName}</div>
          <div style={{ fontSize: 13, color: "rgba(226,201,126,.55)", marginTop: 3, fontStyle: "italic" }}>ArcStallion Pro · Season Overview</div>
        </div>
      </div>
    </div>

    <div className="stats-row">
      <div className="stat-card" style={{ cursor: "pointer" }} onClick={() => setPage("mares")}><div className="stat-icon">🐴</div><div className="stat-label">Total Mares</div><div className="stat-value">{mares.length}</div><div className="stat-meta">Active in program →</div></div>
      <div className="stat-card green" style={{ cursor: "pointer" }} onClick={() => setPage("mares")}><div className="stat-icon">🤰</div><div className="stat-label">Pregnant</div><div className="stat-value">{pregnant}</div><div className="stat-meta">{mares.length ? Math.round(pregnant / mares.length * 100) : 0}% pregnancy rate →</div></div>
      <div className="stat-card blue" style={{ cursor: "pointer" }} onClick={() => setPage("frozen-semen")}><div className="stat-icon">🧊</div><div className="stat-label">Frozen Straws</div><div className="stat-value">{totalStraws}</div><div className="stat-meta">In cryogenic storage →</div></div>
      <div className="stat-card red" style={{ cursor: "pointer" }} onClick={() => setPage("orders")}><div className="stat-icon">💰</div><div className="stat-label">Pending Revenue</div><div className="stat-value">${(pendingRev / 1000).toFixed(1)}k</div><div className="stat-meta">Awaiting payment →</div></div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 18, marginBottom: 22 }}>
      <div className="panel">
        <div className="panel-header"><span className="panel-title">Cycle Overview</span></div>
        <div style={{ padding: "12px 17px 14px" }}>
          <div style={{ fontSize: 11, color: "var(--text-light)", marginBottom: 6, fontStyle: "italic" }}>21-day cycle position</div>
          {mares.filter(m => m.days_in_cycle).map(m => <CycleTimeline key={m.id} mare={m} />)}
          {mares.filter(m => m.days_in_cycle).length === 0 && <div className="empty-state" style={{ padding: "14px 0" }}>No mares in active cycle.</div>}
        </div>
      </div>
      <div className="panel">
        <div className="panel-header"><span className="panel-title">Quick Stats</span></div>
        <div style={{ padding: "14px 17px" }}>
          {[
            ["Embryos in Storage", stored],
            ["In Transit Shipments", inTransit],
            ["Open Mares", mares.filter(m => m.status === "Open").length],
            ["Flush Scheduled", mares.filter(m => m.status === "Flush Scheduled").length],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid var(--parchment-dark)" }}>
              <span style={{ fontSize: 13, color: "var(--text-mid)" }}>{k}</span>
              <span style={{ fontFamily: "Playfair Display,serif", fontSize: 20, color: "var(--forest)", fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {(() => {
      // Overdue plus the next 7 days.
      const today = localToday(), weekOut = shiftISO(today, 7);
      const checks = scheduledChecksFrom(breedingEvents, mares).filter(e => e.event_date <= weekOut);
      return (
        <div className="panel" style={{ marginBottom: 22 }}>
          <div className="panel-header"><span className="panel-title">📡 Upcoming Checks</span>
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--cream)", cursor: "pointer" }} role="button" tabIndex={0}
              onClick={() => setPage("herd-mgmt")} onKeyDown={e => { if (e.key === "Enter") setPage("herd-mgmt"); }}>Calendar →</span></div>
          <div style={{ padding: "6px 17px 12px" }}>
            <ScheduledChecksList checks={checks} today={today} onOpen={openMare}
              empty="Nothing in the next 7 days. After you log a palpation or pregnancy check, you'll be asked when the next one is." />
          </div>
        </div>
      );
    })()}

    <ShipmentCalendar shipments={shipments} orders={orders} onViewAll={() => setPage("shipping")} />
  </>);
}

// ─── STALLION MANAGEMENT ─────────────────────────────────────────────────────
function StallionManagement({ orgId }) {
  const { data: stallions, setData: setStallions, loading } = useSupabaseTable("stallions", orgId);
  const { data: farrierRecords, setData: setFarrierRecords } = useSupabaseTable("farrier_records", orgId);
  const { data: savedFarriers, setData: setSavedFarriers } = useSupabaseTable("farriers", orgId);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [viewingStallion, setViewingStallion] = useState(null);
  const [editingStallion, setEditingStallion] = useState(null); // null = view mode, object = edit mode
  const [stallionTab, setStallionTab] = useState("details");
  const [showFarrierForm, setShowFarrierForm] = useState(false);
  const [editingFarrier, setEditingFarrier] = useState(null);
  const emptyFarrier = { farrier_name: "", visit_date: localToday(), service_type: "complete_shoeing", cost: "", notes: "", hoof_condition: "", next_appointment: "" };
  const [farrierForm, setFarrierForm] = useState(emptyFarrier);
  const ff = k => e => setFarrierForm(p => ({ ...p, [k]: e.target.value }));
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const emptyForm = { name: "", registered_name: "", registration_number: "", breed: "", age: "", owner: "", status: "Ready", notes: "", photo_url: "" };
  const [form, setForm] = useState(emptyForm);

  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handlePhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const url = await uploadHorsePhoto(file, orgId); setForm(p => ({ ...p, photo_url: url })); }
    catch (err) { alert("Photo upload failed. Make sure the 'horse-photos' storage bucket exists in Supabase. Error: " + err.message); }
    setUploading(false);
  };

  const addStallion = async () => {
    if (!form.name.trim()) return;
    try {
      const row = await dbInsert("stallions", { id: crypto.randomUUID(), ...form, age: parseInt(form.age) || null, org_id: orgId });
      setStallions(p => [row, ...p]);
      setShowAdd(false); setForm(emptyForm);
      save();
    } catch (e) { alert("Error saving: " + e.message); }
  };

  const startEditStallion = () => {
    setEditingStallion({
      name: viewingStallion.name || "", registered_name: viewingStallion.registered_name || "",
      registration_number: viewingStallion.registration_number || "", owner: viewingStallion.owner || "",
      breed: viewingStallion.breed || "", age: viewingStallion.age || "",
      status: viewingStallion.status || "Ready", notes: viewingStallion.notes || "",
      concentration: viewingStallion.concentration || "", motility: viewingStallion.motility || "",
      morphology: viewingStallion.morphology || "", frozen_doses: viewingStallion.frozen_doses || 0,
      fresh_doses: viewingStallion.fresh_doses || 0
    });
  };

  const saveEditStallion = async () => {
    try {
      // Clean all fields — convert empty strings to null for any column that might be integer type
      const updates = {};
      for (const [k, v] of Object.entries(editingStallion)) {
        if (v === "" || v === undefined) { updates[k] = null; }
        else if (["age", "frozen_doses", "fresh_doses"].includes(k)) { updates[k] = parseInt(v) || 0; }
        else { updates[k] = v; }
      }
      if (updates.age === 0) updates.age = null;
      await dbUpdate("stallions", viewingStallion.id, updates);
      const updated = { ...viewingStallion, ...updates };
      setStallions(p => p.map(s => s.id === viewingStallion.id ? updated : s));
      setViewingStallion(updated);
      setEditingStallion(null);
      save();
    } catch (e) { alert("Error updating: " + e.message); }
  };

  const sef = k => e => setEditingStallion(p => ({ ...p, [k]: e.target.value }));

  const updatePhoto = async (stallion, file) => {
    try {
      const url = await uploadHorsePhoto(file, orgId);
      await dbUpdate("stallions", stallion.id, { photo_url: url });
      setStallions(p => p.map(s => s.id === stallion.id ? { ...s, photo_url: url } : s));
      setViewingStallion(v => v ? { ...v, photo_url: url } : v);
      save();
    } catch (err) { alert("Photo upload failed: " + err.message); }
  };

  // Farrier CRUD for stallions
  const addFarrierRecord = async () => {
    if (!farrierForm.farrier_name || !viewingStallion) return;
    try {
      const row = await dbInsert("farrier_records", { id: crypto.randomUUID(), org_id: orgId, horse_id: viewingStallion.id, horse_name: viewingStallion.name, horse_type: "stallion", ...farrierForm, cost: parseFloat(farrierForm.cost) || 0, next_appointment: farrierForm.next_appointment || null });
      setFarrierRecords(p => [row, ...p]);
      setShowFarrierForm(false); setFarrierForm(emptyFarrier); save();
    } catch (e) { alert("Error: " + e.message); }
  };
  const saveFarrierEdit = async () => {
    if (!editingFarrier) return;
    try {
      const updates = { ...farrierForm, cost: parseFloat(farrierForm.cost) || 0, next_appointment: farrierForm.next_appointment || null };
      await dbUpdate("farrier_records", editingFarrier.id, updates);
      setFarrierRecords(p => p.map(r => r.id === editingFarrier.id ? { ...r, ...updates } : r));
      setEditingFarrier(null); setShowFarrierForm(false); setFarrierForm(emptyFarrier); save();
    } catch (e) { alert("Error: " + e.message); }
  };
  const deleteFarrierRecord = async (rec) => {
    if (!window.confirm("Delete this farrier record?")) return;
    try { await dbDelete("farrier_records", rec.id); } catch (e) { alert(e.message); return; }
    setFarrierRecords(p => p.filter(r => r.id !== rec.id)); save();
  };

  const filtered = stallions.filter(s => (s.name + (s.breed || "")).toLowerCase().includes(search.toLowerCase()));

  return (<>
    <div className="page-header-row">
      
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        {saved && <span style={{ color: "var(--gold)", fontSize: 13 }}>✓ Saved</span>}
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Stallion</button>
      </div>
    </div>
    <div className="stats-row" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
      <div className="stat-card"><div className="stat-icon">🐎</div><div className="stat-label">Total Stallions</div><div className="stat-value">{stallions.length}</div><div className="stat-meta">{stallions.filter(s => s.status === "Ready").length} ready to breed</div></div>
      <div className="stat-card green"><div className="stat-icon">🧊</div><div className="stat-label">Frozen Doses</div><div className="stat-value">{stallions.reduce((a, s) => a + (s.frozen_doses || 0), 0)}</div><div className="stat-meta">Across all stallions</div></div>
      <div className="stat-card"><div className="stat-icon">💉</div><div className="stat-label">Fresh Doses</div><div className="stat-value">{stallions.reduce((a, s) => a + (s.fresh_doses || 0), 0)}</div><div className="stat-meta">Available this season</div></div>
    </div>
    {loading ? <div className="empty-state">Loading stallions…</div> : (
      <div className="full-panel">
        <div className="filter-bar">
          <input className="search-input" placeholder="Search stallions…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {filtered.length === 0
          ? <div className="empty-state">No stallions yet. Click "+ Add Stallion" to begin.</div>
          : <div className="horse-grid">
              {filtered.map(s => (
                <div className="horse-card" key={s.id} onClick={() => setViewingStallion(s)}>
                  {s.photo_url
                    ? <HorsePhoto className="horse-photo" value={s.photo_url} alt={s.name} />
                    : <div className="horse-photo-placeholder">🐎</div>}
                  <div className="horse-card-body">
                    <div className="horse-name">{s.name}</div>
                    {s.registered_name && <div style={{ fontSize: 11, fontStyle: "italic", color: "var(--text-light)", marginBottom: 2 }}>{s.registered_name}</div>}
                    <div className="horse-detail">{[s.breed, s.age ? `${s.age} yrs` : null].filter(Boolean).join(" · ")}</div>
                    <StatusBadge status={s.status} />
                    {s.owner && <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-light)" }}>👤 {s.owner}</div>}
                    {(s.frozen_doses || s.fresh_doses) ? (
                      <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--text-light)", display: "flex", gap: 10 }}>
                        {s.frozen_doses ? <span>🧊 {s.frozen_doses} frozen</span> : null}
                        {s.fresh_doses ? <span>💉 {s.fresh_doses} fresh</span> : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
        }
      </div>
    )}
    {/* ADD MODAL */}
    {showAdd && (
      <div className="modal-overlay" onClick={() => setShowAdd(false)}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><div className="modal-title">Add New Stallion</div><button className="modal-close" onClick={() => setShowAdd(false)}>✕</button></div>
          <div className="modal-body">
            <div style={{ marginBottom: 16 }}>
              {form.photo_url
                ? <HorsePhoto value={form.photo_url} alt="preview" style={{ width: "100%", height: 160, objectFit: "cover", borderRadius: 6, marginBottom: 8 }} />
                : null}
              <label className="upload-photo-btn">
                {uploading ? "Uploading…" : "📷 Upload Photo"}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhoto} disabled={uploading} />
              </label>
            </div>
            <div className="form-grid">
              <div className="form-row"><label className="form-label">Barn Name *</label><input className="form-input" value={form.name} onChange={sf("name")} placeholder="Common / barn name" /></div>
              <div className="form-row"><label className="form-label">Registered Name</label><input className="form-input" value={form.registered_name} onChange={sf("registered_name")} placeholder="Full AQHA / APHA name" /></div>
              <div className="form-row"><label className="form-label">Registration #</label><input className="form-input" value={form.registration_number} onChange={sf("registration_number")} placeholder="e.g. AQHA 1234567" /></div>
              <div className="form-row"><label className="form-label">Breed</label><input className="form-input" value={form.breed} onChange={sf("breed")} /></div>
              <div className="form-row"><label className="form-label">Age</label><input className="form-input" type="number" value={form.age} onChange={sf("age")} /></div>
              <div className="form-row"><label className="form-label">Owner</label><input className="form-input" value={form.owner} onChange={sf("owner")} placeholder="Owner name" /></div>
              <div className="form-row"><label className="form-label">Status</label>
                <select className="form-input" value={form.status} onChange={sf("status")}><option>Ready</option><option>Resting</option><option>Retired</option><option>Injured</option></select>
              </div>
              <div className="form-row" style={{ gridColumn: "1/-1" }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={sf("notes")} /></div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={addStallion}>Save Stallion</button>
          </div>
        </div>
      </div>
    )}
    {/* FLYOUT */}
    {viewingStallion && (
      <div className="modal-overlay" onClick={() => setViewingStallion(null)}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, padding: 0, overflow: "hidden" }}>
          <div style={{ position: "relative" }}>
            {viewingStallion.photo_url
              ? <HorsePhoto value={viewingStallion.photo_url} alt={viewingStallion.name} style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }} />
              : <div style={{ width: "100%", height: 160, background: "linear-gradient(160deg,var(--forest),#1e3d1e)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 72 }}>🐎</div>}
            <button className="modal-close" onClick={() => setViewingStallion(null)} style={{ position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,.5)", color: "white", border: "none" }}>✕</button>
            <label style={{ position: "absolute", bottom: 10, right: 10, background: "rgba(0,0,0,.55)", color: "white", fontSize: 11.5, padding: "5px 10px", borderRadius: 5, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
              📷 Change Photo
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) updatePhoto(viewingStallion, e.target.files[0]); }} />
            </label>
          </div>
          <div style={{ padding: "18px 22px 22px", maxHeight: "60vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <div style={{ fontFamily: "Playfair Display,serif", fontSize: 22, fontWeight: 700, color: "var(--forest)" }}>{viewingStallion.name}</div>
                {viewingStallion.registered_name && <div style={{ fontSize: 12.5, color: "var(--text-mid)", fontStyle: "italic", marginTop: 2 }}>{viewingStallion.registered_name}</div>}
                <div style={{ fontSize: 13, color: "var(--text-light)", marginTop: 3 }}>{[viewingStallion.breed, viewingStallion.age ? `${viewingStallion.age} years old` : null].filter(Boolean).join(" · ")}</div>
              </div>
              <StatusBadge status={viewingStallion.status} />
            </div>
            <div className="breed-tab-bar">
              <button className={`breed-tab ${stallionTab === "details" ? "active" : ""}`} onClick={() => setStallionTab("details")}>Details</button>
              <button className={`breed-tab ${stallionTab === "farrier" ? "active" : ""}`} onClick={() => setStallionTab("farrier")}>Farrier ({farrierRecords.filter(r => r.horse_id === viewingStallion.id).length})</button>
            </div>
            {stallionTab === "details" && (<>
            {!editingStallion ? (<>
              {/* VIEW MODE */}
              {(viewingStallion.registration_number || viewingStallion.owner) && (
                <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 12.5 }}>
                  {viewingStallion.registration_number && <span>📋 <strong>Reg #:</strong> {viewingStallion.registration_number}</span>}
                  {viewingStallion.owner && <span>👤 <strong>Owner:</strong> {viewingStallion.owner}</span>}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                <div style={{ background: "var(--parchment)", borderRadius: 7, padding: "11px 14px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--text-light)", marginBottom: 4 }}>Frozen Doses</div>
                  <div style={{ fontFamily: "Playfair Display,serif", fontSize: 24, fontWeight: 700, color: "var(--forest)" }}>{viewingStallion.frozen_doses || 0}</div>
                </div>
                <div style={{ background: "var(--parchment)", borderRadius: 7, padding: "11px 14px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--text-light)", marginBottom: 4 }}>Fresh Doses</div>
                  <div style={{ fontFamily: "Playfair Display,serif", fontSize: 24, fontWeight: 700, color: "var(--forest)" }}>{viewingStallion.fresh_doses || 0}</div>
                </div>
              </div>
              {viewingStallion.notes && <div style={{ background: "var(--parchment)", borderRadius: 6, padding: "10px 13px", fontSize: 13, fontStyle: "italic", color: "var(--text-mid)", lineHeight: 1.55 }}>{viewingStallion.notes}</div>}
            </>) : (<>
              {/* EDIT MODE */}
              <div className="form-grid">
                <div className="form-row"><label className="form-label">Barn Name *</label><input className="form-input" value={editingStallion.name} onChange={sef("name")} /></div>
                <div className="form-row"><label className="form-label">Registered Name</label><input className="form-input" value={editingStallion.registered_name} onChange={sef("registered_name")} placeholder="Full AQHA / APHA name" /></div>
                <div className="form-row"><label className="form-label">Registration #</label><input className="form-input" value={editingStallion.registration_number} onChange={sef("registration_number")} /></div>
                <div className="form-row"><label className="form-label">Breed</label><input className="form-input" value={editingStallion.breed} onChange={sef("breed")} /></div>
                <div className="form-row"><label className="form-label">Age</label><input className="form-input" type="number" value={editingStallion.age} onChange={sef("age")} /></div>
                <div className="form-row"><label className="form-label">Owner</label><input className="form-input" value={editingStallion.owner} onChange={sef("owner")} /></div>
                <div className="form-row"><label className="form-label">Status</label>
                  <select className="form-input" value={editingStallion.status} onChange={sef("status")}><option>Ready</option><option>Resting</option><option>Retired</option><option>Injured</option></select>
                </div>
                <div className="form-row"><label className="form-label">Frozen Doses</label><input className="form-input" type="number" value={editingStallion.frozen_doses} onChange={sef("frozen_doses")} /></div>
                <div className="form-row"><label className="form-label">Fresh Doses</label><input className="form-input" type="number" value={editingStallion.fresh_doses} onChange={sef("fresh_doses")} /></div>
                <div className="form-row" style={{ gridColumn: "1/-1" }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={editingStallion.notes} onChange={sef("notes")} /></div>
              </div>
            </>)}
            </>)}
            {stallionTab === "farrier" && (<>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-light)" }}>Farrier Records</div>
                <button className="btn btn-sm btn-primary" onClick={() => { setShowFarrierForm(true); setEditingFarrier(null); setFarrierForm(emptyFarrier); }}>+ Add Visit</button>
              </div>
              {showFarrierForm && (
                <div style={{ background: "var(--parchment)", borderRadius: 8, padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--forest)", marginBottom: 10 }}>{editingFarrier ? "Edit Farrier Visit" : "Log Farrier Visit"}</div>
                  <div className="form-grid">
                    <div className="form-row"><label className="form-label">Farrier *</label>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <select className="form-input" style={{ flex: 1 }} value={farrierForm.farrier_name} onChange={ff("farrier_name")}>
                          <option value="">Select...</option>
                          {savedFarriers.sort((a, b) => a.name.localeCompare(b.name)).map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                        </select>
                        <button type="button" onClick={async () => { const name = window.prompt("Enter farrier name:"); if (!name || !name.trim()) return; const trimmed = name.trim(); if (savedFarriers.some(f => f.name.toLowerCase() === trimmed.toLowerCase())) { ff("farrier_name")({ target: { value: trimmed } }); return; } const row = await dbInsert("farriers", { org_id: orgId, name: trimmed }); if (row) { setSavedFarriers(p => [...p, row]); ff("farrier_name")({ target: { value: trimmed } }); } }} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "var(--forest)", color: "white", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, lineHeight: 1 }} title="Add new farrier">+</button>
                      </div>
                    </div>
                    <div className="form-row"><label className="form-label">Visit Date *</label><input className="form-input" type="date" value={farrierForm.visit_date} onChange={ff("visit_date")} /></div>
                    <div className="form-row"><label className="form-label">Service Type</label>
                      <select className="form-input" value={farrierForm.service_type} onChange={ff("service_type")}>
                        <option value="complete_shoeing">Complete Shoeing</option><option value="back_shoeing">Back Shoeing</option><option value="front_shoeing">Front Shoeing</option><option value="trimming">Trimming</option><option value="replace_lost">Replace Lost Horseshoe</option><option value="one_shoe_left">One Shoe (Left)</option><option value="one_shoe_right">One Shoe (Right)</option><option value="hind_trim">Hind Trim</option><option value="front_trim">Front Trim</option>
                      </select>
                    </div>
                    <div className="form-row"><label className="form-label">Cost ($)</label><input className="form-input" type="number" step="0.01" value={farrierForm.cost} onChange={ff("cost")} placeholder="0.00" /></div>
                    <div className="form-row"><label className="form-label">Hoof Condition</label>
                      <select className="form-input" value={farrierForm.hoof_condition} onChange={ff("hoof_condition")}>
                        <option value="">-- Select --</option><option value="excellent">Excellent</option><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option><option value="needs_attention">Needs Attention</option>
                      </select>
                    </div>
                    <div className="form-row"><label className="form-label">Next Appointment</label><input className="form-input" type="date" value={farrierForm.next_appointment} onChange={ff("next_appointment")} /></div>
                    <div className="form-row" style={{ gridColumn: "1/-1" }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={farrierForm.notes} onChange={ff("notes")} placeholder="Hoof notes, observations, etc." /></div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button className="btn btn-primary" onClick={editingFarrier ? saveFarrierEdit : addFarrierRecord}>{editingFarrier ? "Save Changes" : "Save Record"}</button>
                    <button className="btn btn-outline" onClick={() => { setShowFarrierForm(false); setEditingFarrier(null); }}>Cancel</button>
                  </div>
                </div>
              )}
              {(() => {
                const horseRecords = farrierRecords.filter(r => r.horse_id === viewingStallion.id).sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date));
                const nextAppt = horseRecords.find(r => r.next_appointment && new Date(r.next_appointment) >= new Date());
                return (<>
                  {nextAppt && (
                    <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 20 }}>📅</span>
                      <div><div style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>Next Appointment</div><div style={{ fontSize: 13, color: "#15803d" }}>{nextAppt.next_appointment} - {nextAppt.farrier_name}</div></div>
                    </div>
                  )}
                  {horseRecords.length === 0 ? <div className="empty-state" style={{ padding: "20px 0" }}>No farrier records yet. Click "+ Add Visit" to log a farrier visit.</div> : (
                    horseRecords.map(rec => (
                      <div key={rec.id} style={{ background: "var(--parchment)", borderRadius: 8, padding: "12px 14px", marginBottom: 8, borderLeft: `3px solid ${rec.hoof_condition === "excellent" || rec.hoof_condition === "good" ? "#4caf50" : rec.hoof_condition === "fair" ? "#ff9800" : rec.hoof_condition === "poor" || rec.hoof_condition === "needs_attention" ? "#f44336" : "#795548"}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--forest)" }}>{rec.visit_date} - {rec.service_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</div>
                            <div style={{ fontSize: 12, color: "var(--text-mid)", marginTop: 2 }}>Farrier: {rec.farrier_name}{rec.cost ? ` | $${parseFloat(rec.cost).toFixed(2)}` : ""}</div>
                            {rec.hoof_condition && <div style={{ fontSize: 11, marginTop: 3 }}>🦶 Hoof: <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{rec.hoof_condition.replace(/_/g, " ")}</span></div>}
                            {rec.notes && <div style={{ fontSize: 12, color: "var(--text-light)", marginTop: 4, fontStyle: "italic" }}>{rec.notes}</div>}
                            {rec.next_appointment && <div style={{ fontSize: 11, color: "#2563eb", marginTop: 3 }}>📅 Next: {rec.next_appointment}</div>}
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13 }} onClick={() => { setEditingFarrier(rec); setFarrierForm({ farrier_name: rec.farrier_name, visit_date: rec.visit_date, service_type: rec.service_type, cost: rec.cost || "", notes: rec.notes || "", hoof_condition: rec.hoof_condition || "", next_appointment: rec.next_appointment || "" }); setShowFarrierForm(true); }}>✏️</button>
                            {canDelete() && <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--red)" }} onClick={() => deleteFarrierRecord(rec)}>✕</button>}
                          </div>
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", marginBottom: 4 }}>Attachments</div>
                          {(rec.photo_urls || []).length > 0 && (
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                              {(rec.photo_urls || []).map((url, pi) => (
                                <div key={pi} style={{ position: "relative", width: 80, height: 80, borderRadius: 6, overflow: "hidden", border: "1px solid #ddd", cursor: "pointer" }}>
                                  <HorsePhoto value={url} alt={`Photo ${pi + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm("Remove this photo?")) return; const updated = (rec.photo_urls || []).filter(p => p !== url); await dbUpdate("farrier_records", rec.id, { photo_urls: updated }); setFarrierRecords(p => p.map(r => r.id === rec.id ? { ...r, photo_urls: updated } : r)); }} style={{ position: "absolute", top: 2, right: 2, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,.6)", color: "white", border: "none", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div
                            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "#eff6ff"; }}
                            onDragLeave={e => { e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.background = "white"; }}
                            onDrop={async e => { e.preventDefault(); e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.background = "white"; const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/")); if (!files.length) return; const newUrls = []; for (const file of files) { const url = await uploadHorsePhoto(file, orgId); newUrls.push(url); } const updated = [...(rec.photo_urls || []), ...newUrls]; await dbUpdate("farrier_records", rec.id, { photo_urls: updated }); setFarrierRecords(p => p.map(r => r.id === rec.id ? { ...r, photo_urls: updated } : r)); }}
                            onClick={() => { const input = document.createElement("input"); input.type = "file"; input.accept = "image/*"; input.multiple = true; input.onchange = async e => { const files = Array.from(e.target.files).filter(f => f.type.startsWith("image/")); if (!files.length) return; const newUrls = []; for (const file of files) { const url = await uploadHorsePhoto(file, orgId); newUrls.push(url); } const updated = [...(rec.photo_urls || []), ...newUrls]; await dbUpdate("farrier_records", rec.id, { photo_urls: updated }); setFarrierRecords(p => p.map(r => r.id === rec.id ? { ...r, photo_urls: updated } : r)); }; input.click(); }}
                            style={{ border: "2px dashed #93c5fd", borderRadius: 8, padding: "18px 14px", textAlign: "center", cursor: "pointer", background: "white", transition: "all .2s" }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                              <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#666" }}>+</span>
                              <span style={{ fontSize: 13, color: "#555", fontWeight: 500 }}>Add attachment files</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </>);
              })()}
            </>)}
          </div>
          <div className="modal-footer">
            {stallionTab === "details" && !editingStallion ? (<>
              <button className="btn btn-outline" onClick={() => { setViewingStallion(null); setEditingStallion(null); setStallionTab("details"); }}>Close</button>
              <button className="btn btn-primary" onClick={startEditStallion}>✏️ Edit</button>
              {canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={() => confirmDelete(`Delete ${viewingStallion.name}? This can't be undone.`, async () => { await dbDelete("stallions", viewingStallion.id); setStallions(p => p.filter(x => x.id !== viewingStallion.id)); setViewingStallion(null); save(); })}>Delete</button>}
            </>) : stallionTab === "details" && editingStallion ? (<>
              <button className="btn btn-outline" onClick={() => setEditingStallion(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEditStallion}>Save Changes</button>
            </>) : (
              <button className="btn btn-outline" onClick={() => { setViewingStallion(null); setStallionTab("details"); }}>Close</button>
            )}
          </div>
        </div>
      </div>
    )}
    <SaveToast show={saved} />
  </>);
}

// ─── WHO DID IT: the farm's own people ───────────────────────────────────────
// "Performed By", "Person Requesting" and "Collector" used to offer Trista and Jessi, Capital
// Quarter Horses' staff, to every farm. The list is now the farm's team (first
// name, or full name when two share a first name, which matches how CQH's
// existing records were written) plus any name already used on the farm's
// records (a vet, "Vet"), and "Someone else" for anyone new.
function useTeamNames(orgId) {
  const [names, setNames] = useState([]);
  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    supabase.from("user_profiles").select("full_name").eq("org_id", orgId).then(({ data, error }) => {
      if (!alive) return;
      if (error) { console.error("[useTeamNames]", error); return; }
      const full = (data || []).map(r => (r.full_name || "").trim()).filter(Boolean);
      const first = n => n.split(/\s+/)[0].toLowerCase();
      const counts = {};
      full.forEach(n => { counts[first(n)] = (counts[first(n)] || 0) + 1; });
      setNames(full.map(n => counts[first(n)] > 1 ? n : n.split(/\s+/)[0]));
    });
    return () => { alive = false; };
  }, [orgId]);
  return names;
}

// Unique names, case-insensitive, sorted.
const mergeNames = (...lists) => {
  const seen = new Set(), out = [];
  for (const n of lists.flat()) {
    const v = (n || "").trim();
    if (v && !seen.has(v.toLowerCase())) { seen.add(v.toLowerCase()); out.push(v); }
  }
  return out.sort((a, b) => a.localeCompare(b));
};

// onChange receives the name, not an event.
function PersonSelect({ value, onChange, options = [], className = "form-input", style }) {
  const v = value || "";
  const [typing, setTyping] = useState(false);
  if (typing) {
    return (
      <div style={{ display: "flex", gap: 6 }}>
        <input className={className} style={{ ...style, flex: 1 }} autoFocus value={v} placeholder="Type a name" aria-label="Name"
          onChange={e => onChange(e.target.value)} />
        <button type="button" className="btn btn-sm btn-outline" onClick={() => setTyping(false)}>Done</button>
      </div>
    );
  }
  const opts = mergeNames(options, v ? [v] : []);
  return (
    <select className={className} style={style} value={v}
      onChange={e => { if (e.target.value === "__other__") { onChange(""); setTyping(true); } else onChange(e.target.value); }}>
      <option value="">— Select —</option>
      {opts.map(n => <option key={n} value={n}>{n}</option>)}
      <option value="__other__">Someone else…</option>
    </select>
  );
}

// ─── MARE GROUPS & SCHEDULED CHECKS ──────────────────────────────────────────
// Groups: each farm makes its own ("Recips", "Broodmares"). A mare's groups are
// ids in mares.group_ids; deleting a group strips its id from every mare in the
// database (trigger in 20260928120000_mare_groups_and_scheduled_checks).
// Scheduled checks: breeding_events rows with status 'scheduled'. When the vet
// does the check, the same row is filled in and flips to 'done'. Anything that
// reads breeding_events as history must skip scheduled rows.
const SCHEDULABLE_EVENTS = ["palpation", "pregnancy_check"];
const isScheduled = e => e?.status === "scheduled";

const chipStyle = on => ({
  fontSize: 12, padding: "5px 12px", borderRadius: 999, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
  border: `1px solid ${on ? "var(--forest)" : "var(--parchment-dark)"}`, background: on ? "var(--forest)" : "#fff",
  color: on ? "#fff" : "var(--text-mid)", fontWeight: on ? 700 : 500,
});
const sortGroups = gs => [...gs].sort((a, b) => ((a.sort_order || 0) - (b.sort_order || 0)) || (a.name || "").localeCompare(b.name || ""));
const mareGroupsOf = (mare, groups) => (mare?.group_ids || []).map(id => groups.find(g => g.id === id)).filter(Boolean);

// Local calendar arithmetic on "YYYY-MM-DD" strings (see localToday()).
const shiftISO = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(y, m - 1, d + n);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};
const fmtDay = iso => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
const fmtTime = t => {
  if (!t) return "";
  const [h, mi] = String(t).split(":").map(Number);
  if (Number.isNaN(h)) return t;
  return new Date(2000, 0, 1, h, mi || 0).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

// Scheduled checks with the mare's name, soonest first. Checks for a mare that
// no longer exists are dropped.
const scheduledChecksFrom = (events, mares) => events.filter(isScheduled)
  .map(e => ({ ...e, mare_name: mares.find(m => m.id === e.mare_id)?.name }))
  .filter(e => e.mare_name)
  .sort((a, b) => (a.event_date + (a.event_time || "")).localeCompare(b.event_date + (b.event_time || "")));

function ScheduledChecksList({ checks, today, onOpen, empty }) {
  if (!checks.length) return <div className="empty-state" style={{ padding: "8px 0" }}>{empty}</div>;
  return checks.map(ev => {
    const late = ev.event_date < today;
    const open = () => onOpen && onOpen(ev.mare_id);
    return (
      <div key={ev.id} role="button" tabIndex={0} onClick={open} onKeyDown={e => { if (e.key === "Enter") open(); }}
        title={`Open ${ev.mare_name}`}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid #f0f0f0", fontSize: 12.5, cursor: "pointer" }}>
        <div><strong>{ev.mare_name}</strong> — {EVENT_LABELS[ev.event_type] || ev.event_type}
          {ev.performed_by ? <span style={{ color: "var(--text-light)", fontSize: 11 }}> ({ev.performed_by})</span> : null}</div>
        <div style={{ fontSize: 11, whiteSpace: "nowrap", color: late ? "var(--red)" : ev.event_date === today ? "var(--blue)" : "var(--text-light)", fontWeight: late || ev.event_date === today ? 700 : 400 }}>
          {late ? "Overdue · " : ev.event_date === today ? "Today · " : ""}{fmtDay(ev.event_date)}{ev.event_time ? `, ${fmtTime(ev.event_time)}` : ""}
        </div>
      </div>
    );
  });
}

function GroupPill({ name }) {
  return <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 999, background: "var(--parchment)", border: "1px solid var(--parchment-dark)", color: "var(--text-mid)", whiteSpace: "nowrap" }}>{name}</span>;
}

function GroupPicker({ groups, value, onChange }) {
  const v = value || [];
  if (groups.length === 0) return <div style={{ fontSize: 12, color: "var(--text-light)", fontStyle: "italic" }}>No groups yet. Use Set up groups on the Mares page to make some.</div>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {sortGroups(groups).map(g => {
        const on = v.includes(g.id);
        return <button type="button" key={g.id} aria-pressed={on} style={chipStyle(on)}
          onClick={() => onChange(on ? v.filter(x => x !== g.id) : [...v, g.id])}>{on ? "✓ " : ""}{g.name}</button>;
      })}
    </div>
  );
}

function ManageGroupsModal({ orgId, groups, setGroups, mares, setMares, onClose }) {
  const [newName, setNewName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [editing, setEditing] = useState(null);   // { group, ids } while choosing a group's mares
  const [q, setQ] = useState("");
  const sorted = sortGroups(groups);
  const count = id => mares.filter(m => (m.group_ids || []).includes(id)).length;
  const taken = (name, exceptId) => groups.some(g => g.id !== exceptId && g.name.trim().toLowerCase() === name.trim().toLowerCase());
  const nameProblem = (name, exceptId) => name.length > 40 ? "Keep group names to 40 characters or fewer."
    : taken(name, exceptId) ? `There's already a group called ${name}.` : "";

  const addGroups = async names => {
    const clean = names.map(n => n.trim()).filter(Boolean);
    if (!clean.length) return;
    const problem = clean.map(n => nameProblem(n)).find(Boolean);
    if (problem) { setErr(problem); return; }
    setBusy(true); setErr("");
    try {
      const rows = [];
      for (const [i, name] of clean.entries()) {
        rows.push(await dbInsert("mare_groups", { id: crypto.randomUUID(), org_id: orgId, name, sort_order: groups.length + i }));
      }
      setGroups(p => [...p, ...rows]);
      setNewName("");
    } catch (e) { setErr("Couldn't add the group. " + e.message); }
    setBusy(false);
  };

  const rename = async () => {
    const name = (renaming?.name || "").trim();
    if (!name) return;
    const problem = nameProblem(name, renaming.id);
    if (problem) { setErr(problem); return; }
    setBusy(true); setErr("");
    try {
      await dbUpdate("mare_groups", renaming.id, { name });
      setGroups(p => p.map(g => g.id === renaming.id ? { ...g, name } : g));
      setRenaming(null);
    } catch (e) { setErr("Couldn't rename the group. " + e.message); }
    setBusy(false);
  };

  const remove = async g => {
    if (!window.confirm(`Delete the ${g.name} group? Your mares stay as they are, they just won't be in this group any more.`)) return;
    setBusy(true); setErr("");
    try {
      assertWritable();
      // .select() so a delete that RLS quietly skipped (Staff) is noticed.
      const { data, error } = await supabase.from("mare_groups").delete().eq("id", g.id).select("id");
      if (error) throw error;
      if (!data || data.length === 0) setErr("Only an Admin can delete a group.");
      else {
        setGroups(p => p.filter(x => x.id !== g.id));
        setMares(p => p.map(m => (m.group_ids || []).includes(g.id) ? { ...m, group_ids: m.group_ids.filter(x => x !== g.id) } : m));
      }
    } catch (e) { setErr("Couldn't delete the group. " + e.message); }
    setBusy(false);
  };

  const saveMembers = async () => {
    const { group, ids } = editing;
    const changes = mares
      .filter(m => (m.group_ids || []).includes(group.id) !== ids.includes(m.id))
      .map(m => ({ id: m.id, group_ids: ids.includes(m.id) ? [...(m.group_ids || []), group.id] : (m.group_ids || []).filter(x => x !== group.id) }));
    setBusy(true); setErr("");
    try {
      for (const c of changes) {
        await dbUpdate("mares", c.id, { group_ids: c.group_ids });
        setMares(p => p.map(m => m.id === c.id ? { ...m, group_ids: c.group_ids } : m));
      }
      setEditing(null); setQ("");
    } catch (e) { setErr("Some mares didn't save. " + e.message); }
    setBusy(false);
  };

  const list = [...mares]
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .filter(m => (m.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="modal-overlay" onClick={() => { if (!busy) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="groups-title" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <div className="modal-title" id="groups-title">{editing ? `Mares in ${editing.group.name}` : "Mare groups"}</div>
          <button className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">
          {!editing ? (<>
            <p style={{ fontSize: 13, color: "var(--text-mid)", margin: "0 0 14px", lineHeight: 1.5 }}>
              Sort your mares into groups, like recips and broodmares, then pick a group at the top of the Mares page to see just those mares. A mare can be in more than one group.
            </p>
            {sorted.length === 0 && (
              <div style={{ background: "var(--parchment)", borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 13, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span>No groups yet.</span>
                <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => addGroups(["Recips", "Broodmares"])}>Start with Recips and Broodmares</button>
              </div>
            )}
            {sorted.map(g => (
              <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0", borderBottom: "1px solid var(--parchment-dark)", flexWrap: "wrap" }}>
                {renaming?.id === g.id ? (<>
                  <input className="form-input" style={{ flex: 1, minWidth: 160 }} value={renaming.name} maxLength={40} autoFocus aria-label="Group name"
                    onChange={e => setRenaming(r => ({ ...r, name: e.target.value }))}
                    onKeyDown={e => { if (e.key === "Enter") rename(); if (e.key === "Escape") setRenaming(null); }} />
                  <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={rename}>Save</button>
                  <button type="button" className="btn btn-sm btn-outline" disabled={busy} onClick={() => setRenaming(null)}>Cancel</button>
                </>) : (<>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <div style={{ fontWeight: 700, color: "var(--forest)", fontSize: 13.5 }}>{g.name}</div>
                    <div style={{ fontSize: 11.5, color: "var(--text-light)" }}>{count(g.id)} mare{count(g.id) === 1 ? "" : "s"}</div>
                  </div>
                  <button type="button" className="btn btn-sm btn-primary" disabled={busy}
                    onClick={() => { setErr(""); setEditing({ group: g, ids: mares.filter(m => (m.group_ids || []).includes(g.id)).map(m => m.id) }); }}>Choose mares</button>
                  <button type="button" className="btn btn-sm btn-outline" disabled={busy} onClick={() => { setErr(""); setRenaming({ id: g.id, name: g.name }); }}>Rename</button>
                  <button type="button" className="btn btn-sm" disabled={busy} aria-label={`Delete ${g.name}`}
                    style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={() => remove(g)}>Delete</button>
                </>)}
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <input className="form-input" style={{ flex: 1 }} placeholder="New group, e.g. Recips" value={newName} maxLength={40} aria-label="New group name"
                onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") addGroups([newName]); }} />
              <button type="button" className="btn btn-primary" disabled={busy || !newName.trim()} onClick={() => addGroups([newName])}>Add group</button>
            </div>
          </>) : (<>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
              <input className="form-input" style={{ flex: 1 }} placeholder="Search mares…" value={q} onChange={e => setQ(e.target.value)} aria-label="Search mares" />
              <span style={{ fontSize: 12, color: "var(--text-light)", whiteSpace: "nowrap" }}>{editing.ids.length} selected</span>
            </div>
            <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid var(--parchment-dark)", borderRadius: 6 }}>
              {list.map(m => {
                const on = editing.ids.includes(m.id);
                const others = mareGroupsOf(m, groups).filter(g => g.id !== editing.group.id);
                return (
                  <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: "1px solid var(--parchment)", cursor: "pointer", background: on ? "rgba(26,46,26,.05)" : "#fff" }}>
                    <input type="checkbox" checked={on} onChange={() => setEditing(ed => ({ ...ed, ids: on ? ed.ids.filter(x => x !== m.id) : [...ed.ids, m.id] }))} />
                    <span style={{ flex: 1, fontSize: 13, fontWeight: on ? 700 : 500 }}>{m.name}</span>
                    {others.map(g => <GroupPill key={g.id} name={g.name} />)}
                  </label>
                );
              })}
              {list.length === 0 && <div className="empty-state" style={{ padding: 16 }}>{mares.length ? "No mares match." : "No mares yet."}</div>}
            </div>
          </>)}
          {err && <div role="alert" style={{ marginTop: 10, fontSize: 12.5, color: "var(--red)" }}>{err}</div>}
        </div>
        <div className="modal-footer">
          {editing ? (<>
            <button className="btn btn-outline" disabled={busy} onClick={() => { setEditing(null); setQ(""); setErr(""); }}>Back</button>
            <button className="btn btn-primary" disabled={busy} onClick={saveMembers}>{busy ? "Saving…" : "Save"}</button>
          </>) : <button className="btn btn-primary" onClick={onClose} disabled={busy}>Done</button>}
        </div>
      </div>
    </div>
  );
}

// Opens after a palpation or pregnancy check is saved ("when's the next one?"),
// from "Schedule a check" on the Breeding Log, and from Reschedule.
function ScheduleCheckModal({ orgId, mare, type, existing, afterSave, upcoming = [], people = [], onClose, onSaved }) {
  const today = localToday();
  const [f, setF] = useState(() => existing
    ? { event_type: existing.event_type, event_date: existing.event_date, event_time: existing.event_time || "", performed_by: existing.performed_by || "", notes: existing.notes || "" }
    : { event_type: SCHEDULABLE_EVENTS.includes(type) ? type : "palpation", event_date: shiftISO(today, 1), event_time: "", performed_by: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
  const quick = [[1, "Tomorrow"], [2, "In 2 days"], [3, "In 3 days"], [7, "In a week"]];

  const submit = async () => {
    if (!f.event_date) { setErr("Pick a date."); return; }
    setBusy(true); setErr("");
    const fields = { event_type: f.event_type, event_date: f.event_date, event_time: f.event_time || "", performed_by: f.performed_by.trim(), notes: f.notes.trim() };
    try {
      if (existing) {
        const upd = { ...fields, updated_at: new Date().toISOString() };
        await dbUpdate("breeding_events", existing.id, upd);
        onSaved({ ...existing, ...upd }, true);
      } else {
        const row = await dbInsert("breeding_events", { id: crypto.randomUUID(), org_id: orgId, mare_id: mare.id, status: "scheduled", ...fields });
        onSaved(row, false);
      }
    } catch (e) { setErr("Couldn't save. " + e.message); setBusy(false); }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1150 }} onClick={() => { if (!busy) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="sched-title" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title" id="sched-title">{existing ? `Reschedule for ${mare.name}` : `Next check for ${mare.name}`}</div>
          <button className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">
          {afterSave && <div role="status" style={{ fontSize: 13, color: "#166534", marginBottom: 12 }}>✓ {EVENT_LABELS[type] || "Event"} saved. When is the next one?</div>}
          {!existing && upcoming.length > 0 && (
            <div style={{ fontSize: 12.5, background: "var(--parchment)", borderRadius: 6, padding: "8px 10px", marginBottom: 12, color: "var(--text-mid)" }}>
              Already scheduled: {upcoming.map(u => `${EVENT_LABELS[u.event_type] || u.event_type} ${fmtDay(u.event_date)}${u.event_time ? " " + fmtTime(u.event_time) : ""}`).join(", ")}
            </div>
          )}
          <div className="form-label" style={{ marginBottom: 6 }}>What</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            {SCHEDULABLE_EVENTS.map(t => (
              <button type="button" key={t} aria-pressed={f.event_type === t} style={chipStyle(f.event_type === t)} onClick={() => setF(p => ({ ...p, event_type: t }))}>
                {EVENT_ICONS[t]} {EVENT_LABELS[t]}</button>
            ))}
          </div>
          <div className="form-label" style={{ marginBottom: 6 }}>When</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {quick.map(([n, label]) => {
              const iso = shiftISO(today, n);
              return <button type="button" key={n} aria-pressed={f.event_date === iso} style={chipStyle(f.event_date === iso)} onClick={() => setF(p => ({ ...p, event_date: iso }))}>
                {label} <span style={{ opacity: .75, fontWeight: 400 }}>· {fmtDay(iso)}</span></button>;
            })}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
            <div className="form-row"><label className="form-label" htmlFor="sched-date">Date</label>
              <input id="sched-date" className="form-input" type="date" min={existing ? undefined : today} value={f.event_date} onChange={set("event_date")} /></div>
            <div className="form-row"><label className="form-label" htmlFor="sched-time">Time (optional)</label>
              <input id="sched-time" className="form-input" type="time" value={f.event_time} onChange={set("event_time")} /></div>
            <div className="form-row"><label className="form-label" htmlFor="sched-by">Who's doing it (optional)</label>
              <input id="sched-by" className="form-input" list="sched-people" value={f.performed_by} onChange={set("performed_by")} placeholder="Vet or staff" />
              <datalist id="sched-people">{people.map(p => <option key={p} value={p} />)}</datalist></div>
            <div className="form-row"><label className="form-label" htmlFor="sched-note">Note (optional)</label>
              <input id="sched-note" className="form-input" value={f.notes} onChange={set("notes")} placeholder="e.g. Recheck left follicle" /></div>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-light)", marginTop: 8 }}>It shows on the Herd Management calendar and on {mare.name}'s Breeding Log.</div>
          {err && <div role="alert" style={{ marginTop: 10, fontSize: 12.5, color: "var(--red)" }}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>{existing ? "Cancel" : "Not now"}</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? "Saving…" : existing ? "Save" : "Schedule"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── MARE MANAGEMENT ─────────────────────────────────────────────────────────
// The foal a pregnant mare is carrying, in Add Mare and Edit Mare. Sire and
// donor are free text with suggestions: both are often horses the farm does not
// own. Most pregnancies on a recip-heavy farm are embryo transfers, so the dam
// defaults to "a donor mare".
function PregnancyFields({ value, onChange, mareName, stallions = [], mares = [], embryos = [] }) {
  const set = patch => onChange({ ...value, ...patch });
  // Date changes refill the due date until the farm types its own.
  const recalc = patch => {
    const next = { ...value, ...patch };
    if (next.due_auto || !next.due_date) {
      const d = dueFromDates(next.dam_mode === "donor" ? next : { ...next, transfer_date: "" });
      if (d) { next.due_date = d; next.due_auto = true; }
    }
    onChange(next);
  };
  const et = value.dam_mode === "donor";
  const preview = foalCross({ stallion_name: value.stallion_name.trim(), dam_name: et ? value.dam_name.trim() : "" }, mareName || "this mare");
  const donorOptions = [...new Set(mares.map(m => m.name).filter(n => n && n !== mareName))];
  return (
    <div style={{ background: "#f4f7f5", borderRadius: 10, padding: "14px 16px", marginTop: 12, border: "1px solid #dde6df" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>🤰 Foal she is carrying</div>
      {embryos.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #dde6df", borderRadius: 8, padding: "9px 12px", marginBottom: 10, fontSize: 12.5 }}>
          <div style={{ fontWeight: 700, color: "var(--forest)", marginBottom: 6 }}>From Embryo Transfer</div>
          {embryos.map(e => (
            <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <span style={{ flex: 1 }}>{[e.stallion, e.mare].filter(Boolean).join(" x ")}{e.transfer_date ? `, transferred ${fmtShortDate(e.transfer_date)}` : ""}{`, day ${e.age_days || DEFAULT_EMBRYO_AGE}`}</span>
              {value.embryo_id === e.id
                ? <span style={{ color: "var(--forest)", fontWeight: 700 }}>✓ Using this</span>
                : <button type="button" className="btn btn-sm btn-outline" onClick={() => onChange(pregFromEmbryo(value, e))}>Use this</button>}
            </div>
          ))}
        </div>
      )}
      <div className="form-grid">
        <div className="form-row"><label className="form-label">Sire</label>
          <input className="form-input" list="preg-sire-options" value={value.stallion_name} onChange={e => set({ stallion_name: e.target.value })} placeholder="Stallion name" />
          <datalist id="preg-sire-options">{stallions.map(s => <option key={s.id} value={s.name} />)}</datalist>
        </div>
        <div className="form-row"><label className="form-label">Dam</label>
          <select className="form-input" value={value.dam_mode} onChange={e => recalc({ dam_mode: e.target.value })}>
            <option value="donor">A donor mare (embryo transfer)</option>
            <option value="self">{mareName ? `${mareName} (her own foal)` : "This mare (her own foal)"}</option>
          </select>
          {et && (<>
            <input className="form-input" style={{ marginTop: 6 }} list="preg-dam-options" value={value.dam_name} onChange={e => set({ dam_name: e.target.value })} placeholder="Donor mare name" />
            <datalist id="preg-dam-options">{donorOptions.map(n => <option key={n} value={n} />)}</datalist>
          </>)}
        </div>
        {et && (<>
          <div className="form-row"><label className="form-label">Transfer Date</label><input className="form-input" type="date" value={value.transfer_date} onChange={e => recalc({ transfer_date: e.target.value })} /></div>
          <div className="form-row"><label className="form-label">Embryo Age</label>
            <select className="form-input" value={value.embryo_age} onChange={e => recalc({ embryo_age: e.target.value })}>
              {EMBRYO_AGES.map(n => <option key={n} value={String(n)}>Day {n}{n === 8 ? " (most fresh flushes)" : n === 6 || n === 7 ? " (common for frozen)" : ""}</option>)}
            </select>
          </div>
        </>)}
        <div className="form-row"><label className="form-label">{et ? "Breeding Date (optional)" : "Breeding Date"}</label><input className="form-input" type="date" value={value.breeding_date} onChange={e => recalc({ breeding_date: e.target.value })} /></div>
        <div className="form-row"><label className="form-label">Due Date</label><input className="form-input" type="date" value={value.due_date} onChange={e => set({ due_date: e.target.value, due_auto: false })} />
          <div style={{ fontSize: 11, color: "var(--text-light)", marginTop: 3 }}>{et && value.transfer_date
            ? `Filled in from the transfer date: ${GESTATION_DAYS} days less the embryo's age. Change it if your vet gives you a different date.`
            : `Filled in ${GESTATION_DAYS} days after the breeding date. Change it if your vet gives you a different date.`}</div>
        </div>
        <div className="form-row"><label className="form-label">Foal Name (optional)</label><input className="form-input" value={value.foal_name} onChange={e => set({ foal_name: e.target.value })} /></div>
      </div>
      {(value.stallion_name.trim() || value.due_date) && (
        <div style={{ marginTop: 8, fontSize: 13, color: "var(--forest)", fontWeight: 600 }}>{preview}{value.due_date ? ` · due ${fmtShortDate(value.due_date)}` : ""}</div>
      )}
    </div>
  );
}

function MareManagement({ orgId, focusMare, onFocusHandled }) {
  const { data: mares, setData: setMares, loading } = useSupabaseTable("mares", orgId);
  const { data: stallions } = useSupabaseTable("stallions", orgId);
  const { data: breedingEvents, setData: setBreedingEvents } = useSupabaseTable("breeding_events", orgId);
  const { data: farrierRecords, setData: setFarrierRecords } = useSupabaseTable("farrier_records", orgId);
  const { data: savedFarriers, setData: setSavedFarriers } = useSupabaseTable("farriers", orgId);
  const { data: groups, setData: setGroups } = useSupabaseTable("mare_groups", orgId);
  const { data: foalings, setData: setFoalings, loading: foalingsLoading } = useSupabaseTable("foaling_records", orgId);
  const { data: embryos } = useSupabaseTable("embryos", orgId);
  const [preg, setPreg] = useState(emptyPreg);
  const teamNames = useTeamNames(orgId);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [groupFilter, setGroupFilter] = useState("all"); // "all" | "none" | a mare_groups id
  const [showGroups, setShowGroups] = useState(false);
  const [scheduling, setScheduling] = useState(null); // { mare, type, existing?, afterSave? }
  const todayStr = localToday();
  const [showAdd, setShowAdd] = useState(false);
  const [viewingMare, setViewingMare] = useState(null);
  const [flyoutTab, setFlyoutTab] = useState("breeding");
  const [showEventForm, setShowEventForm] = useState(null); // event type or null
  const [eventForm, setEventForm] = useState({});
  const [editingMare, setEditingMare] = useState(null);
  const [showFarrierForm, setShowFarrierForm] = useState(false);
  const [editingFarrier, setEditingFarrier] = useState(null);
  const emptyFarrier = { farrier_name: "", visit_date: localToday(), service_type: "complete_shoeing", cost: "", notes: "", hoof_condition: "", next_appointment: "" };
  const [farrierForm, setFarrierForm] = useState(emptyFarrier);
  const mff = k => e => setFarrierForm(p => ({ ...p, [k]: e.target.value }));
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const emptyForm = { name: "", registered_name: "", registration_number: "", breed: "", age: "", owner: "", status: "Open", stallion: "", stallion_2: "", stallion_3: "", notes: "", photo_url: "", group_ids: [] };
  const [form, setForm] = useState(emptyForm);
  const [stallionCount, setStallionCount] = useState(1);

  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handlePhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const url = await uploadHorsePhoto(file, orgId); setForm(p => ({ ...p, photo_url: url })); }
    catch (err) { alert("Photo upload failed. Make sure the 'horse-photos' storage bucket exists in Supabase. Error: " + err.message); }
    setUploading(false);
  };

  // The foal a mare is carrying is her open foaling_records row (Expected or
  // Overdue). The mare form edits that row; it never creates a second one.
  const activePregnancy = m => m ? (foalings.filter(f => f.mare_id === m.id && isActivePregnancy(f))
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0] || null) : null;
  // Embryos logged as transferred into this mare that no other pregnancy uses.
  const embryoChoices = m => !m ? [] : embryos.filter(e => e.status === "Transferred"
    && (e.recipient_mare_id ? e.recipient_mare_id === m.id : (e.recipient || "").trim() === m.name)
    && !foalings.some(f => f.embryo_id === e.id && f.id !== preg.id));
  // A recip can lose a pregnancy at any point: close it as Loss (which stops
  // its foaling alerts) and put her back to Open.
  const endPregnancy = async (mare, rec) => {
    const upd = { status: "Loss", updated_at: new Date().toISOString() };
    await dbUpdate("foaling_records", rec.id, upd);
    setFoalings(list => list.map(f => f.id === rec.id ? { ...f, ...upd } : f));
    if (mare.status === "Pregnant") {
      await dbUpdate("mares", mare.id, { status: "Open", days_pregnant: null });
      const m2 = { ...mare, status: "Open", days_pregnant: null };
      setMares(p => p.map(m => m.id === mare.id ? m2 : m));
      setViewingMare(v => v && v.id === mare.id ? m2 : v);
    }
  };
  // Changing a pregnant mare to another status ends her pregnancy as a loss,
  // unless she is within 30 days of her due date: then she has most likely
  // foaled, and that is recorded on Foaling Records, not guessed here.
  const endsAsLoss = m => { const p = activePregnancy(m); return p && (!p.due_date || daysFromToday(p.due_date) > 30) ? p : null; };
  const markOpen = async (mare, rec) => {
    if (!window.confirm(`Mark ${mare.name} open? This ends her pregnancy (${foalCross(rec, mare.name) || "no sire recorded"}) and stops its foaling alerts.`)) return;
    try { await endPregnancy(mare, rec); save(); }
    catch (e) { alert("Could not mark her open: " + e.message); }
  };
  const savePregnancy = async (mare, p) => {
    const fields = pregnancyRow(p);
    // Fall back to her open row in case Edit was opened before foaling records
    // finished loading, so a second Expected row is never created.
    const existing = (p.id && foalings.find(f => f.id === p.id)) || activePregnancy(mare);
    if (existing) {
      const upd = { ...fields, mare_name: mare.name, updated_at: new Date().toISOString() };
      if (existing.status === "Overdue" && fields.due_date && fields.due_date >= localToday()) upd.status = "Expected";
      await dbUpdate("foaling_records", existing.id, upd);
      setFoalings(list => list.map(f => f.id === existing.id ? { ...f, ...upd } : f));
      return;
    }
    if (!Object.values(fields).some(Boolean)) return;
    const row = await dbInsert("foaling_records", { id: crypto.randomUUID(), org_id: orgId, mare_id: mare.id, mare_name: mare.name, status: "Expected", ...fields });
    setFoalings(list => [row, ...list]);
  };

  const addMare = async () => {
    if (!form.name.trim()) return;
    try {
      const row = await dbInsert("mares", { id: crypto.randomUUID(), ...form, age: parseInt(form.age) || 0, org_id: orgId, days_in_cycle: 1, tags: [], hormones: { LH: 50, Progesterone: 50, Estrogen: 50 } });
      setMares(p => [row, ...p]);
      if (row.status === "Pregnant") {
        try { await savePregnancy(row, preg); }
        catch (e) { alert("The mare was saved, but the foal she is carrying was not: " + e.message); }
      }
      setShowAdd(false); setForm(emptyForm); setPreg(emptyPreg);
      save();
    } catch (e) { alert("Error saving: " + e.message); }
  };

  const updatePhoto = async (mare, file) => {
    try {
      const url = await uploadHorsePhoto(file, orgId);
      await dbUpdate("mares", mare.id, { photo_url: url });
      setMares(p => p.map(m => m.id === mare.id ? { ...m, photo_url: url } : m));
      setViewingMare(v => v ? { ...v, photo_url: url } : v);
      save();
    } catch (err) { alert("Photo upload failed: " + err.message); }
  };

  const deleteMare = (mare) => confirmDelete(
    `Delete ${mare.name}? Her breeding log and ultrasounds are deleted with her. This can't be undone.`,
    async () => { await dbDelete("mares", mare.id); setMares(p => p.filter(m => m.id !== mare.id)); save(); });

  // Called from the flyout's Edit button (with the click event) or with a mare
  // from her card / the "no due date" list, which opens her flyout in edit mode.
  const startEditMare = (arg) => {
    const vm = arg && arg.id ? arg : viewingMare;
    if (vm !== viewingMare) setViewingMare(vm);
    const sc = 1 + (vm.stallion_2 ? 1 : 0) + (vm.stallion_3 ? 1 : 0);
    setStallionCount(sc || 1);
    setEditingMare({
      name: vm.name || "", registered_name: vm.registered_name || "",
      registration_number: vm.registration_number || "", owner: vm.owner || "",
      breed: vm.breed || "", age: vm.age || "",
      status: vm.status || "Open",
      stallion: vm.stallion || "", stallion_2: vm.stallion_2 || "", stallion_3: vm.stallion_3 || "",
      notes: vm.notes || "",
      days_in_cycle: vm.days_in_cycle || "", days_pregnant: vm.days_pregnant || "",
      last_ultrasound: vm.last_ultrasound || "", dominant_follicle: vm.dominant_follicle || "",
      group_ids: vm.group_ids || []
    });
    setPreg(pregFromRecord(activePregnancy(vm)));
    // The edit form lives on the Details tab. This used to switch to the
    // Breeding Log, which hid the form and left only Save/Cancel showing.
    setFlyoutTab("details");
  };

  const saveEditMare = async () => {
    try {
      // Clean all fields — convert empty strings to null for any column that might be integer type
      const updates = {};
      for (const [k, v] of Object.entries(editingMare)) {
        if (v === "" || v === undefined) { updates[k] = null; }
        else if (["age", "days_in_cycle", "days_pregnant"].includes(k)) { updates[k] = parseInt(v) || null; }
        else { updates[k] = v; }
      }
      await dbUpdate("mares", viewingMare.id, updates);
      const updated = { ...viewingMare, ...updates };
      setMares(p => p.map(m => m.id === viewingMare.id ? updated : m));
      setViewingMare(updated);
      if (updated.status === "Pregnant") {
        try { await savePregnancy(updated, preg); }
        catch (e) { alert("The mare was saved, but the foal she is carrying was not: " + e.message); }
      } else {
        // No longer Pregnant: her open pregnancy ends too, or its foaling
        // alerts would keep going for a mare who isn't carrying.
        const open = endsAsLoss(viewingMare);
        if (open) {
          try { await endPregnancy(updated, open); }
          catch (e) { alert("The mare was saved, but her pregnancy could not be closed: " + e.message); }
        }
      }
      setEditingMare(null);
      save();
    } catch (e) { alert("Error updating: " + e.message); }
  };

  const emf = k => e => setEditingMare(p => ({ ...p, [k]: e.target.value }));

  // Farrier CRUD for mares
  const addMareFarrier = async () => {
    if (!farrierForm.farrier_name || !viewingMare) return;
    try {
      const row = await dbInsert("farrier_records", { id: crypto.randomUUID(), org_id: orgId, horse_id: viewingMare.id, horse_name: viewingMare.name, horse_type: "mare", ...farrierForm, cost: parseFloat(farrierForm.cost) || 0, next_appointment: farrierForm.next_appointment || null });
      setFarrierRecords(p => [row, ...p]);
      setShowFarrierForm(false); setFarrierForm(emptyFarrier); save();
    } catch (e) { alert("Error: " + e.message); }
  };
  const saveMareFarrierEdit = async () => {
    if (!editingFarrier) return;
    try {
      const updates = { ...farrierForm, cost: parseFloat(farrierForm.cost) || 0, next_appointment: farrierForm.next_appointment || null };
      await dbUpdate("farrier_records", editingFarrier.id, updates);
      setFarrierRecords(p => p.map(r => r.id === editingFarrier.id ? { ...r, ...updates } : r));
      setEditingFarrier(null); setShowFarrierForm(false); setFarrierForm(emptyFarrier); save();
    } catch (e) { alert("Error: " + e.message); }
  };
  const deleteMareFarrier = async (rec) => {
    if (!window.confirm("Delete this farrier record?")) return;
    try { await dbDelete("farrier_records", rec.id); } catch (e) { alert(e.message); return; }
    setFarrierRecords(p => p.filter(r => r.id !== rec.id)); save();
  };

  const buildEventPayload = (formData, eventType) => ({
    event_type: eventType,
    event_date: formData.event_date || localToday(),
    event_time: formData.event_time || new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }),
    stallion_name: formData.stallion_name || "", method: formData.method || "",
    medication_name: formData.medication_name || "", dosage: formData.dosage || "",
    route: formData.route || "", follicle_size: formData.follicle_size || "",
    tease_score: formData.tease_score ? parseInt(formData.tease_score) : null,
    pregnancy_status: formData.pregnancy_status || "",
    performed_by: formData.performed_by || "", notes: formData.notes || "",
    left_ovary: formData.left_ovary || "", right_ovary: formData.right_ovary || "",
    uterine_tone: formData.uterine_tone || "", cervix: formData.cervix || "",
    edema_score: formData.edema_score || "", uterine_fluid: formData.uterine_fluid || "",
    icsi_procedure: formData.icsi_procedure || "",
    oocytes_collected: formData.oocytes_collected ? parseInt(formData.oocytes_collected) : null,
    oocytes_mature: formData.oocytes_mature ? parseInt(formData.oocytes_mature) : null,
    oocytes_injected: formData.oocytes_injected ? parseInt(formData.oocytes_injected) : null,
    embryos_produced: formData.embryos_produced ? parseInt(formData.embryos_produced) : null,
    lab_facility: formData.lab_facility || "", disposition: formData.disposition || ""
  });

  const addBreedingEvent = async () => {
    if (!viewingMare || !showEventForm) return;
    const type = showEventForm, mare = viewingMare, scheduledId = eventForm._scheduledId;
    try {
      const payload = buildEventPayload(eventForm, type);
      if (scheduledId) {
        // Recording results for a scheduled check: fill in that row, don't add a second one.
        const upd = { ...payload, status: "done", updated_at: new Date().toISOString() };
        await dbUpdate("breeding_events", scheduledId, upd);
        setBreedingEvents(p => p.map(e => e.id === scheduledId ? { ...e, ...upd } : e));
      } else {
        const row = await dbInsert("breeding_events", { id: crypto.randomUUID(), mare_id: mare.id, org_id: orgId, ...payload });
        setBreedingEvents(p => [row, ...p]);
      }
      setShowEventForm(null); setEventForm({});
      save();
      // Trista (Capital Quarter Horses): "it didn't give me an option to schedule my next one. THATS A MUST!"
      if (SCHEDULABLE_EVENTS.includes(type)) setScheduling({ mare, type, afterSave: true });
    } catch (e) { alert("Error logging event: " + e.message); }
  };

  const recordScheduled = ev => {
    setShowEventForm(ev.event_type);
    setEventForm({ event_date: localToday(), event_time: ev.event_time || "", performed_by: ev.performed_by || "", notes: ev.notes || "", _scheduledId: ev.id, _scheduledFor: ev.event_date });
  };

  const cancelScheduled = async ev => {
    if (!window.confirm(`Cancel the ${EVENT_LABELS[ev.event_type] || "check"} scheduled for ${fmtDay(ev.event_date)}?`)) return;
    try {
      assertWritable();
      const { data, error } = await supabase.from("breeding_events").delete().eq("id", ev.id).select("id");
      if (error) throw error;
      if (!data || data.length === 0) { alert("Only an Admin can cancel a scheduled check. You can reschedule it instead."); return; }
      setBreedingEvents(p => p.filter(x => x.id !== ev.id));
      save();
    } catch (e) { alert("Couldn't cancel it: " + e.message); }
  };

  // View/Edit breeding log entry
  const [viewingEvent, setViewingEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(false);
  const [editEventForm, setEditEventForm] = useState({});

  const openEventDetail = (ev) => {
    setViewingEvent(ev);
    setEditingEvent(false);
    setEditEventForm({});
  };

  const startEditEvent = () => {
    setEditingEvent(true);
    setEditEventForm({ ...viewingEvent });
  };

  const saveEditEvent = async () => {
    if (!viewingEvent) return;
    try {
      const payload = buildEventPayload(editEventForm, editEventForm.event_type);
      await dbUpdate("breeding_events", viewingEvent.id, { ...payload, updated_at: new Date().toISOString() });
      setBreedingEvents(p => p.map(e => e.id === viewingEvent.id ? { ...e, ...payload } : e));
      setViewingEvent(prev => ({ ...prev, ...payload }));
      setEditingEvent(false);
      save();
    } catch (e) { alert("Error updating event: " + e.message); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this breeding log entry?")) return;
    try { await dbDelete("breeding_events", id); } catch (e) { alert(e.message); return; }
    setBreedingEvents(p => p.filter(x => x.id !== id));
    setViewingEvent(null);
    save();
  };

  // History only; scheduled checks are listed separately (mareScheduled).
  const mareEvents = viewingMare ? breedingEvents.filter(e => e.mare_id === viewingMare.id && !isScheduled(e)).sort((a, b) => (b.event_date + (b.event_time || "")).localeCompare(a.event_date + (a.event_time || ""))) : [];
  const mareScheduled = viewingMare ? breedingEvents.filter(e => e.mare_id === viewingMare.id && isScheduled(e)).sort((a, b) => (a.event_date + (a.event_time || "")).localeCompare(b.event_date + (b.event_time || ""))) : [];
  const people = mergeNames(teamNames, breedingEvents.map(e => e.performed_by));

  // Opened from another page (Herd Management calendar, Dashboard): show that mare's Breeding Log.
  useEffect(() => {
    if (!focusMare || loading) return;
    const m = mares.find(x => x.id === focusMare.id);
    if (m) { setViewingMare(m); setFlyoutTab("breeding"); setEditingMare(null); }
    if (onFocusHandled) onFocusHandled();
  }, [focusMare, loading, mares]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeGroup = groupFilter === "none" || groups.some(g => g.id === groupFilter) ? groupFilter : "all";
  const inGroup = m => activeGroup === "all" ? true
    : activeGroup === "none" ? mareGroupsOf(m, groups).length === 0
    : (m.group_ids || []).includes(activeGroup);
  const ungroupedCount = mares.filter(m => mareGroupsOf(m, groups).length === 0).length;

  const filtered = mares.filter(m =>
    (filter === "All" || m.status === filter) &&
    inGroup(m) &&
    m.name.toLowerCase().includes(search.toLowerCase())
  );

  const STATUS_EMOJI = { Pregnant: "🤰", "In Cycle": "🔄", Open: "⭕", "Flush Scheduled": "📅" };

  return (<>
    <div className="page-header-row">
      
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {saved && <span style={{ fontSize: 12, color: "var(--forest)", fontStyle: "italic" }}>✓ Saved</span>}
        <button className="btn btn-primary" onClick={() => { setForm(p => ({ ...p, group_ids: groups.some(g => g.id === activeGroup) ? [activeGroup] : [] })); setPreg(emptyPreg); setShowAdd(true); }}>+ Add Mare</button>
      </div>
    </div>
    {loading && <div className="empty-state">Loading mares…</div>}
    {!loading && !foalingsLoading && (() => {
      const missing = mares.filter(m => m.status === "Pregnant" && !activePregnancy(m)).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      if (!missing.length) return null;
      return (
        <div style={{ background: "#fff8ec", border: "1px solid #f3d9a4", borderRadius: 8, padding: "11px 14px", marginBottom: 14, fontSize: 13, lineHeight: 1.5 }}>
          🤰 {missing.length === 1 ? "1 pregnant mare has" : `${missing.length} pregnant mares have`} no breeding or due date yet, so foaling alerts can't go out for {missing.length === 1 ? "her" : "them"}. Click a name to add them:
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 7 }}>
            {missing.map(m => <button key={m.id} type="button" className="btn btn-sm btn-outline" style={{ fontSize: 11.5 }} onClick={() => startEditMare(m)}>{m.name}</button>)}
          </div>
        </div>
      );
    })()}
    {!loading && (
      <div className="full-panel">
        <div className="filter-bar">
          <input className="search-input" placeholder="Search mares…" value={search} onChange={e => setSearch(e.target.value)} />
          <select className="filter-select" value={filter} onChange={e => setFilter(e.target.value)}>
            <option>All</option><option>In Cycle</option><option>Pregnant</option><option>Open</option><option>Flush Scheduled</option>
          </select>
          <span style={{ fontSize: 12, color: "var(--text-light)", marginLeft: "auto" }}>{filtered.length} mares</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", padding: "10px 17px", borderBottom: "1px solid var(--parchment-dark)" }}>
          {groups.length > 0 ? (<>
            <button type="button" aria-pressed={activeGroup === "all"} style={chipStyle(activeGroup === "all")} onClick={() => setGroupFilter("all")}>All mares ({mares.length})</button>
            {sortGroups(groups).map(g => (
              <button type="button" key={g.id} aria-pressed={activeGroup === g.id} style={chipStyle(activeGroup === g.id)} onClick={() => setGroupFilter(g.id)}>
                {g.name} ({mares.filter(m => (m.group_ids || []).includes(g.id)).length})</button>
            ))}
            {ungroupedCount > 0 && <button type="button" aria-pressed={activeGroup === "none"} style={chipStyle(activeGroup === "none")} onClick={() => setGroupFilter("none")}>No group ({ungroupedCount})</button>}
          </>) : <span style={{ fontSize: 12.5, color: "var(--text-light)" }}>Keep recips, broodmares or any other set of mares in their own group.</span>}
          <button type="button" className="btn btn-sm btn-outline" style={{ marginLeft: "auto" }} onClick={() => setShowGroups(true)}>{groups.length ? "Manage groups" : "Set up groups"}</button>
        </div>
        {filtered.length === 0
          ? <div className="empty-state">{activeGroup !== "all" && mares.length > 0 ? "No mares here. Use Manage groups to choose which mares are in this group." : 'No mares found. Click "+ Add Mare" to get started.'}</div>
          : <div className="horse-grid">
              {filtered.map(m => (
                <div className="horse-card" key={m.id} onClick={() => setViewingMare(m)}>
                  {m.photo_url
                    ? <HorsePhoto className="horse-photo" value={m.photo_url} alt={m.name} />
                    : <div className="horse-photo-placeholder">{STATUS_EMOJI[m.status] || "🐴"}</div>}
                  <div className="horse-card-body">
                    <div className="horse-name">{m.name}</div>
                    {m.registered_name && <div style={{ fontSize: 11, fontStyle: "italic", color: "var(--text-light)", marginBottom: 2 }}>{m.registered_name}</div>}
                    <div className="horse-detail">{[m.breed, m.age ? `${m.age} yrs` : null].filter(Boolean).join(" · ")}</div>
                    <StatusBadge status={m.status} />
                    {mareGroupsOf(m, groups).length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>{sortGroups(mareGroupsOf(m, groups)).map(g => <GroupPill key={g.id} name={g.name} />)}</div>}
                    {m.owner && <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-light)" }}>👤 {m.owner}</div>}
                    {m.stallion && <div style={{ marginTop: 3, fontSize: 11.5, color: "var(--text-light)", fontStyle: "italic" }}>♂ {m.stallion}{m.stallion_2 ? `, ${m.stallion_2}` : ""}{m.stallion_3 ? `, ${m.stallion_3}` : ""}</div>}
                    {(m.days_in_cycle || m.days_pregnant) && (
                      <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--text-mid)" }}>
                        {m.days_pregnant ? `Day ${m.days_pregnant} pregnant` : `Cycle day ${m.days_in_cycle}`}
                      </div>
                    )}
                    {m.status === "Pregnant" && !foalingsLoading && (() => {
                      const p = activePregnancy(m);
                      if (!p) return <button type="button" className="btn btn-sm btn-outline" style={{ marginTop: 6, fontSize: 11 }} onClick={e => { e.stopPropagation(); startEditMare(m); }}>+ Add breeding and due dates</button>;
                      const d = daysFromToday(p.due_date);
                      return (
                        <div style={{ marginTop: 5, fontSize: 11.5, color: "var(--forest)", fontWeight: 600, lineHeight: 1.4 }}>
                          🍼 {foalCross(p, m.name) || "In foal"}
                          {p.due_date && <div style={{ fontWeight: 500, color: d < 0 ? "var(--red)" : "var(--text-mid)" }}>Due {fmtShortDate(p.due_date)} · {d < 0 ? `${-d} days overdue` : d === 0 ? "due today" : `${d} days to go`}</div>}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
        }
      </div>
    )}
    {/* ADD MODAL */}
    {showAdd && (
      <div className="modal-overlay" onClick={() => setShowAdd(false)}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><div className="modal-title">Add New Mare</div><button className="modal-close" onClick={() => setShowAdd(false)}>✕</button></div>
          <div className="modal-body">
            <div style={{ marginBottom: 16 }}>
              {form.photo_url ? <HorsePhoto value={form.photo_url} alt="preview" style={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 6, marginBottom: 8 }} /> : null}
              <label className="upload-photo-btn">
                {uploading ? "Uploading…" : "📷 Upload Photo"}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhoto} disabled={uploading} />
              </label>
            </div>
            <div className="form-grid">
              <div className="form-row"><label className="form-label">Barn Name *</label><input className="form-input" value={form.name} onChange={sf("name")} placeholder="Common / barn name" /></div>
              <div className="form-row"><label className="form-label">Registered Name</label><input className="form-input" value={form.registered_name} onChange={sf("registered_name")} placeholder="Full AQHA / APHA name" /></div>
              <div className="form-row"><label className="form-label">Registration #</label><input className="form-input" value={form.registration_number} onChange={sf("registration_number")} placeholder="e.g. AQHA 1234567" /></div>
              <div className="form-row"><label className="form-label">Breed</label><input className="form-input" value={form.breed} onChange={sf("breed")} /></div>
              <div className="form-row"><label className="form-label">Age</label><input className="form-input" type="number" value={form.age} onChange={sf("age")} /></div>
              <div className="form-row"><label className="form-label">Owner</label><input className="form-input" value={form.owner} onChange={sf("owner")} placeholder="Owner name" /></div>
              <div className="form-row"><label className="form-label">Status</label><select className="form-input" value={form.status} onChange={sf("status")}><option>Open</option><option>In Cycle</option><option>Pregnant</option><option>Flush Scheduled</option></select></div>
            </div>
            <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Groups</label>
              <GroupPicker groups={groups} value={form.group_ids} onChange={ids => setForm(p => ({ ...p, group_ids: ids }))} /></div>
            {/* Multiple Stallion Assignments */}
            <div style={{ marginTop: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-light)" }}>Stallion Assignments</span>
                {stallionCount < 3 && <button type="button" className="btn btn-sm" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => setStallionCount(c => Math.min(c + 1, 3))}>+ Add Stallion</button>}
              </div>
              {[1, 2, 3].slice(0, stallionCount).map(n => {
                const key = n === 1 ? "stallion" : `stallion_${n}`;
                return (
                  <div key={n} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", minWidth: 18 }}>#{n}</span>
                    <select className="form-input" style={{ flex: 1 }} value={form[key]} onChange={sf(key)}>
                      <option value="">— Select Stallion —</option>
                      {stallions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                    {n > 1 && <button type="button" style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 14, padding: "2px 4px" }} onClick={() => { setForm(p => ({ ...p, [`stallion_${n}`]: "" })); setStallionCount(c => c - 1); }}>✕</button>}
                  </div>
                );
              })}
            </div>
            {form.status === "Pregnant" && <PregnancyFields value={preg} onChange={setPreg} mareName={form.name.trim()} stallions={stallions} mares={mares} />}
            <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={sf("notes")} /></div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={() => { setShowAdd(false); setStallionCount(1); setPreg(emptyPreg); }}>Cancel</button><button className="btn btn-primary" onClick={addMare}>Add Mare</button></div>
        </div>
      </div>
    )}
    {/* FLYOUT */}
    {viewingMare && (
      <div className="modal-overlay" onClick={() => { setViewingMare(null); setFlyoutTab("breeding"); setShowEventForm(null); setEditingMare(null); }}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 680, padding: 0, overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            {viewingMare.photo_url
              ? <HorsePhoto value={viewingMare.photo_url} alt={viewingMare.name} style={{ width: "100%", height: 180, objectFit: "cover", display: "block" }} />
              : <div style={{ width: "100%", height: 120, background: "linear-gradient(160deg,var(--forest),#1e3d1e)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56 }}>{STATUS_EMOJI[viewingMare.status] || "🐴"}</div>}
            <button className="modal-close" onClick={() => { setViewingMare(null); setFlyoutTab("breeding"); setShowEventForm(null); setEditingMare(null); }} style={{ position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,.5)", color: "white", border: "none" }}>✕</button>
            <label style={{ position: "absolute", bottom: 10, right: 10, background: "rgba(0,0,0,.55)", color: "white", fontSize: 11.5, padding: "5px 10px", borderRadius: 5, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
              📷 Change Photo
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) updatePhoto(viewingMare, e.target.files[0]); }} />
            </label>
          </div>
          <div style={{ padding: "14px 22px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <div style={{ fontFamily: "Playfair Display,serif", fontSize: 20, fontWeight: 700, color: "var(--forest)" }}>{viewingMare.name}</div>
                {viewingMare.registered_name && <div style={{ fontSize: 12, color: "var(--text-mid)", fontStyle: "italic", marginTop: 1 }}>{viewingMare.registered_name}</div>}
                <div style={{ fontSize: 12.5, color: "var(--text-light)", marginTop: 2 }}>{[viewingMare.breed, viewingMare.age ? `${viewingMare.age} yrs` : null].filter(Boolean).join(" · ")}</div>
              </div>
              <StatusBadge status={viewingMare.status} />
            </div>
            <div className="breed-tab-bar">
              <button className={`breed-tab ${flyoutTab === "details" ? "active" : ""}`} onClick={() => setFlyoutTab("details")}>Details</button>
              <button className={`breed-tab ${flyoutTab === "breeding" ? "active" : ""}`} onClick={() => setFlyoutTab("breeding")}>Breeding Log ({mareEvents.length})</button>
              <button className={`breed-tab ${flyoutTab === "farrier" ? "active" : ""}`} onClick={() => setFlyoutTab("farrier")}>Farrier ({farrierRecords.filter(r => r.horse_id === viewingMare.id).length})</button>
            </div>
          </div>
          <div style={{ padding: "0 22px 18px", overflowY: "auto", flex: 1 }}>
            {flyoutTab === "details" && (<>
              {!editingMare ? (<>
                {/* VIEW MODE */}
                {(viewingMare.registration_number || viewingMare.owner) && (
                  <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: 12.5 }}>
                    {viewingMare.registration_number && <span>📋 <strong>Reg #:</strong> {viewingMare.registration_number}</span>}
                    {viewingMare.owner && <span>👤 <strong>Owner:</strong> {viewingMare.owner}</span>}
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 14 }}>
                  {[
                    ["Cycle Day", viewingMare.days_in_cycle ? `Day ${viewingMare.days_in_cycle}` : "—"],
                    ["Preg Day", viewingMare.days_pregnant ? `Day ${viewingMare.days_pregnant}` : "—"],
                    ["Last U/S", viewingMare.last_ultrasound || "—"],
                  ].map(([label, val]) => (
                    <div key={label} style={{ background: "var(--parchment)", borderRadius: 7, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--text-light)", marginBottom: 3 }}>{label}</div>
                      <div style={{ fontFamily: "Playfair Display,serif", fontSize: 17, fontWeight: 700, color: "var(--forest)" }}>{val}</div>
                    </div>
                  ))}
                </div>
                {viewingMare.status === "Pregnant" && !foalingsLoading && (() => {
                  const p = activePregnancy(viewingMare);
                  if (!p) return (
                    <div style={{ background: "#fff8ec", border: "1px solid #f3d9a4", borderRadius: 8, padding: "10px 13px", marginBottom: 12, fontSize: 12.5, display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ flex: 1 }}>No breeding or due date yet, so no foaling alerts will go out for her.</span>
                      <button type="button" className="btn btn-sm btn-primary" onClick={() => startEditMare()}>Add them</button>
                    </div>
                  );
                  const d = daysFromToday(p.due_date);
                  return (
                    <div style={{ background: "var(--parchment)", borderRadius: 8, padding: "10px 13px", marginBottom: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 4 }}>Carrying</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--forest)" }}>{foalCross(p, viewingMare.name) || "Sire not recorded"}{p.foal_name ? ` · ${p.foal_name}` : ""}</div>
                      <div style={{ fontSize: 12.5, color: "var(--text-mid)", marginTop: 3 }}>
                        {p.dam_name ? `Embryo from ${p.dam_name}. ` : ""}
                        {pregDatesLine(p) || "No breeding or transfer date"}
                        {p.due_date ? ` · Due ${fmtShortDate(p.due_date)} (${d < 0 ? `${-d} days overdue` : d === 0 ? "today" : `${d} days to go`})` : " · No due date"}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <button type="button" className="btn btn-sm btn-outline" onClick={() => markOpen(viewingMare, p)}>Mark open (lost pregnancy)</button>
                      </div>
                    </div>
                  );
                })()}
                {mareGroupsOf(viewingMare, groups).length > 0 && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 10, fontSize: 12.5 }}>
                    <strong>Groups:</strong> {sortGroups(mareGroupsOf(viewingMare, groups)).map(g => <GroupPill key={g.id} name={g.name} />)}
                  </div>
                )}
                {/* Stallion assignments */}
                {(viewingMare.stallion || viewingMare.stallion_2 || viewingMare.stallion_3) && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 6 }}>Stallion Assignments</div>
                    {[viewingMare.stallion, viewingMare.stallion_2, viewingMare.stallion_3].filter(Boolean).map((s, i) => (
                      <div key={i} style={{ fontSize: 13, marginBottom: 3 }}><span style={{ fontWeight: 700, color: "var(--forest)" }}>#{i + 1}</span> ♂ {s}</div>
                    ))}
                  </div>
                )}
                {viewingMare.dominant_follicle && <div style={{ fontSize: 13, marginBottom: 8 }}>🔬 <strong>Dominant follicle:</strong> {viewingMare.dominant_follicle}</div>}
                {viewingMare.notes && <div style={{ background: "var(--parchment)", borderRadius: 6, padding: "10px 13px", fontSize: 13, fontStyle: "italic", color: "var(--text-mid)", lineHeight: 1.55, marginBottom: 8 }}>{viewingMare.notes}</div>}
                {/* Recent breeding events preview */}
                {mareEvents.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 8 }}>Recent Activity</div>
                    {mareEvents.slice(0, 3).map(ev => (
                      <div key={ev.id} className={`breed-event ${ev.event_type}`}>
                        <div className="breed-event-date">{ev.event_date}{ev.event_time ? ` · ${ev.event_time}` : ""} {ev.performed_by ? `· ${ev.performed_by}` : ""}</div>
                        <div className="breed-event-title">{EVENT_ICONS[ev.event_type] || "📝"} {EVENT_LABELS[ev.event_type] || ev.event_type}</div>
                        {ev.notes && <div className="breed-event-detail">{ev.notes}</div>}
                      </div>
                    ))}
                    {mareEvents.length > 3 && <div style={{ fontSize: 12, color: "var(--blue)", cursor: "pointer", marginTop: 4 }} onClick={() => setFlyoutTab("breeding")}>View all {mareEvents.length} events →</div>}
                  </div>
                )}
              </>) : (<>
                {/* EDIT MODE */}
                <div className="form-grid">
                  <div className="form-row"><label className="form-label">Barn Name *</label><input className="form-input" value={editingMare.name} onChange={emf("name")} /></div>
                  <div className="form-row"><label className="form-label">Registered Name</label><input className="form-input" value={editingMare.registered_name} onChange={emf("registered_name")} placeholder="Full AQHA / APHA name" /></div>
                  <div className="form-row"><label className="form-label">Registration #</label><input className="form-input" value={editingMare.registration_number} onChange={emf("registration_number")} /></div>
                  <div className="form-row"><label className="form-label">Breed</label><input className="form-input" value={editingMare.breed} onChange={emf("breed")} /></div>
                  <div className="form-row"><label className="form-label">Age</label><input className="form-input" type="number" value={editingMare.age} onChange={emf("age")} /></div>
                  <div className="form-row"><label className="form-label">Owner</label><input className="form-input" value={editingMare.owner} onChange={emf("owner")} /></div>
                  <div className="form-row"><label className="form-label">Status</label>
                    <select className="form-input" value={editingMare.status} onChange={emf("status")}><option>Open</option><option>In Cycle</option><option>Pregnant</option><option>Flush Scheduled</option></select>
                  </div>
                </div>
                {editingMare.status === "Pregnant" && <PregnancyFields value={preg} onChange={setPreg} mareName={editingMare.name.trim()} stallions={stallions} mares={mares} embryos={embryoChoices(viewingMare)} />}
                {editingMare.status !== "Pregnant" && activePregnancy(viewingMare) && (
                  <div style={{ background: "#fff8ec", border: "1px solid #f3d9a4", borderRadius: 8, padding: "9px 12px", marginTop: 10, fontSize: 12.5 }}>
                    {endsAsLoss(viewingMare)
                      ? <>Saving ends her pregnancy ({foalCross(activePregnancy(viewingMare), viewingMare.name) || "no sire recorded"}) as a loss and stops its foaling alerts.</>
                      : <>She's close to her due date. If she foaled, record it on Foaling Records, which closes the pregnancy.</>}
                  </div>
                )}
                <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Groups</label>
                  <GroupPicker groups={groups} value={editingMare.group_ids} onChange={ids => setEditingMare(p => ({ ...p, group_ids: ids }))} /></div>
                {/* Multiple Stallion Assignments */}
                <div style={{ marginTop: 8, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-light)" }}>Stallion Assignments</span>
                    {stallionCount < 3 && <button type="button" className="btn btn-sm" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => setStallionCount(c => Math.min(c + 1, 3))}>+ Add</button>}
                  </div>
                  {[1, 2, 3].slice(0, stallionCount).map(n => {
                    const key = n === 1 ? "stallion" : `stallion_${n}`;
                    return (
                      <div key={n} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", minWidth: 18 }}>#{n}</span>
                        <select className="form-input" style={{ flex: 1 }} value={editingMare[key]} onChange={emf(key)}>
                          <option value="">— None —</option>
                          {stallions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                        {n > 1 && <button type="button" style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 14 }} onClick={() => { setEditingMare(p => ({ ...p, [`stallion_${n}`]: "" })); setStallionCount(c => c - 1); }}>✕</button>}
                      </div>
                    );
                  })}
                </div>
                <div className="form-grid">
                  <div className="form-row"><label className="form-label">Cycle Day</label><input className="form-input" type="number" value={editingMare.days_in_cycle} onChange={emf("days_in_cycle")} /></div>
                  <div className="form-row"><label className="form-label">Pregnancy Day</label><input className="form-input" type="number" value={editingMare.days_pregnant} onChange={emf("days_pregnant")} /></div>
                  <div className="form-row"><label className="form-label">Last Ultrasound</label><input className="form-input" value={editingMare.last_ultrasound} onChange={emf("last_ultrasound")} placeholder="e.g. 03/15/2026" /></div>
                  <div className="form-row"><label className="form-label">Dominant Follicle</label><input className="form-input" value={editingMare.dominant_follicle} onChange={emf("dominant_follicle")} placeholder="e.g. 35mm left ovary" /></div>
                  <div className="form-row" style={{ gridColumn: "1/-1" }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={editingMare.notes} onChange={emf("notes")} /></div>
                </div>
              </>)}
            </>)}
            {flyoutTab === "breeding" && (<>
              {/* Quick action buttons */}
              <div className="breed-quick-actions">
                {Object.entries(EVENT_ICONS).map(([type, icon]) => (
                  <button key={type} className="breed-quick-btn" onClick={() => { setShowEventForm(type); setEventForm({ event_date: localToday() }); }}>
                    {icon} {EVENT_LABELS[type]}
                  </button>
                ))}
                <button className="breed-quick-btn" onClick={() => setScheduling({ mare: viewingMare, type: "palpation" })}>📅 Schedule a check</button>
              </div>
              {/* Scheduled checks, soonest first */}
              {mareScheduled.length > 0 && (
                <div style={{ border: "1px solid rgba(44,95,138,.25)", background: "rgba(44,95,138,.05)", borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--blue)", marginBottom: 4 }}>📅 Scheduled</div>
                  {mareScheduled.map(ev => {
                    const late = ev.event_date < todayStr;
                    const tag = late ? "Overdue" : ev.event_date === todayStr ? "Today" : ev.event_date === shiftISO(todayStr, 1) ? "Tomorrow" : "";
                    return (
                      <div key={ev.id} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", padding: "7px 0", borderTop: "1px solid rgba(44,95,138,.12)" }}>
                        <div style={{ flex: 1, minWidth: 180 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--forest)" }}>
                            {EVENT_ICONS[ev.event_type]} {EVENT_LABELS[ev.event_type] || ev.event_type} · {fmtDay(ev.event_date)}{ev.event_time ? `, ${fmtTime(ev.event_time)}` : ""}
                            {tag && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: late ? "var(--red)" : "var(--blue)" }}>{tag}</span>}
                          </div>
                          {(ev.performed_by || ev.notes) && <div style={{ fontSize: 11.5, color: "var(--text-light)" }}>{[ev.performed_by, ev.notes].filter(Boolean).join(" · ")}</div>}
                        </div>
                        <button className="btn btn-sm btn-primary" onClick={() => recordScheduled(ev)}>Record results</button>
                        <button className="btn btn-sm btn-outline" onClick={() => setScheduling({ mare: viewingMare, type: ev.event_type, existing: ev })}>Reschedule</button>
                        <button className="btn btn-sm btn-outline" onClick={() => cancelScheduled(ev)}>Cancel</button>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Event entry form */}
              {showEventForm && (
                <div style={{ background: "var(--parchment)", borderRadius: 8, padding: 14, marginBottom: 14, border: "1px solid var(--parchment-dark)" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--forest)", marginBottom: 10 }}>{EVENT_ICONS[showEventForm]} Log {EVENT_LABELS[showEventForm]}{eventForm._scheduledFor ? <span style={{ fontWeight: 400, color: "var(--text-light)" }}> · scheduled for {fmtDay(eventForm._scheduledFor)}</span> : null}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div className="form-row"><label className="form-label">Date</label><input className="form-input" type="date" value={eventForm.event_date || ""} onChange={e => setEventForm(p => ({ ...p, event_date: e.target.value }))} /></div>
                    <div className="form-row"><label className="form-label">Time</label><input className="form-input" type="time" value={eventForm.event_time || ""} onChange={e => setEventForm(p => ({ ...p, event_time: e.target.value }))} /></div>
                    <div className="form-row"><label className="form-label">Performed By</label>
                      <PersonSelect value={eventForm.performed_by} options={people} onChange={v => setEventForm(p => ({ ...p, performed_by: v }))} />
                    </div>
                    {showEventForm === "teasing" && (
                      <div className="form-row"><label className="form-label">Tease Score (1-5)</label>
                        <select className="form-input" value={eventForm.tease_score || ""} onChange={e => setEventForm(p => ({ ...p, tease_score: e.target.value }))}>
                          <option value="">—</option><option value="1">1 — Resistant</option><option value="2">2 — Indifferent</option><option value="3">3 — Interested</option><option value="4">4 — Receptive</option><option value="5">5 — Strong Heat</option>
                        </select>
                      </div>
                    )}
                    {showEventForm === "palpation" && (<>
                      <div className="form-row"><label className="form-label">Follicle Size (mm)</label><input className="form-input" value={eventForm.follicle_size || ""} onChange={e => setEventForm(p => ({ ...p, follicle_size: e.target.value }))} placeholder="e.g. 35mm" /></div>
                    </>) }
                    {showEventForm === "palpation" && (
                      <div style={{ gridColumn: "1/-1", background: "rgba(33,150,243,.06)", borderRadius: 8, padding: "10px 12px", border: "1px solid rgba(33,150,243,.15)" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--blue)", marginBottom: 8 }}>📡 Palpation Findings</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Left Ovary</label><input className="form-input" value={eventForm.left_ovary || ""} onChange={e => setEventForm(p => ({ ...p, left_ovary: e.target.value }))} placeholder="e.g. 35mm follicle" style={{ fontSize: 12 }} /></div>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Right Ovary</label><input className="form-input" value={eventForm.right_ovary || ""} onChange={e => setEventForm(p => ({ ...p, right_ovary: e.target.value }))} placeholder="e.g. 28mm follicle" style={{ fontSize: 12 }} /></div>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Uterine Tone</label>
                            <select className="form-input" value={eventForm.uterine_tone || ""} onChange={e => setEventForm(p => ({ ...p, uterine_tone: e.target.value }))} style={{ fontSize: 12 }}>
                              <option value="">— Select —</option><option>Flaccid</option><option>Mild</option><option>Moderate</option><option>Turgid</option>
                            </select>
                          </div>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Cervix</label>
                            <select className="form-input" value={eventForm.cervix || ""} onChange={e => setEventForm(p => ({ ...p, cervix: e.target.value }))} style={{ fontSize: 12 }}>
                              <option value="">— Select —</option><option>Open</option><option>Relaxing</option><option>Closed</option><option>Tight</option>
                            </select>
                          </div>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Edema Score</label>
                            <select className="form-input" value={eventForm.edema_score || ""} onChange={e => setEventForm(p => ({ ...p, edema_score: e.target.value }))} style={{ fontSize: 12 }}>
                              <option value="">—</option><option value="0">0 — None</option><option value="1">1 — Mild</option><option value="2">2 — Moderate</option><option value="3">3 — Marked</option><option value="4">4 — Extreme</option>
                            </select>
                          </div>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Uterine Fluid</label>
                            <select className="form-input" value={eventForm.uterine_fluid || ""} onChange={e => setEventForm(p => ({ ...p, uterine_fluid: e.target.value }))} style={{ fontSize: 12 }}>
                              <option value="">— Select —</option><option>None</option><option>Small amount</option><option>Moderate</option><option>Excessive</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                    {showEventForm === "icsi" && (<>
                      <div className="form-row"><label className="form-label">Stallion</label>
                        <select className="form-input" value={eventForm.stallion_name || ""} onChange={e => setEventForm(p => ({ ...p, stallion_name: e.target.value }))}>
                          <option value="">— Select —</option>{stallions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                      </div>
                    </>)}
                    {showEventForm === "icsi" && (
                      <div style={{ gridColumn: "1/-1", background: "rgba(156,39,176,.06)", borderRadius: 8, padding: "10px 12px", border: "1px solid rgba(156,39,176,.15)" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#9c27b0", marginBottom: 8 }}>🧫 ICSI / Aspiration Details</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Procedure</label>
                            <select className="form-input" value={eventForm.icsi_procedure || ""} onChange={e => setEventForm(p => ({ ...p, icsi_procedure: e.target.value }))} style={{ fontSize: 12 }}>
                              <option value="">— Select —</option><option>OPU (Ovum Pickup)</option><option>ICSI Injection</option><option>Embryo Transfer</option><option>Oocyte Ship</option>
                            </select>
                          </div>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}># Oocytes Collected</label><input className="form-input" type="number" value={eventForm.oocytes_collected || ""} onChange={e => setEventForm(p => ({ ...p, oocytes_collected: e.target.value }))} style={{ fontSize: 12 }} /></div>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}># Mature Oocytes</label><input className="form-input" type="number" value={eventForm.oocytes_mature || ""} onChange={e => setEventForm(p => ({ ...p, oocytes_mature: e.target.value }))} style={{ fontSize: 12 }} /></div>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}># Injected</label><input className="form-input" type="number" value={eventForm.oocytes_injected || ""} onChange={e => setEventForm(p => ({ ...p, oocytes_injected: e.target.value }))} style={{ fontSize: 12 }} /></div>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}># Embryos Produced</label><input className="form-input" type="number" value={eventForm.embryos_produced || ""} onChange={e => setEventForm(p => ({ ...p, embryos_produced: e.target.value }))} style={{ fontSize: 12 }} /></div>
                          <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Lab / Facility</label><input className="form-input" value={eventForm.lab_facility || ""} onChange={e => setEventForm(p => ({ ...p, lab_facility: e.target.value }))} placeholder="e.g. Texas A&M" style={{ fontSize: 12 }} /></div>
                          <div className="form-row" style={{ gridColumn: "1/-1" }}><label className="form-label" style={{ fontSize: 10 }}>Disposition</label>
                            <select className="form-input" value={eventForm.disposition || ""} onChange={e => setEventForm(p => ({ ...p, disposition: e.target.value }))} style={{ fontSize: 12 }}>
                              <option value="">— Select —</option><option>Fresh Transfer</option><option>Frozen</option><option>Shipped to Lab</option><option>Discarded</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                    {showEventForm === "breeding" && (<>
                      <div className="form-row"><label className="form-label">Stallion</label>
                        <select className="form-input" value={eventForm.stallion_name || ""} onChange={e => setEventForm(p => ({ ...p, stallion_name: e.target.value }))}>
                          <option value="">— Select —</option>{stallions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                      </div>
                      <div className="form-row"><label className="form-label">Method</label>
                        <select className="form-input" value={eventForm.method || ""} onChange={e => setEventForm(p => ({ ...p, method: e.target.value }))}>
                          <option value="">— Select —</option><option>AI — Fresh</option><option>AI — Chilled</option><option>AI — Frozen</option><option>Natural Cover</option>
                        </select>
                      </div>
                    </>)}
                    {showEventForm === "medication" && (<>
                      <div className="form-row"><label className="form-label">Medication</label>
                        <select className="form-input" value={eventForm.medication_name || ""} onChange={e => setEventForm(p => ({ ...p, medication_name: e.target.value }))}>
                          <option value="">— Select —</option><option>Regumate (Altrenogest)</option><option>Deslorelin (Ovuplant)</option><option>hCG</option><option>PGF2α (Lutalyse)</option><option>Cloprostenol (Estrumate)</option><option>Oxytocin</option><option>Progesterone</option><option>Antibiotics</option><option>Other</option>
                        </select>
                      </div>
                      <div className="form-row"><label className="form-label">Dosage</label><input className="form-input" value={eventForm.dosage || ""} onChange={e => setEventForm(p => ({ ...p, dosage: e.target.value }))} placeholder="e.g. 10mg IM" /></div>
                    </>)}
                    {showEventForm === "pregnancy_check" && (
                      <div className="form-row"><label className="form-label">Result</label>
                        <select className="form-input" value={eventForm.pregnancy_status || ""} onChange={e => setEventForm(p => ({ ...p, pregnancy_status: e.target.value }))}>
                          <option value="">— Select —</option><option>Pregnant — Single</option><option>Pregnant — Twins</option><option>Open (Not Pregnant)</option><option>Recheck Needed</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={eventForm.notes || ""} onChange={e => setEventForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional observations…" /></div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button className="btn btn-primary" onClick={addBreedingEvent}>Save Event</button>
                    <button className="btn btn-outline" onClick={() => { setShowEventForm(null); setEventForm({}); }}>Cancel</button>
                  </div>
                </div>
              )}
              {/* Event timeline */}
              {mareEvents.length === 0 && !showEventForm && (
                <div className="empty-state" style={{ padding: "20px 0" }}>No breeding events logged yet. Use the buttons above to start recording.</div>
              )}
              {mareEvents.map(ev => (
                <div key={ev.id} className={`breed-event ${ev.event_type}`} onClick={() => openEventDetail(ev)} style={{ cursor: "pointer", transition: "box-shadow 0.15s" }} onMouseOver={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,.1)"} onMouseOut={e => e.currentTarget.style.boxShadow = "none"}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div className="breed-event-date">{ev.event_date}{ev.event_time ? ` · ${ev.event_time}` : ""} {ev.performed_by ? `· ${ev.performed_by}` : ""}</div>
                      <div className="breed-event-title">{EVENT_ICONS[ev.event_type] || "📝"} {EVENT_LABELS[ev.event_type] || ev.event_type}
                        {ev.event_type === "teasing" && ev.tease_score ? ` — Score ${ev.tease_score}/5` : ""}
                        {ev.event_type === "palpation" && ev.follicle_size ? ` — ${ev.follicle_size}` : ""}
                        {ev.event_type === "breeding" ? ` — ${ev.stallion_name || ""}${ev.method ? ` (${ev.method})` : ""}` : ""}
                        {ev.event_type === "medication" ? ` — ${ev.medication_name || ""}${ev.dosage ? ` ${ev.dosage}` : ""}` : ""}
                        {ev.event_type === "pregnancy_check" && ev.pregnancy_status ? ` — ${ev.pregnancy_status}` : ""}
                        {ev.event_type === "icsi" ? ` — ${ev.stallion_name || "ICSI"}` : ""}
                      </div>
                      {ev.notes && <div className="breed-event-detail">{ev.notes}</div>}
                    </div>
                    <span style={{ fontSize: 11, color: "var(--text-light)", flexShrink: 0, marginLeft: 8, marginTop: 2 }}>View →</span>
                  </div>
                </div>
              ))}
            </>)}
            {flyoutTab === "farrier" && (<>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-light)" }}>Farrier Records</div>
                <button className="btn btn-sm btn-primary" onClick={() => { setShowFarrierForm(true); setEditingFarrier(null); setFarrierForm(emptyFarrier); }}>+ Add Visit</button>
              </div>
              {showFarrierForm && (
                <div style={{ background: "var(--parchment)", borderRadius: 8, padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--forest)", marginBottom: 10 }}>{editingFarrier ? "Edit Farrier Visit" : "Log Farrier Visit"}</div>
                  <div className="form-grid">
                    <div className="form-row"><label className="form-label">Farrier *</label>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <select className="form-input" style={{ flex: 1 }} value={farrierForm.farrier_name} onChange={mff("farrier_name")}>
                          <option value="">Select...</option>
                          {savedFarriers.sort((a, b) => a.name.localeCompare(b.name)).map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                        </select>
                        <button type="button" onClick={async () => { const name = window.prompt("Enter farrier name:"); if (!name || !name.trim()) return; const trimmed = name.trim(); if (savedFarriers.some(f => f.name.toLowerCase() === trimmed.toLowerCase())) { mff("farrier_name")({ target: { value: trimmed } }); return; } const row = await dbInsert("farriers", { org_id: orgId, name: trimmed }); if (row) { setSavedFarriers(p => [...p, row]); mff("farrier_name")({ target: { value: trimmed } }); } }} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "var(--forest)", color: "white", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, lineHeight: 1 }} title="Add new farrier">+</button>
                      </div>
                    </div>
                    <div className="form-row"><label className="form-label">Visit Date *</label><input className="form-input" type="date" value={farrierForm.visit_date} onChange={mff("visit_date")} /></div>
                    <div className="form-row"><label className="form-label">Service Type</label>
                      <select className="form-input" value={farrierForm.service_type} onChange={mff("service_type")}>
                        <option value="complete_shoeing">Complete Shoeing</option><option value="back_shoeing">Back Shoeing</option><option value="front_shoeing">Front Shoeing</option><option value="trimming">Trimming</option><option value="replace_lost">Replace Lost Horseshoe</option><option value="one_shoe_left">One Shoe (Left)</option><option value="one_shoe_right">One Shoe (Right)</option><option value="hind_trim">Hind Trim</option><option value="front_trim">Front Trim</option>
                      </select>
                    </div>
                    <div className="form-row"><label className="form-label">Cost ($)</label><input className="form-input" type="number" step="0.01" value={farrierForm.cost} onChange={mff("cost")} placeholder="0.00" /></div>
                    <div className="form-row"><label className="form-label">Hoof Condition</label>
                      <select className="form-input" value={farrierForm.hoof_condition} onChange={mff("hoof_condition")}>
                        <option value="">-- Select --</option><option value="excellent">Excellent</option><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option><option value="needs_attention">Needs Attention</option>
                      </select>
                    </div>
                    <div className="form-row"><label className="form-label">Next Appointment</label><input className="form-input" type="date" value={farrierForm.next_appointment} onChange={mff("next_appointment")} /></div>
                    <div className="form-row" style={{ gridColumn: "1/-1" }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={farrierForm.notes} onChange={mff("notes")} placeholder="Hoof notes, observations, etc." /></div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button className="btn btn-primary" onClick={editingFarrier ? saveMareFarrierEdit : addMareFarrier}>{editingFarrier ? "Save Changes" : "Save Record"}</button>
                    <button className="btn btn-outline" onClick={() => { setShowFarrierForm(false); setEditingFarrier(null); }}>Cancel</button>
                  </div>
                </div>
              )}
              {(() => {
                const horseRecords = farrierRecords.filter(r => r.horse_id === viewingMare.id).sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date));
                const nextAppt = horseRecords.find(r => r.next_appointment && new Date(r.next_appointment) >= new Date());
                return (<>
                  {nextAppt && (
                    <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 20 }}>📅</span>
                      <div><div style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>Next Appointment</div><div style={{ fontSize: 13, color: "#15803d" }}>{nextAppt.next_appointment} - {nextAppt.farrier_name}</div></div>
                    </div>
                  )}
                  {horseRecords.length === 0 ? <div className="empty-state" style={{ padding: "20px 0" }}>No farrier records yet. Click "+ Add Visit" to log a farrier visit.</div> : (
                    horseRecords.map(rec => (
                      <div key={rec.id} style={{ background: "var(--parchment)", borderRadius: 8, padding: "12px 14px", marginBottom: 8, borderLeft: `3px solid ${rec.hoof_condition === "excellent" || rec.hoof_condition === "good" ? "#4caf50" : rec.hoof_condition === "fair" ? "#ff9800" : rec.hoof_condition === "poor" || rec.hoof_condition === "needs_attention" ? "#f44336" : "#795548"}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--forest)" }}>{rec.visit_date} - {rec.service_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</div>
                            <div style={{ fontSize: 12, color: "var(--text-mid)", marginTop: 2 }}>Farrier: {rec.farrier_name}{rec.cost ? ` | $${parseFloat(rec.cost).toFixed(2)}` : ""}</div>
                            {rec.hoof_condition && <div style={{ fontSize: 11, marginTop: 3 }}>🦶 Hoof: <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{rec.hoof_condition.replace(/_/g, " ")}</span></div>}
                            {rec.notes && <div style={{ fontSize: 12, color: "var(--text-light)", marginTop: 4, fontStyle: "italic" }}>{rec.notes}</div>}
                            {rec.next_appointment && <div style={{ fontSize: 11, color: "#2563eb", marginTop: 3 }}>📅 Next: {rec.next_appointment}</div>}
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13 }} onClick={() => { setEditingFarrier(rec); setFarrierForm({ farrier_name: rec.farrier_name, visit_date: rec.visit_date, service_type: rec.service_type, cost: rec.cost || "", notes: rec.notes || "", hoof_condition: rec.hoof_condition || "", next_appointment: rec.next_appointment || "" }); setShowFarrierForm(true); }}>✏️</button>
                            {canDelete() && <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--red)" }} onClick={() => deleteMareFarrier(rec)}>✕</button>}
                          </div>
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", marginBottom: 4 }}>Attachments</div>
                          {(rec.photo_urls || []).length > 0 && (
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                              {(rec.photo_urls || []).map((url, pi) => (
                                <div key={pi} style={{ position: "relative", width: 80, height: 80, borderRadius: 6, overflow: "hidden", border: "1px solid #ddd", cursor: "pointer" }}>
                                  <HorsePhoto value={url} alt={`Photo ${pi + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm("Remove this photo?")) return; const updated = (rec.photo_urls || []).filter(p => p !== url); await dbUpdate("farrier_records", rec.id, { photo_urls: updated }); setFarrierRecords(p => p.map(r => r.id === rec.id ? { ...r, photo_urls: updated } : r)); }} style={{ position: "absolute", top: 2, right: 2, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,.6)", color: "white", border: "none", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div
                            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "#eff6ff"; }}
                            onDragLeave={e => { e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.background = "white"; }}
                            onDrop={async e => { e.preventDefault(); e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.background = "white"; const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/")); if (!files.length) return; const newUrls = []; for (const file of files) { const url = await uploadHorsePhoto(file, orgId); newUrls.push(url); } const updated = [...(rec.photo_urls || []), ...newUrls]; await dbUpdate("farrier_records", rec.id, { photo_urls: updated }); setFarrierRecords(p => p.map(r => r.id === rec.id ? { ...r, photo_urls: updated } : r)); }}
                            onClick={() => { const input = document.createElement("input"); input.type = "file"; input.accept = "image/*"; input.multiple = true; input.onchange = async e => { const files = Array.from(e.target.files).filter(f => f.type.startsWith("image/")); if (!files.length) return; const newUrls = []; for (const file of files) { const url = await uploadHorsePhoto(file, orgId); newUrls.push(url); } const updated = [...(rec.photo_urls || []), ...newUrls]; await dbUpdate("farrier_records", rec.id, { photo_urls: updated }); setFarrierRecords(p => p.map(r => r.id === rec.id ? { ...r, photo_urls: updated } : r)); }; input.click(); }}
                            style={{ border: "2px dashed #93c5fd", borderRadius: 8, padding: "18px 14px", textAlign: "center", cursor: "pointer", background: "white", transition: "all .2s" }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                              <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#666" }}>+</span>
                              <span style={{ fontSize: 13, color: "#555", fontWeight: 500 }}>Add attachment files</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </>);
              })()}
            </>)}
          </div>
          <div className="modal-footer" style={{ flexShrink: 0 }}>
            {flyoutTab !== "farrier" && !editingMare ? (<>
              <button className="btn btn-outline" onClick={() => { setViewingMare(null); setFlyoutTab("breeding"); setShowEventForm(null); setEditingMare(null); }}>Close</button>
              <button className="btn btn-primary" onClick={startEditMare}>✏️ Edit</button>
              {canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={async () => { if (await deleteMare(viewingMare)) { setViewingMare(null); setFlyoutTab("breeding"); } }}>Delete</button>}
            </>) : flyoutTab !== "farrier" && editingMare ? (<>
              <button className="btn btn-outline" onClick={() => setEditingMare(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEditMare}>Save Changes</button>
            </>) : (
              <button className="btn btn-outline" onClick={() => { setViewingMare(null); setFlyoutTab("breeding"); }}>Close</button>
            )}
          </div>
        </div>
      </div>
    )}

    {scheduling && (
      <ScheduleCheckModal orgId={orgId} mare={scheduling.mare} type={scheduling.type} existing={scheduling.existing} afterSave={scheduling.afterSave}
        upcoming={breedingEvents.filter(e => isScheduled(e) && e.mare_id === scheduling.mare.id && e.id !== scheduling.existing?.id)}
        people={people}
        onClose={() => setScheduling(null)}
        onSaved={(row, isUpdate) => { setBreedingEvents(p => isUpdate ? p.map(e => e.id === row.id ? row : e) : [row, ...p]); setScheduling(null); save(); }} />
    )}
    {showGroups && <ManageGroupsModal orgId={orgId} groups={groups} setGroups={setGroups} mares={mares} setMares={setMares} onClose={() => setShowGroups(false)} />}

    {/* ─── BREEDING EVENT DETAIL / EDIT MODAL ─── */}
    {viewingEvent && (
      <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => { setViewingEvent(null); setEditingEvent(false); }}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
          <div className="modal-header" style={{ background: "var(--forest)", color: "white" }}>
            <div className="modal-title" style={{ color: "white" }}>
              {EVENT_ICONS[viewingEvent.event_type]} {EVENT_LABELS[viewingEvent.event_type] || viewingEvent.event_type} — {editingEvent ? "Edit" : "Details"}
            </div>
            <button className="modal-close" style={{ color: "white" }} onClick={() => { setViewingEvent(null); setEditingEvent(false); }}>✕</button>
          </div>
          <div className="modal-body">
            {!editingEvent ? (
              /* ── VIEW MODE ── */
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div><div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-light)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 2 }}>Date</div><div style={{ fontSize: 13, fontWeight: 600 }}>{viewingEvent.event_date || "—"}</div></div>
                  <div><div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-light)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 2 }}>Time</div><div style={{ fontSize: 13, fontWeight: 600 }}>{viewingEvent.event_time || "—"}</div></div>
                  <div><div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-light)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 2 }}>Performed By</div><div style={{ fontSize: 13, fontWeight: 600 }}>{viewingEvent.performed_by || "—"}</div></div>
                </div>

                {viewingEvent.event_type === "teasing" && viewingEvent.tease_score && (
                  <div style={{ background: "var(--parchment)", borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-light)", textTransform: "uppercase", marginBottom: 4 }}>Tease Score</div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{viewingEvent.tease_score}/5 — {["","Resistant","Indifferent","Interested","Receptive","Strong Heat"][viewingEvent.tease_score] || ""}</div>
                  </div>
                )}

                {viewingEvent.event_type === "palpation" && (
                  <div style={{ background: "rgba(33,150,243,.06)", borderRadius: 8, padding: 12, marginBottom: 12, border: "1px solid rgba(33,150,243,.15)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--blue)", textTransform: "uppercase", marginBottom: 8 }}>Palpation Findings</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, fontSize: 12 }}>
                      {[["Follicle Size", viewingEvent.follicle_size], ["Left Ovary", viewingEvent.left_ovary], ["Right Ovary", viewingEvent.right_ovary], ["Uterine Tone", viewingEvent.uterine_tone], ["Cervix", viewingEvent.cervix], ["Edema Score", viewingEvent.edema_score], ["Uterine Fluid", viewingEvent.uterine_fluid]].map(([label, val]) => val ? (
                        <div key={label}><div style={{ fontSize: 10, color: "var(--text-light)", fontWeight: 600 }}>{label}</div><div style={{ fontWeight: 600 }}>{val}</div></div>
                      ) : null)}
                    </div>
                  </div>
                )}

                {viewingEvent.event_type === "breeding" && (
                  <div style={{ background: "rgba(76,175,80,.06)", borderRadius: 8, padding: 12, marginBottom: 12, border: "1px solid rgba(76,175,80,.15)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--forest)", textTransform: "uppercase", marginBottom: 8 }}>Breeding Details</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 12 }}>
                      <div><div style={{ fontSize: 10, color: "var(--text-light)", fontWeight: 600 }}>Stallion</div><div style={{ fontWeight: 600 }}>{viewingEvent.stallion_name || "—"}</div></div>
                      <div><div style={{ fontSize: 10, color: "var(--text-light)", fontWeight: 600 }}>Method</div><div style={{ fontWeight: 600 }}>{viewingEvent.method || "—"}</div></div>
                    </div>
                  </div>
                )}

                {viewingEvent.event_type === "medication" && (
                  <div style={{ background: "rgba(255,152,0,.06)", borderRadius: 8, padding: 12, marginBottom: 12, border: "1px solid rgba(255,152,0,.15)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#e65100", textTransform: "uppercase", marginBottom: 8 }}>Medication Details</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 12 }}>
                      <div><div style={{ fontSize: 10, color: "var(--text-light)", fontWeight: 600 }}>Medication</div><div style={{ fontWeight: 600 }}>{viewingEvent.medication_name || "—"}</div></div>
                      <div><div style={{ fontSize: 10, color: "var(--text-light)", fontWeight: 600 }}>Dosage</div><div style={{ fontWeight: 600 }}>{viewingEvent.dosage || "—"}</div></div>
                    </div>
                  </div>
                )}

                {viewingEvent.event_type === "pregnancy_check" && viewingEvent.pregnancy_status && (
                  <div style={{ background: "rgba(233,30,99,.06)", borderRadius: 8, padding: 12, marginBottom: 12, border: "1px solid rgba(233,30,99,.15)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#c2185b", textTransform: "uppercase", marginBottom: 4 }}>Result</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: viewingEvent.pregnancy_status.includes("Pregnant") ? "var(--forest)" : "var(--red)" }}>{viewingEvent.pregnancy_status}</div>
                  </div>
                )}

                {viewingEvent.event_type === "icsi" && (
                  <div style={{ background: "rgba(156,39,176,.06)", borderRadius: 8, padding: 12, marginBottom: 12, border: "1px solid rgba(156,39,176,.15)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9c27b0", textTransform: "uppercase", marginBottom: 8 }}>ICSI / Aspiration Details</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, fontSize: 12 }}>
                      {[["Stallion", viewingEvent.stallion_name], ["Procedure", viewingEvent.icsi_procedure], ["Oocytes Collected", viewingEvent.oocytes_collected], ["Mature Oocytes", viewingEvent.oocytes_mature], ["Injected", viewingEvent.oocytes_injected], ["Embryos Produced", viewingEvent.embryos_produced], ["Lab / Facility", viewingEvent.lab_facility], ["Disposition", viewingEvent.disposition]].map(([label, val]) => val ? (
                        <div key={label}><div style={{ fontSize: 10, color: "var(--text-light)", fontWeight: 600 }}>{label}</div><div style={{ fontWeight: 600 }}>{val}</div></div>
                      ) : null)}
                    </div>
                  </div>
                )}

                {viewingEvent.notes && (
                  <div style={{ background: "var(--parchment)", borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-light)", textTransform: "uppercase", marginBottom: 4 }}>Notes</div>
                    <div style={{ fontSize: 13, lineHeight: 1.5 }}>{viewingEvent.notes}</div>
                  </div>
                )}
              </div>
            ) : (
              /* ── EDIT MODE ── */
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div className="form-row"><label className="form-label">Date</label><input className="form-input" type="date" value={editEventForm.event_date || ""} onChange={e => setEditEventForm(p => ({ ...p, event_date: e.target.value }))} /></div>
                  <div className="form-row"><label className="form-label">Time</label><input className="form-input" type="time" value={editEventForm.event_time || ""} onChange={e => setEditEventForm(p => ({ ...p, event_time: e.target.value }))} /></div>
                  <div className="form-row"><label className="form-label">Performed By</label>
                    <PersonSelect value={editEventForm.performed_by} options={people} onChange={v => setEditEventForm(p => ({ ...p, performed_by: v }))} />
                  </div>
                  {editEventForm.event_type === "teasing" && (
                    <div className="form-row"><label className="form-label">Tease Score (1-5)</label>
                      <select className="form-input" value={editEventForm.tease_score || ""} onChange={e => setEditEventForm(p => ({ ...p, tease_score: e.target.value }))}>
                        <option value="">—</option><option value="1">1 — Resistant</option><option value="2">2 — Indifferent</option><option value="3">3 — Interested</option><option value="4">4 — Receptive</option><option value="5">5 — Strong Heat</option>
                      </select>
                    </div>
                  )}
                  {editEventForm.event_type === "palpation" && (
                    <div className="form-row"><label className="form-label">Follicle Size (mm)</label><input className="form-input" value={editEventForm.follicle_size || ""} onChange={e => setEditEventForm(p => ({ ...p, follicle_size: e.target.value }))} placeholder="e.g. 35mm" /></div>
                  )}
                </div>
                {editEventForm.event_type === "palpation" && (
                  <div style={{ background: "rgba(33,150,243,.06)", borderRadius: 8, padding: "10px 12px", marginTop: 8, border: "1px solid rgba(33,150,243,.15)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--blue)", marginBottom: 8 }}>Palpation Findings</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Left Ovary</label><input className="form-input" value={editEventForm.left_ovary || ""} onChange={e => setEditEventForm(p => ({ ...p, left_ovary: e.target.value }))} style={{ fontSize: 12 }} /></div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Right Ovary</label><input className="form-input" value={editEventForm.right_ovary || ""} onChange={e => setEditEventForm(p => ({ ...p, right_ovary: e.target.value }))} style={{ fontSize: 12 }} /></div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Uterine Tone</label>
                        <select className="form-input" value={editEventForm.uterine_tone || ""} onChange={e => setEditEventForm(p => ({ ...p, uterine_tone: e.target.value }))} style={{ fontSize: 12 }}>
                          <option value="">— Select —</option><option>Flaccid</option><option>Mild</option><option>Moderate</option><option>Turgid</option>
                        </select>
                      </div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Cervix</label>
                        <select className="form-input" value={editEventForm.cervix || ""} onChange={e => setEditEventForm(p => ({ ...p, cervix: e.target.value }))} style={{ fontSize: 12 }}>
                          <option value="">— Select —</option><option>Open</option><option>Relaxing</option><option>Closed</option><option>Tight</option>
                        </select>
                      </div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Edema Score</label>
                        <select className="form-input" value={editEventForm.edema_score || ""} onChange={e => setEditEventForm(p => ({ ...p, edema_score: e.target.value }))} style={{ fontSize: 12 }}>
                          <option value="">—</option><option value="0">0 — None</option><option value="1">1 — Mild</option><option value="2">2 — Moderate</option><option value="3">3 — Marked</option><option value="4">4 — Extreme</option>
                        </select>
                      </div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Uterine Fluid</label>
                        <select className="form-input" value={editEventForm.uterine_fluid || ""} onChange={e => setEditEventForm(p => ({ ...p, uterine_fluid: e.target.value }))} style={{ fontSize: 12 }}>
                          <option value="">— Select —</option><option>None</option><option>Small amount</option><option>Moderate</option><option>Excessive</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                {editEventForm.event_type === "breeding" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                    <div className="form-row"><label className="form-label">Stallion</label>
                      <select className="form-input" value={editEventForm.stallion_name || ""} onChange={e => setEditEventForm(p => ({ ...p, stallion_name: e.target.value }))}>
                        <option value="">— Select —</option>{stallions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                    </div>
                    <div className="form-row"><label className="form-label">Method</label>
                      <select className="form-input" value={editEventForm.method || ""} onChange={e => setEditEventForm(p => ({ ...p, method: e.target.value }))}>
                        <option value="">— Select —</option><option>AI — Fresh</option><option>AI — Chilled</option><option>AI — Frozen</option><option>Natural Cover</option>
                      </select>
                    </div>
                  </div>
                )}
                {editEventForm.event_type === "medication" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                    <div className="form-row"><label className="form-label">Medication</label>
                      <select className="form-input" value={editEventForm.medication_name || ""} onChange={e => setEditEventForm(p => ({ ...p, medication_name: e.target.value }))}>
                        <option value="">— Select —</option><option>Regumate (Altrenogest)</option><option>Deslorelin (Ovuplant)</option><option>hCG</option><option>PGF2α (Lutalyse)</option><option>Cloprostenol (Estrumate)</option><option>Oxytocin</option><option>Progesterone</option><option>Antibiotics</option><option>Other</option>
                      </select>
                    </div>
                    <div className="form-row"><label className="form-label">Dosage</label><input className="form-input" value={editEventForm.dosage || ""} onChange={e => setEditEventForm(p => ({ ...p, dosage: e.target.value }))} placeholder="e.g. 10mg IM" /></div>
                  </div>
                )}
                {editEventForm.event_type === "pregnancy_check" && (
                  <div style={{ marginTop: 8 }}>
                    <div className="form-row"><label className="form-label">Result</label>
                      <select className="form-input" value={editEventForm.pregnancy_status || ""} onChange={e => setEditEventForm(p => ({ ...p, pregnancy_status: e.target.value }))}>
                        <option value="">— Select —</option><option>Pregnant — Single</option><option>Pregnant — Twins</option><option>Open (Not Pregnant)</option><option>Recheck Needed</option>
                      </select>
                    </div>
                  </div>
                )}
                {editEventForm.event_type === "icsi" && (
                  <div style={{ background: "rgba(156,39,176,.06)", borderRadius: 8, padding: "10px 12px", marginTop: 8, border: "1px solid rgba(156,39,176,.15)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#9c27b0", marginBottom: 8 }}>ICSI / Aspiration Details</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Stallion</label>
                        <select className="form-input" value={editEventForm.stallion_name || ""} onChange={e => setEditEventForm(p => ({ ...p, stallion_name: e.target.value }))} style={{ fontSize: 12 }}>
                          <option value="">— Select —</option>{stallions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                      </div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Procedure</label>
                        <select className="form-input" value={editEventForm.icsi_procedure || ""} onChange={e => setEditEventForm(p => ({ ...p, icsi_procedure: e.target.value }))} style={{ fontSize: 12 }}>
                          <option value="">— Select —</option><option>OPU (Ovum Pickup)</option><option>ICSI Injection</option><option>Embryo Transfer</option><option>Oocyte Ship</option>
                        </select>
                      </div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}># Oocytes Collected</label><input className="form-input" type="number" value={editEventForm.oocytes_collected || ""} onChange={e => setEditEventForm(p => ({ ...p, oocytes_collected: e.target.value }))} style={{ fontSize: 12 }} /></div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}># Mature Oocytes</label><input className="form-input" type="number" value={editEventForm.oocytes_mature || ""} onChange={e => setEditEventForm(p => ({ ...p, oocytes_mature: e.target.value }))} style={{ fontSize: 12 }} /></div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}># Injected</label><input className="form-input" type="number" value={editEventForm.oocytes_injected || ""} onChange={e => setEditEventForm(p => ({ ...p, oocytes_injected: e.target.value }))} style={{ fontSize: 12 }} /></div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}># Embryos Produced</label><input className="form-input" type="number" value={editEventForm.embryos_produced || ""} onChange={e => setEditEventForm(p => ({ ...p, embryos_produced: e.target.value }))} style={{ fontSize: 12 }} /></div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Lab / Facility</label><input className="form-input" value={editEventForm.lab_facility || ""} onChange={e => setEditEventForm(p => ({ ...p, lab_facility: e.target.value }))} style={{ fontSize: 12 }} /></div>
                      <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Disposition</label>
                        <select className="form-input" value={editEventForm.disposition || ""} onChange={e => setEditEventForm(p => ({ ...p, disposition: e.target.value }))} style={{ fontSize: 12 }}>
                          <option value="">— Select —</option><option>Fresh Transfer</option><option>Frozen</option><option>Shipped to Lab</option><option>Discarded</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={editEventForm.notes || ""} onChange={e => setEditEventForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional observations…" /></div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {!editingEvent ? (<>
              <button className="btn btn-outline" onClick={() => { setViewingEvent(null); setEditingEvent(false); }}>Close</button>
              <button className="btn btn-primary" onClick={startEditEvent}>✏️ Edit</button>
              {canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={() => deleteEvent(viewingEvent.id)}>Delete</button>}
            </>) : (<>
              <button className="btn btn-outline" onClick={() => setEditingEvent(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEditEvent}>Save Changes</button>
            </>)}
          </div>
        </div>
      </div>
    )}

    <SaveToast show={saved} />
  </>);
}

// ─── CUSTOMERS ────────────────────────────────────────────────────────────────
// ─── IN-APP EMAIL COMPOSER ───────────────────────────────────────────────────
//
// Why this exists alongside the mailto: links rather than replacing them.
//
// mailto: hands off to whatever the operating system registered as the default
// mail handler. For someone who lives in Gmail in a browser tab with nothing
// configured on the desktop, clicking it opens nothing, or an Apple Mail they
// have never set up. It also leaves no trace: six months later, "did we ever
// tell that customer their shipment was delayed" has no answer.
//
// This sends through the farm's OWN connected mailbox via send-email, so it
// lands in the customer's inbox from the farm's address, and sendOrgEmail
// writes it to email_log on the way out. That log is the point as much as the
// delivery is.
//
// If the farm has not connected a mailbox yet the send fails with a config
// error, and rather than stranding whatever they just typed, the failure state
// offers a mailto: carrying the subject and body across. Degrading to the thing
// that always sort of works beats an apology.

function htmlEscape(s) {
  return String(s ?? "").replace(/[&<>"']/g, c =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function EmailComposer({ to, name, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [status, setStatus] = useState("idle");   // idle | sending | sent | error
  const [error, setError] = useState("");

  const send = async () => {
    if (!subject.trim()) { setError("Give it a subject first."); setStatus("error"); return; }
    if (!body.trim()) { setError("The message is empty."); setStatus("error"); return; }
    setStatus("sending"); setError("");
    try {
      // Plain text in, HTML out. Escape first, then turn newlines into breaks --
      // the other order would let a customer name containing < break the mail.
      const html = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.6;color:#2c2a26;">${
        htmlEscape(body).replace(/\n/g, "<br>")}</div>`;
      await callFunction("send-email", {
        to, subject: subject.trim(), body: html, event_type: "customer_message",
        signature: true,
      });
      setStatus("sent");
      setTimeout(onClose, 1200);
    } catch (e) {
      setError(e.message || "Send failed.");
      setStatus("error");
    }
  };

  const fallbackHref = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div className="modal-title">Email {name || to}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 12, color: "var(--text-light)", marginBottom: 12 }}>
            To <strong style={{ color: "var(--text-mid)" }}>{to}</strong> — sent from your farm's
            connected mailbox and saved to your Email Log.
          </div>
          <div className="form-row">
            <label className="form-label">Subject</label>
            <input className="form-input" value={subject} autoFocus
                   onChange={e => setSubject(e.target.value)}
                   disabled={status === "sending" || status === "sent"} />
          </div>
          <div className="form-row">
            <label className="form-label">Message</label>
            <textarea className="form-input" rows={9} value={body}
                      onChange={e => setBody(e.target.value)}
                      disabled={status === "sending" || status === "sent"} />
          </div>
          {status === "error" && (
            <div style={{ fontSize: 12.5, color: "var(--red)", background: "#fde8e8", border: "1px solid #f5c6c6",
                          borderRadius: 6, padding: "9px 11px", marginTop: 4 }}>
              {error}
              <div style={{ marginTop: 6 }}>
                <a href={fallbackHref} style={{ color: "var(--blue)" }}>
                  Open this in your own mail app instead →
                </a>
              </div>
            </div>
          )}
          {status === "sent" && (
            <div style={{ fontSize: 12.5, color: "#166534", background: "#dcfce7", border: "1px solid #bbf7d0",
                          borderRadius: 6, padding: "9px 11px", marginTop: 4 }}>
              Sent. It is in your Email Log under Settings.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>
            {status === "sent" ? "Close" : "Cancel"}
          </button>
          <button className="btn btn-primary" onClick={send}
                  disabled={status === "sending" || status === "sent"}>
            {status === "sending" ? "Sending…" : status === "sent" ? "Sent" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Customers({ orgId }) {
  const [composeTo, setComposeTo] = useState(null);   // { email, name } or null
  const { data: customers, setData: setCustomers, loading } = useSupabaseTable("customers", orgId);
  const { data: orders, setData: setOrders } = useSupabaseTable("orders", orgId);
  const { data: semen, setData: setSemen } = useSupabaseTable("frozen_semen", orgId);
  const { data: stallions } = useSupabaseTable("stallions", orgId);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [newOrderMode, setNewOrderMode] = useState(false);
  const [semenOrderMode, setSemenOrderMode] = useState(false);
  const [noticeOrderId, setNoticeOrderId] = useState(null);
  const [noticeToast, setNoticeToast] = useState("");
  const [saved, setSaved] = useState(false);
  const todayISO = localToday();
  const emptyCustomer = { name: "", company: "", email: "", phone: "", address: "", city: "", state: "", zip: "", country: "US", type: "Active", notes: "", shipping_address: "", shipping_city: "", shipping_state: "", shipping_zip: "", shipping_same: false };
  const emptyOrder = { description: "", total: "", status: "Pending", date: todayISO, notes: "" };
  const emptySemenOrder = {
    stallion: "", batch_id: "", qty: 1, semen_type: "Frozen", shipping_method: "FedEx Priority", tracking: "", date: todayISO,
    mare_name: "", mare_reg_number: "", person_requesting: "",
    processed_by: "", progr_cells: "", net_volume: "", extender_vol: "", extender_used: "", antibiotic_added: "",
    centrifuged: "No", ext_sperm: "", equitainer_number: "", shipment_comments: "", notes: ""
  };
  const [form, setForm] = useState(emptyCustomer);
  const [orderForm, setOrderForm] = useState(emptyOrder);
  const [semenForm, setSemenForm] = useState(emptySemenOrder);
  const ssf = k => e => setSemenForm(p => ({ ...p, [k]: e.target.value }));
  const teamNames = useTeamNames(orgId);

  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  // Contract management
  const { data: customerContracts, setData: setCustomerContracts } = useSupabaseTable("customer_contracts", orgId);
  const { data: contractTemplates } = useSupabaseTable("contract_templates", orgId);
  const [custDetailTab, setCustDetailTab] = useState("details");
  const [showAssignContract, setShowAssignContract] = useState(false);
  const [assignForm, setAssignForm] = useState({ template_id: "", stallion_name: "", mare_name: "", notes: "" });
  const [signedFile, setSignedFile] = useState(null);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [showUploadSigned, setShowUploadSigned] = useState(null); // contract id
  const [sendingContract, setSendingContract] = useState(null);   // contract row, e-signing
  const [previewPdf, setPreviewPdf] = useState(null);             // { path, fileName, title }

  const activeTemplates = contractTemplates.filter(t => t.is_active);
  const CONTRACT_TYPES_CUST = { service_agreement: "Service Agreement", icsi_contract: "ICSI Contract", boarding: "Boarding", lease: "Lease", other: "Other" };

  const customerContractsFiltered = viewingCustomer ? customerContracts.filter(c => c.customer_id === viewingCustomer.id).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")) : [];

  // A ref, not state: a fast double click fires twice before `disabled` re-renders.
  const assignInFlight = useRef(false);
  const assignContract = async () => {
    if (!viewingCustomer || !assignForm.template_id) { alert("Please select a contract template"); return; }
    if (assignInFlight.current) return;
    const template = contractTemplates.find(t => t.id === assignForm.template_id);
    const norm = v => (v || "").trim().toLowerCase();
    const stallionFor = assignForm.stallion_name || template?.stallion_name || "";
    const already = customerContracts.find(c =>
      c.customer_id === viewingCustomer.id && c.template_id === assignForm.template_id &&
      norm(c.stallion_name) === norm(stallionFor) && norm(c.mare_name) === norm(assignForm.mare_name) &&
      !["cancelled", "expired"].includes(c.status));
    if (already) {
      const what = [template?.name || already.contract_name, stallionFor, assignForm.mare_name && "mare " + assignForm.mare_name].filter(Boolean).join(", ");
      if (!window.confirm(`${viewingCustomer.name} already has ${what} (${already.status}). Assign a second copy anyway?`)) return;
    }
    assignInFlight.current = true;
    setUploadingContract(true);
    try {
      const row = await dbInsert("customer_contracts", {
        id: crypto.randomUUID(), org_id: orgId, customer_id: viewingCustomer.id,
        template_id: assignForm.template_id, contract_name: template?.name || "Contract",
        stallion_name: assignForm.stallion_name || template?.stallion_name || "",
        mare_name: assignForm.mare_name || "", status: "pending",
        sent_date: localToday(),
        notes: assignForm.notes || null
      });
      setCustomerContracts(p => [row, ...p]);
      setShowAssignContract(false); setAssignForm({ template_id: "", stallion_name: "", mare_name: "", notes: "" });
      save();
    } catch (e) { alert("Error: " + e.message); }
    assignInFlight.current = false;
    setUploadingContract(false);
  };

  const uploadSignedContract = async (contractId) => {
    if (!signedFile) { alert("Please select the signed PDF"); return; }
    setUploadingContract(true);
    try {
      const contract = customerContracts.find(c => c.id === contractId);
      const safeName = (contract?.contract_name || "contract").replace(/[^a-zA-Z0-9-_]/g, "_");
      // signed/<org_id>/<customer_id>/...: the contracts policies scope on folder [2],
      // so the farm id must sit there or the upload is refused (it was, 2026-09-20 to 09-27).
      if (!orgId) throw new Error("No farm loaded. Refresh and try again.");
      const storagePath = `signed/${orgId}/${viewingCustomer.id}/${safeName}_signed_${Date.now()}.pdf`;
      assertWritable();
      const { error: upErr } = await supabase.storage.from("contracts").upload(storagePath, signedFile, { contentType: "application/pdf", upsert: false });
      if (upErr) throw upErr;
      await dbUpdate("customer_contracts", contractId, { signed_storage_path: storagePath, signed_file_name: signedFile.name, status: "signed", signed_date: localToday(), updated_at: new Date().toISOString() });
      setCustomerContracts(p => p.map(c => c.id === contractId ? { ...c, signed_storage_path: storagePath, signed_file_name: signedFile.name, status: "signed", signed_date: localToday() } : c));
      setShowUploadSigned(null); setSignedFile(null);
      save();
    } catch (e) { alert("Error: " + e.message); }
    setUploadingContract(false);
  };

  const downloadContract = async (storagePath, fileName) => {
    try {
      const { data, error } = await supabase.storage.from("contracts").download(storagePath);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = fileName || "contract.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Error downloading: " + e.message); }
  };

  const updateContractStatus = async (id, status) => {
    try {
      await dbUpdate("customer_contracts", id, { status, updated_at: new Date().toISOString() });
      setCustomerContracts(p => p.map(c => c.id === id ? { ...c, status } : c));
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  // E-signing: take back a link that is out for signature.
  const voidSigningLink = async (cc) => {
    if (!window.confirm(`Cancel the signing link sent to ${cc.sent_to_email || "this customer"}? It will stop working right away.`)) return;
    try {
      await callFunction("contract-send", { action: "void", contract_id: cc.id });
      setCustomerContracts(p => p.map(c => c.id === cc.id ? { ...c, status: "pending", sign_expires_at: null } : c));
    } catch (e) { alert("Error: " + e.message); }
  };
  const fmtShort = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

  const deleteCustomerContract = async (contract) => {
    if (!window.confirm("Remove this contract from the customer?")) return;
    try {
      assertWritable();
      if (contract.signed_storage_path) await supabase.storage.from("contracts").remove([contract.signed_storage_path]);
      await supabase.from("customer_contracts").delete().eq("id", contract.id);
      setCustomerContracts(p => p.filter(c => c.id !== contract.id));
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const sof = k => e => setOrderForm(p => ({ ...p, [k]: e.target.value }));

  // Group available semen by stallion for the order form
  const availableSemen = semen.filter(s => (s.straws_available || 0) > 0);
  const stallionsWithInventory = [...new Set(availableSemen.map(s => s.stallion))].sort();
  const batchesForStallion = semenForm.stallion ? availableSemen.filter(s => s.stallion === semenForm.stallion) : [];
  const selectedBatch = batchesForStallion.find(b => b.id === semenForm.batch_id);
  const pricePerDose = selectedBatch?.price_per_dose || 0;
  const semenTotal = (parseInt(semenForm.qty) || 0) * pricePerDose;

  const addOrder = async () => {
    if (!orderForm.total || !viewingCustomer) return;
    try {
      const row = await dbInsert("orders", { id: crypto.randomUUID(), ...orderForm, customer_name: viewingCustomer.name, total: parseFloat(orderForm.total) || 0, org_id: orgId, date: orderForm.date || null });
      setOrders(p => [row, ...p]);
      setNewOrderMode(false); setOrderForm(emptyOrder);
      save();
    } catch (e) { alert("Error saving order: " + e.message); }
  };

  const addSemenOrder = async () => {
    if (!viewingCustomer || !semenForm.stallion || !semenForm.batch_id || !semenForm.qty) return;
    const qty = parseInt(semenForm.qty) || 0;
    if (!selectedBatch || qty > selectedBatch.straws_available) { alert("Not enough straws available in this batch."); return; }
    // Get stallion details for the email
    const stallionObj = stallions.find(s => s.name === semenForm.stallion);
    try {
      const desc = `${semenForm.semen_type} semen — ${qty} straw${qty > 1 ? "s" : ""} — ${semenForm.stallion}`;
      const orderId = crypto.randomUUID();
      const row = await dbInsert("orders", {
        id: orderId, customer_name: viewingCustomer.name, description: desc,
        total: semenTotal, status: "Processing", date: semenForm.date || todayISO,
        order_type: "semen",
        stallion_name: semenForm.stallion,
        stallion_reg_number: stallionObj?.registration_number || "",
        stallion_owner: stallionObj?.owner || "",
        mare_name: semenForm.mare_name,
        mare_reg_number: semenForm.mare_reg_number,
        semen_type: semenForm.semen_type,
        shipping_method: semenForm.shipping_method,
        tracking_number: semenForm.tracking,
        person_requesting: semenForm.person_requesting,
        shipment_date: semenForm.date || todayISO,
        processed_by: semenForm.processed_by,
        progr_cells: semenForm.progr_cells,
        net_volume: semenForm.net_volume,
        extender_vol: semenForm.extender_vol,
        extender_used: semenForm.extender_used,
        antibiotic_added: semenForm.antibiotic_added,
        centrifuged: semenForm.centrifuged,
        ext_sperm: semenForm.ext_sperm,
        equitainer_number: semenForm.equitainer_number,
        shipment_comments: semenForm.shipment_comments,
        customer_email: viewingCustomer.email || "",
        notes: semenForm.notes,
        org_id: orgId
      });
      setOrders(p => [row, ...p]);

      // Straws leave through the inventory_movements ledger; its trigger
      // recomputes straws_available / straws_used (see CLAUDE.md). Writing the
      // counts directly let the cache and the ledger disagree. Only `status`
      // is written here, because it is not part of the cache.
      const newAvail = (selectedBatch.straws_available || 0) - qty;
      const newUsed = (selectedBatch.straws_used || 0) + qty;
      const newStatus = newAvail <= 0 ? "Depleted" : newAvail <= 5 ? "Low Stock" : "Available";
      await dbInsert("inventory_movements", {
        id: crypto.randomUUID(), org_id: orgId, batch_id: selectedBatch.id, movement_type: "shipment", delta: -qty,
        note: `Semen order for ${viewingCustomer.name}${semenForm.mare_name ? " (" + semenForm.mare_name + ")" : ""}`,
      });
      await dbUpdate("frozen_semen", selectedBatch.id, { status: newStatus });
      setSemen(p => p.map(s => s.id === selectedBatch.id ? { ...s, straws_available: newAvail, straws_used: newUsed, status: newStatus } : s));

      // The shipment notice is no longer sent on save: most orders are placed
      // before the container ships. A tracking number means it has shipped, so
      // offer the notice with a preview. Otherwise it is sent from Orders.
      if (semenForm.tracking && viewingCustomer.email) setNoticeOrderId(orderId);

      setSemenOrderMode(false); setSemenForm(emptySemenOrder);
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const openEdit = (c) => {
    setEditingCustomer(c);
    const sameBilling = !c.shipping_address && !c.shipping_city && !c.shipping_state && !c.shipping_zip;
    setForm({ name: c.name || "", company: c.company || "", email: c.email || "", phone: c.phone || "", address: c.address || "", city: c.city || "", state: c.state || "", zip: c.zip || "", country: c.country || "US", type: c.type || "Active", notes: c.notes || "", shipping_address: c.shipping_address || "", shipping_city: c.shipping_city || "", shipping_state: c.shipping_state || "", shipping_zip: c.shipping_zip || "", shipping_same: sameBilling });
  };

  const prepCustomerForm = (f) => {
    const { shipping_same, ...rest } = f;
    if (shipping_same) { rest.shipping_address = rest.address; rest.shipping_city = rest.city; rest.shipping_state = rest.state; rest.shipping_zip = rest.zip; }
    return rest;
  };

  const addCustomer = async () => {
    if (!form.name.trim()) return;
    try {
      const data = prepCustomerForm(form);
      const row = await dbInsert("customers", { id: crypto.randomUUID(), ...data, org_id: orgId, total_spent: 0 });
      setCustomers(p => [row, ...p]);
      setShowAdd(false); setForm(emptyCustomer);
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const saveEdit = async () => {
    if (!form.name.trim()) return;
    try {
      const data = prepCustomerForm(form);
      await dbUpdate("customers", editingCustomer.id, data);
      setCustomers(p => p.map(c => c.id === editingCustomer.id ? { ...c, ...data } : c));
      setEditingCustomer(null); setForm(emptyCustomer);
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const filtered = customers.filter(c => (c.name + c.company + c.email + c.city).toLowerCase().includes(search.toLowerCase()));
  const totalRevenue = customers.reduce((a, c) => a + (c.total_spent || 0), 0);
  const outstanding = orders.filter(o => o.status === "Pending" || o.status === "Partial").reduce((a, o) => a + (o.total || 0), 0);

  return (<>
    <div className="page-header-row">
      
      <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Customer</button>
    </div>
    <div className="stats-row" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
      <div className="stat-card"><div className="stat-icon">👥</div><div className="stat-label">Total Customers</div><div className="stat-value">{customers.length}</div><div className="stat-meta">{customers.filter(c => c.type === "VIP").length} VIP accounts</div></div>
      <div className="stat-card green"><div className="stat-icon">💰</div><div className="stat-label">Season Revenue</div><div className="stat-value">${(totalRevenue / 1000).toFixed(1)}k</div><div className="stat-meta">All customers</div></div>
      <div className="stat-card red"><div className="stat-icon">⚠️</div><div className="stat-label">Outstanding</div><div className="stat-value">${(outstanding / 1000).toFixed(1)}k</div><div className="stat-meta">Awaiting payment</div></div>
    </div>
    {loading ? <div className="empty-state">Loading customers…</div> : (
      <div className="full-panel">
        <div className="filter-bar">
          <input className="search-input" placeholder="Search customers…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {filtered.length === 0
          ? <div className="empty-state">No customers yet. Click "+ Add Customer" to begin.</div>
          : <div className="customer-grid">
              {filtered.map(c => {
                const initials = (c.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                return (
                  <div className="customer-card" key={c.id}>
                    <div className="customer-card-head" onClick={() => { setViewingCustomer(c); setNewOrderMode(false); setCustDetailTab("details"); }} style={{ cursor: "pointer" }} title="Click to view details & orders">
                      <div className="customer-avatar">{initials}</div>
                      <div style={{ flex: 1 }}>
                        <div className="customer-name">{c.name}</div>
                        {c.company && <div style={{ fontSize: 11.5, color: "var(--text-light)", fontStyle: "italic", marginBottom: 4 }}>{c.company}</div>}
                        <StatusBadge status={c.type} />
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-light)", alignSelf: "flex-start", marginTop: 2 }}>›</div>
                    </div>
                    <div className="customer-body">
                      {(c.city || c.state) && (
                        <div className="customer-row">
                          <span style={{ color: "var(--text-light)" }}>📍 Location</span>
                          <span style={{ fontWeight: 500 }}>{c.city}{c.state ? `, ${c.state}` : ""}</span>
                        </div>
                      )}
                      {c.email && (
                        <div className="customer-row">
                          <span style={{ color: "var(--text-light)" }}>✉ Email</span>
                          <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} title={`Email ${c.name || c.email}`}
                             style={{ color: "var(--blue)", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</a>
                        </div>
                      )}
                      {c.phone && (
                        <div className="customer-row">
                          <span style={{ color: "var(--text-light)" }}>📞 Phone</span>
                          <a href={`tel:${String(c.phone).replace(/[^+\d]/g, "")}`} onClick={e => e.stopPropagation()}
                             title={`Call ${c.name || c.phone}`} style={{ color: "var(--blue)" }}>{c.phone}</a>
                        </div>
                      )}
                      <div className="customer-row" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--parchment-dark)" }}>
                        <span style={{ color: "var(--text-light)" }}>💰 Total Spent</span>
                        <span style={{ fontWeight: 700, color: "var(--forest)" }}>${(c.total_spent || 0).toLocaleString()}</span>
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                        <button className="btn btn-sm" style={{ flex: 1, background: "#e8f0e8", color: "var(--forest)", border: "1px solid #c6d8c6" }} onClick={() => openEdit(c)}>✎ Edit</button>
                        {canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={() => confirmDelete(`Delete ${c.name}? This can't be undone.`, async () => { await dbDelete("customers", c.id); setCustomers(p => p.filter(x => x.id !== c.id)); save(); })}>✕</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        }
      </div>
    )}
    {showAdd && (
      <div className="modal-overlay" onClick={() => setShowAdd(false)}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><div className="modal-title">Add New Customer</div><button className="modal-close" onClick={() => setShowAdd(false)}>✕</button></div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-row"><label className="form-label">Full Name *</label><input className="form-input" value={form.name} onChange={sf("name")} /></div>
              <div className="form-row"><label className="form-label">Company</label><input className="form-input" value={form.company} onChange={sf("company")} /></div>
              <div className="form-row"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={sf("email")} /></div>
              <div className="form-row"><label className="form-label">Phone</label><PhoneInput value={form.phone} onChange={v => setForm(p => ({ ...p, phone: v }))} /></div>
              <div className="form-row"><label className="form-label">Type</label><select className="form-input" value={form.type} onChange={sf("type")}><option>Active</option><option>VIP</option><option>Inactive</option></select></div>
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 6, marginTop: 8 }}>Billing Address</div>
            <div className="form-row"><label className="form-label">Address</label><input className="form-input" value={form.address} onChange={sf("address")} /></div>
            <div className="form-grid-3">
              <div className="form-row"><label className="form-label">City</label><input className="form-input" value={form.city} onChange={sf("city")} /></div>
              <div className="form-row"><label className="form-label">State</label><input className="form-input" value={form.state} onChange={sf("state")} maxLength={2} /></div>
              <div className="form-row"><label className="form-label">ZIP</label><input className="form-input" value={form.zip} onChange={sf("zip")} /></div>
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 6, marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Shipping Address</span>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 500, fontSize: 11, textTransform: "none", letterSpacing: "normal", cursor: "pointer", color: "var(--text-mid)" }}>
                <input type="checkbox" checked={form.shipping_same} onChange={e => { const checked = e.target.checked; setForm(p => ({ ...p, shipping_same: checked, ...(checked ? { shipping_address: "", shipping_city: "", shipping_state: "", shipping_zip: "" } : {}) })); }} style={{ accentColor: "var(--forest)" }} />
                Same as billing
              </label>
            </div>
            {!form.shipping_same && (<>
              <div className="form-row"><label className="form-label">Address</label><input className="form-input" value={form.shipping_address} onChange={sf("shipping_address")} /></div>
              <div className="form-grid-3">
                <div className="form-row"><label className="form-label">City</label><input className="form-input" value={form.shipping_city} onChange={sf("shipping_city")} /></div>
                <div className="form-row"><label className="form-label">State</label><input className="form-input" value={form.shipping_state} onChange={sf("shipping_state")} maxLength={2} /></div>
                <div className="form-row"><label className="form-label">ZIP</label><input className="form-input" value={form.shipping_zip} onChange={sf("shipping_zip")} /></div>
              </div>
            </>)}
            <div className="form-row"><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={sf("notes")} /></div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={addCustomer}>Save Customer</button></div>
        </div>
      </div>
    )}
    {editingCustomer && (
      <div className="modal-overlay" onClick={() => setEditingCustomer(null)}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><div className="modal-title">Edit Customer — {editingCustomer.name}</div><button className="modal-close" onClick={() => setEditingCustomer(null)}>✕</button></div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-row"><label className="form-label">Full Name *</label><input className="form-input" value={form.name} onChange={sf("name")} /></div>
              <div className="form-row"><label className="form-label">Company</label><input className="form-input" value={form.company} onChange={sf("company")} /></div>
              <div className="form-row"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={sf("email")} /></div>
              <div className="form-row"><label className="form-label">Phone</label><PhoneInput value={form.phone} onChange={v => setForm(p => ({ ...p, phone: v }))} /></div>
              <div className="form-row"><label className="form-label">Type</label><select className="form-input" value={form.type} onChange={sf("type")}><option>Active</option><option>VIP</option><option>Inactive</option></select></div>
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 6, marginTop: 8 }}>Billing Address</div>
            <div className="form-row"><label className="form-label">Address</label><input className="form-input" value={form.address} onChange={sf("address")} /></div>
            <div className="form-grid-3">
              <div className="form-row"><label className="form-label">City</label><input className="form-input" value={form.city} onChange={sf("city")} /></div>
              <div className="form-row"><label className="form-label">State</label><input className="form-input" value={form.state} onChange={sf("state")} maxLength={2} /></div>
              <div className="form-row"><label className="form-label">ZIP</label><input className="form-input" value={form.zip} onChange={sf("zip")} /></div>
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 6, marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Shipping Address</span>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 500, fontSize: 11, textTransform: "none", letterSpacing: "normal", cursor: "pointer", color: "var(--text-mid)" }}>
                <input type="checkbox" checked={form.shipping_same} onChange={e => { const checked = e.target.checked; setForm(p => ({ ...p, shipping_same: checked, ...(checked ? { shipping_address: "", shipping_city: "", shipping_state: "", shipping_zip: "" } : {}) })); }} style={{ accentColor: "var(--forest)" }} />
                Same as billing
              </label>
            </div>
            {!form.shipping_same && (<>
              <div className="form-row"><label className="form-label">Address</label><input className="form-input" value={form.shipping_address} onChange={sf("shipping_address")} /></div>
              <div className="form-grid-3">
                <div className="form-row"><label className="form-label">City</label><input className="form-input" value={form.shipping_city} onChange={sf("shipping_city")} /></div>
                <div className="form-row"><label className="form-label">State</label><input className="form-input" value={form.shipping_state} onChange={sf("shipping_state")} maxLength={2} /></div>
                <div className="form-row"><label className="form-label">ZIP</label><input className="form-input" value={form.shipping_zip} onChange={sf("shipping_zip")} /></div>
              </div>
            </>)}
            <div className="form-row"><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={sf("notes")} /></div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={() => setEditingCustomer(null)}>Cancel</button><button className="btn btn-primary" onClick={saveEdit}>Save Changes</button></div>
        </div>
      </div>
    )}
    {viewingCustomer && (
      <div className="modal-overlay" onClick={() => { setViewingCustomer(null); setNewOrderMode(false); setSemenOrderMode(false); }}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
          <div className="modal-header" style={{ alignItems: "flex-start" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div className="customer-avatar" style={{ width: 46, height: 46, fontSize: 18 }}>{(viewingCustomer.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}</div>
              <div>
                <div className="modal-title" style={{ marginBottom: 3 }}>{viewingCustomer.name}</div>
                {viewingCustomer.company && <div style={{ fontSize: 12, color: "var(--text-light)", fontStyle: "italic" }}>{viewingCustomer.company}</div>}
                <div style={{ marginTop: 4 }}><StatusBadge status={viewingCustomer.type} /></div>
              </div>
            </div>
            <button className="modal-close" onClick={() => { setViewingCustomer(null); setNewOrderMode(false); }}>✕</button>
          </div>
          <div className="modal-body">
            {/* Customer detail tabs */}
            {!newOrderMode && !semenOrderMode && (
              <div style={{ display: "flex", gap: 0, borderBottom: "2px solid var(--parchment-dark)", marginBottom: 14 }}>
                {[{ id: "details", label: "Details & Orders" }, { id: "contracts", label: `Contracts (${customerContractsFiltered.length})` }].map(tab => (
                  <button key={tab.id} onClick={() => setCustDetailTab(tab.id)} style={{ padding: "8px 16px", fontSize: 12, fontWeight: 600, border: "none", background: "none", cursor: "pointer", borderBottom: custDetailTab === tab.id ? "2px solid var(--forest)" : "2px solid transparent", marginBottom: -2, color: custDetailTab === tab.id ? "var(--forest)" : "var(--text-light)" }}>{tab.label}</button>
                ))}
              </div>
            )}

            {!newOrderMode && !semenOrderMode && custDetailTab === "contracts" ? (
              /* ── CONTRACTS TAB ── */
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: "var(--text-mid)" }}>Manage contracts assigned to {viewingCustomer.name}</div>
                  <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => { setAssignForm({ template_id: "", stallion_name: "", mare_name: "", notes: "" }); setShowAssignContract(true); }}>+ Assign Contract</button>
                </div>

                {customerContractsFiltered.length === 0 ? (
                  <div className="empty-state" style={{ padding: 24, textAlign: "center" }}>
                    <div style={{ fontSize: 30, marginBottom: 6 }}>📄</div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>No contracts assigned yet</div>
                    <div style={{ fontSize: 12, color: "var(--text-light)", marginBottom: 12 }}>Assign a contract template to this customer and upload the signed copy.</div>
                    <button className="btn" style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" }} onClick={() => { setAssignForm({ template_id: "", stallion_name: "", mare_name: "", notes: "" }); setShowAssignContract(true); }}>+ Assign First Contract</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {customerContractsFiltered.map(cc => {
                      const template = contractTemplates.find(t => t.id === cc.template_id);
                      const statusColors = { pending: { bg: "#fef3c7", color: "#92400e" }, sent: { bg: "#dbeafe", color: "#1e40af" }, signed: { bg: "#dcfce7", color: "#166534" }, expired: { bg: "#fde8e8", color: "#dc2626" }, cancelled: { bg: "#e5e7eb", color: "#6b7280" } };
                      const sc = statusColors[cc.status] || statusColors.pending;
                      return (
                        <div key={cc.id} style={{ border: "1px solid var(--parchment-dark)", borderRadius: 8, padding: 14, background: "white" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--forest)" }}>{cc.contract_name}</div>
                              <div style={{ fontSize: 11, color: "var(--text-light)", marginTop: 2 }}>
                                {cc.stallion_name && <span>🐎 {cc.stallion_name}</span>}
                                {cc.mare_name && <span> · Mare: {cc.mare_name}</span>}
                              </div>
                            </div>
                            <select value={cc.status} onChange={e => updateContractStatus(cc.id, e.target.value)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 10, border: "1px solid " + sc.color + "33", background: sc.bg, color: sc.color, fontWeight: 700, cursor: "pointer" }}>
                              <option value="pending">Pending</option><option value="sent">Sent</option><option value="signed">Signed</option><option value="expired">Expired</option><option value="cancelled">Cancelled</option>
                            </select>
                          </div>

                          <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--text-light)", marginBottom: 8, flexWrap: "wrap" }}>
                            {cc.sent_date && <span>Sent: {cc.sent_date}</span>}
                            {cc.signed_date && <span>Signed: {cc.signed_date}</span>}
                          </div>
                          {cc.status === "sent" && cc.sent_at && cc.sign_expires_at && (
                            <div style={{ fontSize: 11.5, color: "#1e40af", background: "#eff6ff", borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
                              Out for signature to {cc.sent_to_email} since {fmtShort(cc.sent_at)}{cc.viewed_at ? ` · opened ${fmtShort(cc.viewed_at)}` : " · not opened yet"} · link works until {fmtShort(cc.sign_expires_at)}
                            </div>
                          )}
                          {cc.status === "signed" && cc.signed_at && (
                            <div style={{ fontSize: 11.5, color: "#166534", background: "#f0fdf4", borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
                              Signed online by {cc.signer_name}{cc.farm_signer_name ? ` and ${cc.farm_signer_name}` : ""} on {fmtShort(cc.signed_at)}
                            </div>
                          )}

                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {template?.storage_path && (
                              <>
                              <button className="btn btn-sm" style={{ fontSize: 10, padding: "3px 10px" }} onClick={() => setPreviewPdf({ path: template.storage_path, fileName: template.file_name, title: `${cc.contract_name} (template)` })}>👁 View Template</button>
                              <button className="btn btn-sm" style={{ fontSize: 10, padding: "3px 10px" }} onClick={() => downloadContract(template.storage_path, template.file_name)}>📥 Download Template</button>
                              </>
                            )}
                            {!cc.signed_storage_path && template?.storage_path && cc.status !== "cancelled" && (
                              <button className="btn btn-sm" style={{ fontSize: 10, padding: "3px 10px", background: "#1a2e1a", color: "#fff", border: "1px solid #1a2e1a" }} onClick={() => setSendingContract(cc)}>✍️ {cc.status === "sent" && cc.sign_expires_at ? "Resend for signature" : "Send for signature"}</button>
                            )}
                            {cc.status === "sent" && cc.sign_expires_at && (
                              <button className="btn btn-sm" style={{ fontSize: 10, padding: "3px 10px" }} onClick={() => voidSigningLink(cc)}>Cancel link</button>
                            )}
                            {cc.signed_storage_path ? (
                              <>
                              <button className="btn btn-sm" style={{ fontSize: 10, padding: "3px 10px", background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" }} onClick={() => setPreviewPdf({ path: cc.signed_storage_path, fileName: cc.signed_file_name, title: `${cc.contract_name} (signed)` })}>👁 View Signed</button>
                              <button className="btn btn-sm" style={{ fontSize: 10, padding: "3px 10px", background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" }} onClick={() => downloadContract(cc.signed_storage_path, cc.signed_file_name)}>✅ Download Signed</button>
                              </>
                            ) : (
                              <button className="btn btn-sm" style={{ fontSize: 10, padding: "3px 10px", background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" }} onClick={() => { setShowUploadSigned(cc.id); setSignedFile(null); }}>📤 Upload Signed Copy</button>
                            )}
                            <button className="btn btn-sm" style={{ fontSize: 10, padding: "3px 10px", background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={() => deleteCustomerContract(cc)}>✕</button>
                          </div>

                          {cc.notes && <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-mid)", fontStyle: "italic" }}>{cc.notes}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : !newOrderMode && !semenOrderMode ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                {/* Left: Contact details */}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 10 }}>Contact Info</div>
                  {viewingCustomer.email && (
                    <div style={{ fontSize: 13, marginBottom: 7, display: "flex", gap: 8, alignItems: "center" }}>
                      <span>✉</span>
                      <a href={`mailto:${viewingCustomer.email}`} style={{ color: "var(--blue)" }}>{viewingCustomer.email}</a>
                      <button className="btn btn-sm"
                              title="Compose here — sends from your farm's connected mailbox and is saved to your Email Log"
                              onClick={() => setComposeTo({ email: viewingCustomer.email, name: viewingCustomer.name })}
                              style={{ fontSize: 10, padding: "2px 9px", background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" }}>
                        Write
                      </button>
                    </div>
                  )}
                  {viewingCustomer.phone && <div style={{ fontSize: 13, marginBottom: 7, display: "flex", gap: 8 }}><span>📞</span><a href={`tel:${String(viewingCustomer.phone).replace(/[^+\d]/g, "")}`} style={{ color: "var(--blue)" }}>{viewingCustomer.phone}</a></div>}
                  {(viewingCustomer.address || viewingCustomer.city) && (
                    <div style={{ fontSize: 13, marginBottom: 3, display: "flex", gap: 8 }}>
                      <span>📍</span>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 2 }}>Billing</div>
                        <span>{[viewingCustomer.address, viewingCustomer.city && `${viewingCustomer.city}${viewingCustomer.state ? `, ${viewingCustomer.state}` : ""}`, viewingCustomer.zip].filter(Boolean).join(" · ")}</span>
                      </div>
                    </div>
                  )}
                  {(viewingCustomer.shipping_address || viewingCustomer.shipping_city) && (
                    <div style={{ fontSize: 13, marginBottom: 7, display: "flex", gap: 8 }}>
                      <span>📦</span>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 2 }}>Shipping</div>
                        <span>{[viewingCustomer.shipping_address, viewingCustomer.shipping_city && `${viewingCustomer.shipping_city}${viewingCustomer.shipping_state ? `, ${viewingCustomer.shipping_state}` : ""}`, viewingCustomer.shipping_zip].filter(Boolean).join(" · ")}</span>
                      </div>
                    </div>
                  )}
                  {viewingCustomer.notes && (
                    <div style={{ marginTop: 10, padding: "10px 12px", background: "var(--parchment)", borderRadius: 6, fontSize: 12.5, fontStyle: "italic", color: "var(--text-mid)", lineHeight: 1.5 }}>
                      {viewingCustomer.notes}
                    </div>
                  )}
                  <div style={{ marginTop: 14, padding: "12px 16px", background: "linear-gradient(135deg,var(--forest),#1a3a1a)", borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(201,168,76,.6)", marginBottom: 4 }}>Lifetime Spent</div>
                    <div style={{ fontFamily: "Playfair Display,serif", fontSize: 26, color: "var(--gold-light)", fontWeight: 700 }}>${(viewingCustomer.total_spent || 0).toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: "rgba(201,168,76,.5)", marginTop: 2 }}>{orders.filter(o => o.customer_name === viewingCustomer.name).length} orders total</div>
                  </div>
                </div>
                {/* Right: Order history */}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 10 }}>Order History</div>
                  {orders.filter(o => o.customer_name === viewingCustomer.name).length === 0
                    ? <div style={{ fontSize: 13, color: "var(--text-light)", fontStyle: "italic", padding: "14px 0" }}>No orders yet — start one below.</div>
                    : <div style={{ maxHeight: 220, overflowY: "auto" }}>
                        {orders.filter(o => o.customer_name === viewingCustomer.name).map(o => (
                          <div key={o.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid var(--parchment-dark)" }}>
                            <div>
                              <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--forest)" }}>${(o.total || 0).toLocaleString()}</div>
                              {o.description && <div style={{ fontSize: 11.5, color: "var(--text-mid)", marginTop: 1 }}>{o.description}</div>}
                              <div style={{ fontSize: 11, color: "var(--text-light)" }}>{o.date || "—"}</div>
                            </div>
                            <StatusBadge status={o.status} />
                          </div>
                        ))}
                      </div>
                  }
                </div>
              </div>
            ) : semenOrderMode ? (
              /* ── SEMEN ORDER FORM ── */
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 20 }}>🧊</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--forest)" }}>New Semen Order</div>
                    <div style={{ fontSize: 12, color: "var(--text-light)" }}>for {viewingCustomer.name}</div>
                  </div>
                </div>

                {/* Stallion selection */}
                <div className="form-row" style={{ marginBottom: 12 }}>
                  <label className="form-label">Stallion *</label>
                  <select className="form-input" value={semenForm.stallion} onChange={e => { setSemenForm(p => ({ ...p, stallion: e.target.value, batch_id: "" })); }}>
                    <option value="">— Select Stallion —</option>
                    {stallionsWithInventory.map(s => {
                      const totalAvail = availableSemen.filter(b => b.stallion === s).reduce((a, b) => a + (b.straws_available || 0), 0);
                      return <option key={s} value={s}>{s} — {totalAvail} straws available</option>;
                    })}
                  </select>
                </div>

                {/* Batch selection (shows after stallion picked) */}
                {semenForm.stallion && (
                  <div style={{ background: "linear-gradient(135deg,#0d1f2d,#0a2840)", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#4fc3f7", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>Select Batch</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {batchesForStallion.map(b => (
                        <label key={b.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 6, cursor: "pointer",
                          background: semenForm.batch_id === b.id ? "rgba(79,195,247,.15)" : "rgba(255,255,255,.04)",
                          border: semenForm.batch_id === b.id ? "1px solid rgba(79,195,247,.4)" : "1px solid rgba(255,255,255,.08)" }}>
                          <input type="radio" name="semen-batch" checked={semenForm.batch_id === b.id}
                            onChange={() => setSemenForm(p => ({ ...p, batch_id: b.id }))}
                            style={{ accentColor: "#4fc3f7" }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12.5, color: "#fff", fontWeight: 600 }}>
                              {b.tank}/{b.canister} — <span style={{ color: "#4fc3f7" }}>{b.straws_available} straws</span>
                            </div>
                            <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)" }}>
                              Collected: {b.collection_date || "—"} · Post-thaw: {b.motility_post || "—"}% · {b.price_per_dose ? `$${b.price_per_dose.toLocaleString()}/dose` : "No price set"}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quantity & pricing */}
                {semenForm.batch_id && (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
                      <div className="form-row">
                        <label className="form-label"># Straws *</label>
                        <input className="form-input" type="number" min="1" max={selectedBatch?.straws_available || 99}
                          value={semenForm.qty} onChange={ssf("qty")} />
                        <div style={{ fontSize: 10, color: "var(--text-light)", marginTop: 2 }}>Max: {selectedBatch?.straws_available || 0}</div>
                      </div>
                      <div className="form-row">
                        <label className="form-label">Price/Dose</label>
                        <div className="form-input" style={{ background: "var(--parchment)", fontWeight: 700, color: "var(--forest)" }}>
                          {pricePerDose ? `$${pricePerDose.toLocaleString()}` : "—"}
                        </div>
                      </div>
                      <div className="form-row">
                        <label className="form-label">Order Total</label>
                        <div style={{ fontFamily: "Playfair Display,serif", fontSize: 22, fontWeight: 700, color: "var(--forest)", padding: "6px 0" }}>
                          ${semenTotal.toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {/* SHIPMENT REQUEST INFO */}
                    <div style={{ background: "#f4f7f5", borderRadius: 10, padding: "12px 14px", marginBottom: 14, border: "1px solid #dde6df" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--forest)", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>📋 Shipment Request Info</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                        <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Mare Name</label><input className="form-input" value={semenForm.mare_name} onChange={ssf("mare_name")} placeholder="Shipped to (Mare)" style={{ fontSize: 12 }} /></div>
                        <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Mare Reg. #</label><input className="form-input" value={semenForm.mare_reg_number} onChange={ssf("mare_reg_number")} style={{ fontSize: 12 }} /></div>
                        <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Person Requesting</label>
                          <PersonSelect value={semenForm.person_requesting} options={teamNames} style={{ fontSize: 12 }} onChange={v => setSemenForm(p => ({ ...p, person_requesting: v }))} />
                        </div>
                        <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Semen Type</label>
                          <select className="form-input" value={semenForm.semen_type} onChange={ssf("semen_type")} style={{ fontSize: 12 }}>
                            <option>Frozen</option><option>Cooled</option><option>Fresh</option>
                          </select>
                        </div>
                        <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Ship Method</label>
                          <select className="form-input" value={semenForm.shipping_method} onChange={ssf("shipping_method")} style={{ fontSize: 12 }}>
                            <option>FedEx Priority</option><option>FedEx Standard</option><option>UPS Next Day</option><option>Pickup</option><option>Counter-to-Counter</option><option>Other</option>
                          </select>
                        </div>
                        <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>📅 Shipment Date</label><input className="form-input" type="date" value={semenForm.date} onChange={ssf("date")} style={{ fontSize: 12 }} /></div>
                      </div>
                      <div className="form-row" style={{ marginTop: 6 }}><label className="form-label" style={{ fontSize: 10 }}>Tracking #</label><input className="form-input" value={semenForm.tracking} onChange={ssf("tracking")} placeholder="Optional" style={{ fontSize: 12 }} /></div>
                    </div>

                    {/* SHIPPED SEMEN INFO */}
                    <div style={{ background: "linear-gradient(135deg,#0d1f2d,#0a2840)", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#4fc3f7", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>🧪 Shipped Semen Info</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 10px" }}>
                        <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Processed By</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={semenForm.processed_by} onChange={ssf("processed_by")} /></div>
                        <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}># Progr. Cells (billion)</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={semenForm.progr_cells} onChange={ssf("progr_cells")} /></div>
                        <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Net Volume (ml)</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={semenForm.net_volume} onChange={ssf("net_volume")} /></div>
                        <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Extender Vol.</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={semenForm.extender_vol} onChange={ssf("extender_vol")} /></div>
                        <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Extender Used</label>
                          <select className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={semenForm.extender_used} onChange={ssf("extender_used")}>
                            <option value="">— Select —</option><option>INRA</option><option>INRA 96</option><option>BotuSemen</option><option>EquiPro</option><option>EZ-Mixin</option><option>Other</option>
                          </select>
                        </div>
                        <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Antibiotic Added</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={semenForm.antibiotic_added} onChange={ssf("antibiotic_added")} /></div>
                        <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Centrifuged</label>
                          <select className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={semenForm.centrifuged} onChange={ssf("centrifuged")}>
                            <option>No</option><option>Yes</option>
                          </select>
                        </div>
                        <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Ext. Sperm</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={semenForm.ext_sperm} onChange={ssf("ext_sperm")} /></div>
                        <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Equitainer #</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={semenForm.equitainer_number} onChange={ssf("equitainer_number")} /></div>
                      </div>
                      <div className="form-row" style={{ marginTop: 8 }}><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Comments</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={semenForm.shipment_comments} onChange={ssf("shipment_comments")} /></div>
                    </div>

                    <div className="form-row">
                      <label className="form-label">Notes</label>
                      <textarea className="form-input" rows={2} value={semenForm.notes} onChange={ssf("notes")} placeholder="Special instructions…" />
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* ── GENERIC ORDER FORM ── */
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 14 }}>New Order for {viewingCustomer.name}</div>
                <div className="form-grid">
                  <div className="form-row"><label className="form-label">Description / Item</label><input className="form-input" value={orderForm.description} onChange={sof("description")} placeholder="e.g. Consultation fee, Boarding, etc." /></div>
                  <div className="form-row"><label className="form-label">Total Amount ($) *</label><input className="form-input" type="number" value={orderForm.total} onChange={sof("total")} placeholder="0.00" /></div>
                  <div className="form-row"><label className="form-label">Status</label><select className="form-input" value={orderForm.status} onChange={sof("status")}><option>Pending</option><option>Processing</option><option>Partial</option><option>Paid</option><option>Cancelled</option></select></div>
                  <div className="form-row"><label className="form-label">📅 Order Date</label><input className="form-input" type="date" value={orderForm.date} onChange={sof("date")} /></div>
                </div>
                <div className="form-row"><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={orderForm.notes} onChange={sof("notes")} /></div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {!newOrderMode && !semenOrderMode ? (<>
              <button className="btn btn-outline" onClick={() => { setViewingCustomer(null); }}>Close</button>
              <button className="btn btn-outline" onClick={() => { const c = viewingCustomer; setViewingCustomer(null); openEdit(c); }}>✎ Edit</button>
              <button className="btn btn-outline" onClick={() => { setNewOrderMode(true); setOrderForm({ ...emptyOrder, date: todayISO }); }}>+ Other Order</button>
              <button className="btn btn-primary" onClick={() => { setSemenOrderMode(true); setSemenForm({ ...emptySemenOrder, date: todayISO }); }} style={{ display: "flex", alignItems: "center", gap: 6 }}>🧊 New Semen Order</button>
            </>) : semenOrderMode ? (<>
              <button className="btn btn-outline" onClick={() => setSemenOrderMode(false)}>← Back</button>
              <button className="btn btn-primary" onClick={addSemenOrder} disabled={!semenForm.stallion || !semenForm.batch_id}>Place Semen Order</button>
            </>) : (<>
              <button className="btn btn-outline" onClick={() => setNewOrderMode(false)}>← Back</button>
              <button className="btn btn-primary" onClick={addOrder}>Save Order</button>
            </>)}
          </div>
        </div>
      </div>
    )}
    {previewPdf && (
      <Suspense fallback={null}>
        <PdfPreviewModal {...previewPdf} onClose={() => setPreviewPdf(null)} />
      </Suspense>
    )}

    {/* E-signing: Send for signature */}
    {sendingContract && viewingCustomer && (
      <Suspense fallback={null}>
        <SendForSignatureModal
          contract={sendingContract}
          template={contractTemplates.find(t => t.id === sendingContract.template_id)}
          customer={viewingCustomer}
          orgId={orgId}
          onClose={() => setSendingContract(null)}
          onSent={(patch) => setCustomerContracts(p => p.map(c => c.id === sendingContract.id ? { ...c, ...patch } : c))}
        />
      </Suspense>
    )}

    {/* Assign Contract Modal */}
    {showAssignContract && viewingCustomer && (
      <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setShowAssignContract(false)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
          <div className="modal-header"><div className="modal-title">Assign Contract to {viewingCustomer.name}</div><button className="modal-close" onClick={() => setShowAssignContract(false)}>✕</button></div>
          <div className="modal-body">
            <div className="form-row"><label className="form-label">Contract Template *</label>
              <select className="form-input" value={assignForm.template_id} onChange={e => { const t = activeTemplates.find(x => x.id === e.target.value); setAssignForm(p => ({ ...p, template_id: e.target.value, stallion_name: t?.stallion_name || "" })); }}>
                <option value="">— Select Template —</option>
                {activeTemplates.map(t => <option key={t.id} value={t.id}>{t.name}{t.stallion_name ? ` (${t.stallion_name})` : ""}{t.season ? ` — ${t.season}` : ""}</option>)}
              </select>
            </div>
            {activeTemplates.length === 0 && (
              <div style={{ padding: 12, background: "#fef3c7", borderRadius: 8, fontSize: 12, color: "#92400e", marginBottom: 8 }}>No contract templates found. Go to Settings → Contract Templates to upload your first template.</div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="form-row"><label className="form-label">Stallion</label><input className="form-input" value={assignForm.stallion_name} onChange={e => setAssignForm(p => ({ ...p, stallion_name: e.target.value }))} placeholder="e.g. Golden Coins" /></div>
              <div className="form-row"><label className="form-label">Mare Name</label><input className="form-input" value={assignForm.mare_name} onChange={e => setAssignForm(p => ({ ...p, mare_name: e.target.value }))} placeholder="e.g. Summer Breeze" /></div>
            </div>
            <div className="form-row"><label className="form-label">Notes (optional)</label><input className="form-input" value={assignForm.notes} onChange={e => setAssignForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes about this contract..." /></div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={() => setShowAssignContract(false)}>Cancel</button><button className="btn btn-primary" onClick={assignContract} disabled={uploadingContract}>{uploadingContract ? "Assigning..." : "Assign Contract"}</button></div>
        </div>
      </div>
    )}

    {/* Upload Signed Copy Modal */}
    {showUploadSigned && (
      <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => { setShowUploadSigned(null); setSignedFile(null); }}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
          <div className="modal-header"><div className="modal-title">Upload Signed Contract</div><button className="modal-close" onClick={() => { setShowUploadSigned(null); setSignedFile(null); }}>✕</button></div>
          <div className="modal-body">
            <div className="form-row">
              <label className="form-label">Signed PDF *</label>
              <div style={{ border: "2px dashed var(--parchment-dark)", borderRadius: 8, padding: 20, textAlign: "center", background: "var(--parchment)", cursor: "pointer" }} onClick={() => document.getElementById("signed-file-input").click()}>
                <input id="signed-file-input" type="file" accept=".pdf,application/pdf" style={{ display: "none" }} onChange={e => setSignedFile(e.target.files[0] || null)} />
                {signedFile ? (
                  <div><span style={{ fontSize: 24 }}>📄</span><div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{signedFile.name}</div><div style={{ fontSize: 11, color: "var(--text-light)" }}>{(signedFile.size / 1024).toFixed(0)} KB</div></div>
                ) : (
                  <div><span style={{ fontSize: 24 }}>📤</span><div style={{ fontSize: 13, color: "var(--text-mid)", marginTop: 4 }}>Click to select the signed PDF</div></div>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={() => { setShowUploadSigned(null); setSignedFile(null); }}>Cancel</button><button className="btn btn-primary" onClick={() => uploadSignedContract(showUploadSigned)} disabled={!signedFile || uploadingContract}>{uploadingContract ? "Uploading..." : "Upload Signed Copy"}</button></div>
        </div>
      </div>
    )}

    {composeTo && (
      <EmailComposer to={composeTo.email} name={composeTo.name} onClose={() => setComposeTo(null)} />
    )}

    {noticeOrderId && (
      <ShipmentNoticeModal orderId={noticeOrderId} onClose={() => setNoticeOrderId(null)}
        onSent={(sentAt, to) => { setOrders(p => p.map(o => o.id === noticeOrderId ? { ...o, notice_sent_at: sentAt } : o)); setNoticeToast(to || "customer"); setTimeout(() => setNoticeToast(""), 3500); }}
        onOrderUpdated={u => setOrders(p => p.map(o => o.id === noticeOrderId ? { ...o, ...u } : o))} />
    )}

    <SaveToast show={saved} />
    <SaveToast show={!!noticeToast} text={`Shipment notice sent to ${noticeToast}`} />
  </>);
}

// ─── FROZEN SEMEN ─────────────────────────────────────────────────────────────
// Export destinations offered on both storage locations. One list: Location 1
// used to offer Australia/Argentina/Brazil and Location 2 EU/Brazil/Other, so a
// farm could not mark straws in their main tank as EU-eligible.
const SEMEN_EXPORT_REGIONS = ["Australia", "Argentina", "Brazil", "European Union", "Other"];

// frozen_semen has no batch_code column. The code lives in details.batch_code;
// rows from before 2026-09-25 were backfilled in collection order.
const semenBatchCode = (b) => b?.details?.batch_code || b?.batch_code || "—";

// Straws ÷ straws-per-dose, to 3 decimals. Farms price and ship by the dose,
// and 100 straws at 8 per dose is 12.500 doses, not 12.
const semenDoses = (straws, perDose) => {
  const st = parseFloat(straws) || 0, pd = parseFloat(perDose) || 8;
  return (Math.round((st / pd) * 1000) / 1000).toFixed(3);
};

function FrozenSemen({ orgId }) {
  const { data: semen, setData: setSemen, loading } = useSupabaseTable("frozen_semen", orgId);
  const teamNames = useTeamNames(orgId);
  const { data: stallions } = useSupabaseTable("stallions", orgId);
  // Storage tanks. `containers` existed since the inventory-ledger migration but
  // nothing used it; the Tank dropdown was hardcoded to Tank 1-3.
  const { data: containers, setData: setContainers } = useSupabaseTable("containers", orgId);
  const [addingTankFor, setAddingTankFor] = useState(null); // "tank" | "tank2" | null
  const [newTankName, setNewTankName] = useState("");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [viewingBatch, setViewingBatch] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [saved, setSaved] = useState(false);
  const todayISO = localToday();
  const emptyFSForm = {
    stallion: "", breed: "", registration: "", technician: "", semen_owner: "",
    collection_date: todayISO, time_of_collection: "", collector: "", mounts: "", days_rest: "",
    freeze_extender_1: "", freeze_ext_1_lot: "", freeze_ext_1_exp: "", freeze_extender_2: "", freeze_ext_2_lot: "", freeze_ext_2_exp: "", freeze_extender_3: "", freeze_ext_3_lot: "", freeze_ext_3_exp: "",
    penicillin_level: "1.2", amikacin_level: "0.5",
    cent_extender: "", cent_lot: "", cent_exp: "",
    gf_volume: "", conc_nc: "", total_sperm: "",
    mot_total_init: "", mot_total_post_cent: "", mot_total_post_thaw: "",
    mot_prog_init: "", mot_prog_post_cent: "", mot_prog_post_thaw: "",
    mot_vel_init: "", mot_vel_post_cent: "", mot_vel_post_thaw: "",
    morphology: "",
    pt_read_tech: "", pt_date: "", culture_results: "",
    vol_initial_resuspension: "", desired_concentration: "250", resuspended_concentration: "", nucleocounter_count: "", pt_conc: "",
    final_volume: "", final_concentration: "200",
    straws: "", straws_per_dose: "8", doses: "", straw_code: "", straw_color: "",
    tank: "Tank 1", canister: "1", section: "", loc1_straws: "",
    loc1_export: [],
    loc1_domestic_only: false,
    tank2: "", canister2: "", section2: "", loc2_straws: "",
    loc2_export: [],
    loc2_domestic_only: false,
    notes: ""
  };
  const [form, setForm] = useState(emptyFSForm);
  const [otherStallionFS, setOtherStallionFS] = useState("");

  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  // The farm's tanks: every storage container not retired, plus any tank name
  // already on a batch (existing records predate the containers table).
  const tankOptions = [...new Set([
    ...containers.filter(c => c.container_type === "storage" && c.status !== "retired").map(c => c.name),
    ...semen.map(b => b.tank), ...semen.map(b => b.details?.tank2),
  ].filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  if (!tankOptions.length) tankOptions.push("Tank 1");
  const canisterOptions = [...new Set(["1", "2", "3", "4", "5", "6",
    ...semen.map(b => b.canister), ...semen.map(b => b.details?.canister2)].filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  const pickTank = key => e => {
    if (e.target.value === "__add") { setAddingTankFor(key); setNewTankName(""); return; }
    setForm(p => ({ ...p, [key]: e.target.value }));
  };
  const addTank = async () => {
    const name = newTankName.trim();
    if (!name) return;
    const key = addingTankFor;
    try {
      if (!tankOptions.some(t => t.toLowerCase() === name.toLowerCase())) {
        const row = await dbInsert("containers", {
          id: crypto.randomUUID(), org_id: orgId, name,
          container_type: "storage", status: "in_storage",
        });
        setContainers(p => [...p, row]);
      }
      setForm(p => ({ ...p, [key]: name }));
      setAddingTankFor(null); setNewTankName("");
    } catch (e) { alert("Could not add that tank: " + e.message); }
  };
  const tankPicker = (key, allowBlank) => addingTankFor === key ? (
    <div style={{ display: "flex", gap: 4 }}>
      <input className="form-input" autoFocus placeholder="New tank name" value={newTankName}
        onChange={e => setNewTankName(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTank(); } if (e.key === "Escape") setAddingTankFor(null); }}
        style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} />
      <button type="button" className="btn btn-sm" onClick={addTank} style={{ background: "#4fc3f7", color: "#0a2840", fontWeight: 700 }}>Add</button>
      <button type="button" className="btn btn-sm" onClick={() => setAddingTankFor(null)} style={{ background: "transparent", color: "rgba(255,255,255,.6)" }}>✕</button>
    </div>
  ) : (
    <select className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={form[key]} onChange={pickTank(key)}>
      {allowBlank && <option value="">—</option>}
      {[...new Set([...tankOptions, form[key]].filter(Boolean))].map(t => <option key={t} value={t}>{t}</option>)}
      <option value="__add">＋ Add a new tank…</option>
    </select>
  );

  const addBatch = async () => {
    const stallionName = form.stallion === "__other" ? otherStallionFS.trim() : form.stallion;
    if (!stallionName || !form.straws) { alert("Please choose the stallion and enter the total number of straws."); return; }
    // Next code after the highest already used. Not semen.length + 1, which
    // repeats a code as soon as any batch has been deleted.
    const maxCode = semen.reduce((m, b) => Math.max(m, parseInt(String(semenBatchCode(b)).replace(/\D/g, "")) || 0), 0);
    const batch_code = "FS-" + String(maxCode + 1).padStart(3, "0");
    const details = {
      batch_code,
      breed: form.breed, registration: form.registration, age: form.age, technician: form.technician,
      semen_owner: form.semen_owner, stallion_agent: form.stallion_agent,
      time_of_collection: form.time_of_collection, mounts: form.mounts, days_rest: form.days_rest, room_temp: form.room_temp,
      freeze_extender_1: form.freeze_extender_1, freeze_ext_1_exp: form.freeze_ext_1_exp,
      freeze_extender_2: form.freeze_extender_2, freeze_ext_2_exp: form.freeze_ext_2_exp,
      freeze_extender_3: form.freeze_extender_3, freeze_ext_3_exp: form.freeze_ext_3_exp,
      penicillin_level: form.penicillin_level, amikacin_level: form.amikacin_level,
      cent_extender: form.cent_extender, cent_lot: form.cent_lot, cent_exp: form.cent_exp,
      gf_volume: form.gf_volume, conc_nc: form.conc_nc, total_sperm: form.total_sperm,
      mot_total_init: form.mot_total_init, mot_total_post_cent: form.mot_total_post_cent, mot_total_post_thaw: form.mot_total_post_thaw,
      mot_prog_init: form.mot_prog_init, mot_prog_post_cent: form.mot_prog_post_cent, mot_prog_post_thaw: form.mot_prog_post_thaw,
      mot_vel_init: form.mot_vel_init, mot_vel_post_cent: form.mot_vel_post_cent, mot_vel_post_thaw: form.mot_vel_post_thaw,
      thaw_tech: form.thaw_tech, pt_read_tech: form.pt_read_tech, pt_date: form.pt_date, culture_results: form.culture_results,
      vol_initial_resuspension: form.vol_initial_resuspension, proc_concentration: form.proc_concentration,
      nucleocounter_count: form.nucleocounter_count, pt_conc: form.pt_conc,
      final_volume: form.final_volume, final_concentration: form.final_concentration,
      desired_concentration: form.desired_concentration, resuspended_concentration: form.resuspended_concentration,
      straws_per_dose: form.straws_per_dose, doses: semenDoses(form.straws, form.straws_per_dose), straw_code: form.straw_code, straw_color: form.straw_color,
      loc1_straws: form.loc1_straws, loc1_export: form.loc1_export, loc1_domestic_only: form.loc1_domestic_only,
      tank2: form.tank2, canister2: form.canister2, section2: form.section2, loc2_straws: form.loc2_straws, loc2_export: form.loc2_export, loc2_domestic_only: form.loc2_domestic_only,
    };
    try {
      const row = await dbInsert("frozen_semen", {
        id: crypto.randomUUID(), org_id: orgId,
        stallion: stallionName,
        collector: form.collector,
        collection_date: form.collection_date,
        straws: parseInt(form.straws) || 0,
        straws_available: parseInt(form.straws) || 0,
        straws_used: 0,
        tank: form.tank, canister: form.canister, goblet: form.section.trim() || null,
        concentration: form.conc_nc || "",
        motility_pre: parseInt(form.mot_total_init) || null,
        motility_post: parseInt(form.mot_total_post_thaw) || null,
        morphology: parseInt(form.morphology) || null,
        volume: parseInt(form.gf_volume) || null,
        extender: form.freeze_extender_1 || "",
        notes: form.notes,
        details: details,
        status: "Available",
      });
      // Straw counts are a cache of the inventory_movements ledger (see
      // CLAUDE.md). Record the collection there too; the trigger recomputes to
      // the same number. A failure here leaves the cache correct, so log it
      // rather than tell the farm their save failed.
      try {
        await dbInsert("inventory_movements", {
          id: crypto.randomUUID(), org_id: orgId, batch_id: row.id,
          movement_type: "collection", delta: parseInt(form.straws) || 0, note: "Logged collection " + batch_code,
        });
      } catch (e) { console.error("[FrozenSemen] ledger entry failed for", row.id, e); }
      setSemen(p => [row, ...p]);
      setShowAdd(false);
      setForm({ ...emptyFSForm, collection_date: localToday() });
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  // Batch edit. Straws Available is a cache of inventory_movements (CLAUDE.md):
  // a changed count is recorded as an 'adjustment' movement and the trigger
  // settles the column. The column is never written directly.
  const saveBatchEdit = async () => {
    const b = editingBatch;
    const before = semen.find(x => x.id === b.id) || viewingBatch;
    const num = v => { const n = parseInt(v); return isNaN(n) ? null : n; };
    const upd = {
      stallion: (b.stallion || "").trim() || before.stallion,
      collection_date: b.collection_date || null,
      collector: b.collector || null,
      tank: b.tank || null, canister: b.canister || null, goblet: (b.goblet || "").trim() || null,
      extender: b.extender || "",
      motility_pre: num(b.motility_pre), motility_post: num(b.motility_post), morphology: num(b.morphology),
      concentration: b.concentration || "", status: b.status || "", notes: b.notes || "", details: { ...b.details },
    };
    try {
      await dbUpdate("frozen_semen", b.id, upd);
      const current = before.straws_available || 0;
      const target = Math.max(parseInt(b.straws_available), 0);
      let avail = current;
      if (!isNaN(target) && target !== current) {
        await dbInsert("inventory_movements", {
          id: crypto.randomUUID(), org_id: orgId, batch_id: b.id, movement_type: "adjustment",
          delta: target - current, note: `Count corrected from ${current} to ${target} in batch edit`,
        });
        avail = Math.max(target, 0);
      }
      const next = { ...before, ...upd, straws_available: avail };
      setSemen(p => p.map(x => x.id === b.id ? next : x));
      setViewingBatch(next); setEditingBatch(null); save();
    } catch (e) { alert("Error saving: " + e.message); }
  };

  const totalStraws = semen.reduce((a, s) => a + (s.straws_available || 0), 0);
  const totalValue = semen.reduce((a, s) => a + ((s.straws_available || 0) * (s.price_per_dose || 0)), 0);
  const filtered = semen.filter(s => (s.stallion + s.id + s.tank).toLowerCase().includes(search.toLowerCase()));

  return (<>
    <div className="page-header-row">
      
      <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Log Collection</button>
    </div>
    <div className="stats-row">
      <div className="stat-card blue"><div className="stat-icon">🧊</div><div className="stat-label">Straws Available</div><div className="stat-value">{totalStraws}</div><div className="stat-meta">{semen.filter(s => s.straws_available > 0).length} active batches</div></div>
      <div className="stat-card"><div className="stat-icon">💰</div><div className="stat-label">Inventory Value</div><div className="stat-value">${(totalValue / 1000).toFixed(1)}k</div><div className="stat-meta">At current pricing</div></div>
      <div className="stat-card red"><div className="stat-icon">⚠️</div><div className="stat-label">Low / Depleted</div><div className="stat-value">{semen.filter(s => s.status === "Low Stock" || s.status === "Depleted").length}</div><div className="stat-meta">Need attention</div></div>
      <div className="stat-card green"><div className="stat-icon">🐎</div><div className="stat-label">Stallions</div><div className="stat-value">{[...new Set(semen.map(s => s.stallion))].length}</div><div className="stat-meta">With inventory</div></div>
    </div>
    {loading ? <div className="empty-state">Loading inventory…</div> : (
      <div className="full-panel">
        <div className="filter-bar">
          <input className="search-input" placeholder="Search by stallion, batch ID, tank…" value={search} onChange={e => setSearch(e.target.value)} />
          <span style={{ fontSize: 12, color: "var(--text-light)", marginLeft: "auto" }}>{filtered.length} batches</span>
        </div>
        <table className="data-table">
          <thead><tr><th>Batch ID</th><th>Stallion</th><th>Collected</th><th>Location</th><th>Straws</th><th>Available</th><th>Post-Thaw</th><th>Price/Dose</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {filtered.map((s, idx) => (
              <tr key={s.id} onClick={() => setViewingBatch(s)} style={{ cursor: "pointer" }}>
                <td style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: "#0288d1" }}>{semenBatchCode(s)}</td>
                <td style={{ fontWeight: 700, color: "var(--forest)" }}>{s.stallion}</td>
                <td>{s.collection_date}</td>
                <td style={{ fontSize: 12, fontFamily: "monospace", color: "#0277bd" }}>{[s.tank, s.canister, s.goblet].filter(Boolean).join(" / ")}</td>
                <td style={{ textAlign: "center", fontWeight: 600 }}>{s.straws}</td>
                <td style={{ fontWeight: 700, color: (s.straws_available || 0) > 5 ? "var(--blue)" : "var(--red)" }}>{s.straws_available}</td>
                <td><span style={{ fontWeight: 700, color: (s.motility_post || 0) >= 60 ? "var(--teal)" : "var(--red)" }}>{s.motility_post || "—"}%</span></td>
                <td>{s.price_per_dose ? `$${s.price_per_dose.toLocaleString()}` : "—"}</td>
                <td><StatusBadge status={s.status} /></td>
                <td>{canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={() => confirmDelete(`Delete the ${s.stallion || ""} collection from ${s.collection_date || "this date"}? Its straw history is deleted with it. This can't be undone.`, async () => { await dbDelete("frozen_semen", s.id); setSemen(p => p.filter(x => x.id !== s.id)); save(); })}>✕</button>}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="empty-state">No batches found. Click "+ Log Collection" to add your first.</td></tr>}
          </tbody>
        </table>
      </div>
    )}
    {viewingBatch && (
      <div className="modal-overlay" onClick={() => { setViewingBatch(null); setEditingBatch(null); }}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, width: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
          <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--parchment-dark)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--forest)" }}>{semenBatchCode(viewingBatch)} — {viewingBatch.stallion}</div>
              <div style={{ fontSize: 12, color: "var(--text-light)", marginTop: 4 }}>Collection: {viewingBatch.collection_date}</div>
            </div>
            <button className="btn btn-text" onClick={() => { setViewingBatch(null); setEditingBatch(null); }}>✕</button>
          </div>
          <div style={{ padding: "20px 24px" }}>
            {!editingBatch ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                  <div><div style={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Stallion</div><div style={{ fontSize: 14, fontWeight: 600 }}>{viewingBatch.stallion}</div></div>
                  <div><div style={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Collection Date</div><div style={{ fontSize: 14, fontWeight: 600 }}>{viewingBatch.collection_date}</div></div>
                  <div><div style={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Straws Available</div><div style={{ fontSize: 14, fontWeight: 600, color: (viewingBatch.straws_available || 0) > 5 ? "var(--blue)" : "var(--red)" }}>{viewingBatch.straws_available}/{viewingBatch.straws}</div></div>
                  <div><div style={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Location</div><div style={{ fontSize: 14, fontFamily: "monospace", fontWeight: 600 }}>{[viewingBatch.tank, viewingBatch.canister, viewingBatch.goblet].filter(Boolean).join(" / ")}</div>
                    {viewingBatch.details?.tank2 && <div style={{ fontSize: 12, fontFamily: "monospace", color: "var(--text-light)", marginTop: 2 }}>+ {[viewingBatch.details.tank2, viewingBatch.details.canister2, viewingBatch.details.section2].filter(Boolean).join(" / ")}</div>}</div>
                  <div><div style={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Motility Pre-Freeze</div><div style={{ fontSize: 14, fontWeight: 600 }}>{viewingBatch.motility_pre || "—"}%</div></div>
                  <div><div style={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Motility Post-Thaw</div><div style={{ fontSize: 14, fontWeight: 600, color: (viewingBatch.motility_post || 0) >= 60 ? "var(--teal)" : "var(--red)" }}>{viewingBatch.motility_post || "—"}%</div></div>
                  <div><div style={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Morphology</div><div style={{ fontSize: 14, fontWeight: 600 }}>{viewingBatch.morphology || "—"}%</div></div>
                  <div><div style={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Concentration</div><div style={{ fontSize: 14, fontWeight: 600 }}>{viewingBatch.concentration || "—"}</div></div>
                  <div><div style={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Extender</div><div style={{ fontSize: 14, fontWeight: 600 }}>{viewingBatch.extender || "—"}</div></div>
                  <div><div style={{ fontSize: 11, color: "var(--text-light)", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>Collector</div><div style={{ fontSize: 14, fontWeight: 600 }}>{viewingBatch.collector || "—"}</div></div>
                </div>
                {viewingBatch.details?.pt_read_tech && (
                  <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 6, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", marginBottom: 8 }}>Post-Thaw Data</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13 }}>
                      <div><span style={{ color: "var(--text-light)" }}>PT Date:</span> {viewingBatch.details.pt_date || "—"}</div>
                      <div><span style={{ color: "var(--text-light)" }}>PT Read Tech:</span> {viewingBatch.details.pt_read_tech || "—"}</div>
                      <div><span style={{ color: "var(--text-light)" }}>Culture Results:</span> {viewingBatch.details.culture_results || "—"}</div>
                    </div>
                  </div>
                )}
                {viewingBatch.notes && (
                  <div style={{ background: "#fafaf7", padding: 12, borderRadius: 6, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", marginBottom: 4 }}>Notes</div>
                    <div style={{ fontSize: 13, color: "var(--text-mid)" }}>{viewingBatch.notes}</div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div className="form-row"><label className="form-label">Stallion</label>
                    <input className="form-input" list="fs-edit-stallions" value={editingBatch.stallion || ""} onChange={e => setEditingBatch({ ...editingBatch, stallion: e.target.value })} />
                    <datalist id="fs-edit-stallions">{stallions.map(x => <option key={x.id} value={x.name} />)}</datalist></div>
                  <div className="form-row"><label className="form-label">Collection Date</label><input className="form-input" type="date" value={editingBatch.collection_date || ""} onChange={e => setEditingBatch({ ...editingBatch, collection_date: e.target.value })} /></div>
                  <div className="form-row"><label className="form-label">Collector</label>
                    <PersonSelect value={editingBatch.collector || ""} options={mergeNames(teamNames, semen.map(x => x.collector))} onChange={v => setEditingBatch({ ...editingBatch, collector: v })} /></div>
                  <div className="form-row"><label className="form-label">Extender</label><input className="form-input" type="text" value={editingBatch.extender || ""} onChange={e => setEditingBatch({ ...editingBatch, extender: e.target.value })} /></div>
                  <div className="form-row"><label className="form-label">Tank</label>
                    <select className="form-input" value={editingBatch.tank || ""} onChange={e => setEditingBatch({ ...editingBatch, tank: e.target.value })}>
                      <option value="">—</option>
                      {[...new Set([...tankOptions, editingBatch.tank].filter(Boolean))].map(t => <option key={t} value={t}>{t}</option>)}
                    </select></div>
                  <div className="form-row"><label className="form-label">Canister</label><input className="form-input" type="text" value={editingBatch.canister || ""} onChange={e => setEditingBatch({ ...editingBatch, canister: e.target.value })} /></div>
                  <div className="form-row"><label className="form-label">Section</label><input className="form-input" type="text" value={editingBatch.goblet || ""} onChange={e => setEditingBatch({ ...editingBatch, goblet: e.target.value })} placeholder="e.g. Top goblet" /></div>
                  <div className="form-row"><label className="form-label">Motility Pre (%)</label><input className="form-input" type="number" value={editingBatch.motility_pre || ""} onChange={e => setEditingBatch({ ...editingBatch, motility_pre: e.target.value })} /></div>
                  <div className="form-row"><label className="form-label">Motility Post (%)</label><input className="form-input" type="number" value={editingBatch.motility_post || ""} onChange={e => setEditingBatch({ ...editingBatch, motility_post: e.target.value })} /></div>
                  <div className="form-row"><label className="form-label">Morphology (%)</label><input className="form-input" type="number" value={editingBatch.morphology || ""} onChange={e => setEditingBatch({ ...editingBatch, morphology: e.target.value })} /></div>
                  <div className="form-row"><label className="form-label">Concentration</label><input className="form-input" type="text" value={editingBatch.concentration || ""} onChange={e => setEditingBatch({ ...editingBatch, concentration: e.target.value })} /></div>
                  <div className="form-row"><label className="form-label">Straws Available</label><input className="form-input" type="number" min="0" value={editingBatch.straws_available ?? ""} onChange={e => setEditingBatch({ ...editingBatch, straws_available: e.target.value })} /></div>
                  <div className="form-row"><label className="form-label">Status</label><select className="form-input" value={editingBatch.status || ""} onChange={e => setEditingBatch({ ...editingBatch, status: e.target.value })}><option value="">—</option><option value="Available">Available</option><option value="Low Stock">Low Stock</option><option value="Depleted">Depleted</option></select></div>
                </div>
                <div className="form-row"><label className="form-label">Notes</label><textarea className="form-input" rows={3} value={editingBatch.notes || ""} onChange={e => setEditingBatch({ ...editingBatch, notes: e.target.value })} style={{ resize: "vertical" }} /></div>
                <div style={{ background: "#f9f7f3", padding: 12, borderRadius: 6, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", marginBottom: 10 }}>Post-Thaw Data</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="form-row"><label className="form-label">PT Motility (%)</label><input className="form-input" type="number" value={editingBatch.details?.mot_total_post_thaw || ""} onChange={e => setEditingBatch({ ...editingBatch, details: { ...editingBatch.details, mot_total_post_thaw: e.target.value } })} /></div>
                    <div className="form-row"><label className="form-label">PT Date</label><input className="form-input" type="date" value={editingBatch.details?.pt_date || ""} onChange={e => setEditingBatch({ ...editingBatch, details: { ...editingBatch.details, pt_date: e.target.value } })} /></div>
                    <div className="form-row"><label className="form-label">PT Read Tech</label><input className="form-input" type="text" value={editingBatch.details?.pt_read_tech || ""} onChange={e => setEditingBatch({ ...editingBatch, details: { ...editingBatch.details, pt_read_tech: e.target.value } })} /></div>
                    <div className="form-row"><label className="form-label">Culture Results</label><input className="form-input" type="text" value={editingBatch.details?.culture_results || ""} onChange={e => setEditingBatch({ ...editingBatch, details: { ...editingBatch.details, culture_results: e.target.value } })} /></div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer" style={{ background: "#fafaf7", borderTop: "1px solid var(--parchment-dark)", padding: "14px 24px" }}>
            {!editingBatch ? (
              <>
                <button className="btn btn-outline" onClick={() => { setViewingBatch(null); setEditingBatch(null); }}>Close</button>
                <button className="btn btn-primary" onClick={() => setEditingBatch({ ...viewingBatch })}>Edit</button>
              </>
            ) : (
              <>
                <button className="btn btn-outline" onClick={() => setEditingBatch(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveBatchEdit}>Save</button>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    {showAdd && (
      <div className="modal-overlay" onClick={() => setShowAdd(false)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, width: "95vw" }}>
          <div className="modal-header" style={{ background: "linear-gradient(135deg,#1a3a2a,#2d5a3d)", borderBottom: "none", padding: "18px 24px" }}>
            <div>
              <div className="modal-title" style={{ color: "#fff", fontSize: 17, fontWeight: 700, letterSpacing: ".02em" }}>Daily Semen Freezing Record</div>
              <div style={{ color: "rgba(255,255,255,.55)", fontSize: 11.5, marginTop: 3 }}>Complete collection, processing & storage details</div>
            </div>
            <button className="modal-close" onClick={() => setShowAdd(false)} style={{ color: "#fff" }}>✕</button>
          </div>
          <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto", padding: "20px 24px" }}>

            {/* ── SECTION: Stallion & Collection Info ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 15 }}>🐴</span>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--forest)" }}>Stallion & Collection Info</span>
                <div style={{ flex: 1, height: 1, background: "var(--parchment-dark)" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px 14px" }}>
                <div className="form-row" style={{ gridColumn: "1 / 2" }}><label className="form-label">Date *</label><input className="form-input" type="date" value={form.collection_date} onChange={sf("collection_date")} /></div>
                <div className="form-row" style={{ gridColumn: "2 / 4" }}><label className="form-label">Stallion *</label>
                  <select className="form-input" value={form.stallion} onChange={e => { sf("stallion")(e); const sel = stallions.find(s => s.name === e.target.value); if (sel) { setForm(p => ({ ...p, breed: sel.breed || "Quarter Horse", registration: sel.registration_number || "" })); } }}>
                    <option value="">— Select —</option>
                    {stallions.map(s => <option key={s.id}>{s.name}</option>)}
                    <option value="__other">Other (type below)</option>
                  </select>
                  {form.stallion === "__other" && <input className="form-input" style={{ marginTop: 4 }} placeholder="Type stallion name" value={otherStallionFS} onChange={e => setOtherStallionFS(e.target.value)} autoFocus />}
                </div>
                <div className="form-row"><label className="form-label">Breed</label><input className="form-input" value={form.breed} onChange={sf("breed")} placeholder="e.g. Quarter Horse" /></div>
                <div className="form-row"><label className="form-label">Registration #</label><input className="form-input" value={form.registration} onChange={sf("registration")} /></div>
                <div className="form-row"><label className="form-label">Technician</label>
                  <select className="form-input" value={form.technician} onChange={sf("technician")}>
                    <option value="">— Select —</option><option value="JK">JK</option><option value="HG">HG</option>
                  </select>
                </div>
                <div className="form-row"><label className="form-label">Semen Owner</label><input className="form-input" value={form.semen_owner} onChange={sf("semen_owner")} /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "10px 14px", marginTop: 10 }}>
                <div className="form-row"><label className="form-label">Time of Collection</label><input className="form-input" type="time" value={form.time_of_collection} onChange={sf("time_of_collection")} /></div>
                <div className="form-row"><label className="form-label"># Mounts</label><input className="form-input" type="number" value={form.mounts} onChange={sf("mounts")} /></div>
                <div className="form-row"><label className="form-label">Collector</label>
                  <PersonSelect value={form.collector} options={teamNames} onChange={v => setForm(p => ({ ...p, collector: v }))} />
                </div>
                <div className="form-row"><label className="form-label">Days Sexual Rest</label><input className="form-input" type="number" value={form.days_rest} onChange={sf("days_rest")} /></div>
              </div>
            </div>

            {/* ── SECTION: Extenders & Antibiotics ── */}
            <div style={{ background: "#f4f7f5", borderRadius: 10, padding: "14px 16px", marginBottom: 20, border: "1px solid #dde6df" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 15 }}>🧪</span>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#2d5a3d" }}>Extenders & Antibiotics</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "8px 10px", marginBottom: 10 }}>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Centrifugation Ext.</label><input className="form-input" value={form.cent_extender} onChange={sf("cent_extender")} style={{ fontSize: 12 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Lot #</label><input className="form-input" value={form.cent_lot} onChange={sf("cent_lot")} style={{ fontSize: 12 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Exp. Date</label><input className="form-input" type="date" value={form.cent_exp} onChange={sf("cent_exp")} style={{ fontSize: 11 }} /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 2fr 1fr 1fr 2fr 1fr 1fr", gap: "8px 10px", marginBottom: 10 }}>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Freeze Extender 1</label><input className="form-input" value={form.freeze_extender_1} onChange={sf("freeze_extender_1")} style={{ fontSize: 12 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Lot #</label><input className="form-input" value={form.freeze_ext_1_lot} onChange={sf("freeze_ext_1_lot")} style={{ fontSize: 12 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Exp.</label><input className="form-input" type="date" value={form.freeze_ext_1_exp} onChange={sf("freeze_ext_1_exp")} style={{ fontSize: 11 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Freeze Extender 2</label><input className="form-input" value={form.freeze_extender_2} onChange={sf("freeze_extender_2")} style={{ fontSize: 12 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Lot #</label><input className="form-input" value={form.freeze_ext_2_lot} onChange={sf("freeze_ext_2_lot")} style={{ fontSize: 12 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Exp.</label><input className="form-input" type="date" value={form.freeze_ext_2_exp} onChange={sf("freeze_ext_2_exp")} style={{ fontSize: 11 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Freeze Extender 3</label><input className="form-input" value={form.freeze_extender_3} onChange={sf("freeze_extender_3")} style={{ fontSize: 12 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Lot #</label><input className="form-input" value={form.freeze_ext_3_lot} onChange={sf("freeze_ext_3_lot")} style={{ fontSize: 12 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Exp.</label><input className="form-input" type="date" value={form.freeze_ext_3_exp} onChange={sf("freeze_ext_3_exp")} style={{ fontSize: 11 }} /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 10px" }}>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Penicillin (mg/ml)</label><input className="form-input" value={form.penicillin_level} onChange={sf("penicillin_level")} style={{ fontSize: 12 }} /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 10 }}>Amikacin (mg/ml)</label><input className="form-input" value={form.amikacin_level} onChange={sf("amikacin_level")} style={{ fontSize: 12 }} /></div>
              </div>
            </div>

            {/* ── SECTION: Raw Semen Analysis ── */}
            {(() => { const totalSpermCalc = ((parseFloat(form.gf_volume) || 0) * (parseFloat(form.conc_nc) || 0) / 1000).toFixed(2); return (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 15 }}>🔬</span>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--forest)" }}>Raw Semen Analysis</span>
                <div style={{ flex: 1, height: 1, background: "var(--parchment-dark)" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px 14px" }}>
                <div className="form-row"><label className="form-label">G-F Volume (ml)</label><input className="form-input" type="number" value={form.gf_volume} onChange={sf("gf_volume")} /></div>
                <div className="form-row"><label className="form-label">Conc. NC (×10⁶/ml)</label><input className="form-input" value={form.conc_nc} onChange={sf("conc_nc")} /></div>
                <div className="form-row"><label className="form-label">Total Sperm (×10⁹)</label><input className="form-input" value={totalSpermCalc} readOnly style={{ background: "rgba(0,0,0,.03)", cursor: "not-allowed" }} /></div>
              </div>
            </div>
            ); })()}

            {/* ── SECTION: Motility Grid ── */}
            <div style={{ background: "linear-gradient(135deg,#0d1f2d,#0a2840)", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 15 }}>📊</span>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#4fc3f7" }}>Motility</span>
              </div>
              {/* Initial section */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#4fc3f7", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Initial</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px 10px" }}>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(255,255,255,.7)", fontSize: 11 }}>Total</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.2)", textAlign: "center", fontSize: 13, padding: "6px 8px" }} type="number" value={form.mot_total_init} onChange={sf("mot_total_init")} placeholder="%" /></div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(255,255,255,.7)", fontSize: 11 }}>Progressive</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.2)", textAlign: "center", fontSize: 13, padding: "6px 8px" }} type="number" value={form.mot_prog_init} onChange={sf("mot_prog_init")} placeholder="%" /></div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(255,255,255,.7)", fontSize: 11 }}>Velocity</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.2)", textAlign: "center", fontSize: 13, padding: "6px 8px" }} type="number" value={form.mot_vel_init} onChange={sf("mot_vel_init")} placeholder="%" /></div>
                </div>
              </div>
              {/* Post Centrifugation section */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#4fc3f7", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Post Centrifugation</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px 10px" }}>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(255,255,255,.7)", fontSize: 11 }}>Total</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.2)", textAlign: "center", fontSize: 13, padding: "6px 8px" }} type="number" value={form.mot_total_post_cent} onChange={sf("mot_total_post_cent")} placeholder="%" /></div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(255,255,255,.7)", fontSize: 11 }}>Progressive</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.2)", textAlign: "center", fontSize: 13, padding: "6px 8px" }} type="number" value={form.mot_prog_post_cent} onChange={sf("mot_prog_post_cent")} placeholder="%" /></div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(255,255,255,.7)", fontSize: 11 }}>Velocity</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.2)", textAlign: "center", fontSize: 13, padding: "6px 8px" }} type="number" value={form.mot_vel_post_cent} onChange={sf("mot_vel_post_cent")} placeholder="%" /></div>
                </div>
              </div>
              {/* Post-Thaw section */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#4fc3f7", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Post-Thaw</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px 10px" }}>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(255,255,255,.7)", fontSize: 11 }}>Total</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.2)", textAlign: "center", fontSize: 13, padding: "6px 8px" }} type="number" value={form.mot_total_post_thaw} onChange={sf("mot_total_post_thaw")} placeholder="%" /></div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(255,255,255,.7)", fontSize: 11 }}>Progressive</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.2)", textAlign: "center", fontSize: 13, padding: "6px 8px" }} type="number" value={form.mot_prog_post_thaw} onChange={sf("mot_prog_post_thaw")} placeholder="%" /></div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(255,255,255,.7)", fontSize: 11 }}>Velocity</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.2)", textAlign: "center", fontSize: 13, padding: "6px 8px" }} type="number" value={form.mot_vel_post_thaw} onChange={sf("mot_vel_post_thaw")} placeholder="%" /></div>
                </div>
              </div>
              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 10px", alignItems: "center" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,.8)" }}>Normal Morphology %</div>
                <input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.2)", textAlign: "center", fontSize: 13, padding: "6px 8px", maxWidth: 120 }} type="number" value={form.morphology} onChange={sf("morphology")} placeholder="%" />
              </div>
            </div>

            {/* ── SECTION: Post-Thaw QC ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 15 }}>✅</span>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--forest)" }}>Post-Thaw QC</span>
                <div style={{ flex: 1, height: 1, background: "var(--parchment-dark)" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px 14px" }}>
                <div className="form-row"><label className="form-label">PT Read Tech</label><input className="form-input" value={form.pt_read_tech} onChange={sf("pt_read_tech")} /></div>
                <div className="form-row"><label className="form-label">Date</label><input className="form-input" type="date" value={form.pt_date} onChange={sf("pt_date")} /></div>
                <div className="form-row"><label className="form-label">Culture Results (48 hrs)</label><input className="form-input" value={form.culture_results} onChange={sf("culture_results")} /></div>
              </div>
            </div>

            {/* ── SECTION: Processing ── */}
            <div style={{ background: "#f8f6f0", borderRadius: 10, padding: "14px 16px", marginBottom: 20, border: "1px solid #e5dfd2" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 15 }}>⚙️</span>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#6b5c3e" }}>Processing</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "10px 14px" }}>
                <div className="form-row"><label className="form-label">Initial Resuspension (ml)</label><input className="form-input" type="number" value={form.vol_initial_resuspension} onChange={sf("vol_initial_resuspension")} /></div>
                <div className="form-row"><label className="form-label">Desired Concentration</label><input className="form-input" value={form.desired_concentration} onChange={sf("desired_concentration")} /></div>
                <div className="form-row"><label className="form-label">NucleoCounter Count</label><input className="form-input" value={form.nucleocounter_count} onChange={sf("nucleocounter_count")} /></div>
                <div className="form-row"><label className="form-label">Final Volume (ml)</label><input className="form-input" type="number" value={form.final_volume} onChange={sf("final_volume")} /></div>
                <div className="form-row"><label className="form-label">Resuspended Concentration</label><input className="form-input" value={form.resuspended_concentration} onChange={sf("resuspended_concentration")} /></div>
                <div className="form-row"><label className="form-label">Final Concentration</label><input className="form-input" value={form.final_concentration} onChange={sf("final_concentration")} /></div>
                <div className="form-row"><label className="form-label">PT Concentration</label><input className="form-input" value={form.pt_conc} onChange={sf("pt_conc")} /></div>
              </div>
            </div>

            {/* ── SECTION: Storage & Shipment ── */}
            <div style={{ background: "linear-gradient(135deg,#0d1f2d,#0a2840)", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 15 }}>🧊</span>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#4fc3f7" }}>Storage & Shipment</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "10px 14px", marginBottom: 12 }}>
                <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)" }}>Total # Straws *</label><input className="form-input" type="number" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)" }} value={form.straws} onChange={sf("straws")} /></div>
                <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)" }}># Straws per Dose</label><input className="form-input" type="number" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)" }} value={form.straws_per_dose} onChange={sf("straws_per_dose")} /></div>
                <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)" }}># Doses</label><input className="form-input" type="number" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)" }} value={semenDoses(form.straws, form.straws_per_dose)} readOnly /></div>
                <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)" }}>Straw Code</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)" }} value={form.straw_code} onChange={sf("straw_code")} /></div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label className="form-label" style={{ color: "rgba(79,195,247,.7)", marginBottom: 6, display: "block" }}>Straw Color</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {["Yellow","Green","Blue","Red","Clear","Grey","Orange","Pistachio","Turquoise"].map(c => {
                    const colorMap = { Yellow: "#ffd700", Green: "#4caf50", Blue: "#2196f3", Red: "#f44336", Clear: "#e0e0e0", Grey: "#9e9e9e", Orange: "#ff9800", Pistachio: "#93c572", Turquoise: "#40e0d0" };
                    const sel = form.straw_color === c;
                    return <button key={c} type="button" onClick={() => setForm(p => ({ ...p, straw_color: sel ? "" : c }))}
                      style={{ padding: "4px 10px", borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: "pointer", border: sel ? "2px solid #fff" : "1px solid rgba(255,255,255,.15)",
                        background: sel ? colorMap[c] : "rgba(255,255,255,.08)", color: sel ? (c === "Yellow" || c === "Clear" || c === "Pistachio" ? "#222" : "#fff") : "rgba(255,255,255,.6)",
                        transition: "all .15s" }}>{c}</button>;
                  })}
                </div>
              </div>
              {/* Location 1 */}
              <div style={{ background: "rgba(255,255,255,.06)", borderRadius: 8, padding: "10px 12px", marginBottom: 8, border: "1px solid rgba(79,195,247,.12)" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#4fc3f7", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>Location 1</div>
                <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr 1fr", gap: "8px 10px", marginBottom: 8 }}>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Tank</label>{tankPicker("tank", false)}</div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Canister</label><select className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={form.canister} onChange={sf("canister")}>{canisterOptions.map(c => <option key={c}>{c}</option>)}</select></div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Section</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} placeholder="e.g. Top goblet" value={form.section} onChange={sf("section")} /></div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}># Straws</label><input className="form-input" type="number" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={form.loc1_straws} onChange={sf("loc1_straws")} /></div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {SEMEN_EXPORT_REGIONS.map(country => {
                    const checked = (form.loc1_export || []).includes(country);
                    return <label key={country} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "rgba(255,255,255,.7)", cursor: "pointer" }}>
                      <input type="checkbox" checked={checked} onChange={() => setForm(p => ({ ...p, loc1_export: checked ? (p.loc1_export||[]).filter(x=>x!==country) : [...(p.loc1_export||[]), country] }))} style={{ accentColor: "#4fc3f7" }} />
                      {country}
                    </label>;
                  })}
                  <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "rgba(255,255,255,.7)", cursor: "pointer" }}>
                    <input type="checkbox" checked={form.loc1_domestic_only} onChange={() => setForm(p => ({ ...p, loc1_domestic_only: !p.loc1_domestic_only }))} style={{ accentColor: "#4fc3f7" }} />
                    Domestic Only
                  </label>
                </div>
              </div>
              {/* Location 2 */}
              <div style={{ background: "rgba(255,255,255,.06)", borderRadius: 8, padding: "10px 12px", border: "1px solid rgba(79,195,247,.12)" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#4fc3f7", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>Location 2</div>
                <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr 1fr", gap: "8px 10px", marginBottom: 8 }}>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Tank</label>{tankPicker("tank2", true)}</div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Canister</label><select className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={form.canister2} onChange={sf("canister2")}><option value="">—</option>{canisterOptions.map(c => <option key={c}>{c}</option>)}</select></div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}>Section</label><input className="form-input" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} placeholder="e.g. Bottom goblet" value={form.section2} onChange={sf("section2")} /></div>
                  <div className="form-row"><label className="form-label" style={{ color: "rgba(79,195,247,.7)", fontSize: 10 }}># Straws</label><input className="form-input" type="number" style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(79,195,247,.25)", fontSize: 12 }} value={form.loc2_straws} onChange={sf("loc2_straws")} /></div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {SEMEN_EXPORT_REGIONS.map(country => {
                    const checked = (form.loc2_export || []).includes(country);
                    return <label key={country} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "rgba(255,255,255,.7)", cursor: "pointer" }}>
                      <input type="checkbox" checked={checked} onChange={() => setForm(p => ({ ...p, loc2_export: checked ? (p.loc2_export||[]).filter(x=>x!==country) : [...(p.loc2_export||[]), country] }))} style={{ accentColor: "#4fc3f7" }} />
                      {country}
                    </label>;
                  })}
                  <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "rgba(255,255,255,.7)", cursor: "pointer" }}>
                    <input type="checkbox" checked={form.loc2_domestic_only} onChange={() => setForm(p => ({ ...p, loc2_domestic_only: !p.loc2_domestic_only }))} style={{ accentColor: "#4fc3f7" }} />
                    Domestic Only
                  </label>
                </div>
              </div>
            </div>

            {/* ── Comments ── */}
            <div className="form-row"><label className="form-label">Comments</label><textarea className="form-input" rows={3} value={form.notes} onChange={sf("notes")} placeholder="Additional notes or observations…" style={{ resize: "vertical" }} /></div>

          </div>
          <div className="modal-footer" style={{ background: "#fafaf7", borderTop: "1px solid var(--parchment-dark)", padding: "14px 24px" }}>
            <button className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={addBatch} style={{ minWidth: 160 }}>Log Collection</button>
          </div>
        </div>
      </div>
    )}
    <SaveToast show={saved} />
  </>);
}

// ─── SHIPMENTS: form <-> row ──────────────────────────────────────────────────
// The shipment forms call the recipient `recipient_name`, but the column is
// `to_name` and there has never been a `recipient_name` column. Every shipment
// insert therefore failed (PGRST204) from the day the forms were written; the
// table had 0 rows. Rows go through shipmentRowFromForm on the way in (a
// whitelist, so a stray form key can never break a save again) and
// shipmentFromRow on the way out, so the screens can keep reading
// `recipient_name`.
const SHIPMENT_TEXT_COLUMNS = [
  "order_id", "customer_id", "carrier", "tracking_number", "status", "ship_date", "estimated_delivery",
  "contents", "notes", "ship_to_address", "ship_to_city", "ship_to_state", "ship_to_zip", "ship_to_country",
  "ship_to_phone", "service_level", "label_url", "easypost_shipment_id", "easypost_rate_id",
];
const SHIPMENT_NUMBER_COLUMNS = ["weight_oz", "length_in", "width_in", "height_in", "rate_amount"];
function shipmentRowFromForm(f) {
  const row = {};
  for (const k of SHIPMENT_TEXT_COLUMNS) if (f[k] !== undefined) row[k] = f[k] === "" ? null : f[k];
  for (const k of SHIPMENT_NUMBER_COLUMNS) if (f[k] !== undefined) row[k] = parseFloat(f[k]) || null;
  const name = (f.recipient_name || "").trim();
  row.to_name = name;
  row.customer_name = name;
  return row;
}
function shipmentFromRow(s) {
  return { ...s, recipient_name: s.to_name || s.customer_name || s.customer || s.recipient_name || "" };
}
function useShipments(orgId) {
  const t = useSupabaseTable("shipments", orgId);
  const data = useMemo(() => t.data.map(shipmentFromRow), [t.data]);
  return { ...t, data };
}

// ─── SEMEN ORDER DETAILS ──────────────────────────────────────────────────────
// Everything a semen order prints on the shipment notice. These were only ever
// entered on the order form in Customers, and the Orders edit screen covered
// none of them, so a misspelled mare or a wrong Equitainer # was permanent and
// printed on every notice for that order. Editable here, from the notice
// preview (Edit details) and from Orders > edit on a semen order.
//
// The stallion itself is not editable: it is tied to the batch the straws came
// out of. Reg. # and owner are, since they are only text on the notice.
const SEMEN_ORDER_FIELDS = [
  ["stallion_reg_number", "Stallion Reg. #"], ["stallion_owner", "Stallion Owner"],
  ["mare_name", "Mare Name"], ["mare_reg_number", "Mare Reg. #"],
  ["person_requesting", "Person Requesting"], ["semen_type", "Semen Type", ["Frozen", "Cooled", "Fresh"]],
  ["shipping_method", "Ship Method", ["FedEx Priority", "FedEx Standard", "UPS Next Day", "Pickup", "Counter-to-Counter", "Other"]],
  ["tracking_number", "Tracking #"], ["shipment_date", "Shipment Date", "date"],
  ["processed_by", "Processed By"], ["progr_cells", "# Progr. Cells (billion)"],
  ["net_volume", "Net Volume (ml)"], ["extender_vol", "Extender Vol."],
  ["extender_used", "Extender Used"], ["antibiotic_added", "Antibiotic Added"],
  ["centrifuged", "Centrifuged", ["No", "Yes"]], ["ext_sperm", "Ext. Sperm"],
  ["equitainer_number", "Equitainer #"], ["shipment_comments", "Comments", "textarea"],
];

function SemenDetailsForm({ orderId, onSaved, onCancel }) {
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    supabase.from("orders").select("id, stallion_name, " + SEMEN_ORDER_FIELDS.map(f => f[0]).join(", ")).eq("id", orderId).single()
      .then(({ data, error }) => {
        if (!live) return;
        if (error) { setError(error.message); return; }
        setOrder(data);
        setForm(Object.fromEntries(SEMEN_ORDER_FIELDS.map(([k]) => [k, data[k] ?? ""])));
      });
    return () => { live = false; };
  }, [orderId]);
  const save = async () => {
    setSaving(true);
    try {
      const updates = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
      await dbUpdate("orders", orderId, updates);
      onSaved(updates);
    } catch (e) { alert("Could not save: " + e.message); }
    setSaving(false);
  };
  if (error) return <div style={{ padding: "10px 14px", background: "#fde8e8", color: "var(--red)", borderRadius: 8, fontSize: 13 }}>{error}</div>;
  if (!order) return <div className="empty-state">Loading…</div>;
  return (<>
    <div style={{ fontSize: 13, marginBottom: 12 }}><strong>Stallion:</strong> {order.stallion_name || "—"}</div>
    <div className="form-grid">
      {SEMEN_ORDER_FIELDS.map(([k, label, kind]) => {
        const set = e => setForm(p => ({ ...p, [k]: e.target.value }));
        const wide = kind === "textarea" ? { gridColumn: "1/-1" } : undefined;
        let input;
        if (Array.isArray(kind)) {
          // A bought label writes the real service ("FedEx PRIORITY_OVERNIGHT"),
          // which is not in the list; keep it selectable rather than lose it.
          const opts = form[k] && !kind.includes(form[k]) ? [form[k], ...kind] : kind;
          input = <select className="form-input" value={form[k] || ""} onChange={set}><option value="">—</option>{opts.map(o => <option key={o}>{o}</option>)}</select>;
        } else if (kind === "textarea") {
          input = <textarea className="form-input" rows={2} value={form[k] || ""} onChange={set} />;
        } else {
          input = <input className="form-input" type={kind === "date" ? "date" : "text"} value={form[k] || ""} onChange={set} />;
        }
        return <div key={k} className="form-row" style={wide}><label className="form-label">{label}</label>{input}</div>;
      })}
    </div>
    <div style={{ fontSize: 12, color: "var(--text-light)", marginTop: 12, lineHeight: 1.5 }}>
      The shipping address comes from this order's shipment, or from the customer's record if there is no shipment yet. To change it, edit the shipment on the Shipping page or the customer in Customers.
    </div>
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
      <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
      <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save details"}</button>
    </div>
  </>);
}

function SemenDetailsModal({ orderId, onClose, onSaved }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-header"><div className="modal-title">Semen shipment details</div><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <SemenDetailsForm orderId={orderId} onCancel={onClose} onSaved={u => { onSaved(u); onClose(); }} />
        </div>
      </div>
    </div>
  );
}

// ─── SHIPMENT NOTICE ──────────────────────────────────────────────────────────
// The mare owner's copy of a semen shipment. It used to go out automatically
// the moment an order was saved, before anything had shipped. Now the farm
// sends it, after a preview built by the same function that sends it, so what
// they see here is exactly what the customer gets.
function ShipmentNoticeModal({ orderId, onClose, onSent, onOrderUpdated }) {
  const [st, setSt] = useState({ loading: true });
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [version, setVersion] = useState(0);
  const zone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; } };
  useEffect(() => {
    let live = true;
    callFunction("send-shipment-email", { order_id: orderId, preview: true, tz: zone() })
      .then(d => { if (live) setSt(d); })
      .catch(e => { if (live) setSt({ error: e.message }); });
    return () => { live = false; };
  }, [orderId, version]);
  const send = async () => {
    setSending(true);
    try {
      const r = await callFunction("send-shipment-email", { order_id: orderId, tz: zone() });
      if (onSent) onSent(r.notice_sent_at || new Date().toISOString(), r.email_sent_to || st.to);
      onClose();
      return;
    } catch (e) { alert("The notice was not sent: " + e.message); }
    setSending(false);
  };
  const note = { padding: "10px 14px", background: "#fef3c7", color: "#92400e", borderRadius: 8, fontSize: 12.5, marginBottom: 10 };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-header"><div className="modal-title">{editing ? "Edit shipment details" : "Send shipment notice"}</div><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          {editing && (
            <SemenDetailsForm orderId={orderId} onCancel={() => setEditing(false)}
              onSaved={u => { if (onOrderUpdated) onOrderUpdated(u); setEditing(false); setSt({ loading: true }); setVersion(v => v + 1); }} />
          )}
          {!editing && (<>
          {st.loading && <div className="empty-state">Preparing the notice…</div>}
          {st.error && <div style={{ ...note, background: "#fde8e8", color: "var(--red)" }}>{st.error === "No customer email found" ? "This customer has no email address. Add one on their customer record to send a shipment notice." : st.error}</div>}
          {st.sent && <div style={{ ...note, background: "#f0fdf4", color: "#166534" }}>Sent to {st.sent}.</div>}
          {st.html && (<>
            <div style={{ fontSize: 13, marginBottom: 4 }}><strong>To:</strong> {st.to}</div>
            <div style={{ fontSize: 13, marginBottom: 10 }}><strong>Subject:</strong> {st.subject}</div>
            {!st.tracking_number && !st.sent && <div style={note}>This order has no tracking number yet. You can send now, or wait until it ships and add tracking first.</div>}
            {st.notice_sent_at && !st.sent && <div style={note}>A notice for this order was already sent on {new Date(st.notice_sent_at).toLocaleDateString()}. Sending again emails the customer a second copy.</div>}
            <iframe title="Shipment notice preview" srcDoc={st.html} sandbox="" style={{ width: "100%", height: 520, border: "1px solid var(--parchment-dark)", borderRadius: 8, background: "#f4f4f4" }} />
          </>)}
          </>)}
        </div>
        {!editing && (
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose}>{st.sent ? "Close" : "Not now"}</button>
            {st.html && !st.sent && <button className="btn btn-outline" onClick={() => setEditing(true)}>Edit details</button>}
            {st.html && !st.sent && <button className="btn btn-primary" onClick={send} disabled={sending}>{sending ? "Sending…" : st.notice_sent_at ? "Send again" : "Send notice"}</button>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ORDERS ───────────────────────────────────────────────────────────────────
function Orders({ orgId }) {
  const { data: orders, setData: setOrders, loading } = useSupabaseTable("orders", orgId);
  const { data: customers } = useSupabaseTable("customers", orgId);
  const { data: priceBook } = useSupabaseTable("price_book", orgId);
  const { data: integrations } = useSupabaseTable("org_integrations", orgId, [], INTEGRATION_COLUMNS);
  const { data: shipments, setData: setShipments } = useShipments(orgId);
  const [noticeOrderId, setNoticeOrderId] = useState(null);
  const [noticeToast, setNoticeToast] = useState("");
  const [detailsOrderId, setDetailsOrderId] = useState(null);
  const [saved, setSaved] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const todayISO = localToday();
  const emptyOrder = { customer_name: "", description: "", total: "", status: "Pending", date: todayISO, notes: "" };
  const [form, setForm] = useState(emptyOrder);
  const [lineItems, setLineItems] = useState([]);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  // Shipment from order
  const [showShipModal, setShowShipModal] = useState(false);
  const [shipForm, setShipForm] = useState({});
  const [shipRates, setShipRates] = useState([]);
  const [shipRateStep, setShipRateStep] = useState("form");
  const [fetchingRates, setFetchingRates] = useState(false);
  const [buyingLabel, setBuyingLabel] = useState(false);
  const shipSf = k => e => setShipForm(p => ({ ...p, [k]: e.target.value }));

  const shipInt = integrations.find(i => i.provider_type === "shipping");
  const shipFrom = shipInt?.config?.ship_from || {};
  // The key is server-side now; the browser only needs to know one is stored.
  // The second clause covers a farm whose key has not been migrated yet.
  const hasEasyPost = !!(shipInt?.config?.secrets_present?.easypost_api_key || shipInt?.config?.easypost_api_key);

  const openShipFromOrder = (o) => {
    const cust = customers.find(c => c.name === o.customer_name);
    setShipForm({
      order_id: o.id, recipient_name: o.customer_name || "", customer_id: cust?.id || null,
      ship_to_address: cust?.shipping_address || cust?.address || "",
      ship_to_city: cust?.shipping_city || cust?.city || "",
      ship_to_state: cust?.shipping_state || cust?.state || "",
      ship_to_zip: cust?.shipping_zip || cust?.zip || "",
      ship_to_country: "US", ship_to_phone: cust?.phone || "",
      weight_oz: "", length_in: "", width_in: "", height_in: "",
      carrier: "FedEx", status: "Label Created", ship_date: todayISO, estimated_delivery: "",
      contents: o.description || (o.line_items ? o.line_items.map(li => `${li.name} x${li.qty}`).join(", ") : ""),
      notes: "", tracking_number: o.tracking_number || "", service_level: "", label_url: "", easypost_shipment_id: "", easypost_rate_id: "", rate_amount: ""
    });
    setShipRates([]); setShipRateStep("form"); setShowShipModal(true);
  };

  const fetchShipRates = async () => {
    if (!hasEasyPost) { alert("Configure your EasyPost API key in Settings → Shipping Carriers first."); return; }
    if (!shipForm.ship_to_address || !shipForm.ship_to_zip || !shipForm.weight_oz) { alert("Please fill in the shipping address and package weight."); return; }
    if (!shipFrom.address || !shipFrom.zip) { alert("Configure your ship-from address in Settings → Shipping Carriers first."); return; }
    setFetchingRates(true); setShipRates([]);
    try {
      const data = await callFunction("easypost", {
        action: "rates",
        shipment: {
            to_address: { name: shipForm.recipient_name, street1: shipForm.ship_to_address, city: shipForm.ship_to_city, state: shipForm.ship_to_state, zip: shipForm.ship_to_zip, country: "US", phone: shipForm.ship_to_phone },
            from_address: { name: shipFrom.name || "", company: shipFrom.company || "", street1: shipFrom.address, city: shipFrom.city, state: shipFrom.state, zip: shipFrom.zip, country: "US", phone: shipFrom.phone || "" },
            parcel: { weight: parseFloat(shipForm.weight_oz) || 1, length: parseFloat(shipForm.length_in) || null, width: parseFloat(shipForm.width_in) || null, height: parseFloat(shipForm.height_in) || null }
          },
      });
      setShipForm(p => ({ ...p, easypost_shipment_id: data.id }));
      const carrierFilter = ["FedEx", "UPS", "Unishippers", "USPS"];
      const filtered = (data.rates || []).filter(r => carrierFilter.some(cf => r.carrier.toLowerCase().includes(cf.toLowerCase()))).sort((a, b) => parseFloat(a.rate) - parseFloat(b.rate));
      setShipRates(filtered.length > 0 ? filtered : data.rates || []);
      setShipRateStep("rates");
    } catch (e) { alert("Error fetching rates: " + e.message); }
    setFetchingRates(false);
  };

  const buyShipLabel = async (rate) => {
    setBuyingLabel(true);
    try {
      const data = await callFunction("easypost", {
        action: "buy",
        shipment_id: shipForm.easypost_shipment_id,
        rate_id: rate.id,
      });
      const next = { ...shipForm, label_url: data.label_url || "", tracking_number: data.tracking_code || "", carrier: rate.carrier, service_level: rate.service, rate_amount: rate.rate, easypost_rate_id: rate.id, status: "Label Created" };
      setShipForm(next);
      setShipRateStep("done");
      // The farm is charged the moment EasyPost answers. Save now, so closing
      // this window can never lose a label that has been paid for.
      try {
        const row = await persistShipment(next);
        setShipForm(p => ({ ...p, id: row.id }));
      } catch (e) {
        alert("The label was bought, but saving the shipment failed: " + e.message + "\n\nPrint the label from this window before closing it. Tracking: " + (next.tracking_number || "none"));
      }
    } catch (e) { alert("Error buying label: " + e.message); }
    setBuyingLabel(false);
  };

  // Saves the shipment and writes what the customer needs back onto its order:
  // the link, the tracking number and ship date, and the real service when a
  // label was bought. The shipment notice reads all of these from the order.
  const persistShipment = async (f) => {
    const row = await dbInsert("shipments", { id: crypto.randomUUID(), ...shipmentRowFromForm(f), org_id: orgId });
    setShipments(p => [row, ...p]);
    const order = orders.find(o => o.id === f.order_id);
    if (order) {
      const upd = { shipment_id: row.id };
      if (f.tracking_number) upd.tracking_number = f.tracking_number;
      if (f.ship_date) upd.shipment_date = f.ship_date;
      if (f.service_level) upd.shipping_method = [f.carrier, f.service_level].filter(Boolean).join(" ");
      try {
        await dbUpdate("orders", order.id, upd);
        setOrders(p => p.map(o => o.id === order.id ? { ...o, ...upd } : o));
      } catch (e) { console.error("[Orders] shipment saved but order not updated", e); }
    }
    return row;
  };

  // A semen order that has just shipped is the moment to tell the mare owner,
  // so closing after a save opens the notice preview. Never sent without it.
  const closeShipModal = (offerNotice) => {
    const order = orders.find(o => o.id === shipForm.order_id);
    const cust = order && customers.find(c => c.name === order.customer_name);
    setShowShipModal(false); setShipForm({}); setShipRates([]); setShipRateStep("form");
    if (offerNotice && order?.order_type === "semen" && !order.notice_sent_at && (order.customer_email || cust?.email)) setNoticeOrderId(order.id);
  };

  const saveShipment = async () => {
    if (!shipForm.recipient_name) return;
    try {
      if (!shipForm.id) await persistShipment(shipForm);
      save();
      closeShipModal(true);
    } catch (e) { alert("Error saving shipment: " + e.message); }
  };

  const activeItems = priceBook.filter(i => i.is_active).sort((a, b) => a.name.localeCompare(b.name));
  const lineTotal = lineItems.reduce((sum, li) => sum + (li.qty * li.price), 0);

  const addLineItem = (item) => {
    const existing = lineItems.find(li => li.price_book_id === item.id);
    if (existing) {
      setLineItems(p => p.map(li => li.price_book_id === item.id ? { ...li, qty: li.qty + 1 } : li));
    } else {
      const cp = parseFloat(item.customer_price) || parseFloat(item.cost_per_unit || 0) * (1 + parseFloat(item.markup_pct || 0));
      setLineItems(p => [...p, { price_book_id: item.id, name: item.name, type: item.type, qty: 1, price: cp }]);
    }
    setShowItemPicker(false);
  };

  const updateLineQty = (idx, qty) => {
    if (qty < 1) { setLineItems(p => p.filter((_, i) => i !== idx)); return; }
    setLineItems(p => p.map((li, i) => i === idx ? { ...li, qty } : li));
  };

  const removeLineItem = (idx) => setLineItems(p => p.filter((_, i) => i !== idx));

  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const addOrder = async () => {
    if (!form.customer_name.trim()) return;
    const total = lineItems.length > 0 ? lineTotal : (parseFloat(form.total) || 0);
    if (total === 0 && !form.total) return;
    const desc = lineItems.length > 0 ? lineItems.map(li => `${li.name} x${li.qty}`).join(", ") : form.description;
    try {
      const row = await dbInsert("orders", { id: crypto.randomUUID(), ...form, description: desc, total, line_items: lineItems.length > 0 ? lineItems : null, org_id: orgId, date: form.date || null });
      setOrders(p => [row, ...p]);
      setShowAdd(false); setForm(emptyOrder); setLineItems([]);
      save();
      if (form.status === "Paid") recalcCustomerSpent(form.customer_name);
    } catch (e) { alert("Error: " + e.message); }
  };

  const openEditOrder = (o) => {
    setEditingOrder(o);
    setForm({ customer_name: o.customer_name || "", description: o.description || "", total: o.total || "", status: o.status || "Pending", date: o.date || todayISO, notes: o.notes || "" });
    setLineItems(o.line_items && Array.isArray(o.line_items) ? o.line_items.map(li => ({ ...li })) : []);
  };

  const recalcCustomerSpent = async (customerName) => {
    const custOrders = orders.filter(o => o.customer_name === customerName && o.status === "Paid");
    const total = custOrders.reduce((a, o) => a + (o.total || 0), 0);
    const cust = customers.find(c => c.name === customerName);
    if (cust) { try { await dbUpdate("customers", cust.id, { total_spent: total }); } catch {} }
  };

  const saveEditOrder = async () => {
    if (!editingOrder) return;
    try {
      const total = lineItems.length > 0 ? lineTotal : (parseFloat(form.total) || 0);
      const desc = lineItems.length > 0 ? lineItems.map(li => `${li.name} x${li.qty}`).join(", ") : form.description;
      const updates = { ...form, description: desc, total, line_items: lineItems.length > 0 ? lineItems : null, date: form.date || null };
      await dbUpdate("orders", editingOrder.id, updates);
      setOrders(p => p.map(o => o.id === editingOrder.id ? { ...o, ...updates } : o));
      const oldName = editingOrder.customer_name;
      setEditingOrder(null); setForm(emptyOrder); setLineItems([]);
      save();
      recalcCustomerSpent(updates.customer_name);
      if (oldName !== updates.customer_name) recalcCustomerSpent(oldName);
    } catch (e) { alert("Error: " + e.message); }
  };

  const deleteOrder = async (o) => {
    if (!window.confirm(`Delete this order for ${o.customer_name}?`)) return;
    try {
      await dbDelete("orders", o.id);
      setOrders(p => p.filter(x => x.id !== o.id));
      if (editingOrder && editingOrder.id === o.id) { setEditingOrder(null); setForm(emptyOrder); setLineItems([]); }
      save();
      recalcCustomerSpent(o.customer_name);
    } catch (e) { alert("Error: " + e.message); }
  };

  const markPaid = async (id) => {
    try { await dbUpdate("orders", id, { status: "Paid" }); setOrders(p => p.map(o => o.id === id ? { ...o, status: "Paid" } : o)); save(); }
    catch (e) { alert("Error: " + e.message); }
  };

  const updateStatus = async (id, status) => {
    try { await dbUpdate("orders", id, { status }); setOrders(p => p.map(o => o.id === id ? { ...o, status } : o)); save(); }
    catch (e) { alert("Error: " + e.message); }
  };

  const totalRevenue = orders.filter(o => o.status === "Paid").reduce((a, o) => a + (o.total || 0), 0);
  const totalPending = orders.filter(o => o.status === "Pending" || o.status === "Partial").reduce((a, o) => a + (o.total || 0), 0);

  const filtered = orders.filter(o => {
    const matchSearch = (o.customer_name + (o.description || "") + (o.id || "")).toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "All" || o.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (<>
    <div className="page-header-row">
      
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {saved && <span style={{ fontSize: 12, color: "var(--forest)", fontStyle: "italic" }}>✓ Saved</span>}
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ New Order</button>
      </div>
    </div>
    <div className="stats-row" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
      <div className="stat-card green"><div className="stat-icon">💳</div><div className="stat-label">Revenue Collected</div><div className="stat-value">${(totalRevenue / 1000).toFixed(1)}k</div><div className="stat-meta">Paid orders</div></div>
      <div className="stat-card red"><div className="stat-icon">⏳</div><div className="stat-label">Pending</div><div className="stat-value">${(totalPending / 1000).toFixed(1)}k</div><div className="stat-meta">Awaiting collection</div></div>
      <div className="stat-card"><div className="stat-icon">📋</div><div className="stat-label">Total Orders</div><div className="stat-value">{orders.length}</div><div className="stat-meta">This season</div></div>
    </div>
    {loading ? <div className="empty-state">Loading orders…</div> : (
      <div className="full-panel">
        <div className="filter-bar" style={{ display: "flex", gap: 10 }}>
          <input className="search-input" placeholder="Search by customer, description…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
          <select className="form-input" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="All">All Statuses</option>
            <option>Pending</option><option>Processing</option><option>Partial</option><option>Paid</option><option>Cancelled</option>
          </select>
        </div>
        <table className="data-table">
          <thead><tr><th>Customer</th><th>Description</th><th>Total</th><th>Date</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id} onClick={() => openEditOrder(o)} style={{ cursor: "pointer", transition: "background 0.1s" }} onMouseOver={e => e.currentTarget.style.background = "#f8f6f0"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ fontWeight: 700, color: "var(--forest)" }}>{o.customer_name}</td>
                <td style={{ fontSize: 12.5, color: "var(--text-mid)" }}>
                  {o.line_items && o.line_items.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                      {o.line_items.map((li, idx) => (
                        <span key={idx} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 8, background: li.type === "Service" ? "#dcfce7" : "#fef3c7", color: li.type === "Service" ? "#166534" : "#92400e", fontWeight: 500 }}>{li.name} x{li.qty}</span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontStyle: o.description ? "normal" : "italic" }}>{o.description || "—"}</span>
                  )}
                </td>
                <td style={{ fontWeight: 700 }}>${(o.total || 0).toLocaleString()}</td>
                <td style={{ fontSize: 12 }}>{o.date || "—"}</td>
                <td>
                  <select value={o.status} onChange={e => { e.stopPropagation(); updateStatus(o.id, e.target.value); }} onClick={e => e.stopPropagation()}
                    style={{ fontSize: 11, padding: "3px 7px", borderRadius: 12, border: "1px solid var(--parchment-dark)", background: "white", fontWeight: 700, cursor: "pointer",
                      color: o.status === "Paid" ? "#1a7f50" : o.status === "Pending" ? "var(--red)" : o.status === "Processing" ? "#2563eb" : o.status === "Partial" ? "#b8860b" : "var(--text-mid)" }}>
                    <option>Pending</option><option>Processing</option><option>Partial</option><option>Paid</option><option>Cancelled</option>
                  </select>
                </td>
                <td style={{ display: "flex", gap: 4 }}>
                  <button className="btn btn-sm" title="Create Shipment" style={{ background: "#f0fdf4", color: "var(--forest)", border: "1px solid #bbf7d0", fontSize: 11 }} onClick={e => { e.stopPropagation(); openShipFromOrder(o); }}>📦</button>
                  {o.order_type === "semen" && <button className="btn btn-sm" title={o.notice_sent_at ? `Shipment notice sent ${new Date(o.notice_sent_at).toLocaleDateString()}` : "Send shipment notice"} style={{ background: o.notice_sent_at ? "#f0fdf4" : "#fff7ed", color: o.notice_sent_at ? "#166534" : "#C65217", border: `1px solid ${o.notice_sent_at ? "#bbf7d0" : "#fed7aa"}`, fontSize: 11 }} onClick={e => { e.stopPropagation(); setNoticeOrderId(o.id); }}>{o.notice_sent_at ? "✉️✓" : "✉️"}</button>}
                  <button className="btn btn-sm" title="Edit Order" style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", fontSize: 11 }} onClick={e => { e.stopPropagation(); openEditOrder(o); }}>✏️</button>
                  {canDelete() && <button className="btn btn-sm" title="Delete Order" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={e => { e.stopPropagation(); deleteOrder(o); }}>✕</button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="empty-state">No orders found. Click "+ New Order" to create one.</td></tr>}
          </tbody>
        </table>
      </div>
    )}
    {showAdd && (
      <div className="modal-overlay" onClick={() => { setShowAdd(false); setLineItems([]); }}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
          <div className="modal-header"><div className="modal-title">New Order</div><button className="modal-close" onClick={() => { setShowAdd(false); setLineItems([]); }}>✕</button></div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-row"><label className="form-label">Customer *</label>
                <select className="form-input" value={form.customer_name} onChange={sf("customer_name")}>
                  <option value="">— Select Customer —</option>
                  {customers.map(c => <option key={c.id} value={c.name}>{c.name}{c.company ? ` (${c.company})` : ""}</option>)}
                </select>
              </div>
              <div className="form-row"><label className="form-label">Status</label>
                <select className="form-input" value={form.status} onChange={sf("status")}><option>Pending</option><option>Processing</option><option>Partial</option><option>Paid</option><option>Cancelled</option></select>
              </div>
              <div className="form-row"><label className="form-label">Order Date</label><input className="form-input" type="date" value={form.date} onChange={sf("date")} /></div>
            </div>

            {/* Line Items from Price Book */}
            <div style={{ marginTop: 16, borderTop: "1px solid var(--parchment-dark)", paddingTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>Line Items (from Price Book)</label>
                <button className="btn btn-sm" style={{ fontSize: 11, background: "#f0fdf4", color: "var(--forest)", border: "1px solid #bbf7d0" }} onClick={() => setShowItemPicker(true)}>+ Add Service / Medication</button>
              </div>

              {lineItems.length > 0 ? (
                <div style={{ border: "1px solid var(--parchment-dark)", borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                    <thead><tr style={{ background: "var(--parchment)", fontSize: 11, fontWeight: 600, color: "var(--text-mid)" }}>
                      <th style={{ padding: "6px 10px", textAlign: "left" }}>Item</th>
                      <th style={{ padding: "6px 10px", width: 60, textAlign: "center" }}>Qty</th>
                      <th style={{ padding: "6px 10px", width: 90, textAlign: "right" }}>Unit Price</th>
                      <th style={{ padding: "6px 10px", width: 90, textAlign: "right" }}>Subtotal</th>
                      <th style={{ padding: "6px 10px", width: 30 }}></th>
                    </tr></thead>
                    <tbody>
                      {lineItems.map((li, idx) => (
                        <tr key={idx} style={{ borderTop: "1px solid var(--parchment-dark)" }}>
                          <td style={{ padding: "6px 10px" }}>
                            <span style={{ fontWeight: 600 }}>{li.name}</span>
                            <span style={{ fontSize: 10, marginLeft: 6, padding: "1px 6px", borderRadius: 8, background: li.type === "Service" ? "#dcfce7" : "#fef3c7", color: li.type === "Service" ? "#166534" : "#92400e" }}>{li.type}</span>
                          </td>
                          <td style={{ padding: "4px 6px", textAlign: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                              <button onClick={() => updateLineQty(idx, li.qty - 1)} style={{ width: 22, height: 22, border: "1px solid #ddd", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 12, lineHeight: "20px" }}>−</button>
                              <span style={{ width: 24, textAlign: "center", fontWeight: 600 }}>{li.qty}</span>
                              <button onClick={() => updateLineQty(idx, li.qty + 1)} style={{ width: 22, height: 22, border: "1px solid #ddd", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 12, lineHeight: "20px" }}>+</button>
                            </div>
                          </td>
                          <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "monospace" }}>${li.price.toFixed(2)}</td>
                          <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "monospace", fontWeight: 700 }}>${(li.qty * li.price).toFixed(2)}</td>
                          <td style={{ padding: "4px" }}><button onClick={() => removeLineItem(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--red)", fontSize: 13 }}>✕</button></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr style={{ borderTop: "2px solid var(--parchment-dark)", background: "#f0fdf4" }}>
                      <td colSpan={3} style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, fontSize: 13 }}>Order Total:</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, fontSize: 14, color: "var(--forest)", fontFamily: "monospace" }}>${lineTotal.toFixed(2)}</td>
                      <td></td>
                    </tr></tfoot>
                  </table>
                </div>
              ) : (
                <div style={{ padding: "16px", textAlign: "center", background: "var(--parchment)", borderRadius: 8, fontSize: 12, color: "var(--text-light)", marginBottom: 8 }}>
                  Click "+ Add Service / Medication" to add items from your price book, or enter a manual total below.
                </div>
              )}
            </div>

            {lineItems.length === 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
                <div className="form-row"><label className="form-label">Manual Total ($)</label><input className="form-input" type="number" value={form.total} onChange={sf("total")} placeholder="0.00" /></div>
                <div className="form-row"><label className="form-label">Description</label><input className="form-input" value={form.description} onChange={sf("description")} placeholder="e.g. Frozen semen – 5 straws" /></div>
              </div>
            )}
            <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={sf("notes")} /></div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={() => { setShowAdd(false); setLineItems([]); }}>Cancel</button><button className="btn btn-primary" onClick={addOrder}>Save Order — {lineItems.length > 0 ? `$${lineTotal.toFixed(2)}` : (form.total ? `$${parseFloat(form.total).toFixed(2)}` : "$0.00")}</button></div>
        </div>
      </div>
    )}

    {/* Item Picker Modal */}
    {showItemPicker && (
      <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setShowItemPicker(false)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440, maxHeight: "70vh" }}>
          <div className="modal-header"><div className="modal-title">Add from Price Book</div><button className="modal-close" onClick={() => setShowItemPicker(false)}>✕</button></div>
          <div className="modal-body" style={{ maxHeight: "50vh", overflowY: "auto", padding: 0 }}>
            {activeItems.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "var(--text-light)" }}>No items in price book. Go to Settings → Price Book to add items.</div>
            ) : (
              <>
                <div style={{ padding: "8px 12px", fontWeight: 700, fontSize: 12, color: "var(--forest)", background: "var(--parchment)", borderBottom: "1px solid var(--parchment-dark)" }}>Services</div>
                {activeItems.filter(i => i.type === "Service").map(item => (
                  <div key={item.id} onClick={() => addLineItem(item)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 14px", borderBottom: "1px solid var(--parchment-dark)", cursor: "pointer", transition: "background 0.1s" }} onMouseOver={e => e.currentTarget.style.background = "#f0fdf4"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>🔧 {item.name}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "var(--forest)", fontFamily: "monospace" }}>${(parseFloat(item.customer_price) || 0).toFixed(2)}</span>
                  </div>
                ))}
                <div style={{ padding: "8px 12px", fontWeight: 700, fontSize: 12, color: "#92400e", background: "#fef3c7", borderBottom: "1px solid var(--parchment-dark)" }}>Medications</div>
                {activeItems.filter(i => i.type === "Medication").map(item => (
                  <div key={item.id} onClick={() => addLineItem(item)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 14px", borderBottom: "1px solid var(--parchment-dark)", cursor: "pointer", transition: "background 0.1s" }} onMouseOver={e => e.currentTarget.style.background = "#fffbeb"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>💊 {item.name}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#92400e", fontFamily: "monospace" }}>${(parseFloat(item.customer_price) || 0).toFixed(2)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    )}
    {/* Edit Order Modal */}
    {editingOrder && !showAdd && (
      <div className="modal-overlay" onClick={() => { setEditingOrder(null); setLineItems([]); }}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
          <div className="modal-header">
            <div className="modal-title">Edit Order — {editingOrder.customer_name}</div>
            <button className="modal-close" onClick={() => { setEditingOrder(null); setLineItems([]); }}>✕</button>
          </div>
          <div className="modal-body">
            {editingOrder.order_type === "semen" && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 14px", background: "var(--parchment)", borderRadius: 8, marginBottom: 14, fontSize: 12.5 }}>
                <span>Mare, tracking, Equitainer # and the rest of the shipment details</span>
                <button className="btn btn-sm btn-outline" onClick={() => { const id = editingOrder.id; setEditingOrder(null); setLineItems([]); setDetailsOrderId(id); }}>Edit semen details</button>
              </div>
            )}
            <div className="form-grid">
              <div className="form-row"><label className="form-label">Customer *</label>
                <select className="form-input" value={form.customer_name} onChange={sf("customer_name")}>
                  <option value="">— Select Customer —</option>
                  {customers.map(c => <option key={c.id} value={c.name}>{c.name}{c.company ? ` (${c.company})` : ""}</option>)}
                </select>
              </div>
              <div className="form-row"><label className="form-label">Status</label>
                <select className="form-input" value={form.status} onChange={sf("status")}><option>Pending</option><option>Processing</option><option>Partial</option><option>Paid</option><option>Cancelled</option></select>
              </div>
              <div className="form-row"><label className="form-label">Order Date</label><input className="form-input" type="date" value={form.date} onChange={sf("date")} /></div>
            </div>

            {/* Line Items */}
            <div style={{ marginTop: 16, borderTop: "1px solid var(--parchment-dark)", paddingTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>Line Items (from Price Book)</label>
                <button className="btn btn-sm" style={{ fontSize: 11, background: "#f0fdf4", color: "var(--forest)", border: "1px solid #bbf7d0" }} onClick={() => setShowItemPicker(true)}>+ Add Service / Medication</button>
              </div>

              {lineItems.length > 0 ? (
                <div style={{ border: "1px solid var(--parchment-dark)", borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                    <thead><tr style={{ background: "var(--parchment)", fontSize: 11, fontWeight: 600, color: "var(--text-mid)" }}>
                      <th style={{ padding: "6px 10px", textAlign: "left" }}>Item</th>
                      <th style={{ padding: "6px 10px", width: 60, textAlign: "center" }}>Qty</th>
                      <th style={{ padding: "6px 10px", width: 90, textAlign: "right" }}>Unit Price</th>
                      <th style={{ padding: "6px 10px", width: 90, textAlign: "right" }}>Subtotal</th>
                      <th style={{ padding: "6px 10px", width: 30 }}></th>
                    </tr></thead>
                    <tbody>
                      {lineItems.map((li, idx) => (
                        <tr key={idx} style={{ borderTop: "1px solid var(--parchment-dark)" }}>
                          <td style={{ padding: "6px 10px" }}>
                            <span style={{ fontWeight: 600 }}>{li.name}</span>
                            <span style={{ fontSize: 10, marginLeft: 6, padding: "1px 6px", borderRadius: 8, background: li.type === "Service" ? "#dcfce7" : "#fef3c7", color: li.type === "Service" ? "#166534" : "#92400e" }}>{li.type}</span>
                          </td>
                          <td style={{ padding: "4px 6px", textAlign: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                              <button onClick={() => updateLineQty(idx, li.qty - 1)} style={{ width: 22, height: 22, border: "1px solid #ddd", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 12, lineHeight: "20px" }}>−</button>
                              <span style={{ width: 24, textAlign: "center", fontWeight: 600 }}>{li.qty}</span>
                              <button onClick={() => updateLineQty(idx, li.qty + 1)} style={{ width: 22, height: 22, border: "1px solid #ddd", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 12, lineHeight: "20px" }}>+</button>
                            </div>
                          </td>
                          <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "monospace" }}>${li.price.toFixed(2)}</td>
                          <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "monospace", fontWeight: 700 }}>${(li.qty * li.price).toFixed(2)}</td>
                          <td style={{ padding: "4px" }}><button onClick={() => removeLineItem(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--red)", fontSize: 13 }}>✕</button></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr style={{ borderTop: "2px solid var(--parchment-dark)", background: "#f0fdf4" }}>
                      <td colSpan={3} style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, fontSize: 13 }}>Order Total:</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, fontSize: 14, color: "var(--forest)", fontFamily: "monospace" }}>${lineTotal.toFixed(2)}</td>
                      <td></td>
                    </tr></tfoot>
                  </table>
                </div>
              ) : (
                <div style={{ padding: "16px", textAlign: "center", background: "var(--parchment)", borderRadius: 8, fontSize: 12, color: "var(--text-light)", marginBottom: 8 }}>
                  Click "+ Add Service / Medication" to add items from your price book, or enter a manual total below.
                </div>
              )}
            </div>

            {lineItems.length === 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
                <div className="form-row"><label className="form-label">Manual Total ($)</label><input className="form-input" type="number" value={form.total} onChange={sf("total")} placeholder="0.00" /></div>
                <div className="form-row"><label className="form-label">Description</label><input className="form-input" value={form.description} onChange={sf("description")} placeholder="e.g. Frozen semen – 5 straws" /></div>
              </div>
            )}
            <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={sf("notes")} /></div>
          </div>
          <div className="modal-footer" style={{ justifyContent: "space-between" }}>
            {canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6", fontSize: 12 }} onClick={() => deleteOrder(editingOrder)}>Delete Order</button>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-outline" onClick={() => { setEditingOrder(null); setLineItems([]); }}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEditOrder}>Save Changes — {lineItems.length > 0 ? `$${lineTotal.toFixed(2)}` : (form.total ? `$${parseFloat(form.total).toFixed(2)}` : "$0.00")}</button>
            </div>
          </div>
        </div>
      </div>
    )}
    {/* Ship from Order Modal */}
    {showShipModal && (
      <div className="modal-overlay" onClick={() => { setShowShipModal(false); setShipRates([]); setShipRateStep("form"); }}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
          <div className="modal-header">
            <div className="modal-title">📦 Create Shipment — {shipForm.recipient_name}</div>
            <button className="modal-close" onClick={() => { setShowShipModal(false); setShipRates([]); setShipRateStep("form"); }}>✕</button>
          </div>
          <div className="modal-body">
            {shipRateStep === "form" && (<>
              {/* Recipient (read-only) */}
              <div style={{ marginBottom: 14 }}>
                <label className="form-label" style={{ fontWeight: 700 }}>Recipient</label>
                <input className="form-input" value={shipForm.recipient_name} readOnly style={{ background: "var(--parchment)", fontWeight: 600 }} />
              </div>

              {/* Ship-to address */}
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 6 }}>Ship-To Address</div>
              <div className="form-row" style={{ marginBottom: 8 }}><input className="form-input" value={shipForm.ship_to_address} onChange={shipSf("ship_to_address")} placeholder="Street address" /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, marginBottom: 14 }}>
                <input className="form-input" value={shipForm.ship_to_city} onChange={shipSf("ship_to_city")} placeholder="City" />
                <input className="form-input" value={shipForm.ship_to_state} onChange={shipSf("ship_to_state")} placeholder="ST" maxLength={2} style={{ width: 55 }} />
                <input className="form-input" value={shipForm.ship_to_zip} onChange={shipSf("ship_to_zip")} placeholder="ZIP" style={{ width: 85 }} />
              </div>

              {/* Package details */}
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 6 }}>Package Details</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                <div className="form-row"><label className="form-label" style={{ fontSize: 11 }}>Weight (oz) *</label><input className="form-input" type="number" value={shipForm.weight_oz} onChange={shipSf("weight_oz")} placeholder="16" /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 11 }}>Length (in)</label><input className="form-input" type="number" value={shipForm.length_in} onChange={shipSf("length_in")} placeholder="12" /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 11 }}>Width (in)</label><input className="form-input" type="number" value={shipForm.width_in} onChange={shipSf("width_in")} placeholder="8" /></div>
                <div className="form-row"><label className="form-label" style={{ fontSize: 11 }}>Height (in)</label><input className="form-input" type="number" value={shipForm.height_in} onChange={shipSf("height_in")} placeholder="6" /></div>
              </div>

              {/* Get Rates button */}
              {hasEasyPost ? (
                <button className="btn" onClick={fetchShipRates} disabled={fetchingRates}
                  style={{ width: "100%", padding: "12px 20px", fontSize: 14, fontWeight: 700, marginBottom: 14, background: "linear-gradient(135deg, #eff6ff, #dbeafe)", color: "#1d4ed8", border: "2px solid #93c5fd", borderRadius: 10, cursor: fetchingRates ? "wait" : "pointer", transition: "all .15s" }}>
                  {fetchingRates ? "⏳ Fetching rates from all carriers…" : "🚚 Get Rates from FedEx, UPS & Unishippers"}
                </button>
              ) : (
                <div style={{ padding: "10px 14px", background: "#fef3c7", borderRadius: 8, fontSize: 12, color: "#92400e", marginBottom: 14, textAlign: "center" }}>
                  ⚠️ Connect EasyPost in Settings → Shipping Carriers to get live rates and print labels
                </div>
              )}

              {/* Contents & notes */}
              <div className="form-grid">
                <div className="form-row"><label className="form-label">📅 Ship Date</label><input className="form-input" type="date" value={shipForm.ship_date} onChange={shipSf("ship_date")} /></div>
                <div className="form-row"><label className="form-label">📅 Est. Delivery</label><input className="form-input" type="date" value={shipForm.estimated_delivery} onChange={shipSf("estimated_delivery")} /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8, marginTop: 8 }}>
                <div className="form-row"><label className="form-label">Carrier</label>
                  <select className="form-input" value={shipForm.carrier} onChange={shipSf("carrier")}>
                    {["FedEx", "UPS", "USPS", "Unishippers", "Other"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-row"><label className="form-label">Tracking #</label><input className="form-input" value={shipForm.tracking_number} onChange={shipSf("tracking_number")} placeholder="If you made the label outside ArcStallion" /></div>
              </div>
              <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Contents</label><input className="form-input" value={shipForm.contents} onChange={shipSf("contents")} placeholder="e.g. Frozen semen – 10 straws" /></div>
              <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={shipForm.notes} onChange={shipSf("notes")} /></div>
            </>)}

            {shipRateStep === "rates" && (<>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => setShipRateStep("form")}>← Back</button>
                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)" }}>Available Rates</div>
                <span style={{ fontSize: 11, color: "var(--text-light)", marginLeft: "auto" }}>{shipRates.length} options</span>
              </div>
              {shipRates.length === 0 ? <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "var(--text-light)" }}>No rates available. Check your EasyPost carrier accounts.</div> : (
                <div style={{ maxHeight: 360, overflowY: "auto" }}>
                  {shipRates.map((r, idx) => (
                    <div key={r.id || idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 8, border: "1px solid var(--parchment-dark)", marginBottom: 6, transition: "all .15s" }} onMouseOver={e => e.currentTarget.style.background = "#f0fdf4"} onMouseOut={e => e.currentTarget.style.background = "#fff"}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{r.carrier} — {r.service}</div>
                        <div style={{ fontSize: 11, color: "var(--text-light)" }}>{r.delivery_days ? `${r.delivery_days} business day${r.delivery_days > 1 ? "s" : ""}` : "Delivery time varies"}</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontWeight: 700, fontSize: 16, color: "var(--forest)", fontFamily: "monospace" }}>${parseFloat(r.rate).toFixed(2)}</span>
                        <button className="btn btn-primary" style={{ fontSize: 11, padding: "6px 14px" }} onClick={() => buyShipLabel(r)} disabled={buyingLabel}>{buyingLabel ? "Processing…" : "Buy Label"}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>)}

            {shipRateStep === "done" && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 10 }}>✅</div>
                <div style={{ fontWeight: 700, fontSize: 18, color: "var(--forest)", marginBottom: 6 }}>Label Created!</div>
                <div style={{ fontSize: 13, color: "var(--text-mid)", marginBottom: 4 }}>{shipForm.carrier} — {shipForm.service_level}</div>
                {shipForm.tracking_number && <div style={{ fontSize: 12, fontFamily: "monospace", background: "var(--parchment)", padding: "6px 12px", borderRadius: 6, display: "inline-block", marginBottom: 12 }}>Tracking: {shipForm.tracking_number}</div>}
                {shipForm.label_url && (
                  <div style={{ marginTop: 12 }}>
                    <a href={shipForm.label_url} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ fontSize: 13, padding: "10px 24px", textDecoration: "none", display: "inline-block" }}>🖨️ Print Label</a>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            {shipRateStep === "form" && (<>
              <button className="btn btn-outline" onClick={() => { setShowShipModal(false); setShipRates([]); setShipRateStep("form"); }}>Cancel</button>
              <button className="btn btn-primary" onClick={saveShipment}>Save Shipment</button>
            </>)}
            {shipRateStep === "done" && <button className="btn btn-primary" onClick={saveShipment}>{shipForm.id ? "Done" : "Save & Close"}</button>}
          </div>
        </div>
      </div>
    )}
    {noticeOrderId && (
      <ShipmentNoticeModal orderId={noticeOrderId} onClose={() => setNoticeOrderId(null)}
        onSent={(sentAt, to) => { setOrders(p => p.map(o => o.id === noticeOrderId ? { ...o, notice_sent_at: sentAt } : o)); setNoticeToast(to || "customer"); setTimeout(() => setNoticeToast(""), 3500); }}
        onOrderUpdated={u => setOrders(p => p.map(o => o.id === noticeOrderId ? { ...o, ...u } : o))} />
    )}
    {detailsOrderId && (
      <SemenDetailsModal orderId={detailsOrderId} onClose={() => setDetailsOrderId(null)}
        onSaved={u => { setOrders(p => p.map(o => o.id === detailsOrderId ? { ...o, ...u } : o)); save(); }} />
    )}
    <SaveToast show={saved} />
    <SaveToast show={!!noticeToast} text={`Shipment notice sent to ${noticeToast}`} />
  </>);
}

// ─── AI ADVISOR ───────────────────────────────────────────────────────────────
function AIAdvisor({ orgId }) {
  const { data: mares } = useSupabaseTable("mares", orgId);
  const { data: semen } = useSupabaseTable("frozen_semen", orgId);
  const [q, setQ] = useState(""); const [resp, setResp] = useState(""); const [loading, setLoading] = useState(false);
  const chips = ["Which mares are ready for breeding?", "Assess frozen semen inventory status", "Recommend management priorities this week", "Analyze pregnancy rate performance"];
  const run = async p => {
    const ask = p || q; if (!ask.trim()) return;
    setLoading(true); setResp("");
    try {
      const ctx = `Mares: ${mares.length} total, ${mares.filter(m => m.status === "Pregnant").length} pregnant, ${mares.filter(m => m.status === "In Cycle").length} in cycle. Frozen semen: ${semen.reduce((a, s) => a + (s.straws_available || 0), 0)} straws available across ${semen.length} batches.`;
      setResp(await askClaude(`${ask}\n\nFarm data: ${ctx}`));
    } catch { setResp("Could not reach AI."); }
    setLoading(false);
  };
  return (<>
    <div className="page-header-row"></div>
    <div style={{ background: "linear-gradient(135deg,#0d1f0d,#1a2e3a)", border: "1px solid rgba(201,168,76,.3)", borderRadius: 8, overflow: "hidden", marginBottom: 22 }}>
      <div style={{ padding: "13px 18px", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid rgba(201,168,76,.2)" }}>
        <span style={{ fontSize: 19 }}>🤖</span>
        <div><div style={{ fontFamily: "Playfair Display,serif", color: "var(--gold-light)", fontSize: 14.5, fontWeight: 600 }}>ArcStallion AI Advisor</div><div style={{ fontSize: 11, color: "rgba(201,168,76,.5)", fontStyle: "italic" }}>Ask anything about your breeding program</div></div>
      </div>
      <div style={{ padding: "14px 18px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 12 }}>{chips.map((c, i) => <div key={i} style={{ padding: "5px 11px", background: "rgba(201,168,76,.12)", border: "1px solid rgba(201,168,76,.25)", borderRadius: 20, fontSize: 12, color: "var(--gold-light)", cursor: "pointer" }} onClick={() => { setQ(c); run(c); }}>{c}</div>)}</div>
        <div style={{ display: "flex", gap: 9, marginBottom: 12 }}>
          <input style={{ flex: 1, padding: "8px 12px", background: "rgba(255,255,255,.08)", border: "1px solid rgba(201,168,76,.25)", borderRadius: 4, color: "var(--parchment)", fontFamily: "Crimson Pro,serif", fontSize: 13 }} value={q} onChange={e => setQ(e.target.value)} placeholder="Ask anything about your breeding program…" onKeyDown={e => e.key === "Enter" && run()} />
          <button className="btn btn-gold" onClick={() => run()} disabled={loading}>{loading ? "Thinking…" : "Ask AI"}</button>
        </div>
        {(resp || loading) && <div style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(201,168,76,.15)", borderRadius: 6, padding: "12px 14px", fontSize: 13, color: "var(--parchment)", lineHeight: 1.75, whiteSpace: "pre-wrap", fontFamily: "Crimson Pro,serif" }}>{loading ? "Consulting veterinary AI…" : resp}</div>}
      </div>
    </div>
  </>);
}

// ─── BREEDING MANAGEMENT ──────────────────────────────────────────────────────
function BreedingManagement({ orgId }) {
  const { data: mares } = useSupabaseTable("mares", orgId);
  const { data: stallions } = useSupabaseTable("stallions", orgId);
  const { data: allBreedingEvents, setData: setBreedingEvents } = useSupabaseTable("breeding_events", orgId);
  // Scheduled checks are plans, not history (see MARE GROUPS & SCHEDULED CHECKS).
  const breedingEvents = allBreedingEvents.filter(e => !isScheduled(e));
  const [selectedMare, setSelectedMare] = useState(null);
  const [activeProtocol, setActiveProtocol] = useState(null);
  const [saved, setSaved] = useState(false);
  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const today = new Date();
  const todayISO = localToday(today);

  // Get events for selected mare
  const mareEvents = selectedMare
    ? breedingEvents.filter(e => e.mare_id === selectedMare.id).sort((a, b) => (b.event_date + (b.event_time || "")).localeCompare(a.event_date + (a.event_time || "")))
    : breedingEvents.sort((a, b) => (b.event_date + (b.event_time || "")).localeCompare(a.event_date + (a.event_time || "")));

  // Stats
  const activeMares = mares.filter(m => m.status === "In Cycle" || m.status === "Pregnant" || m.status === "Flush Scheduled");
  const pregnantCount = mares.filter(m => m.status === "Pregnant").length;
  const inCycleCount = mares.filter(m => m.status === "In Cycle").length;
  const todayEvents = breedingEvents.filter(e => e.event_date === todayISO);

  // Get upcoming protocol steps (mares needing attention)
  const needsAttention = mares.filter(m => {
    const lastEvent = breedingEvents.filter(e => e.mare_id === m.id).sort((a, b) => b.event_date.localeCompare(a.event_date))[0];
    if (!lastEvent) return m.status === "In Cycle";
    const daysSince = Math.floor((today - new Date(lastEvent.event_date)) / 86400000);
    if (m.status === "In Cycle" && daysSince >= 2) return true;
    if (lastEvent.event_type === "breeding" && daysSince >= 12 && daysSince <= 18) return true;
    return false;
  });

  return (<>
    <div className="page-header-row">
      
      {saved && <span style={{ fontSize: 12, color: "var(--forest)", fontStyle: "italic" }}>✓ Saved</span>}
    </div>

    <div className="stats-row" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
      <div className="stat-card"><div className="stat-icon">🐴</div><div className="stat-label">Active Mares</div><div className="stat-value">{activeMares.length}</div><div className="stat-meta">In breeding program</div></div>
      <div className="stat-card green"><div className="stat-icon">🤰</div><div className="stat-label">Pregnant</div><div className="stat-value">{pregnantCount}</div><div className="stat-meta">{mares.length ? Math.round(pregnantCount / mares.length * 100) : 0}% rate</div></div>
      <div className="stat-card blue"><div className="stat-icon">🔄</div><div className="stat-label">In Cycle</div><div className="stat-value">{inCycleCount}</div><div className="stat-meta">Monitoring</div></div>
      <div className="stat-card red"><div className="stat-icon">📋</div><div className="stat-label">Today's Events</div><div className="stat-value">{todayEvents.length}</div><div className="stat-meta">{todayISO}</div></div>
    </div>

    <div className="breed-mgmt-grid">
      {/* Left: Mare list */}
      <div className="panel" style={{ height: "fit-content" }}>
        <div className="panel-header"><span className="panel-title">Mares</span></div>
        <div style={{ padding: "8px" }}>
          <div style={{ padding: "4px 8px 10px", cursor: "pointer", borderRadius: 6, background: !selectedMare ? "var(--parchment)" : "transparent", marginBottom: 2, fontSize: 13, fontWeight: !selectedMare ? 700 : 400, color: "var(--forest)" }}
            onClick={() => setSelectedMare(null)}>📋 All Events ({breedingEvents.length})</div>
          {mares.map(m => {
            const evCount = breedingEvents.filter(e => e.mare_id === m.id).length;
            const needsAtt = needsAttention.includes(m);
            return (
              <div key={m.id}
                style={{ padding: "8px 10px", cursor: "pointer", borderRadius: 6, background: selectedMare?.id === m.id ? "var(--parchment)" : "transparent", marginBottom: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}
                onClick={() => { setSelectedMare(m); setActiveProtocol(null); }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--forest)" }}>{needsAtt ? "⚠️ " : ""}{m.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-light)" }}>{m.status} · {evCount} events</div>
                </div>
                <StatusBadge status={m.status} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: Content area */}
      <div>
        {/* Needs attention alerts */}
        {needsAttention.length > 0 && !selectedMare && (
          <div className="panel" style={{ marginBottom: 18, borderLeft: "3px solid var(--red)" }}>
            <div className="panel-header"><span className="panel-title">⚠️ Needs Attention</span></div>
            <div style={{ padding: "10px 16px" }}>
              {needsAttention.map(m => {
                const lastEv = breedingEvents.filter(e => e.mare_id === m.id).sort((a, b) => b.event_date.localeCompare(a.event_date))[0];
                const daysSince = lastEv ? Math.floor((today - new Date(lastEv.event_date)) / 86400000) : null;
                return (
                  <div key={m.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--parchment-dark)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--forest)" }}>{m.name}</div>
                      <div style={{ fontSize: 12, color: "var(--text-mid)" }}>
                        {lastEv ? `Last: ${EVENT_LABELS[lastEv.event_type]} (${daysSince}d ago)` : "No events logged — needs initial check"}
                        {lastEv?.event_type === "breeding" && daysSince >= 12 ? " · Due for pregnancy check" : ""}
                      </div>
                    </div>
                    <button className="btn btn-sm" onClick={() => setSelectedMare(m)}>View →</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Protocol templates (when mare selected) */}
        {selectedMare && (
          <div className="panel" style={{ marginBottom: 18 }}>
            <div className="panel-header"><span className="panel-title">🧬 Protocol for {selectedMare.name}</span></div>
            <div style={{ padding: "12px 16px" }}>
              {!activeProtocol ? (<>
                <div style={{ fontSize: 12.5, color: "var(--text-mid)", marginBottom: 12 }}>Select a breeding protocol template:</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {Object.entries(PROTOCOL_TEMPLATES).map(([key, tmpl]) => (
                    <div key={key} className="breed-protocol-card" onClick={() => setActiveProtocol(key)}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--forest)", marginBottom: 4 }}>{tmpl.name}</div>
                      <div style={{ fontSize: 12, color: "var(--text-light)" }}>{tmpl.steps.length} steps</div>
                    </div>
                  ))}
                </div>
              </>) : (<>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--forest)" }}>{PROTOCOL_TEMPLATES[activeProtocol].name}</div>
                  <button className="btn btn-sm btn-outline" onClick={() => setActiveProtocol(null)}>← Change Protocol</button>
                </div>
                {PROTOCOL_TEMPLATES[activeProtocol].steps.map((step, idx) => {
                  const matchingEvent = mareEvents.find(e => e.event_type === step.type && e.notes?.includes(step.label.substring(0, 20)));
                  const typeEvents = mareEvents.filter(e => e.event_type === step.type);
                  const isDone = step.type === "pregnancy_check" ? typeEvents.some(e => e.pregnancy_status?.includes("Pregnant")) :
                    step.type === "breeding" ? typeEvents.length > 0 :
                    step.type === "medication" ? typeEvents.some(e => e.medication_name) :
                    typeEvents.length >= (idx < 2 ? 1 : 0);
                  return (
                    <div key={idx} className="breed-protocol-step">
                      <div className={`breed-step-check ${isDone ? "done" : ""}`}>{isDone ? "✓" : idx + 1}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: isDone ? "var(--text-light)" : "var(--forest)", textDecoration: isDone ? "line-through" : "none" }}>{step.label}</div>
                        <div style={{ fontSize: 11.5, color: "var(--text-light)", marginTop: 2 }}>{EVENT_ICONS[step.type]} {step.day}</div>
                      </div>
                    </div>
                  );
                })}
              </>)}
            </div>
          </div>
        )}

        {/* Event timeline */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-title">{selectedMare ? `📅 ${selectedMare.name}'s Breeding Log` : "📅 All Breeding Events"}</span>
            <span style={{ fontSize: 12, color: "var(--text-light)" }}>{mareEvents.length} events</span>
          </div>
          <div style={{ padding: "12px 16px" }}>
            {mareEvents.length === 0 ? (
              <div className="empty-state" style={{ padding: "20px 0" }}>No breeding events logged{selectedMare ? ` for ${selectedMare.name}` : ""}. Log events from the Mare Management page.</div>
            ) : mareEvents.map(ev => {
              const mareName = !selectedMare ? mares.find(m => m.id === ev.mare_id)?.name : null;
              return (
                <div key={ev.id} className={`breed-event ${ev.event_type}`}>
                  <div className="breed-event-date">
                    {ev.event_date}{ev.event_time ? ` · ${ev.event_time}` : ""}
                    {ev.performed_by ? ` · ${ev.performed_by}` : ""}
                    {mareName ? <span style={{ fontWeight: 700, marginLeft: 8 }}>🐴 {mareName}</span> : ""}
                  </div>
                  <div className="breed-event-title">{EVENT_ICONS[ev.event_type] || "📝"} {EVENT_LABELS[ev.event_type] || ev.event_type}
                    {ev.event_type === "teasing" && ev.tease_score ? ` — Score ${ev.tease_score}/5` : ""}
                    {ev.event_type === "palpation" && ev.follicle_size ? ` — ${ev.follicle_size}` : ""}
                    {ev.event_type === "breeding" ? ` — ${ev.stallion_name || ""}${ev.method ? ` (${ev.method})` : ""}` : ""}
                    {ev.event_type === "medication" ? ` — ${ev.medication_name || ""}${ev.dosage ? ` ${ev.dosage}` : ""}` : ""}
                    {ev.event_type === "pregnancy_check" && ev.pregnancy_status ? ` — ${ev.pregnancy_status}` : ""}
                    {ev.event_type === "icsi" ? ` — ${ev.stallion_name || "ICSI"}` : ""}
                  </div>
                  {ev.notes && <div className="breed-event-detail">{ev.notes}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    <SaveToast show={saved} />
  </>);
}

// ─── SHIPPING ─────────────────────────────────────────────────────────────────
function Shipping({ orgId }) {
  const { data: shipments, setData: setShipments, loading } = useShipments(orgId);
  const { data: customers } = useSupabaseTable("customers", orgId);
  const { data: integrations } = useSupabaseTable("org_integrations", orgId, [], INTEGRATION_COLUMNS);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [showAdd, setShowAdd] = useState(false);
  const [editingShipment, setEditingShipment] = useState(null);
  const [saved, setSaved] = useState(false);
  const [custSearch, setCustSearch] = useState("");
  const [showCustPicker, setShowCustPicker] = useState(false);
  const [rates, setRates] = useState([]);
  const [fetchingRates, setFetchingRates] = useState(false);
  const [buyingLabel, setBuyingLabel] = useState(false);
  const [rateStep, setRateStep] = useState("form"); // form | rates | done
  const todayISO = localToday();
  const emptyForm = { recipient_name: "", customer_id: null, tracking_number: "", carrier: "FedEx", status: "Label Created", ship_date: todayISO, estimated_delivery: "", contents: "", notes: "",
    ship_to_address: "", ship_to_city: "", ship_to_state: "", ship_to_zip: "", ship_to_country: "US", ship_to_phone: "",
    weight_oz: "", length_in: "", width_in: "", height_in: "", service_level: "", label_url: "", easypost_shipment_id: "", easypost_rate_id: "", rate_amount: "" };
  const [form, setForm] = useState(emptyForm);

  const shipInt = integrations.find(i => i.provider_type === "shipping");
  const shipFrom = shipInt?.config?.ship_from || {};
  // The key is server-side now; the browser only needs to know one is stored.
  // The second clause covers a farm whose key has not been migrated yet.
  const hasEasyPost = !!(shipInt?.config?.secrets_present?.easypost_api_key || shipInt?.config?.easypost_api_key);

  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const selectCustomer = (c) => {
    setForm(p => ({
      ...p, recipient_name: c.name, customer_id: c.id,
      ship_to_address: c.shipping_address || c.address || "",
      ship_to_city: c.shipping_city || c.city || "",
      ship_to_state: c.shipping_state || c.state || "",
      ship_to_zip: c.shipping_zip || c.zip || "",
      ship_to_phone: c.phone || ""
    }));
    setShowCustPicker(false); setCustSearch("");
  };

  const filteredCust = customers.filter(c => c.name.toLowerCase().includes(custSearch.toLowerCase()) || (c.company || "").toLowerCase().includes(custSearch.toLowerCase()));

  const fetchRates = async () => {
    if (!hasEasyPost) { alert("Please configure your EasyPost API key in Settings → Shipping Carriers first."); return; }
    if (!form.ship_to_address || !form.ship_to_zip || !form.weight_oz) { alert("Please fill in the shipping address and package weight."); return; }
    if (!shipFrom.address || !shipFrom.zip) { alert("Please configure your ship-from address in Settings → Shipping Carriers first."); return; }
    setFetchingRates(true); setRates([]);
    try {
      const data = await callFunction("easypost", {
        action: "rates",
        shipment: {
            to_address: { name: form.recipient_name, street1: form.ship_to_address, city: form.ship_to_city, state: form.ship_to_state, zip: form.ship_to_zip, country: form.ship_to_country || "US", phone: form.ship_to_phone },
            from_address: { name: shipFrom.name || "", company: shipFrom.company || "", street1: shipFrom.address, city: shipFrom.city, state: shipFrom.state, zip: shipFrom.zip, country: "US", phone: shipFrom.phone || "" },
            parcel: { weight: parseFloat(form.weight_oz) || 1, length: parseFloat(form.length_in) || null, width: parseFloat(form.width_in) || null, height: parseFloat(form.height_in) || null }
          },
      });
      setForm(p => ({ ...p, easypost_shipment_id: data.id }));
      const carrierFilter = ["FedEx", "UPS", "Unishippers", "USPS"];
      const filtered = (data.rates || []).filter(r => carrierFilter.some(cf => r.carrier.toLowerCase().includes(cf.toLowerCase()))).sort((a, b) => parseFloat(a.rate) - parseFloat(b.rate));
      setRates(filtered.length > 0 ? filtered : data.rates || []);
      setRateStep("rates");
    } catch (e) { alert("Error fetching rates: " + e.message); }
    setFetchingRates(false);
  };

  const buyLabel = async (rate) => {
    setBuyingLabel(true);
    try {
      const data = await callFunction("easypost", {
        action: "buy",
        shipment_id: form.easypost_shipment_id,
        rate_id: rate.id,
      });
      // callFunction throws on a non-2xx, so an error no longer arrives in the
      // body. The function returns a flattened shape, not EasyPost's raw one.
      const labelUrl = data.label_url || "";
      const trackingNum = data.tracking_code || "";
      setForm(p => ({ ...p, label_url: labelUrl, tracking_number: trackingNum, carrier: rate.carrier, service_level: rate.service, rate_amount: rate.rate, easypost_rate_id: rate.id, status: "Label Created" }));
      setRateStep("done");
    } catch (e) { alert("Error buying label: " + e.message); }
    setBuyingLabel(false);
  };

  const addShipment = async () => {
    if (!form.recipient_name.trim()) return;
    try {
      const row = await dbInsert("shipments", { id: crypto.randomUUID(), ...shipmentRowFromForm(form), org_id: orgId });
      setShipments(p => [row, ...p]);
      setShowAdd(false); setForm(emptyForm); setRates([]); setRateStep("form");
      save();
    } catch (e) { alert("Error saving shipment: " + e.message); }
  };

  const openEdit = (s) => {
    setEditingShipment(s);
    setForm({ ...emptyForm, ...Object.fromEntries(Object.entries(s).filter(([_, v]) => v != null).map(([k, v]) => [k, String(v)])), customer_id: s.customer_id });
    setRates([]); setRateStep("form");
  };

  const saveEdit = async () => {
    if (!editingShipment) return;
    try {
      const saveData = shipmentRowFromForm(form);
      await dbUpdate("shipments", editingShipment.id, saveData);
      // Keep the order's tracking in step, since the shipment notice reads it.
      if (saveData.order_id && saveData.tracking_number) {
        try { await dbUpdate("orders", saveData.order_id, { tracking_number: saveData.tracking_number }); }
        catch (e) { console.error("[Shipping] order tracking not updated", e); }
      }
      setShipments(p => p.map(s => s.id === editingShipment.id ? { ...s, ...saveData } : s));
      setEditingShipment(null); setForm(emptyForm); setRates([]); setRateStep("form");
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const deleteShipment = async (s) => {
    if (!window.confirm(`Delete shipment for ${s.recipient_name}?`)) return;
    try { await dbDelete("shipments", s.id); } catch (e) { alert(e.message); return; }
    setShipments(p => p.filter(x => x.id !== s.id));
    if (editingShipment?.id === s.id) { setEditingShipment(null); setForm(emptyForm); }
    save();
  };

  const CARRIERS = ["FedEx", "UPS", "Unishippers"];
  const STATUSES = ["Label Created", "In Transit", "Out for Delivery", "Delivered", "Exception"];
  const STATUS_COLOR = { "Label Created": "#e8f0ff", "In Transit": "#e8f4e8", "Out for Delivery": "#fff8e0", "Delivered": "#e8f8ef", "Exception": "#fde8e8" };
  const STATUS_TEXT = { "Label Created": "var(--blue)", "In Transit": "var(--forest)", "Out for Delivery": "#b8860b", "Delivered": "#1a7f50", "Exception": "var(--red)" };
  const CARRIER_ICON = { FedEx: "🟣", UPS: "🟤", Unishippers: "🔵" };

  const filtered = shipments.filter(s => {
    const matchSearch = (s.recipient_name + (s.tracking_number || "") + (s.contents || "")).toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "All" || s.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const inTransit = shipments.filter(s => s.status === "In Transit" || s.status === "Label Created" || s.status === "Out for Delivery").length;
  const delivered = shipments.filter(s => s.status === "Delivered").length;
  const exceptions = shipments.filter(s => s.status === "Exception").length;

  const renderShipmentForm = (isEdit) => (
    <div className="modal-body">
      {rateStep === "form" && (<>
        {/* Customer search */}
        <div style={{ marginBottom: 14 }}>
          <label className="form-label" style={{ fontWeight: 700 }}>Recipient *</label>
          <div style={{ position: "relative" }}>
            <input className="form-input" value={form.recipient_name} onChange={e => { sf("recipient_name")(e); setCustSearch(e.target.value); setShowCustPicker(true); }} placeholder="Search customers…" onFocus={() => setShowCustPicker(true)} />
            {showCustPicker && custSearch.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid var(--parchment-dark)", borderRadius: 8, maxHeight: 180, overflowY: "auto", zIndex: 20, boxShadow: "0 4px 16px rgba(0,0,0,.12)" }}>
                {filteredCust.length === 0 ? <div style={{ padding: 12, fontSize: 12, color: "var(--text-light)" }}>No matching customers</div> : filteredCust.slice(0, 8).map(c => (
                  <div key={c.id} onClick={() => selectCustomer(c)} style={{ padding: "8px 14px", cursor: "pointer", borderBottom: "1px solid #f5f5f5", transition: "background .1s" }} onMouseOver={e => e.currentTarget.style.background = "#f0fdf4"} onMouseOut={e => e.currentTarget.style.background = "#fff"}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}{c.company ? ` · ${c.company}` : ""}</div>
                    <div style={{ fontSize: 11, color: "var(--text-light)" }}>{[c.shipping_address || c.address, c.shipping_city || c.city, c.shipping_state || c.state, c.shipping_zip || c.zip].filter(Boolean).join(", ") || "No address"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Ship-to address */}
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 6 }}>Ship-To Address</div>
        <div className="form-row" style={{ marginBottom: 8 }}><input className="form-input" value={form.ship_to_address} onChange={sf("ship_to_address")} placeholder="Street address" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, marginBottom: 14 }}>
          <input className="form-input" value={form.ship_to_city} onChange={sf("ship_to_city")} placeholder="City" />
          <input className="form-input" value={form.ship_to_state} onChange={sf("ship_to_state")} placeholder="ST" maxLength={2} style={{ width: 55 }} />
          <input className="form-input" value={form.ship_to_zip} onChange={sf("ship_to_zip")} placeholder="ZIP" style={{ width: 85 }} />
        </div>

        {/* Package details */}
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 6 }}>Package Details</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
          <div className="form-row"><label className="form-label" style={{ fontSize: 11 }}>Weight (oz) *</label><input className="form-input" type="number" value={form.weight_oz} onChange={sf("weight_oz")} placeholder="16" /></div>
          <div className="form-row"><label className="form-label" style={{ fontSize: 11 }}>Length (in)</label><input className="form-input" type="number" value={form.length_in} onChange={sf("length_in")} placeholder="12" /></div>
          <div className="form-row"><label className="form-label" style={{ fontSize: 11 }}>Width (in)</label><input className="form-input" type="number" value={form.width_in} onChange={sf("width_in")} placeholder="8" /></div>
          <div className="form-row"><label className="form-label" style={{ fontSize: 11 }}>Height (in)</label><input className="form-input" type="number" value={form.height_in} onChange={sf("height_in")} placeholder="6" /></div>
        </div>

        {/* Get Rates button */}
        {hasEasyPost && (
          <button className="btn" onClick={fetchRates} disabled={fetchingRates}
            style={{ width: "100%", padding: "12px 20px", fontSize: 14, fontWeight: 700, marginBottom: 16, background: "linear-gradient(135deg, #eff6ff, #dbeafe)", color: "#1d4ed8", border: "2px solid #93c5fd", borderRadius: 10, cursor: fetchingRates ? "wait" : "pointer", transition: "all .15s" }}>
            {fetchingRates ? "⏳ Fetching rates from all carriers…" : "🚚 Get Rates from FedEx, UPS & Unishippers"}
          </button>
        )}
        {!hasEasyPost && (
          <div style={{ padding: "10px 14px", background: "#fef3c7", borderRadius: 8, fontSize: 12, color: "#92400e", marginBottom: 14, textAlign: "center" }}>
            ⚠️ Connect EasyPost in Settings → Shipping Carriers to get live rates and print labels
          </div>
        )}

        {/* Shipment info */}
        <div className="form-grid">
          <div className="form-row"><label className="form-label">Carrier</label>
            <select className="form-input" value={form.carrier} onChange={sf("carrier")}>{CARRIERS.map(c => <option key={c}>{c}</option>)}</select>
          </div>
          <div className="form-row"><label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={sf("status")}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select>
          </div>
          <div className="form-row"><label className="form-label">📅 Ship Date</label><input className="form-input" type="date" value={form.ship_date} onChange={sf("ship_date")} /></div>
          <div className="form-row"><label className="form-label">📅 Est. Delivery</label><input className="form-input" type="date" value={form.estimated_delivery} onChange={sf("estimated_delivery")} /></div>
        </div>
        <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Tracking Number</label><input className="form-input" value={form.tracking_number} onChange={sf("tracking_number")} placeholder="Auto-filled when label is purchased" /></div>
        <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Contents</label><input className="form-input" value={form.contents} onChange={sf("contents")} placeholder="e.g. Frozen semen – 10 straws" /></div>
        <div className="form-row" style={{ marginTop: 8 }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={sf("notes")} /></div>
      </>)}

      {rateStep === "rates" && (<>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => setRateStep("form")}>← Back</button>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)" }}>Available Rates</div>
          <span style={{ fontSize: 11, color: "var(--text-light)", marginLeft: "auto" }}>{rates.length} options</span>
        </div>
        {rates.length === 0 ? <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "var(--text-light)" }}>No rates available for this shipment. Check your EasyPost carrier accounts.</div> : (
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {rates.map((r, idx) => (
              <div key={r.id || idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 8, border: "1px solid var(--parchment-dark)", marginBottom: 6, cursor: "pointer", transition: "all .15s" }} onMouseOver={e => e.currentTarget.style.background = "#f0fdf4"} onMouseOut={e => e.currentTarget.style.background = "#fff"}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{r.carrier} — {r.service}</div>
                  <div style={{ fontSize: 11, color: "var(--text-light)" }}>{r.delivery_days ? `${r.delivery_days} business day${r.delivery_days > 1 ? "s" : ""}` : "Delivery time varies"}{r.est_delivery_days ? ` · Est. ${r.est_delivery_days} days` : ""}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 16, color: "var(--forest)", fontFamily: "monospace" }}>${parseFloat(r.rate).toFixed(2)}</span>
                  <button className="btn btn-primary" style={{ fontSize: 11, padding: "6px 14px" }} onClick={() => buyLabel(r)} disabled={buyingLabel}>{buyingLabel ? "Processing…" : "Buy Label"}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </>)}

      {rateStep === "done" && (<>
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>✅</div>
          <div style={{ fontWeight: 700, fontSize: 18, color: "var(--forest)", marginBottom: 6 }}>Label Created!</div>
          <div style={{ fontSize: 13, color: "var(--text-mid)", marginBottom: 4 }}>{form.carrier} — {form.service_level}</div>
          {form.tracking_number && <div style={{ fontSize: 12, fontFamily: "monospace", background: "var(--parchment)", padding: "6px 12px", borderRadius: 6, display: "inline-block", marginBottom: 12 }}>Tracking: {form.tracking_number}</div>}
          {form.label_url && (
            <div style={{ marginTop: 12 }}>
              <a href={form.label_url} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ fontSize: 13, padding: "10px 24px", textDecoration: "none", display: "inline-block" }}>🖨️ Print Label</a>
            </div>
          )}
        </div>
      </>)}
    </div>
  );

  return (<>
    <div className="page-header-row">
      
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {saved && <span style={{ fontSize: 12, color: "var(--forest)", fontStyle: "italic" }}>✓ Saved</span>}
        {!hasEasyPost && <span style={{ fontSize: 11, padding: "4px 12px", borderRadius: 6, background: "#fef3c7", color: "#92400e", fontWeight: 500 }}>⚠️ Configure EasyPost in Settings</span>}
        <button className="btn btn-primary" onClick={() => { setShowAdd(true); setForm({ ...emptyForm }); setRates([]); setRateStep("form"); }}>+ New Shipment</button>
      </div>
    </div>
    <div className="stats-row" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
      <div className="stat-card"><div className="stat-icon">📦</div><div className="stat-label">Total Shipments</div><div className="stat-value">{shipments.length}</div><div className="stat-meta">This season</div></div>
      <div className="stat-card blue"><div className="stat-icon">🚚</div><div className="stat-label">In Transit</div><div className="stat-value">{inTransit}</div><div className="stat-meta">Active shipments</div></div>
      <div className="stat-card green"><div className="stat-icon">✅</div><div className="stat-label">Delivered</div><div className="stat-value">{delivered}</div><div className="stat-meta">Completed</div></div>
      <div className="stat-card red"><div className="stat-icon">⚠️</div><div className="stat-label">Exceptions</div><div className="stat-value">{exceptions}</div><div className="stat-meta">Needs attention</div></div>
    </div>
    {loading ? <div className="empty-state">Loading shipments…</div> : (
      <div className="full-panel">
        <div className="filter-bar" style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input className="search-input" placeholder="Search by recipient, tracking, or contents…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
          <select className="form-input" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="All">All Statuses</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        {filtered.length === 0
          ? <div className="empty-state">No shipments found. Click "+ New Shipment" to create one.</div>
          : filtered.map(s => (
            <div className="shipment-row" key={s.id} onClick={() => openEdit(s)} style={{ cursor: "pointer", transition: "background .1s" }} onMouseOver={e => e.currentTarget.style.background = "#f8f6f0"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ fontSize: 22 }}>{CARRIER_ICON[s.carrier] || "📦"}</div>
              <div className="shipment-info">
                <div className="shipment-name">{s.recipient_name}</div>
                <div className="shipment-detail">{s.carrier}{s.service_level ? ` · ${s.service_level}` : ""}{s.contents ? ` · ${s.contents}` : ""}{s.ship_date ? ` · Shipped ${s.ship_date}` : ""}{s.rate_amount ? ` · $${parseFloat(s.rate_amount).toFixed(2)}` : ""}</div>
                {s.tracking_number && <div className="shipment-tracking">{s.tracking_number}</div>}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                <span style={{ background: STATUS_COLOR[s.status] || "#eee", color: STATUS_TEXT[s.status] || "#333", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20, whiteSpace: "nowrap" }}>{s.status}</span>
                <div style={{ display: "flex", gap: 4 }}>
                  {s.label_url && <a href={s.label_url} target="_blank" rel="noreferrer" className="btn btn-sm" style={{ fontSize: 10, background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", textDecoration: "none" }} onClick={e => e.stopPropagation()}>🖨️ Label</a>}
                  {canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6", fontSize: 11 }} onClick={e => { e.stopPropagation(); deleteShipment(s); }}>✕</button>}
                </div>
              </div>
            </div>
          ))
        }
      </div>
    )}

    {/* New Shipment Modal */}
    {showAdd && (
      <div className="modal-overlay" onClick={() => { setShowAdd(false); setRates([]); setRateStep("form"); }}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
          <div className="modal-header"><div className="modal-title">New Shipment</div><button className="modal-close" onClick={() => { setShowAdd(false); setRates([]); setRateStep("form"); }}>✕</button></div>
          {renderShipmentForm(false)}
          <div className="modal-footer">
            {rateStep === "form" && (<>
              <button className="btn btn-outline" onClick={() => { setShowAdd(false); setRates([]); setRateStep("form"); }}>Cancel</button>
              <button className="btn btn-primary" onClick={addShipment}>Save Shipment</button>
            </>)}
            {rateStep === "done" && <button className="btn btn-primary" onClick={addShipment}>Save & Close</button>}
          </div>
        </div>
      </div>
    )}

    {/* Edit Shipment Modal */}
    {editingShipment && !showAdd && (
      <div className="modal-overlay" onClick={() => { setEditingShipment(null); setRates([]); setRateStep("form"); }}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
          <div className="modal-header"><div className="modal-title">Edit Shipment — {editingShipment.recipient_name}</div><button className="modal-close" onClick={() => { setEditingShipment(null); setRates([]); setRateStep("form"); }}>✕</button></div>
          {renderShipmentForm(true)}
          <div className="modal-footer" style={{ justifyContent: "space-between" }}>
            {canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6", fontSize: 12 }} onClick={() => deleteShipment(editingShipment)}>Delete Shipment</button>}
            <div style={{ display: "flex", gap: 8 }}>
              {rateStep === "form" && (
                <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
              )}
              {rateStep === "done" && <button className="btn btn-primary" onClick={saveEdit}>Save & Close</button>}
            </div>
          </div>
        </div>
      </div>
    )}
    <SaveToast show={saved} />
  </>);
}

// ─── REPORTS ──────────────────────────────────────────────────────────────────
function Reports({ orgId }) {
  const { data: mares } = useSupabaseTable("mares", orgId);
  const { data: foals } = useSupabaseTable("foals", orgId);
  const { data: orders } = useSupabaseTable("orders", orgId);
  const { data: customers } = useSupabaseTable("customers", orgId);
  const { data: shipments } = useSupabaseTable("shipments", orgId);
  const { data: semen } = useSupabaseTable("frozen_semen", orgId);

  const pregnant = mares.filter(m => m.status === "Pregnant").length;
  const pregRate = mares.length ? Math.round(pregnant / mares.length * 100) : 0;
  const totalRevenue = orders.filter(o => o.status === "Paid").reduce((a, o) => a + (o.total || 0), 0);

  const generateCSV = () => {
    const rows = [
      ["MARE RECORDS"], ["Name","Breed","Age","Status"], ...mares.map(m => [m.name, m.breed, m.age, m.status]),
      [], ["CUSTOMER RECORDS"], ["Name","Company","Email","City","State","Type"], ...customers.map(c => [c.name, c.company, c.email, c.city, c.state, c.type]),
      [], ["ORDER RECORDS"], ["ID","Customer","Total","Status","Date"], ...orders.map(o => [o.id, o.customer_name, o.total, o.status, o.date]),
      [], ["FROZEN SEMEN INVENTORY"], ["Batch ID","Stallion","Collection Date","Straws","Available","Post-Thaw Motility","Price/Dose"], ...semen.map(s => [s.id, s.stallion, s.collection_date, s.straws, s.straws_available, `${s.motility_post||""}%`, `$${s.price_per_dose||""}`]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "equinetrack-export.csv"; a.click();
  };

  return (<>
    <div className="page-header-row"><button className="btn btn-gold" onClick={generateCSV}>📄 Export CSV</button></div>
    <div className="stats-row">
      <div className="stat-card green"><div className="stat-icon">📈</div><div className="stat-label">Pregnancy Rate</div><div className="stat-value">{pregRate}%</div><div className="stat-meta">{pregnant}/{mares.length} mares</div></div>
      <div className="stat-card blue"><div className="stat-icon">💰</div><div className="stat-label">Season Revenue</div><div className="stat-value">${(totalRevenue / 1000).toFixed(1)}k</div><div className="stat-meta">Collected</div></div>
      <div className="stat-card"><div className="stat-icon">🐣</div><div className="stat-label">Foals Born</div><div className="stat-value">{foals.length}</div><div className="stat-meta">This season</div></div>
      <div className="stat-card"><div className="stat-icon">📦</div><div className="stat-label">Shipments</div><div className="stat-value">{shipments.length}</div><div className="stat-meta">This season</div></div>
    </div>
    <div className="full-panel">
      <div className="panel-header"><span className="panel-title">Mare Status Summary</span></div>
      <table className="data-table">
        <thead><tr><th>Mare</th><th>Breed</th><th>Age</th><th>Status</th><th>Stallion</th><th>Last U/S</th></tr></thead>
        <tbody>{mares.map(m => <tr key={m.id}><td style={{ fontWeight: 700, color: "var(--forest)" }}>{m.name}</td><td>{m.breed}</td><td>{m.age}</td><td><StatusBadge status={m.status} /></td><td style={{ fontStyle: "italic", fontSize: 12 }}>{m.stallion || "—"}</td><td>{m.last_ultrasound || "—"}</td></tr>)}</tbody>
      </table>
    </div>
  </>);
}

// ─── EMBRYO TRANSFER ──────────────────────────────────────────────────────────
function EmbryoTransfer({ orgId }) {
  const { data: embryos, setData: setEmbryos, loading } = useSupabaseTable("embryos", orgId);
  const { data: mares, setData: setMares } = useSupabaseTable("mares", orgId);
  const { data: foalings, setData: setFoalings } = useSupabaseTable("foaling_records", orgId);
  const [notice, setNotice] = useState("");
  const [otherRecip, setOtherRecip] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: stallions } = useSupabaseTable("stallions", orgId);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [saved, setSaved] = useState(false);
  const todayISO = localToday();
  const emptyForm = { mare: "", stallion: "", flush_date: todayISO, age_days: String(DEFAULT_EMBRYO_AGE), grade: "1", quality: "Excellent", status: "Stored", tank: "Tank 1", canister: "A", goblet: "1", recipient: "", recipient_mare_id: "", transfer_date: "", notes: "" };
  const [form, setForm] = useState(emptyForm);
  const [otherMare, setOtherMare] = useState("");
  const [otherStallion, setOtherStallion] = useState("");

  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const [editingId, setEditingId] = useState(null);
  const closeForm = () => { setShowAdd(false); setEditingId(null); setForm({ ...emptyForm, flush_date: localToday() }); setOtherMare(""); setOtherStallion(""); setOtherRecip(""); };
  // Picking a recipient and a transfer date on a stored embryo means it went in.
  const setTransfer = patch => setForm(p => {
    const n = { ...p, ...patch };
    if (n.status === "Stored" && n.transfer_date && n.recipient_mare_id && n.recipient_mare_id !== "__other") n.status = "Transferred";
    return n;
  });
  const linkedPregnancy = e => foalings.filter(f => f.embryo_id === e.id).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0] || null;

  // Trista (Capital Quarter Horses): create the recip's pregnancy at transfer,
  // not at the check; she is marked Open later if she loses it. Runs after an
  // embryo is saved as Transferred into one of the farm's mares with a date.
  const releaseRecip = async (mareId, exceptRecId) => {
    const m = mares.find(x => x.id === mareId);
    if (m && m.status === "Pregnant" && !foalings.some(f => f.id !== exceptRecId && f.mare_id === m.id && isActivePregnancy(f))) {
      await dbUpdate("mares", m.id, { status: "Open", days_pregnant: null });
      setMares(p => p.map(x => x.id === m.id ? { ...x, status: "Open", days_pregnant: null } : x));
    }
  };
  const syncPregnancy = async (e) => {
    const recip = e.status === "Transferred" && e.recipient_mare_id && e.transfer_date ? mares.find(m => m.id === e.recipient_mare_id) : null;
    if (!recip) {
      // No longer transferred into one of the farm's mares (a correction): the
      // pregnancy this embryo created goes away, and so does her Pregnant status.
      const linked = linkedPregnancy(e);
      if (!linked || !isActivePregnancy(linked)) return "";
      const { data: gone } = await supabase.from("foaling_records").delete().eq("id", linked.id).select("id");
      if (gone && gone.length) setFoalings(list => list.filter(f => f.id !== linked.id));
      else { // Staff can't delete (RLS skips it silently): close it instead.
        const upd = { status: "Loss", notes: [linked.notes, "Embryo transfer undone."].filter(Boolean).join(" "), updated_at: new Date().toISOString() };
        await dbUpdate("foaling_records", linked.id, upd);
        setFoalings(list => list.map(f => f.id === linked.id ? { ...f, ...upd } : f));
      }
      await releaseRecip(linked.mare_id, linked.id);
      return linked.mare_name ? `${linked.mare_name} is no longer marked pregnant with this embryo.` : "";
    }
    const fields = { mare_id: recip.id, mare_name: recip.name, stallion_name: e.stallion || null, dam_name: e.mare || null,
      transfer_date: e.transfer_date, embryo_age_days: e.age_days || DEFAULT_EMBRYO_AGE, embryo_id: e.id };
    const now = new Date().toISOString();
    let rec = linkedPregnancy(e);
    if (rec && !isActivePregnancy(rec)) return ""; // she already foaled or lost it: history stays as it was
    if (rec && rec.mare_id !== recip.id && foalings.some(f => f.id !== rec.id && f.mare_id === recip.id && isActivePregnancy(f))) {
      alert(`${recip.name} already has an open pregnancy. If she lost it, mark her open on the Mares page first, then save this embryo again.`);
      return "";
    }
    if (!rec) {
      // A pregnancy typed in by hand before this embryo was logged is the same
      // pregnancy: link it rather than create a second one for her.
      const open = foalings.find(f => f.mare_id === recip.id && isActivePregnancy(f));
      if (open && open.embryo_id && open.embryo_id !== e.id) {
        alert(`${recip.name} already has an open pregnancy from another embryo. If she lost that one, mark her open on the Mares page first, then save this embryo again.`);
        return "";
      }
      rec = open || null;
    }
    let due;
    if (rec) {
      const upd = { ...fields, updated_at: now };
      if (!rec.due_date || rec.due_date === recordDue(rec)) upd.due_date = recordDue({ ...rec, ...fields });
      await dbUpdate("foaling_records", rec.id, upd);
      setFoalings(list => list.map(f => f.id === rec.id ? { ...f, ...upd } : f));
      due = upd.due_date || rec.due_date;
      // Moved to a different recip: the first one is no longer carrying it.
      if (rec.mare_id && rec.mare_id !== recip.id) await releaseRecip(rec.mare_id, rec.id);
    } else {
      due = recordDue(fields);
      const row = await dbInsert("foaling_records", { id: crypto.randomUUID(), org_id: orgId, status: "Expected", ...fields, due_date: due || null });
      setFoalings(list => [row, ...list]);
    }
    if (recip.status !== "Pregnant") {
      await dbUpdate("mares", recip.id, { status: "Pregnant" });
      setMares(p => p.map(m => m.id === recip.id ? { ...m, status: "Pregnant" } : m));
    }
    return `${recip.name} is marked Pregnant${due ? `, due ${fmtShortDate(due)}` : ""}.`;
  };
  const startEdit = e => {
    const next = { ...emptyForm };
    for (const k of Object.keys(emptyForm)) next[k] = e[k] ?? "";
    next.age_days = String(e.age_days || DEFAULT_EMBRYO_AGE);
    // Older rows name the recipient in text only: match it to a mare if we can.
    const byName = !next.recipient_mare_id && next.recipient ? mares.find(m => m.name === next.recipient.trim()) : null;
    if (byName) next.recipient_mare_id = byName.id;
    if (!next.recipient_mare_id && next.recipient) { setOtherRecip(next.recipient); next.recipient_mare_id = "__other"; } else setOtherRecip("");
    // Names not on the farm's list go in the "Other…" box.
    if (next.mare && !mares.some(m => m.name === next.mare)) { setOtherMare(next.mare); next.mare = "__other"; } else setOtherMare("");
    if (next.stallion && !stallions.some(x => x.name === next.stallion)) { setOtherStallion(next.stallion); next.stallion = "__other"; } else setOtherStallion("");
    setForm(next); setEditingId(e.id); setShowAdd(true);
  };

  const addEmbryo = async () => {
    const mareName = form.mare === "__other" ? otherMare.trim() : form.mare.trim();
    const stallionName = form.stallion === "__other" ? otherStallion.trim() : form.stallion;
    if (!mareName || busy) return;
    setBusy(true);
    try { await saveEmbryo(mareName, stallionName); } finally { setBusy(false); }
  };
  const saveEmbryo = async (mareName, stallionName) => {
    const recipMare = form.recipient_mare_id && form.recipient_mare_id !== "__other" ? mares.find(m => m.id === form.recipient_mare_id) : null;
    const fields = { ...form, mare: mareName, stallion: stallionName || null, transfer_date: form.transfer_date || null, flush_date: form.flush_date || null,
      age_days: parseInt(form.age_days) || null,
      recipient_mare_id: recipMare ? recipMare.id : null,
      recipient: recipMare ? recipMare.name : (form.recipient_mare_id === "__other" ? otherRecip.trim() || null : null) };
    let savedRow;
    try {
      if (editingId) {
        await dbUpdate("embryos", editingId, fields);
        savedRow = { ...(embryos.find(x => x.id === editingId) || {}), ...fields, id: editingId };
        setEmbryos(p => p.map(x => x.id === editingId ? savedRow : x));
      } else {
        savedRow = await dbInsert("embryos", { id: crypto.randomUUID(), ...fields, org_id: orgId });
        setEmbryos(p => [savedRow, ...p]);
      }
      closeForm();
      save();
    } catch (e) { alert("Error saving: " + e.message); return; }
    try { const msg = await syncPregnancy(savedRow); if (msg) { setNotice(msg); setTimeout(() => setNotice(""), 6000); } }
    catch (e) { alert("The embryo was saved, but the recipient's pregnancy was not: " + e.message); }
  };

  const filtered = embryos.filter(e => (e.mare + (e.stallion || "") + (e.status || "")).toLowerCase().includes(search.toLowerCase()));
  const stored = embryos.filter(e => e.status === "Stored").length;
  const transferred = embryos.filter(e => e.status === "Transferred").length;

  return (<>
    <div className="page-header-row">
      
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        {notice ? <span style={{ color: "var(--forest)", fontSize: 13, fontWeight: 600 }}>✓ {notice}</span> : saved && <span style={{ color: "var(--gold)", fontSize: 13 }}>✓ Saved</span>}
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Log Embryo</button>
      </div>
    </div>
    <div className="stats-row" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
      <div className="stat-card"><div className="stat-icon">🔬</div><div className="stat-label">Total Embryos</div><div className="stat-value">{embryos.length}</div><div className="stat-meta">All flush records</div></div>
      <div className="stat-card green"><div className="stat-icon">🧊</div><div className="stat-label">In Storage</div><div className="stat-value">{stored}</div><div className="stat-meta">Cryopreserved</div></div>
      <div className="stat-card"><div className="stat-icon">✅</div><div className="stat-label">Transferred</div><div className="stat-value">{transferred}</div><div className="stat-meta">To recipients</div></div>
    </div>
    {loading ? <div className="empty-state">Loading embryos…</div> : (
      <div className="full-panel">
        <div className="filter-bar">
          <input className="search-input" placeholder="Search by mare, stallion, status…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <table className="data-table">
          <thead><tr><th>Mare</th><th>Stallion</th><th>Flush Date</th><th>Age</th><th>Grade</th><th>Quality</th><th>Status</th><th>Storage</th><th>Recipient</th><th></th></tr></thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.id} style={{ cursor: "pointer" }} title="Click to edit" onClick={() => startEdit(e)}>
                <td style={{ fontWeight: 700, color: "var(--forest)" }}>{e.mare}</td>
                <td style={{ fontStyle: "italic", fontSize: 12 }}>{e.stallion || "—"}</td>
                <td>{e.flush_date || "—"}</td>
                <td style={{ fontSize: 12 }}>{e.age_days ? `Day ${e.age_days}` : "—"}</td>
                <td style={{ fontWeight: 600 }}>Grade {e.grade}</td>
                <td><StatusBadge status={e.quality} /></td>
                <td><StatusBadge status={e.status} /></td>
                <td style={{ fontSize: 11 }}>{[e.tank, e.canister, e.goblet].filter(Boolean).join(" / ") || "—"}</td>
                <td style={{ fontSize: 12 }}>{e.recipient || "—"}{(() => {
                  const f = linkedPregnancy(e);
                  if (!f) return null;
                  const label = isActivePregnancy(f) ? `Pregnant${f.due_date ? ` · due ${fmtShortDate(f.due_date)}` : ""}` : f.status === "Loss" ? "Open (lost)" : f.status;
                  return <div style={{ fontSize: 11, color: isActivePregnancy(f) ? "var(--forest)" : "var(--text-light)", fontWeight: 600 }}>{label}</div>;
                })()}</td>
                <td>{canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={(ev) => { ev.stopPropagation(); confirmDelete("Delete this embryo? This can't be undone.", async () => { await dbDelete("embryos", e.id); setEmbryos(p => p.filter(x => x.id !== e.id)); }); }}>✕</button>}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="empty-state">No embryos logged yet. Click "+ Log Embryo" to begin.</td></tr>}
          </tbody>
        </table>
      </div>
    )}
    {showAdd && (
      <div className="modal-overlay" onClick={closeForm}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><div className="modal-title">{editingId ? "Edit Embryo" : "Log Embryo"}</div><button className="modal-close" onClick={closeForm}>✕</button></div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-row">
                <label className="form-label">Donor Mare *</label>
                <select className="form-input" value={form.mare} onChange={sf("mare")}>
                  <option value="">— Select —</option>
                  {mares.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                  <option value="__other">Other…</option>
                </select>
                {form.mare === "__other" && <input className="form-input" style={{ marginTop: 6 }} placeholder="Type mare name" value={otherMare} onChange={e => setOtherMare(e.target.value)} autoFocus />}
              </div>
              <div className="form-row">
                <label className="form-label">Sire Stallion</label>
                <select className="form-input" value={form.stallion} onChange={sf("stallion")}>
                  <option value="">— Select —</option>
                  {stallions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                  <option value="__other">Other…</option>
                </select>
                {form.stallion === "__other" && <input className="form-input" style={{ marginTop: 6 }} placeholder="Type stallion name" value={otherStallion} onChange={e => setOtherStallion(e.target.value)} autoFocus />}
              </div>
              <div className="form-row">
                <label className="form-label">Flush Date</label>
                <div style={{ position: "relative" }}>
                  <input className="form-input" type="date" value={form.flush_date} onChange={sf("flush_date")} style={{ paddingRight: 36, cursor: "pointer" }} />
                  <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 16, pointerEvents: "none", opacity: .6 }}>📅</span>
                </div>
              </div>
              <div className="form-row">
                <label className="form-label">Embryo Age</label>
                <select className="form-input" value={form.age_days} onChange={sf("age_days")}>
                  {EMBRYO_AGES.map(n => <option key={n} value={String(n)}>Day {n}{n === 8 ? " (most fresh flushes)" : n === 6 || n === 7 ? " (common for frozen)" : ""}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label className="form-label">Grade</label>
                <select className="form-input" value={form.grade} onChange={sf("grade")}>
                  <option value="1">Grade 1 — Excellent</option>
                  <option value="2">Grade 2 — Good</option>
                  <option value="3">Grade 3 — Fair</option>
                  <option value="4">Grade 4 — Poor</option>
                </select>
              </div>
              <div className="form-row">
                <label className="form-label">Quality</label>
                <select className="form-input" value={form.quality} onChange={sf("quality")}>
                  <option>Excellent</option><option>Good</option><option>Fair</option><option>Poor</option>
                </select>
              </div>
              <div className="form-row">
                <label className="form-label">Status</label>
                <select className="form-input" value={form.status} onChange={sf("status")}>
                  <option>Stored</option><option>Transferred</option><option>Discarded</option><option>Sold</option>
                </select>
              </div>
            </div>
            <div style={{ background: "linear-gradient(135deg,#0d1f2d,#0a2840)", borderRadius: 8, padding: "12px 15px", margin: "10px 0" }}>
              <div style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "#4fc3f7", fontWeight: 600, marginBottom: 10 }}>🧊 Storage Location</div>
              <div className="form-grid-3">
                <div className="form-row">
                  <label className="form-label" style={{ color: "#a0c4d8" }}>Tank</label>
                  <select className="form-input" value={form.tank} onChange={sf("tank")} style={{ background: "rgba(255,255,255,.08)", color: "#e8f4f8", border: "1px solid rgba(79,195,247,.3)" }}>
                    {["Tank 1","Tank 2","Tank 3","Tank 4","Tank 5"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <label className="form-label" style={{ color: "#a0c4d8" }}>Canister</label>
                  <select className="form-input" value={form.canister} onChange={sf("canister")} style={{ background: "rgba(255,255,255,.08)", color: "#e8f4f8", border: "1px solid rgba(79,195,247,.3)" }}>
                    {["A","B","C","D","E","F"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <label className="form-label" style={{ color: "#a0c4d8" }}>Goblet</label>
                  <input className="form-input" value={form.goblet} onChange={sf("goblet")} style={{ background: "rgba(255,255,255,.08)", color: "#e8f4f8", border: "1px solid rgba(79,195,247,.3)" }} />
                </div>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-row">
                <label className="form-label">Recipient Mare</label>
                <select className="form-input" value={form.recipient_mare_id} onChange={e => setTransfer({ recipient_mare_id: e.target.value })}>
                  <option value="">— Not transferred yet —</option>
                  {mares.filter(m => m.name !== (form.mare === "__other" ? otherMare.trim() : form.mare)).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  <option value="__other">Someone else's mare…</option>
                </select>
                {form.recipient_mare_id === "__other" && <input className="form-input" style={{ marginTop: 6 }} placeholder="Recipient mare's name" value={otherRecip} onChange={e => setOtherRecip(e.target.value)} />}
              </div>
              <div className="form-row">
                <label className="form-label">Transfer Date</label>
                <div style={{ position: "relative" }}>
                  <input className="form-input" type="date" value={form.transfer_date} onChange={e => setTransfer({ transfer_date: e.target.value })} style={{ paddingRight: 36, cursor: "pointer" }} />
                  <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 16, pointerEvents: "none", opacity: .6 }}>📅</span>
                </div>
              </div>
              {form.status === "Transferred" && form.recipient_mare_id && form.recipient_mare_id !== "__other" && form.transfer_date
                && !(editingId && foalings.some(f => f.embryo_id === editingId && !isActivePregnancy(f))) && (
                <div style={{ gridColumn: "1/-1", background: "#f4f7f5", border: "1px solid #dde6df", borderRadius: 8, padding: "9px 12px", fontSize: 12.5, color: "var(--forest)" }}>
                  Saving marks {(mares.find(m => m.id === form.recipient_mare_id) || {}).name || "the recipient"} Pregnant, due {fmtShortDate(dueFromDates({ transfer_date: form.transfer_date, embryo_age: form.age_days }))}. If she loses it, mark her open on the Mares page.
                </div>
              )}
              <div className="form-row" style={{ gridColumn: "1/-1" }}>
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows={3} value={form.notes} onChange={sf("notes")} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn" onClick={closeForm}>Cancel</button>
            <button className="btn btn-primary" disabled={busy} onClick={addEmbryo}>{busy ? "Saving…" : editingId ? "Save Changes" : "Save Embryo"}</button>
          </div>
        </div>
      </div>
    )}
  </>);
}

// ─── PLACEHOLDER ──────────────────────────────────────────────────────────────
function ComingSoon({ title, icon }) {
  return (
    <div style={{ padding: "60px 0", textAlign: "center" }}>
      <div style={{ fontSize: 44, marginBottom: 14 }}>{icon}</div>
      <div style={{ fontFamily: "Playfair Display,serif", fontSize: 22, color: "var(--forest)", marginBottom: 8 }}>{title}</div>
      <div style={{ color: "var(--text-light)", fontStyle: "italic" }}>Full module coming soon. Contact support@arcstallion.com for early access.</div>
    </div>
  );
}

// ─── HERD MANAGEMENT ──────────────────────────────────────────────────────────
function HerdManagement({ orgId, openMare }) {
  const { data: stallions } = useSupabaseTable("stallions", orgId);
  const { data: mares } = useSupabaseTable("mares", orgId);
  const { data: breedingEvents } = useSupabaseTable("breeding_events", orgId);
  const teamNames = useTeamNames(orgId);
  const { data: herdEvents, setData: setHerdEvents, loading } = useSupabaseTable("herd_events", orgId);
  const [showAdd, setShowAdd] = useState(false);
  const [saved, setSaved] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date());
  const [filter, setFilter] = useState("All");
  const todayISO = localToday();
  const emptyEv = { horse_type: "stallion", horse_id: "", horse_name: "", event_type: "Vaccination", event_date: todayISO, description: "", performed_by: "", next_due: "", notes: "" };
  const [form, setForm] = useState(emptyEv);
  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const allHorses = [
    ...stallions.map(s => ({ ...s, _type: "stallion" })),
    ...mares.map(m => ({ ...m, _type: "mare" }))
  ].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const [editingId, setEditingId] = useState(null);
  const closeForm = () => { setShowAdd(false); setEditingId(null); setForm(emptyEv); };
  const startEdit = ev => {
    const next = { ...emptyEv };
    for (const k of Object.keys(emptyEv)) next[k] = ev[k] ?? emptyEv[k];
    if (!next.horse_id) {
      const h = allHorses.find(x => x.name === ev.horse_name && x._type === ev.horse_type);
      if (h) next.horse_id = h.id;
    }
    setForm(next); setEditingId(ev.id); setShowAdd(true);
  };

  const addEvent = async () => {
    if (!form.horse_name || !form.event_date) return;
    try {
      if (editingId) {
        const upd = { ...form, horse_id: form.horse_id || null, next_due: form.next_due || null };
        await dbUpdate("herd_events", editingId, upd);
        setHerdEvents(p => p.map(x => x.id === editingId ? { ...x, ...upd } : x));
      } else {
        const row = await dbInsert("herd_events", { id: crypto.randomUUID(), org_id: orgId, ...form, next_due: form.next_due || null });
        setHerdEvents(p => [row, ...p]);
      }
      closeForm(); save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const HERD_EVENT_TYPES = ["Vaccination", "Deworming", "Farrier", "Dental", "Vet Check", "Coggins", "Health Cert", "Export Testing", "Other"];
  const EVENT_COLORS = { Vaccination: "#4caf50", Deworming: "#ff9800", Farrier: "#795548", Dental: "#9c27b0", "Vet Check": "#2196f3", Coggins: "#f44336", "Health Cert": "#00bcd4", "Export Testing": "#e91e63", Other: "#607d8b" };

  // Calendar logic
  const year = calMonth.getFullYear(), month = calMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const calDays = [];
  for (let i = 0; i < firstDay; i++) calDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calDays.push(d);
  const monthStr = calMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const eventsForDay = (d) => {
    if (!d) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return herdEvents.filter(e => e.event_date === dateStr);
  };

  // Palpations and pregnancy checks scheduled from a mare's Breeding Log.
  const scheduledChecks = scheduledChecksFrom(breedingEvents, mares);
  const checksForDay = (d) => {
    if (!d) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return scheduledChecks.filter(e => e.event_date === dateStr);
  };
  const listedChecks = [...scheduledChecks.filter(e => e.event_date < todayISO), ...scheduledChecks.filter(e => e.event_date >= todayISO).slice(0, 8)];

  const upcomingEvents = herdEvents
    .filter(e => e.event_date >= todayISO)
    .sort((a, b) => a.event_date.localeCompare(b.event_date))
    .slice(0, 10);

  const overdueEvents = herdEvents
    .filter(e => e.next_due && e.next_due < todayISO)
    .sort((a, b) => a.next_due.localeCompare(b.next_due));

  const filteredEvents = filter === "All" ? herdEvents : herdEvents.filter(e => e.event_type === filter);

  return (<>
    <div className="page-header-row">
      
      <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Log Event</button>
    </div>
    <div className="stats-row">
      <div className="stat-card"><div className="stat-icon">🐴</div><div className="stat-label">Total Herd</div><div className="stat-value">{allHorses.length}</div><div className="stat-meta">{stallions.length} stallions · {mares.length} mares</div></div>
      <div className="stat-card green"><div className="stat-icon">📅</div><div className="stat-label">Upcoming Events</div><div className="stat-value">{upcomingEvents.length}</div><div className="stat-meta">Next 30 days</div></div>
      <div className="stat-card red"><div className="stat-icon">⚠️</div><div className="stat-label">Overdue</div><div className="stat-value">{overdueEvents.length}</div><div className="stat-meta">Past due date</div></div>
      <div className="stat-card blue"><div className="stat-icon">📋</div><div className="stat-label">Total Records</div><div className="stat-value">{herdEvents.length}</div><div className="stat-meta">All events logged</div></div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
      {/* Calendar */}
      <div className="full-panel" style={{ padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <button className="btn btn-sm btn-outline" onClick={() => setCalMonth(new Date(year, month - 1, 1))}>‹</button>
          <div style={{ fontFamily: "Playfair Display,serif", fontSize: 16, fontWeight: 700, color: "var(--forest)" }}>{monthStr}</div>
          <button className="btn btn-sm btn-outline" onClick={() => setCalMonth(new Date(year, month + 1, 1))}>›</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, textAlign: "center", fontSize: 10, fontWeight: 600, color: "var(--text-light)", marginBottom: 4 }}>
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d}>{d}</div>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
          {calDays.map((d, i) => {
            const dayEvents = eventsForDay(d);
            const dayChecks = checksForDay(d);
            const isToday = d && `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}` === todayISO;
            const dayDate = d ? `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}` : null;
            return (
              <div key={i} onClick={() => { if (d) { setShowAdd(true); setForm(p => ({ ...p, event_date: dayDate })); } }} style={{ minHeight: 38, padding: "3px 4px", borderRadius: 4, fontSize: 11,
                background: isToday ? "rgba(45,90,61,.1)" : d ? "#fff" : "transparent",
                border: isToday ? "2px solid var(--forest)" : "1px solid #eee",
                cursor: d ? "pointer" : "default" }}>
                {d && <div style={{ fontWeight: isToday ? 700 : 400, color: isToday ? "var(--forest)" : "var(--text-mid)" }}>{d}</div>}
                {dayEvents.slice(0, 2).map(ev => (
                  <div key={ev.id} style={{ fontSize: 8, padding: "1px 3px", borderRadius: 3, marginTop: 1, background: EVENT_COLORS[ev.event_type] || "#607d8b", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {ev.horse_name?.split(" ")[0]}
                  </div>
                ))}
                {dayEvents.length > 2 && <div style={{ fontSize: 8, color: "var(--text-light)" }}>+{dayEvents.length - 2}</div>}
                {dayChecks.slice(0, 2).map(ev => (
                  <div key={ev.id} role="button" tabIndex={0}
                    title={`${EVENT_LABELS[ev.event_type]}: ${ev.mare_name}${ev.event_time ? " at " + fmtTime(ev.event_time) : ""}. Click to open ${ev.mare_name}.`}
                    onClick={e => { e.stopPropagation(); if (openMare) openMare(ev.mare_id); }}
                    onKeyDown={e => { if (e.key === "Enter") { e.stopPropagation(); if (openMare) openMare(ev.mare_id); } }}
                    style={{ fontSize: 8, padding: "1px 3px", borderRadius: 3, marginTop: 1, background: "var(--blue)", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                    {EVENT_ICONS[ev.event_type]} {ev.mare_name.split(" ")[0]}
                  </div>
                ))}
                {dayChecks.length > 2 && <div style={{ fontSize: 8, color: "var(--blue)" }}>+{dayChecks.length - 2}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming & Overdue */}
      <div>
        <div className="full-panel" style={{ padding: 14, marginBottom: 14, borderLeft: "4px solid var(--blue)" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--blue)", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>📡 Scheduled breeding checks</div>
          <ScheduledChecksList checks={listedChecks} today={todayISO} onOpen={openMare}
            empty="Nothing scheduled. After you log a palpation or pregnancy check on a mare, you'll be asked when the next one is." />
          {scheduledChecks.length > 0 && <div style={{ fontSize: 11, color: "var(--text-light)", marginTop: 6 }}>Blue on the calendar. Click one to open the mare.</div>}
        </div>
        {overdueEvents.length > 0 && (
          <div className="full-panel" style={{ padding: 14, marginBottom: 14, borderLeft: "4px solid var(--red)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--red)", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>⚠️ Overdue</div>
            {overdueEvents.slice(0, 5).map(ev => (
              <div key={ev.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0", fontSize: 12.5 }}>
                <div><strong>{ev.horse_name}</strong> — {ev.event_type}</div>
                <div style={{ color: "var(--red)", fontSize: 11 }}>Due: {ev.next_due}</div>
              </div>
            ))}
          </div>
        )}
        <div className="full-panel" style={{ padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>📅 Upcoming Events</div>
          {upcomingEvents.length === 0 ? <div className="empty-state" style={{ padding: "12px 0" }}>No upcoming events</div>
           : upcomingEvents.map(ev => (
            <div key={ev.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0", fontSize: 12.5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: EVENT_COLORS[ev.event_type] || "#607d8b", display: "inline-block" }} />
                <strong>{ev.horse_name}</strong> — {ev.event_type}
                {ev.description && <span style={{ color: "var(--text-light)", fontSize: 11 }}>({ev.description})</span>}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-light)" }}>{ev.event_date}</div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Event History Table */}
    <div className="full-panel">
      <div className="filter-bar" style={{ display: "flex", gap: 10 }}>
        <select className="form-input" style={{ width: 180 }} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="All">All Event Types</option>
          {HERD_EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <span style={{ fontSize: 12, color: "var(--text-light)", marginLeft: "auto" }}>{filteredEvents.length} records</span>
      </div>
      <table className="data-table">
        <thead><tr><th>Date</th><th>Horse</th><th>Type</th><th>Event</th><th>Description</th><th>By</th><th>Next Due</th><th></th></tr></thead>
        <tbody>
          {filteredEvents.sort((a,b) => b.event_date.localeCompare(a.event_date)).slice(0, 50).map(ev => (
            <tr key={ev.id} style={{ cursor: "pointer" }} title="Click to edit" onClick={() => startEdit(ev)}>
              <td style={{ fontSize: 12 }}>{ev.event_date}</td>
              <td style={{ fontWeight: 700, color: "var(--forest)" }}>{ev.horse_name}</td>
              <td><span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: EVENT_COLORS[ev.event_type] || "#607d8b", color: "#fff", fontWeight: 600 }}>{ev.event_type}</span></td>
              <td style={{ fontSize: 12 }}>{ev.horse_type === "stallion" ? "♂" : "♀"}</td>
              <td style={{ fontSize: 12, color: "var(--text-mid)" }}>{ev.description || "—"}</td>
              <td style={{ fontSize: 12 }}>{ev.performed_by || "—"}</td>
              <td style={{ fontSize: 12, color: ev.next_due && ev.next_due < todayISO ? "var(--red)" : "var(--text-mid)", fontWeight: ev.next_due && ev.next_due < todayISO ? 700 : 400 }}>{ev.next_due || "—"}</td>
              <td>{canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={(e) => { e.stopPropagation(); confirmDelete("Delete this herd event? This can't be undone.", async () => { await dbDelete("herd_events", ev.id); setHerdEvents(p => p.filter(x => x.id !== ev.id)); save(); }); }}>✕</button>}</td>
            </tr>
          ))}
          {filteredEvents.length === 0 && <tr><td colSpan={8} className="empty-state">No events logged yet. Click "+ Log Event" to start.</td></tr>}
        </tbody>
      </table>
    </div>

    {/* Add Event Modal */}
    {showAdd && (
      <div className="modal-overlay" onClick={closeForm}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><div className="modal-title">{editingId ? "Edit Herd Event" : "Log Herd Event"}</div><button className="modal-close" onClick={closeForm}>✕</button></div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-row"><label className="form-label">Horse *</label>
                <select className="form-input" value={form.horse_name ? `${form.horse_type}|${form.horse_id}|${form.horse_name}` : ""} onChange={e => {
                  const [ht, hid, ...hn] = e.target.value.split("|");
                  setForm(p => ({ ...p, horse_type: ht, horse_id: hid, horse_name: hn.join("|") }));
                }}>
                  <option value="">— Select Horse —</option>
                  {allHorses.map(h => <option key={h.id} value={`${h._type}|${h.id}|${h.name}`}>{h._type === "stallion" ? "♂" : "♀"} {h.name}</option>)}
                </select>
              </div>
              <div className="form-row"><label className="form-label">Event Type *</label>
                <select className="form-input" value={form.event_type} onChange={sf("event_type")}>
                  {HERD_EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-row"><label className="form-label">Date *</label><input className="form-input" type="date" value={form.event_date} onChange={sf("event_date")} /></div>
              <div className="form-row"><label className="form-label">Performed By</label>
                <PersonSelect value={form.performed_by} options={mergeNames(teamNames, ["Vet"], herdEvents.map(e => e.performed_by))} onChange={v => setForm(p => ({ ...p, performed_by: v }))} />
              </div>
              <div className="form-row" style={{ gridColumn: "1/-1" }}><label className="form-label">Description</label><input className="form-input" value={form.description} onChange={sf("description")} placeholder="e.g. West Nile + EWT, Front shoes reset" /></div>
              <div className="form-row"><label className="form-label">Next Due Date</label><input className="form-input" type="date" value={form.next_due} onChange={sf("next_due")} /></div>
              <div className="form-row" style={{ gridColumn: "1/-1" }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={sf("notes")} /></div>
            </div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={closeForm}>Cancel</button><button className="btn btn-primary" onClick={addEvent}>{editingId ? "Save Changes" : "Save Event"}</button></div>
        </div>
      </div>
    )}
    <SaveToast show={saved} />
  </>);
}

// ─── FOALING RECORDS ──────────────────────────────────────────────────────────
function FoalingRecords({ orgId }) {
  const { data: foalings, setData: setFoalings, loading } = useSupabaseTable("foaling_records", orgId);
  const { data: mares, setData: setMares } = useSupabaseTable("mares", orgId);
  const { data: stallions } = useSupabaseTable("stallions", orgId);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saved, setSaved] = useState(false);
  const [viewing, setViewing] = useState(null);
  const todayISO = localToday();
  const emptyForm = { mare_id: "", mare_name: "", stallion_name: "", dam_name: "", breeding_date: "", transfer_date: "", embryo_age_days: String(DEFAULT_EMBRYO_AGE), due_date: "", actual_foaling_date: "", foal_name: "", foal_sex: "Unknown", foal_color: "", foal_weight: "", birth_type: "Normal", placenta_passed: false, placenta_time: "", mare_condition: "", foal_condition: "", vet_present: false, first_nurse: "", igg_level: "", notes: "", status: "Expected" };
  const [form, setForm] = useState(emptyForm);
  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const closeForm = () => { setShowAdd(false); setEditingId(null); setForm(emptyForm); };
  const startEdit = f => {
    const next = { ...emptyForm };
    for (const k of Object.keys(emptyForm)) next[k] = f[k] ?? emptyForm[k];
    next.embryo_age_days = String(next.embryo_age_days || DEFAULT_EMBRYO_AGE);
    setForm(next); setEditingId(f.id); setViewing(null); setShowAdd(true);
  };
  // Breeding date fills in the due date (340 days) when there isn't one yet.
  // Transfer date (less the embryo's age) or breeding date fills in the due
  // date, until the due date is typed over by hand.
  const datesOf = f => ({ transfer_date: f.transfer_date, embryo_age: f.embryo_age_days, breeding_date: f.breeding_date });
  const setDates = patch => setForm(p => {
    const n = { ...p, ...patch };
    if (!p.due_date || p.due_date === dueFromDates(datesOf(p))) { const d = dueFromDates(datesOf(n)); if (d) n.due_date = d; }
    return n;
  });
  const setBred = e => setDates({ breeding_date: e.target.value });

  // Foaled or lost: she is no longer carrying, so the mare goes back to Open
  // (unless another pregnancy is still open for her).
  const releaseMare = async (rec, status) => {
    if (!["Foaled", "Loss"].includes(status)) return;
    const mare = mares.find(m => m.id === rec.mare_id);
    if (!mare || mare.status !== "Pregnant") return;
    if (foalings.some(f => f.id !== rec.id && f.mare_id === mare.id && isActivePregnancy(f))) return;
    try {
      await dbUpdate("mares", mare.id, { status: "Open", days_pregnant: null });
      setMares(p => p.map(m => m.id === mare.id ? { ...m, status: "Open", days_pregnant: null } : m));
    } catch (e) { console.error("[Foaling] could not set mare Open", mare.id, e); }
  };

  const addFoaling = async () => {
    if (!form.mare_name) return;
    const t = v => (v || "").trim() || null;
    const clean = { ...form, stallion_name: t(form.stallion_name), dam_name: t(form.dam_name), breeding_date: form.breeding_date || null, due_date: form.due_date || null, actual_foaling_date: form.actual_foaling_date || null,
      transfer_date: form.transfer_date || null, embryo_age_days: form.transfer_date ? (parseInt(form.embryo_age_days) || DEFAULT_EMBRYO_AGE) : null };
    try {
      if (editingId) {
        const before = foalings.find(f => f.id === editingId);
        const upd = { ...clean, updated_at: new Date().toISOString() };
        await dbUpdate("foaling_records", editingId, upd);
        setFoalings(p => p.map(f => f.id === editingId ? { ...f, ...upd } : f));
        if (before && before.status !== upd.status) await releaseMare({ ...before, ...upd }, upd.status);
      } else {
        const row = await dbInsert("foaling_records", { id: crypto.randomUUID(), org_id: orgId, ...clean });
        setFoalings(p => [row, ...p]);
        await releaseMare(row, row.status);
      }
      closeForm(); save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const updateStatus = async (id, status) => {
    try {
      await dbUpdate("foaling_records", id, { status });
      const rec = foalings.find(f => f.id === id);
      setFoalings(p => p.map(f => f.id === id ? { ...f, status } : f)); save();
      if (rec && rec.status !== status) await releaseMare(rec, status);
    }
    catch (e) { alert("Error: " + e.message); }
  };

  const expected = foalings.filter(f => f.status === "Expected" || f.status === "Overdue");
  const foaled = foalings.filter(f => f.status === "Foaled");
  const daysUntilDue = (d) => { if (!d) return null; return Math.round((new Date(d) - new Date()) / 86400000); };

  // Auto-mark overdue
  expected.forEach(f => {
    if (f.due_date && f.due_date < todayISO && f.status === "Expected") {
      updateStatus(f.id, "Overdue");
    }
  });

  return (<>
    <div className="page-header-row">
      
      <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Foaling Record</button>
    </div>
    <div className="stats-row">
      <div className="stat-card"><div className="stat-icon">🤰</div><div className="stat-label">Expected</div><div className="stat-value">{expected.length}</div><div className="stat-meta">Mares due to foal</div></div>
      <div className="stat-card green"><div className="stat-icon">🐣</div><div className="stat-label">Foaled</div><div className="stat-value">{foaled.length}</div><div className="stat-meta">This season</div></div>
      <div className="stat-card red"><div className="stat-icon">⚠️</div><div className="stat-label">Overdue</div><div className="stat-value">{foalings.filter(f => f.status === "Overdue").length}</div><div className="stat-meta">Past due date</div></div>
      <div className="stat-card blue"><div className="stat-icon">📋</div><div className="stat-label">Total Records</div><div className="stat-value">{foalings.length}</div><div className="stat-meta">All foaling records</div></div>
    </div>

    {/* Expected / Upcoming */}
    {expected.length > 0 && (
      <div className="full-panel" style={{ marginBottom: 20 }}>
        <div style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700, color: "var(--forest)", borderBottom: "1px solid var(--parchment-dark)" }}>🤰 Mares Due to Foal</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12, padding: 16 }}>
          {expected.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999")).map(f => {
            const days = daysUntilDue(f.due_date);
            const isOverdue = f.status === "Overdue" || (days !== null && days < 0);
            return (
              <div key={f.id} style={{ background: isOverdue ? "#fef2f2" : "var(--parchment)", borderRadius: 8, padding: "12px 14px", border: isOverdue ? "1px solid #fca5a5" : "1px solid var(--parchment-dark)", cursor: "pointer" }} onClick={() => setViewing(f)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, color: "var(--forest)", fontSize: 14 }}>♀ {f.mare_name}</div>
                  <StatusBadge status={f.status} />
                </div>
                {(f.stallion_name || f.dam_name) && <div style={{ fontSize: 12, color: "var(--text-mid)", fontWeight: 600, marginBottom: 2 }}>🍼 {foalCross(f)}</div>}
                {f.dam_name && <div style={{ fontSize: 11.5, color: "var(--text-light)", marginBottom: 2 }}>Embryo from {f.dam_name}</div>}
                {pregDatesLine(f) && <div style={{ fontSize: 11.5, color: "var(--text-light)", marginBottom: 4 }}>{pregDatesLine(f)}</div>}
                {f.due_date && (
                  <div style={{ fontSize: 12, fontWeight: 600, color: isOverdue ? "var(--red)" : "var(--forest)" }}>
                    Due: {f.due_date} {days !== null ? `(${isOverdue ? Math.abs(days) + " days overdue" : days + " days"})` : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    )}

    {/* Foaling History */}
    <div className="full-panel">
      <div style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700, color: "var(--forest)", borderBottom: "1px solid var(--parchment-dark)" }}>📋 All Foaling Records</div>
      <table className="data-table">
        <thead><tr><th>Mare</th><th>Sire</th><th>Dam</th><th>Bred / Transferred</th><th>Due Date</th><th>Foaled</th><th>Foal</th><th>Sex</th><th>Birth</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {foalings.sort((a,b) => (b.due_date || "").localeCompare(a.due_date || "")).map(f => (
            <tr key={f.id} style={{ cursor: "pointer" }} onClick={() => setViewing(f)}>
              <td style={{ fontWeight: 700, color: "var(--forest)" }}>{f.mare_name}</td>
              <td style={{ fontSize: 12 }}>{f.stallion_name || "—"}</td>
              <td style={{ fontSize: 12 }}>{f.dam_name || f.mare_name}</td>
              <td style={{ fontSize: 12 }}>{f.transfer_date ? `ET ${f.transfer_date} (d${f.embryo_age_days || DEFAULT_EMBRYO_AGE})` : (f.breeding_date || "—")}</td>
              <td style={{ fontSize: 12 }}>{f.due_date || "—"}</td>
              <td style={{ fontSize: 12 }}>{f.actual_foaling_date || "—"}</td>
              <td style={{ fontWeight: 600 }}>{f.foal_name || "—"}</td>
              <td style={{ fontSize: 12 }}>{f.foal_sex || "—"}</td>
              <td style={{ fontSize: 12 }}>{f.birth_type || "—"}</td>
              <td>
                <select value={f.status} onClick={e => e.stopPropagation()} onChange={e => updateStatus(f.id, e.target.value)}
                  style={{ fontSize: 11, padding: "3px 7px", borderRadius: 12, border: "1px solid var(--parchment-dark)", background: "white", fontWeight: 700, cursor: "pointer",
                    color: f.status === "Foaled" ? "#1a7f50" : f.status === "Overdue" ? "var(--red)" : f.status === "Expected" ? "#2563eb" : f.status === "Complications" ? "#dc2626" : "var(--text-mid)" }}>
                  <option>Expected</option><option>Overdue</option><option>Foaled</option><option>Complications</option><option>Loss</option>
                </select>
              </td>
              <td>{canDelete() && <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }} onClick={(e) => { e.stopPropagation(); confirmDelete("Delete this foaling record? This can't be undone.", async () => { await dbDelete("foaling_records", f.id); setFoalings(p => p.filter(x => x.id !== f.id)); save(); }); }}>✕</button>}</td>
            </tr>
          ))}
          {foalings.length === 0 && <tr><td colSpan={11} className="empty-state">No foaling records yet. Click "+ Add Foaling Record" to start tracking.</td></tr>}
        </tbody>
      </table>
    </div>

    {/* Viewing detail flyout */}
    {viewing && (
      <div className="modal-overlay" onClick={() => setViewing(null)}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 580 }}>
          <div className="modal-header" style={{ background: "linear-gradient(135deg,#1a3a2a,#2d5a3d)", borderBottom: "none" }}>
            <div>
              <div className="modal-title" style={{ color: "#fff" }}>♀ {viewing.mare_name}</div>
              <div style={{ color: "rgba(255,255,255,.6)", fontSize: 12 }}>{viewing.stallion_name ? foalCross(viewing) : "No sire recorded"}{viewing.dam_name ? ` · embryo from ${viewing.dam_name}` : ""}</div>
            </div>
            <button className="modal-close" onClick={() => setViewing(null)} style={{ color: "#fff" }}>✕</button>
          </div>
          <div className="modal-body">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
              {[[viewing.transfer_date ? "Transferred" : "Bred", viewing.transfer_date ? `${viewing.transfer_date} (day ${viewing.embryo_age_days || DEFAULT_EMBRYO_AGE})` : (viewing.breeding_date || "—")], ["Due Date", viewing.due_date || "—"], ["Foaled", viewing.actual_foaling_date || "—"], ["Status", viewing.status]].map(([l, v]) => (
                <div key={l} style={{ background: "var(--parchment)", borderRadius: 7, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--text-light)", marginBottom: 3 }}>{l}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--forest)" }}>{v}</div>
                </div>
              ))}
            </div>
            {viewing.foal_name && <div style={{ fontSize: 13, marginBottom: 6 }}>🐣 <strong>Foal:</strong> {viewing.foal_name} ({viewing.foal_sex || "Unknown"}){viewing.foal_color ? ` — ${viewing.foal_color}` : ""}</div>}
            {viewing.foal_weight && <div style={{ fontSize: 13, marginBottom: 6 }}>⚖️ <strong>Weight:</strong> {viewing.foal_weight}</div>}
            {viewing.birth_type && <div style={{ fontSize: 13, marginBottom: 6 }}>🏥 <strong>Birth:</strong> {viewing.birth_type}{viewing.vet_present ? " (Vet present)" : ""}</div>}
            {viewing.placenta_passed && <div style={{ fontSize: 13, marginBottom: 6 }}>✅ Placenta passed{viewing.placenta_time ? ` at ${viewing.placenta_time}` : ""}</div>}
            {viewing.first_nurse && <div style={{ fontSize: 13, marginBottom: 6 }}>🍼 <strong>First nurse:</strong> {viewing.first_nurse}</div>}
            {viewing.igg_level && <div style={{ fontSize: 13, marginBottom: 6 }}>🔬 <strong>IgG:</strong> {viewing.igg_level}</div>}
            {viewing.mare_condition && <div style={{ fontSize: 13, marginBottom: 6 }}>♀ <strong>Mare condition:</strong> {viewing.mare_condition}</div>}
            {viewing.foal_condition && <div style={{ fontSize: 13, marginBottom: 6 }}>🐣 <strong>Foal condition:</strong> {viewing.foal_condition}</div>}
            {viewing.notes && <div style={{ background: "var(--parchment)", borderRadius: 6, padding: "10px 13px", fontSize: 13, fontStyle: "italic", color: "var(--text-mid)", lineHeight: 1.55, marginTop: 10 }}>{viewing.notes}</div>}
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={() => setViewing(null)}>Close</button><button className="btn btn-primary" onClick={() => startEdit(viewing)}>✏️ Edit</button></div>
        </div>
      </div>
    )}

    {/* Add Modal */}
    {showAdd && (
      <div className="modal-overlay" onClick={closeForm}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, width: "95vw" }}>
          <div className="modal-header"><div className="modal-title">{editingId ? "Edit Foaling Record" : "Add Foaling Record"}</div><button className="modal-close" onClick={closeForm}>✕</button></div>
          <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
            <div className="form-grid">
              <div className="form-row"><label className="form-label">Mare *</label>
                <select className="form-input" value={form.mare_name} onChange={e => {
                  const mare = mares.find(m => m.name === e.target.value);
                  setForm(p => ({ ...p, mare_name: e.target.value, mare_id: mare?.id || "" }));
                }}>
                  <option value="">— Select Mare —</option>
                  {mares.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
              <div className="form-row"><label className="form-label">Sire</label>
                <input className="form-input" list="foaling-sire-options" value={form.stallion_name} onChange={sf("stallion_name")} placeholder="Stallion name" />
                <datalist id="foaling-sire-options">{stallions.map(s => <option key={s.id} value={s.name} />)}</datalist>
              </div>
              <div className="form-row"><label className="form-label">Donor Mare (dam)</label>
                <input className="form-input" list="foaling-dam-options" value={form.dam_name} onChange={sf("dam_name")} placeholder="Leave blank if it's her own foal" />
                <datalist id="foaling-dam-options">{[...new Set(mares.map(m => m.name).filter(n => n && n !== form.mare_name))].map(n => <option key={n} value={n} />)}</datalist>
              </div>
              <div className="form-row"><label className="form-label">Transfer Date (embryo transfer)</label><input className="form-input" type="date" value={form.transfer_date} onChange={e => setDates({ transfer_date: e.target.value })} /></div>
              <div className="form-row"><label className="form-label">Embryo Age</label>
                <select className="form-input" value={form.embryo_age_days} onChange={e => setDates({ embryo_age_days: e.target.value })}>
                  {EMBRYO_AGES.map(n => <option key={n} value={String(n)}>Day {n}</option>)}
                </select>
              </div>
              <div className="form-row"><label className="form-label">Breeding Date</label><input className="form-input" type="date" value={form.breeding_date} onChange={setBred} /></div>
              <div className="form-row"><label className="form-label">Due Date</label><input className="form-input" type="date" value={form.due_date} onChange={sf("due_date")} /></div>
              <div className="form-row"><label className="form-label">Status</label>
                <select className="form-input" value={form.status} onChange={sf("status")}><option>Expected</option><option>Overdue</option><option>Foaled</option><option>Complications</option><option>Loss</option></select>
              </div>
            </div>
            {/* Birth Details - collapsible section */}
            <div style={{ background: "#f4f7f5", borderRadius: 10, padding: "14px 16px", marginTop: 14, border: "1px solid #dde6df" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>🐣 Birth Details (fill after foaling)</div>
              <div className="form-grid">
                <div className="form-row"><label className="form-label">Actual Foaling Date</label><input className="form-input" type="date" value={form.actual_foaling_date} onChange={sf("actual_foaling_date")} /></div>
                <div className="form-row"><label className="form-label">Foal Name</label><input className="form-input" value={form.foal_name} onChange={sf("foal_name")} /></div>
                <div className="form-row"><label className="form-label">Sex</label>
                  <select className="form-input" value={form.foal_sex} onChange={sf("foal_sex")}><option>Unknown</option><option>Colt</option><option>Filly</option></select>
                </div>
                <div className="form-row"><label className="form-label">Color</label><input className="form-input" value={form.foal_color} onChange={sf("foal_color")} placeholder="e.g. Bay, Sorrel" /></div>
                <div className="form-row"><label className="form-label">Weight</label><input className="form-input" value={form.foal_weight} onChange={sf("foal_weight")} placeholder="e.g. 95 lbs" /></div>
                <div className="form-row"><label className="form-label">Birth Type</label>
                  <select className="form-input" value={form.birth_type} onChange={sf("birth_type")}><option>Normal</option><option>Dystocia</option><option>C-Section</option><option>Assisted</option></select>
                </div>
              </div>
              <div className="form-grid" style={{ marginTop: 8 }}>
                <div className="form-row"><label className="form-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={form.placenta_passed} onChange={e => setForm(p => ({ ...p, placenta_passed: e.target.checked }))} style={{ accentColor: "var(--forest)" }} /> Placenta Passed
                </label></div>
                <div className="form-row"><label className="form-label">Placenta Time</label><input className="form-input" value={form.placenta_time} onChange={sf("placenta_time")} placeholder="e.g. 45 min post-foal" /></div>
                <div className="form-row"><label className="form-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={form.vet_present} onChange={e => setForm(p => ({ ...p, vet_present: e.target.checked }))} style={{ accentColor: "var(--forest)" }} /> Vet Present
                </label></div>
              </div>
              <div className="form-grid" style={{ marginTop: 8 }}>
                <div className="form-row"><label className="form-label">First Nurse</label><input className="form-input" value={form.first_nurse} onChange={sf("first_nurse")} placeholder="e.g. 1 hr post-foal" /></div>
                <div className="form-row"><label className="form-label">IgG Level</label><input className="form-input" value={form.igg_level} onChange={sf("igg_level")} placeholder="e.g. >800 mg/dL" /></div>
                <div className="form-row"><label className="form-label">Mare Condition</label><input className="form-input" value={form.mare_condition} onChange={sf("mare_condition")} placeholder="e.g. Good, recovering" /></div>
                <div className="form-row"><label className="form-label">Foal Condition</label><input className="form-input" value={form.foal_condition} onChange={sf("foal_condition")} placeholder="e.g. Healthy, nursing well" /></div>
              </div>
            </div>
            <div className="form-row" style={{ marginTop: 12 }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={sf("notes")} /></div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={closeForm}>Cancel</button><button className="btn btn-primary" onClick={addFoaling}>{editingId ? "Save Changes" : "Save Record"}</button></div>
        </div>
      </div>
    )}
    <SaveToast show={saved} />
  </>);
}

// ─── ADMIN SETTINGS ───────────────────────────────────────────────────────────
// OAuth Client IDs — configured once by the developer
// Gmail: Create at https://console.cloud.google.com/apis/credentials
// Outlook: Create at https://portal.azure.com → App registrations
// ─── FARRIER SCHEDULE (Barn-wide) ────────────────────────────────────────────
function FarrierSchedule({ orgId }) {
  const { data: farrierRecords, setData: setFarrierRecords, loading } = useSupabaseTable("farrier_records", orgId);
  const { data: stallions } = useSupabaseTable("stallions", orgId);
  const { data: mares } = useSupabaseTable("mares", orgId);
  const { data: savedFarriers, setData: setSavedFarriers } = useSupabaseTable("farriers", orgId);
  const [filter, setFilter] = useState("upcoming");
  const [showAdd, setShowAdd] = useState(false);
  const [editingFarrier, setEditingFarrier] = useState(null);
  const [saved, setSaved] = useState(false);
  const emptyFarrier = { horse_id: "", horse_name: "", horse_type: "", farrier_name: "", visit_date: localToday(), service_type: "complete_shoeing", cost: "", notes: "", hoof_condition: "", next_appointment: "" };
  const [form, setForm] = useState(emptyFarrier);
  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const allHorses = [...stallions.map(s => ({ ...s, type: "stallion" })), ...mares.map(m => ({ ...m, type: "mare" }))].sort((a, b) => a.name.localeCompare(b.name));

  const selectHorse = (e) => {
    const h = allHorses.find(x => x.id === e.target.value);
    if (h) setForm(p => ({ ...p, horse_id: h.id, horse_name: h.name, horse_type: h.type }));
  };

  const addRecord = async () => {
    if (!form.farrier_name || !form.horse_id) return;
    try {
      const row = await dbInsert("farrier_records", { id: crypto.randomUUID(), org_id: orgId, ...form, cost: parseFloat(form.cost) || 0, next_appointment: form.next_appointment || null });
      setFarrierRecords(p => [row, ...p]);
      setShowAdd(false); setForm(emptyFarrier); save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const saveEdit = async () => {
    if (!editingFarrier) return;
    try {
      const updates = { ...form, cost: parseFloat(form.cost) || 0, next_appointment: form.next_appointment || null };
      await dbUpdate("farrier_records", editingFarrier.id, updates);
      setFarrierRecords(p => p.map(r => r.id === editingFarrier.id ? { ...r, ...updates } : r));
      setEditingFarrier(null); setShowAdd(false); setForm(emptyFarrier); save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const deleteRecord = async (rec) => {
    if (!window.confirm("Delete this farrier record?")) return;
    try { await dbDelete("farrier_records", rec.id); } catch (e) { alert(e.message); return; }
    setFarrierRecords(p => p.filter(r => r.id !== rec.id)); save();
  };

  const today = localToday();
  const upcoming = farrierRecords.filter(r => r.next_appointment && r.next_appointment >= today).sort((a, b) => a.next_appointment.localeCompare(b.next_appointment));
  const overdue = farrierRecords.filter(r => r.next_appointment && r.next_appointment < today).sort((a, b) => a.next_appointment.localeCompare(b.next_appointment));
  const allSorted = [...farrierRecords].sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date));
  const totalCost = farrierRecords.reduce((s, r) => s + (parseFloat(r.cost) || 0), 0);
  const uniqueFarriers = [...new Set(farrierRecords.map(r => r.farrier_name).filter(Boolean))];

  const SERVICE_LABELS = { complete_shoeing: "Complete Shoeing", back_shoeing: "Back Shoeing", front_shoeing: "Front Shoeing", trimming: "Trimming", replace_lost: "Replace Lost Horseshoe", one_shoe_left: "One Shoe (Left)", one_shoe_right: "One Shoe (Right)", hind_trim: "Hind Trim", front_trim: "Front Trim" };
  const CONDITION_COLORS = { excellent: "#4caf50", good: "#4caf50", fair: "#ff9800", poor: "#f44336", needs_attention: "#f44336" };

  const renderRecord = (rec) => (
    <div key={rec.id} style={{ background: "var(--parchment)", borderRadius: 8, padding: "12px 16px", marginBottom: 8, borderLeft: `3px solid ${CONDITION_COLORS[rec.hoof_condition] || "#795548"}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: "Playfair Display,serif", fontSize: 14, fontWeight: 700, color: "var(--forest)" }}>{rec.horse_name || "Unknown"}</span>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: rec.horse_type === "stallion" ? "#dbeafe" : "#fce7f3", color: rec.horse_type === "stallion" ? "#1e40af" : "#9d174d", fontWeight: 600, textTransform: "uppercase" }}>{rec.horse_type || "horse"}</span>
          </div>
          <div style={{ fontSize: 13, color: "var(--text-mid)" }}>{rec.visit_date} - {SERVICE_LABELS[rec.service_type] || rec.service_type}</div>
          <div style={{ fontSize: 12, color: "var(--text-light)", marginTop: 2 }}>Farrier: {rec.farrier_name}{rec.cost ? ` | $${parseFloat(rec.cost).toFixed(2)}` : ""}</div>
          {rec.hoof_condition && <div style={{ fontSize: 11, marginTop: 3 }}>🦶 <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{rec.hoof_condition.replace(/_/g, " ")}</span></div>}
          {rec.notes && <div style={{ fontSize: 12, color: "var(--text-light)", marginTop: 4, fontStyle: "italic" }}>{rec.notes}</div>}
          {rec.next_appointment && <div style={{ fontSize: 11, color: rec.next_appointment < today ? "#dc2626" : "#2563eb", marginTop: 3, fontWeight: 600 }}>📅 Next: {rec.next_appointment}{rec.next_appointment < today ? " (OVERDUE)" : ""}</div>}
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13 }} onClick={() => { setEditingFarrier(rec); setForm({ horse_id: rec.horse_id, horse_name: rec.horse_name, horse_type: rec.horse_type || "", farrier_name: rec.farrier_name, visit_date: rec.visit_date, service_type: rec.service_type, cost: rec.cost || "", notes: rec.notes || "", hoof_condition: rec.hoof_condition || "", next_appointment: rec.next_appointment || "" }); setShowAdd(true); }}>✏️</button>
          {canDelete() && <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--red)" }} onClick={() => deleteRecord(rec)}>✕</button>}
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--forest)", marginBottom: 4 }}>Attachments</div>
        {(rec.photo_urls || []).length > 0 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            {(rec.photo_urls || []).map((url, pi) => (
              <div key={pi} style={{ position: "relative", width: 80, height: 80, borderRadius: 6, overflow: "hidden", border: "1px solid #ddd", cursor: "pointer" }}>
                <HorsePhoto value={url} alt={`Photo ${pi + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm("Remove this photo?")) return; const updated = (rec.photo_urls || []).filter(p => p !== url); await dbUpdate("farrier_records", rec.id, { photo_urls: updated }); setFarrierRecords(p => p.map(r => r.id === rec.id ? { ...r, photo_urls: updated } : r)); }} style={{ position: "absolute", top: 2, right: 2, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,.6)", color: "white", border: "none", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <div
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "#eff6ff"; }}
          onDragLeave={e => { e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.background = "white"; }}
          onDrop={async e => { e.preventDefault(); e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.background = "white"; const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/")); if (!files.length) return; const newUrls = []; for (const file of files) { const url = await uploadHorsePhoto(file, orgId); newUrls.push(url); } const updated = [...(rec.photo_urls || []), ...newUrls]; await dbUpdate("farrier_records", rec.id, { photo_urls: updated }); setFarrierRecords(p => p.map(r => r.id === rec.id ? { ...r, photo_urls: updated } : r)); }}
          onClick={() => { const input = document.createElement("input"); input.type = "file"; input.accept = "image/*"; input.multiple = true; input.onchange = async e => { const files = Array.from(e.target.files).filter(f => f.type.startsWith("image/")); if (!files.length) return; const newUrls = []; for (const file of files) { const url = await uploadHorsePhoto(file, orgId); newUrls.push(url); } const updated = [...(rec.photo_urls || []), ...newUrls]; await dbUpdate("farrier_records", rec.id, { photo_urls: updated }); setFarrierRecords(p => p.map(r => r.id === rec.id ? { ...r, photo_urls: updated } : r)); }; input.click(); }}
          style={{ border: "2px dashed #93c5fd", borderRadius: 8, padding: "18px 14px", textAlign: "center", cursor: "pointer", background: "white", transition: "all .2s" }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#666" }}>+</span>
            <span style={{ fontSize: 13, color: "#555", fontWeight: 500 }}>Add attachment files</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (<>
    <div className="page-header-row">
      
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        {saved && <span style={{ color: "var(--gold)", fontSize: 13 }}>✓ Saved</span>}
        <button className="btn btn-primary" onClick={() => { setShowAdd(true); setEditingFarrier(null); setForm(emptyFarrier); }}>+ Log Farrier Visit</button>
      </div>
    </div>
    <div className="stats-row" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
      <div className="stat-card"><div className="stat-icon">🦶</div><div className="stat-label">Total Visits</div><div className="stat-value">{farrierRecords.length}</div><div className="stat-meta">All time</div></div>
      <div className="stat-card green"><div className="stat-icon">📅</div><div className="stat-label">Upcoming</div><div className="stat-value">{upcoming.length}</div><div className="stat-meta">Scheduled appointments</div></div>
      <div className="stat-card" style={{ borderColor: overdue.length > 0 ? "#f44336" : undefined }}><div className="stat-icon">⚠️</div><div className="stat-label">Overdue</div><div className="stat-value" style={{ color: overdue.length > 0 ? "#dc2626" : undefined }}>{overdue.length}</div><div className="stat-meta">Past due date</div></div>
      <div className="stat-card"><div className="stat-icon">💰</div><div className="stat-label">Total Spent</div><div className="stat-value">${totalCost.toFixed(0)}</div><div className="stat-meta">{uniqueFarriers.length} farrier{uniqueFarriers.length !== 1 ? "s" : ""}</div></div>
    </div>
    {loading ? <div className="empty-state">Loading farrier records...</div> : (
      <div className="full-panel">
        <div className="filter-bar">
          {["upcoming", "overdue", "all"].map(f => (
            <button key={f} className={`filter-btn ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)} style={{ textTransform: "capitalize" }}>
              {f === "upcoming" ? `Upcoming (${upcoming.length})` : f === "overdue" ? `Overdue (${overdue.length})` : `All Records (${allSorted.length})`}
            </button>
          ))}
        </div>
        {filter === "upcoming" && (upcoming.length === 0 ? <div className="empty-state">No upcoming farrier appointments. Log a visit and set a next appointment date.</div> : upcoming.map(renderRecord))}
        {filter === "overdue" && (overdue.length === 0 ? <div className="empty-state">No overdue appointments. All horses are up to date!</div> : overdue.map(renderRecord))}
        {filter === "all" && (allSorted.length === 0 ? <div className="empty-state">No farrier records yet. Click "+ Log Farrier Visit" to get started.</div> : allSorted.map(renderRecord))}
      </div>
    )}
    {showAdd && (
      <div className="modal-overlay" onClick={() => { setShowAdd(false); setEditingFarrier(null); }}>
        <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><div className="modal-title">{editingFarrier ? "Edit Farrier Record" : "Log Farrier Visit"}</div><button className="modal-close" onClick={() => { setShowAdd(false); setEditingFarrier(null); }}>✕</button></div>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-row"><label className="form-label">Horse *</label>
                <select className="form-input" value={form.horse_id} onChange={selectHorse} disabled={!!editingFarrier}>
                  <option value="">-- Select Horse --</option>
                  {allHorses.map(h => <option key={h.id} value={h.id}>{h.name} ({h.type})</option>)}
                </select>
              </div>
              <div className="form-row"><label className="form-label">Farrier *</label>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <select className="form-input" style={{ flex: 1 }} value={form.farrier_name} onChange={sf("farrier_name")}>
                    <option value="">Select...</option>
                    {savedFarriers.sort((a, b) => a.name.localeCompare(b.name)).map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                  </select>
                  <button type="button" onClick={async () => { const name = window.prompt("Enter farrier name:"); if (!name || !name.trim()) return; const trimmed = name.trim(); if (savedFarriers.some(f => f.name.toLowerCase() === trimmed.toLowerCase())) { sf("farrier_name")({ target: { value: trimmed } }); return; } const row = await dbInsert("farriers", { org_id: orgId, name: trimmed }); if (row) { setSavedFarriers(p => [...p, row]); sf("farrier_name")({ target: { value: trimmed } }); } }} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "var(--forest)", color: "white", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, lineHeight: 1 }} title="Add new farrier">+</button>
                </div>
              </div>
              <div className="form-row"><label className="form-label">Visit Date *</label><input className="form-input" type="date" value={form.visit_date} onChange={sf("visit_date")} /></div>
              <div className="form-row"><label className="form-label">Service Type</label>
                <select className="form-input" value={form.service_type} onChange={sf("service_type")}>
                  <option value="complete_shoeing">Complete Shoeing</option><option value="back_shoeing">Back Shoeing</option><option value="front_shoeing">Front Shoeing</option><option value="trimming">Trimming</option><option value="replace_lost">Replace Lost Horseshoe</option><option value="one_shoe_left">One Shoe (Left)</option><option value="one_shoe_right">One Shoe (Right)</option><option value="hind_trim">Hind Trim</option><option value="front_trim">Front Trim</option>
                </select>
              </div>
              <div className="form-row"><label className="form-label">Cost ($)</label><input className="form-input" type="number" step="0.01" value={form.cost} onChange={sf("cost")} placeholder="0.00" /></div>
              <div className="form-row"><label className="form-label">Hoof Condition</label>
                <select className="form-input" value={form.hoof_condition} onChange={sf("hoof_condition")}>
                  <option value="">-- Select --</option><option value="excellent">Excellent</option><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option><option value="needs_attention">Needs Attention</option>
                </select>
              </div>
              <div className="form-row"><label className="form-label">Next Appointment</label><input className="form-input" type="date" value={form.next_appointment} onChange={sf("next_appointment")} /></div>
              <div className="form-row" style={{ gridColumn: "1/-1" }}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes} onChange={sf("notes")} placeholder="Hoof notes, observations, recommendations..." /></div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={() => { setShowAdd(false); setEditingFarrier(null); }}>Cancel</button>
            <button className="btn btn-primary" onClick={editingFarrier ? saveEdit : addRecord}>{editingFarrier ? "Save Changes" : "Save Record"}</button>
          </div>
        </div>
      </div>
    )}
    <SaveToast show={saved} />
  </>);
}


// onBrandingChange: the app shell loads the farm's name and logo once, at
// sign-in, into `org`. Without this callback a new logo or farm name only
// appeared after a hard refresh.
function AdminSettings({ orgId, profile, initialTab, billing, onAddCard, onRefreshBilling, onFeedback, onBrandingChange }) {
  const isAdmin = profile?.role === "Admin";
  const { data: integrations, setData: setIntegrations, loading, refetch: refetchIntegrations } = useSupabaseTable("org_integrations", orgId, [], INTEGRATION_COLUMNS);
  const { data: notifSettings, setData: setNotifSettings } = useSupabaseTable("notification_settings", orgId);

  // The other half of the OAuth handshake.
  //
  // Connecting a mailbox happens in a window.open() popup. The popup lands back
  // on the app, posts the outcome to this window and closes itself -- but this
  // page is holding the copy of `integrations` it fetched on mount, so without
  // this listener the card still says "not connected" until the customer
  // reloads by hand. They have just watched themselves sign in successfully, so
  // a screen insisting otherwise reads as a failure. Refetch instead.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;        // our popup only
      if (event.data?.type !== "arcstallion:email-oauth") return;
      if (event.data.ok) refetchIntegrations();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [refetchIntegrations]);
  const { data: emailLogs } = useSupabaseTable("email_log", orgId);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab || (isAdmin ? "team" : "email"));
  const [emailForm, setEmailForm] = useState({ provider: "resend", api_key: "", from_email: "", from_name: "ArcStallion" });
  const [qbForm, setQbForm] = useState({ client_id: "", client_secret: "", company_id: "" });
  const [testEmail, setTestEmail] = useState("");
  const [testStatus, setTestStatus] = useState(null);
  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  // Load existing integration config
  useEffect(() => {
    const emailInt = integrations.find(i => i.provider_type === "email");
    if (emailInt) {
      // The browser can no longer read `credentials`, so the form shows a mask
      // when a secret is stored. save-integration keeps config.secrets_present
      // up to date and ignores a masked value on save.
      const hasKey = !!emailInt.config?.secrets_present?.api_key;
      setEmailForm({ provider: emailInt.provider || "resend", api_key: hasKey ? "••••••••" : "", from_email: emailInt.config?.from_email || "", from_name: emailInt.config?.from_name || "ArcStallion" });
    }
    const qbInt = integrations.find(i => i.provider === "quickbooks");
    if (qbInt) {
      setQbForm({ client_id: qbInt.config?.client_id || "", client_secret: qbInt.config?.secrets_present?.client_secret ? "••••••••" : "", company_id: qbInt.config?.company_id || "" });
    }
  }, [integrations]);

  // `live` marks the events something actually sends. Everything on this screen
  // used to write to notification_settings, which nothing read -- a farm could
  // tick "Foaling Overdue Alert", watch it save, and believe ArcStallion would
  // tell them when a mare went past her date. It never would. The four live ones
  // are now driven by scheduled edge functions (foaling-watch, herd-watch,
  // inventory-watch); the rest are shown disabled with the reason, because a
  // checkbox that saves and does nothing is worse than no checkbox.
  const NOTIFICATION_EVENTS = [
    { id: "foaling_due", label: "Foaling Due Reminder", desc: "7 days before expected foaling date", live: true },
    { id: "foaling_overdue", label: "Foaling Overdue Alert", desc: "When a mare is past her due date", live: true },
    { id: "herd_event_due", label: "Herd Event Due", desc: "Vaccination, farrier, deworming reminders", live: true },
    { id: "low_inventory", label: "Low Semen Inventory", desc: "When straw count drops to or below your threshold", live: true },
    // The three below email the farm about something the farm just did -- somebody
    // clicks save and gets a mail saying they clicked save. The version worth
    // building points outward at the customer, which is a different feature.
    { id: "order_created", label: "New Order Created", desc: "When a new order is placed", note: "Planned as a customer confirmation" },
    { id: "order_paid", label: "Order Paid", desc: "When an order is marked as paid", note: "Planned as a customer receipt" },
    { id: "breeding_event", label: "Breeding Event Logged", desc: "When a breeding event is recorded", note: "Planned as a mare-owner notice" },
    // These two are blocked on data, not effort: breeding_events.pregnancy_status
    // is never populated, mares.owner holds a surname rather than an address, and
    // no shipment email is wired to this switch yet.
    { id: "pregnancy_confirmed", label: "Pregnancy Confirmed", desc: "When pregnancy check is positive", note: "Needs mare-owner email addresses" },
    { id: "order_shipped", label: "Order Shipped", desc: "When a shipment is created for an order", note: "Not yet wired to shipping" },
  ];

  const saveEmailConfig = async () => {
    if (!emailForm.from_email) { alert("From Email is required"); return; }
    const storedKey = !!integrations.find(i => i.provider_type === "email")?.config?.secrets_present?.api_key;
    if (!emailForm.api_key && !storedKey) { alert("API key is required"); return; }
    try {
      // The secret goes to save-integration, which merges it server-side. The
      // browser never reads the stored value in order to write it back.
      const res = await callFunction("save-integration", {
        provider: emailForm.provider,
        provider_type: "email",
        config: { from_email: emailForm.from_email, from_name: emailForm.from_name },
        secrets: { api_key: emailForm.api_key },
        is_active: true,
      });
      setIntegrations(p => {
        const existing = p.find(i => i.provider_type === "email");
        return existing
          ? p.map(i => i.id === existing.id ? { ...i, provider: emailForm.provider, config: res.config, is_active: true } : i)
          : [...p, { id: crypto.randomUUID(), org_id: orgId, provider: emailForm.provider, provider_type: "email", config: res.config, is_active: true }];
      });
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const saveQBConfig = async () => {
    if (!qbForm.client_id) { alert("Client ID is required"); return; }
    try {
      // A masked client_secret means "leave the stored one alone"; the function
      // drops masked values rather than overwriting. This replaces a
      // read-modify-write that can no longer work.
      const res = await callFunction("save-integration", {
        provider: "quickbooks",
        provider_type: "accounting",
        config: { company_id: qbForm.company_id, client_id: qbForm.client_id },
        secrets: { client_secret: qbForm.client_secret },
      });
      setIntegrations(p => {
        const existing = p.find(i => i.provider === "quickbooks");
        return existing
          ? p.map(i => i.id === existing.id ? { ...i, config: res.config } : i)
          : [...p, { id: crypto.randomUUID(), org_id: orgId, provider: "quickbooks", provider_type: "accounting", config: res.config, is_active: false }];
      });
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  // Farm-wide threshold for the low semen inventory alert.
  //
  // Fetched here rather than threaded down from the app-level `org` state, which
  // deliberately carries only branding (name and logo_url). Written back on blur
  // so a half-typed number never reaches the database, and reverted on a bad
  // value or a failed write so the box never shows something that was not saved.
  const [strawThreshold, setStrawThreshold] = useState(10);
  const [savedThreshold, setSavedThreshold] = useState(10);
  const [orgRow, setOrgRow] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase.from("organizations")
        .select("name, address, city, state, zip, phone, email, website, logo_url, low_straw_threshold, email_signature")
        .eq("id", orgId).maybeSingle();
      if (!alive) return;
      setOrgRow(data || null);
      setFarmForm(Object.fromEntries(FARM_FIELDS.map(k => [k, data?.[k] ?? ""])));
      const n = Number.isFinite(data?.low_straw_threshold) ? data.low_straw_threshold : 10;
      setStrawThreshold(n); setSavedThreshold(n);
      const sig = data?.email_signature ?? "";
      setSignature(sig); setSavedSignature(sig);
    })();
    return () => { alive = false; };
  }, [orgId]);

  // ─── FARM PROFILE ───
  //
  // These columns have been farm-editable since lock_billing_columns_properly
  // granted UPDATE on exactly this list -- name, address, city, state, zip,
  // phone, email, website, logo_url -- and no screen was ever built to write
  // them. Hillary's details are in the database because they were seeded.
  //
  // That gap ran downhill: the email signature "generates from your farm
  // details", which for any farm but the seeded one meant generating from
  // nothing.
  const FARM_FIELDS = ["name", "address", "city", "state", "zip", "phone", "email", "website"];
  const [farmForm, setFarmForm] = useState(null);
  const [farmSaving, setFarmSaving] = useState(false);
  const [farmSaved, setFarmSaved] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const farmDirty = !!farmForm && !!orgRow && FARM_FIELDS.some(k => (farmForm[k] ?? "") !== (orgRow[k] ?? ""));

  const saveFarm = async () => {
    if (!farmForm || !farmDirty) return;
    setFarmSaving(true);
    try {
      const patch = {};
      for (const k of FARM_FIELDS) patch[k] = (farmForm[k] ?? "").trim() || null;
      await dbUpdate("organizations", orgId, patch);
      setOrgRow(p => ({ ...(p || {}), ...patch }));
      if (onBrandingChange && patch.name) onBrandingChange({ name: patch.name });
      setFarmSaved(true); setTimeout(() => setFarmSaved(false), 2200);
      save();
    } catch (e) { alert("Error: " + e.message); }
    setFarmSaving(false);
  };

  /**
   * Logos go in a PUBLIC bucket, unlike horse-photos.
   *
   * A logo in an email is fetched by the recipient's mail client, or by Gmail's
   * and Outlook's image proxies, none of which carry a session. A signed URL
   * expires and a private object 400s, so public read is the only thing that
   * renders in an inbox. Writes are still scoped to this farm by the object key.
   *
   * And hosting it here rather than linking the farm's own website is the whole
   * point: Capital Quarter Horses' logo sits on SiteGround, which answers
   * non-browser requests with a CAPTCHA page instead of the image -- so it
   * silently fails wherever mail clients proxy images.
   */
  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("That image is over 2 MB. Try a smaller one."); return; }
    setLogoBusy(true);
    try {
      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const path = `${orgId}/${crypto.randomUUID()}.${ext}`;
      assertWritable();
      const { error: upErr } = await supabase.storage.from("farm-logos")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("farm-logos").getPublicUrl(path);
      await dbUpdate("organizations", orgId, { logo_url: data.publicUrl });
      setOrgRow(p => ({ ...(p || {}), logo_url: data.publicUrl }));
      if (onBrandingChange) onBrandingChange({ logo_url: data.publicUrl });
      save();
    } catch (e) { alert("Logo upload failed: " + e.message); }
    setLogoBusy(false);
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove your farm logo? Emails and the dashboard will show your farm name instead.")) return;
    setLogoBusy(true);
    try {
      await dbUpdate("organizations", orgId, { logo_url: null });
      setOrgRow(p => ({ ...(p || {}), logo_url: null }));
      if (onBrandingChange) onBrandingChange({ logo_url: null });
      save();
    } catch (e) { alert("Error: " + e.message); }
    setLogoBusy(false);
  };

  // Farm sign-off for customer-facing mail. Stored as PLAIN TEXT -- the farm
  // owns the words, send.ts owns the markup and renders the logo.
  //
  // Empty string in the box means "use the generated default", which is stored
  // as NULL. That is not the same as "no signature": a farm that adds their
  // phone number next month gets it picked up automatically rather than being
  // stuck with a copy they pasted once.
  const [signature, setSignature] = useState("");
  const [savedSignature, setSavedSignature] = useState("");
  const [sigStatus, setSigStatus] = useState("idle");   // idle | saving | saved

  // Mirrors defaultSignatureText() in supabase/functions/_shared/send.ts.
  // A browser cannot import a Deno module, so this is duplicated on purpose --
  // the server copy is what actually goes out; this one is the preview. Keep
  // them in step.
  const generatedSignature = useMemo(() => {
    const o = orgRow || {};
    const lines = [];
    if (o.name) lines.push(o.name);
    const cityLine = [o.city, [o.state, o.zip].filter(Boolean).join(" ").trim()].filter(Boolean).join(", ");
    const address = [o.address, cityLine].filter(Boolean).join(", ");
    if (address) lines.push(address);
    const contact = [o.phone, o.email].filter(Boolean).join("  ·  ");
    if (contact) lines.push(contact);
    if (o.website) lines.push(String(o.website).replace(/^https?:\/\//, "").replace(/\/$/, ""));
    return lines.join("\n");
  }, [orgRow]);

  const saveSignature = async () => {
    const next = signature.trim();
    if (next === (savedSignature || "")) return;
    setSigStatus("saving");
    try {
      await dbUpdate("organizations", orgId, { email_signature: next || null });
      setSavedSignature(next);
      setSigStatus("saved");
      setTimeout(() => setSigStatus("idle"), 1800);
      save();
    } catch (e) { setSigStatus("idle"); alert("Error: " + e.message); }
  };

  const saveStrawThreshold = async () => {
    const n = parseInt(strawThreshold, 10);
    if (!Number.isFinite(n) || n < 0) { setStrawThreshold(savedThreshold); return; }
    if (n === savedThreshold) return;
    try {
      await dbUpdate("organizations", orgId, { low_straw_threshold: n });
      setSavedThreshold(n);
      save();
    } catch (e) { alert("Error: " + e.message); setStrawThreshold(savedThreshold); }
  };

  const toggleNotification = async (eventId, field, value) => {
    try {
      const existing = notifSettings.find(n => n.event_type === eventId);
      if (existing) {
        await dbUpdate("notification_settings", existing.id, { [field]: value, updated_at: new Date().toISOString() });
        setNotifSettings(p => p.map(n => n.id === existing.id ? { ...n, [field]: value } : n));
      } else {
        const row = await dbInsert("notification_settings", {
          id: crypto.randomUUID(), org_id: orgId, event_type: eventId, [field]: value
        });
        setNotifSettings(p => [...p, row]);
      }
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const sendTestEmail = async () => {
    if (!testEmail) return;
    setTestStatus("sending");
    try {
      const emailInt = integrations.find(i => i.provider_type === "email" && i.is_active);
      if (!emailInt) { setTestStatus("error"); alert("Configure email provider first"); return; }
      // org_id is derived from the signed-in user's token server-side
      await callFunction("send-email", {
        to: testEmail,
        subject: "ArcStallion - Test Email",
        body: "<h2>It works!</h2><p>Your ArcStallion email integration is configured correctly.</p><p style='color:#666;font-size:13px;'>This is a test email sent from your ArcStallion dashboard.</p>"
      });
      setTestStatus("sent");
    } catch (e) { setTestStatus("error"); alert("Failed: " + e.message); }
  };

  // Prefer the ACTIVE mailbox. A farm can hold several email rows -- connecting
  // Outlook now deactivates Gmail rather than leaving both live -- and a bare
  // .find() returns whichever happens to sort first, so the card could report
  // "not connected" while mail was going out perfectly well from the other row.
  const emailInt = integrations.find(i => i.provider_type === "email" && i.is_active)
                || integrations.find(i => i.provider_type === "email");
  const qbInt = integrations.find(i => i.provider === "quickbooks");

  const TABS = [
    ...(isAdmin ? [{ id: "team", label: "Team", icon: "👥" }] : []),
    // Admin-only because organizations_update_admin requires it in WITH CHECK:
    // a Staff user would get a silent RLS failure on save rather than a form.
    ...(isAdmin ? [{ id: "farm", label: "Farm Profile", icon: "🏇" }] : []),
    ...(isAdmin && billing && (billing.enabled || billing.managedExternally) ? [{ id: "billing", label: "Billing", icon: "💳" }] : []),
    { id: "pricebook", label: "Price Book", icon: "💲" },
    { id: "contracts", label: "Contract Templates", icon: "📄" },
    { id: "email", label: "Email", icon: "✉️" },
    // QuickBooks is hidden, and as of 2026-09-23 the endpoints behind it are
    // retired too. qb-sync and qb-auth ran with verify_jwt:false, took org_id
    // from the caller, and acted with the service-role key -- the same
    // open-relay shape send-email had (AUDIT_REPORT.md S3). Hiding this tab
    // never protected them; they were public URLs. Both are now authenticated
    // stubs returning 410, proven by an unauthenticated call from outside.
    // To restore: rebuild qb-auth/qb-sync the way email OAuth was fixed
    // (authenticated start, HMAC-signed state, verify_jwt:true, org from the
    // JWT) -- the retired stubs carry the full recipe -- then re-add this line.
    { id: "shipping", label: "Shipping Carriers", icon: "🚚" },
    { id: "notifications", label: "Notifications", icon: "🔔" },
    { id: "logs", label: "Email Log", icon: "📋" },
  ];

  // ─── PRICE BOOK STATE ───
  const { data: priceItems, setData: setPriceItems, loading: pbLoading } = useSupabaseTable("price_book", orgId);
  const { data: pbDefaults, setData: setPbDefaults } = useSupabaseTable("price_book_defaults", orgId);
  const [pbFilter, setPbFilter] = useState("All");
  const [pbSearch, setPbSearch] = useState("");
  const [pbEditId, setPbEditId] = useState(null);
  const [pbForm, setPbForm] = useState({ name: "", type: "Service", cost_per_unit: "", markup_pct: "", notes: "" });
  const [showAddPb, setShowAddPb] = useState(false);
  const [seedingPb, setSeedingPb] = useState(false);

  const defaultMarkup = (type) => {
    const d = pbDefaults.find(d => d.type === type);
    return d ? parseFloat(d.default_markup_pct) : type === "Service" ? 1.0 : 0.5;
  };

  const calcCustomerPrice = (cost, markup) => {
    const c = parseFloat(cost) || 0;
    const m = parseFloat(markup) || 0;
    return c > 0 ? (c * (1 + m)).toFixed(2) : "0.00";
  };

  const savePbItem = async () => {
    if (!pbForm.name.trim()) { alert("Name is required"); return; }
    const cost = parseFloat(pbForm.cost_per_unit) || 0;
    const markup = parseFloat(pbForm.markup_pct) || defaultMarkup(pbForm.type);
    try {
      if (pbEditId) {
        await dbUpdate("price_book", pbEditId, { name: pbForm.name, type: pbForm.type, cost_per_unit: cost, markup_pct: markup, notes: pbForm.notes || null, updated_at: new Date().toISOString() });
        setPriceItems(p => p.map(i => i.id === pbEditId ? { ...i, name: pbForm.name, type: pbForm.type, cost_per_unit: cost, markup_pct: markup, customer_price: parseFloat(calcCustomerPrice(cost, markup)), notes: pbForm.notes } : i));
      } else {
        const row = await dbInsert("price_book", { id: crypto.randomUUID(), org_id: orgId, name: pbForm.name, type: pbForm.type, cost_per_unit: cost, markup_pct: markup, is_custom: true, is_active: true, notes: pbForm.notes || null, sort_order: priceItems.length });
        setPriceItems(p => [row, ...p]);
      }
      setPbEditId(null); setShowAddPb(false); setPbForm({ name: "", type: "Service", cost_per_unit: "", markup_pct: "", notes: "" }); save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const togglePbActive = async (id, current) => {
    try {
      await dbUpdate("price_book", id, { is_active: !current, updated_at: new Date().toISOString() });
      setPriceItems(p => p.map(i => i.id === id ? { ...i, is_active: !current } : i));
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const deletePbItem = async (id) => {
    if (!window.confirm("Remove this item from the price book?")) return;
    try {
      assertWritable();
      await supabase.from("price_book").delete().eq("id", id);
      setPriceItems(p => p.filter(i => i.id !== id));
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const saveDefaultMarkup = async (type, pct) => {
    try {
      const existing = pbDefaults.find(d => d.type === type);
      if (existing) {
        await dbUpdate("price_book_defaults", existing.id, { default_markup_pct: pct, updated_at: new Date().toISOString() });
        setPbDefaults(p => p.map(d => d.id === existing.id ? { ...d, default_markup_pct: pct } : d));
      } else {
        const row = await dbInsert("price_book_defaults", { id: crypto.randomUUID(), org_id: orgId, type, default_markup_pct: pct });
        setPbDefaults(p => [...p, row]);
      }
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const seedPriceBook = async () => {
    if (priceItems.length > 0 && !window.confirm("You already have items in your price book. This will add the default breeding items. Continue?")) return;
    setSeedingPb(true);
    const defaults = [
      { name: "Foaling", type: "Service", cost: 250, markup: 1.0 },
      { name: "Full Bucket Kick Start", type: "Medication", cost: 20, markup: 0.5 },
      { name: "Dip Navel", type: "Service", cost: 0, markup: 0.5 },
      { name: "Enema", type: "Medication", cost: 2, markup: 1.0 },
      { name: "Place Foal Alert", type: "Service", cost: 0, markup: 1.0 },
      { name: "Run Plasma", type: "Service", cost: 200, markup: 1.0 },
      { name: "Tube Foal", type: "Service", cost: 0, markup: 1.0 },
      { name: "Lavage", type: "Service", cost: 70, markup: 0.5 },
      { name: "Infuse - Naxcel", type: "Service", cost: 2, markup: 1.0 },
      { name: "Infuse - Ticar", type: "Service", cost: 31, markup: 1.0 },
      { name: "Infuse - Cipro", type: "Service", cost: 16, markup: 1.0 },
      { name: "Infuse - Amikacin", type: "Service", cost: 16, markup: 1.0 },
      { name: "Breed - Cooled", type: "Service", cost: 75, markup: 1.0 },
      { name: "Culture", type: "Service", cost: 20, markup: 1.0 },
      { name: "Sensitivity", type: "Service", cost: 35, markup: 1.0 },
      { name: "Milk Test", type: "Service", cost: 2.5, markup: 1.0 },
      { name: "Full Bucket", type: "Medication", cost: 20, markup: 1.0 },
      { name: "Metro", type: "Medication", cost: 5, markup: 1.0 },
      { name: "Dorm", type: "Medication", cost: 30, markup: 1.0 },
      { name: "Xylazine", type: "Medication", cost: 1.5, markup: 1.0 },
      { name: "Deslorelin", type: "Medication", cost: 13, markup: 1.0 },
      { name: "Histrelin", type: "Medication", cost: 25, markup: 1.0 },
      { name: "Oxytocin", type: "Medication", cost: 1, markup: 1.0 },
      { name: "Estrumate", type: "Medication", cost: 1, markup: 1.0 },
      { name: "Regumate", type: "Medication", cost: 2, markup: 1.0 },
      { name: "Dex", type: "Medication", cost: 1, markup: 1.0 },
      { name: "Baytril Paste", type: "Medication", cost: 18.5, markup: 1.0 },
      { name: "Equidone", type: "Medication", cost: 70, markup: 1.0 },
    ];
    try {
      const rows = defaults.map((d, i) => ({ id: crypto.randomUUID(), org_id: orgId, name: d.name, type: d.type, cost_per_unit: d.cost, markup_pct: d.markup, is_custom: false, is_active: true, sort_order: i }));
      assertWritable();
      const { data, error } = await supabase.from("price_book").insert(rows).select();
      if (error) throw error;
      setPriceItems(p => [...(data || []), ...p]);
      // Also set default markups
      await saveDefaultMarkup("Service", 1.0);
      await saveDefaultMarkup("Medication", 0.5);
      save();
    } catch (e) { alert("Error seeding: " + e.message); }
    setSeedingPb(false);
  };

  const filteredPb = priceItems
    .filter(i => pbFilter === "All" || i.type === pbFilter)
    .filter(i => i.name.toLowerCase().includes(pbSearch.toLowerCase()))
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  // ─── CONTRACT TEMPLATES STATE ───
  const { data: contractTemplates, setData: setContractTemplates, loading: ctLoading } = useSupabaseTable("contract_templates", orgId);
  const { data: stallionsForContracts } = useSupabaseTable("stallions", orgId);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [placingTemplate, setPlacingTemplate] = useState(null);   // e-signing field setup
  const [previewTemplate, setPreviewTemplate] = useState(null);   // quick look at a template PDF
  const [ctUploading, setCtUploading] = useState(false);
  const [ctForm, setCtForm] = useState({ name: "", description: "", stallion_id: "", stallion_name: "", contract_type: "service_agreement", season: "2026" });
  const [ctFile, setCtFile] = useState(null);
  const [ctEditId, setCtEditId] = useState(null);
  const [ctDragOver, setCtDragOver] = useState(false);

  const CONTRACT_TYPES = { service_agreement: "Stallion Service Agreement", icsi_contract: "ICSI Contract", boarding: "Boarding Agreement", lease: "Lease Agreement", other: "Other" };

  const handleCtDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setCtDragOver(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type !== "application/pdf") { alert("Only PDF files are accepted"); return; }
      if (file.size > 5 * 1024 * 1024) { alert("File must be under 5MB"); return; }
      const baseName = file.name.replace(/\.pdf$/i, "").replace(/[_-]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      setCtFile(file);
      setCtEditId(null);
      setCtForm({ name: baseName, description: "", stallion_id: "", stallion_name: "", contract_type: file.name.toLowerCase().includes("icsi") ? "icsi_contract" : "service_agreement", season: "2026" });
      setShowAddTemplate(true);
    }
  };

  const handleCtDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setCtDragOver(true); };
  const handleCtDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setCtDragOver(false); };

  const uploadTemplate = async () => {
    if (!ctForm.name.trim()) { alert("Template name is required"); return; }
    if (!ctFile && !ctEditId) { alert("Please select a PDF file"); return; }
    setCtUploading(true);
    try {
      let storagePath = "";
      let fileName = "";
      let fileSize = 0;
      if (ctFile) {
        const ext = ctFile.name.split(".").pop();
        const safeName = ctForm.name.replace(/[^a-zA-Z0-9-_]/g, "_");
        storagePath = `templates/${orgId}/${safeName}_${Date.now()}.${ext}`;
        fileName = ctFile.name;
        fileSize = ctFile.size;
        assertWritable();
        const { error: upErr } = await supabase.storage.from("contracts").upload(storagePath, ctFile, { contentType: "application/pdf", upsert: false });
        if (upErr) throw upErr;
      }
      const stallion = stallionsForContracts.find(s => s.id === ctForm.stallion_id);
      if (ctEditId) {
        const updates = { name: ctForm.name, description: ctForm.description || null, stallion_id: ctForm.stallion_id || null, stallion_name: stallion?.name || ctForm.stallion_name || null, contract_type: ctForm.contract_type, season: ctForm.season || null, updated_at: new Date().toISOString() };
        if (ctFile) { updates.storage_path = storagePath; updates.file_name = fileName; updates.file_size = fileSize; }
        await dbUpdate("contract_templates", ctEditId, updates);
        setContractTemplates(p => p.map(t => t.id === ctEditId ? { ...t, ...updates } : t));
      } else {
        const row = await dbInsert("contract_templates", { id: crypto.randomUUID(), org_id: orgId, name: ctForm.name, description: ctForm.description || null, stallion_id: ctForm.stallion_id || null, stallion_name: stallion?.name || null, contract_type: ctForm.contract_type, season: ctForm.season || null, storage_path: storagePath, file_name: fileName, file_size: fileSize, is_active: true });
        setContractTemplates(p => [row, ...p]);
      }
      setShowAddTemplate(false); setCtEditId(null); setCtFile(null); setCtForm({ name: "", description: "", stallion_id: "", stallion_name: "", contract_type: "service_agreement", season: "2026" });
      save();
    } catch (e) { alert("Error: " + e.message); }
    setCtUploading(false);
  };

  const downloadTemplate = async (template) => {
    try {
      const { data, error } = await supabase.storage.from("contracts").download(template.storage_path);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = template.file_name || "contract.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Error downloading: " + e.message); }
  };

  const deleteTemplate = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
    try {
      assertWritable();
      if (template.storage_path) await supabase.storage.from("contracts").remove([template.storage_path]);
      await supabase.from("contract_templates").delete().eq("id", template.id);
      setContractTemplates(p => p.filter(t => t.id !== template.id));
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  const toggleTemplateActive = async (id, current) => {
    try {
      await dbUpdate("contract_templates", id, { is_active: !current, updated_at: new Date().toISOString() });
      setContractTemplates(p => p.map(t => t.id === id ? { ...t, is_active: !current } : t));
      save();
    } catch (e) { alert("Error: " + e.message); }
  };

  // Shipping / EasyPost settings
  const [easypostKey, setEasypostKey] = useState("");
  const [shipFromAddr, setShipFromAddr] = useState({ name: "", company: "", address: "", city: "", state: "", zip: "", phone: "" });
  useEffect(() => {
    const shipInt = integrations.find(i => i.provider_type === "shipping");
    if (shipInt) {
      setEasypostKey(shipInt.config?.secrets_present?.easypost_api_key ? "••••••••" : "");
      setShipFromAddr(prev => ({ ...prev, ...shipInt.config?.ship_from }));
    }
  }, [integrations]);

  const saveShippingConfig = async () => {
    try {
      // The EasyPost key used to live in `config`, readable by every signed-in
      // user and used directly from the browser. It is a credential now.
      const res = await callFunction("save-integration", {
        provider: "easypost",
        provider_type: "shipping",
        config: { ship_from: shipFromAddr },
        secrets: { easypost_api_key: easypostKey },
        is_active: true,
      });
      setIntegrations(p => {
        const existing = p.find(i => i.provider_type === "shipping");
        return existing
          ? p.map(i => i.id === existing.id ? { ...i, config: res.config } : i)
          : [...p, { id: crypto.randomUUID(), org_id: orgId, provider: "easypost", provider_type: "shipping", config: res.config, is_active: true }];
      });
      save();
    } catch (e) { alert("Error saving shipping settings: " + e.message); }
  };

  const sfAddr = (field, value) => setShipFromAddr(prev => ({ ...prev, [field]: value }));

  return (<>
    <div className="page-header-row">
      
    </div>

    {/* Tab bar */}
    <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "2px solid var(--parchment-dark)", paddingBottom: 0 }}>
      {TABS.map(t => (
        <button key={t.id} onClick={() => setActiveTab(t.id)}
          style={{ padding: "10px 18px", fontSize: 13, fontWeight: activeTab === t.id ? 700 : 400, color: activeTab === t.id ? "var(--forest)" : "var(--text-light)",
            background: activeTab === t.id ? "var(--parchment)" : "transparent", border: "none", borderBottom: activeTab === t.id ? "2px solid var(--forest)" : "2px solid transparent",
            cursor: "pointer", borderRadius: "6px 6px 0 0", marginBottom: -2, transition: "all .15s" }}>
          {t.icon} {t.label}
        </button>
      ))}
    </div>

    {/* PRICE BOOK TAB */}
    {activeTab === "pricebook" && (
      <div>
        {/* Header row with stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
          <div className="stat-card"><div className="stat-icon">💲</div><div className="stat-label">Total Items</div><div className="stat-value">{priceItems.filter(i => i.is_active).length}</div><div className="stat-meta">Active in price book</div></div>
          <div className="stat-card"><div className="stat-icon">🔧</div><div className="stat-label">Services</div><div className="stat-value">{priceItems.filter(i => i.type === "Service" && i.is_active).length}</div><div className="stat-meta">{`Default markup: ${(defaultMarkup("Service") * 100).toFixed(0)}%`}</div></div>
          <div className="stat-card"><div className="stat-icon">💊</div><div className="stat-label">Medications</div><div className="stat-value">{priceItems.filter(i => i.type === "Medication" && i.is_active).length}</div><div className="stat-meta">{`Default markup: ${(defaultMarkup("Medication") * 100).toFixed(0)}%`}</div></div>
          <div className="stat-card"><div className="stat-icon">✏️</div><div className="stat-label">Custom Items</div><div className="stat-value">{priceItems.filter(i => i.is_custom).length}</div><div className="stat-meta">Added by you</div></div>
        </div>

        {/* Default Markup Settings */}
        <div className="full-panel" style={{ marginBottom: 16 }}>
          <div style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700, color: "var(--forest)", borderBottom: "1px solid var(--parchment-dark)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>⚙️ Default Markup Rules</span>
            <span style={{ fontSize: 11, color: "var(--text-light)", fontWeight: 400 }}>Applied automatically when adding new items</span>
          </div>
          <div style={{ padding: 16, display: "flex", gap: 24, alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-mid)" }}>Services Markup:</label>
              <select className="form-input" style={{ width: 100 }} value={defaultMarkup("Service")} onChange={e => saveDefaultMarkup("Service", parseFloat(e.target.value))}>
                <option value="0.25">25%</option><option value="0.5">50%</option><option value="0.75">75%</option><option value="1.0">100%</option><option value="1.5">150%</option><option value="2.0">200%</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-mid)" }}>Medications Markup:</label>
              <select className="form-input" style={{ width: 100 }} value={defaultMarkup("Medication")} onChange={e => saveDefaultMarkup("Medication", parseFloat(e.target.value))}>
                <option value="0.25">25%</option><option value="0.5">50%</option><option value="0.75">75%</option><option value="1.0">100%</option><option value="1.5">150%</option><option value="2.0">200%</option>
              </select>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "center" }}>
          <input className="search-input" placeholder="Search items..." value={pbSearch} onChange={e => setPbSearch(e.target.value)} style={{ flex: 1 }} />
          <select className="form-input" style={{ width: 150 }} value={pbFilter} onChange={e => setPbFilter(e.target.value)}>
            <option value="All">All Types</option><option value="Service">Services</option><option value="Medication">Medications</option>
          </select>
          {priceItems.length === 0 && (
            <button className="btn" style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", whiteSpace: "nowrap" }} onClick={seedPriceBook} disabled={seedingPb}>
              {seedingPb ? "Loading..." : "📋 Load Default Price List"}
            </button>
          )}
          <button className="btn btn-primary" style={{ whiteSpace: "nowrap" }} onClick={() => { setPbEditId(null); setPbForm({ name: "", type: "Service", cost_per_unit: "", markup_pct: String(defaultMarkup("Service")), notes: "" }); setShowAddPb(true); }}>+ Add Item</button>
        </div>

        {/* Price Book Table */}
        <div className="full-panel">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>Name</th>
                <th style={{ width: 100 }}>Type</th>
                <th style={{ width: 110, textAlign: "right" }}>Cost/Unit</th>
                <th style={{ width: 90, textAlign: "right" }}>Markup %</th>
                <th style={{ width: 120, textAlign: "right" }}>Customer Price</th>
                <th style={{ width: 80, textAlign: "center" }}>Active</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {pbLoading ? (
                <tr><td colSpan={8} className="empty-state">Loading price book...</td></tr>
              ) : filteredPb.length === 0 ? (
                <tr><td colSpan={8} className="empty-state">
                  {priceItems.length === 0 ? (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>No items in your price book yet</div>
                      <div style={{ fontSize: 12, color: "var(--text-light)", marginBottom: 12 }}>Load the default breeding price list to get started, or add items manually.</div>
                      <button className="btn" style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" }} onClick={seedPriceBook} disabled={seedingPb}>
                        {seedingPb ? "Loading..." : "📋 Load Default Price List"}
                      </button>
                    </div>
                  ) : "No items match your search."}
                </td></tr>
              ) : filteredPb.map(item => (
                <tr key={item.id} style={{ opacity: item.is_active ? 1 : 0.45 }}>
                  <td style={{ textAlign: "center", fontSize: 14 }}>{item.type === "Service" ? "🔧" : "💊"}</td>
                  <td>
                    <div style={{ fontWeight: 600, color: "var(--forest)" }}>{item.name}</div>
                    {item.notes && <div style={{ fontSize: 10, color: "var(--text-light)" }}>{item.notes}</div>}
                    {item.is_custom && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: "#eff6ff", color: "#2563eb", fontWeight: 600 }}>Custom</span>}
                  </td>
                  <td><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 600, background: item.type === "Service" ? "#dcfce7" : "#fef3c7", color: item.type === "Service" ? "#166534" : "#92400e" }}>{item.type}</span></td>
                  <td style={{ textAlign: "right", fontFamily: "monospace", fontSize: 13 }}>${(parseFloat(item.cost_per_unit) || 0).toFixed(2)}</td>
                  <td style={{ textAlign: "right", fontFamily: "monospace", fontSize: 13 }}>{((parseFloat(item.markup_pct) || 0) * 100).toFixed(0)}%</td>
                  <td style={{ textAlign: "right", fontWeight: 700, fontFamily: "monospace", fontSize: 13, color: "var(--forest)" }}>${(parseFloat(item.customer_price) || parseFloat(calcCustomerPrice(item.cost_per_unit, item.markup_pct))).toFixed(2)}</td>
                  <td style={{ textAlign: "center" }}>
                    <button onClick={() => togglePbActive(item.id, item.is_active)} style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer" }}>{item.is_active ? "✅" : "⬜"}</button>
                  </td>
                  <td style={{ display: "flex", gap: 4 }}>
                    <button className="btn btn-sm" style={{ fontSize: 11, padding: "3px 8px" }} onClick={() => { setPbEditId(item.id); setPbForm({ name: item.name, type: item.type, cost_per_unit: String(item.cost_per_unit || 0), markup_pct: String(item.markup_pct || 0), notes: item.notes || "" }); setShowAddPb(true); }}>Edit</button>
                    <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6", fontSize: 11, padding: "3px 8px" }} onClick={() => deletePbItem(item.id)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Helpful note */}
        {priceItems.length > 0 && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: "#eff6ff", borderRadius: 8, border: "1px solid #bfdbfe", fontSize: 12, color: "#1e40af" }}>
            <strong>How pricing works:</strong> When creating an order, you can select items from this price book. The customer price is calculated automatically as: Cost + (Cost × Markup %). Toggle items off to hide them from order selection without deleting them.
          </div>
        )}
      </div>
    )}

    {/* Add/Edit Price Book Item Modal */}
    {showAddPb && (
      <div className="modal-overlay" onClick={() => { setShowAddPb(false); setPbEditId(null); }}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
          <div className="modal-header"><div className="modal-title">{pbEditId ? "Edit Item" : "Add Price Book Item"}</div><button className="modal-close" onClick={() => { setShowAddPb(false); setPbEditId(null); }}>✕</button></div>
          <div className="modal-body">
            <div className="form-row"><label className="form-label">Item Name *</label><input className="form-input" value={pbForm.name} onChange={e => setPbForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Lavage, Deslorelin, Custom Service..." /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="form-row"><label className="form-label">Type</label>
                <select className="form-input" value={pbForm.type} onChange={e => { const t = e.target.value; setPbForm(p => ({ ...p, type: t, markup_pct: p.markup_pct || String(defaultMarkup(t)) })); }}>
                  <option value="Service">Service</option><option value="Medication">Medication</option>
                </select>
              </div>
              <div className="form-row"><label className="form-label">Cost Per Unit ($)</label><input className="form-input" type="number" step="0.01" min="0" value={pbForm.cost_per_unit} onChange={e => setPbForm(p => ({ ...p, cost_per_unit: e.target.value }))} placeholder="0.00" /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="form-row"><label className="form-label">Markup %</label>
                <select className="form-input" value={pbForm.markup_pct} onChange={e => setPbForm(p => ({ ...p, markup_pct: e.target.value }))}>
                  <option value="0.25">25%</option><option value="0.5">50%</option><option value="0.75">75%</option><option value="1.0">100%</option><option value="1.5">150%</option><option value="2.0">200%</option><option value="2.5">250%</option><option value="3.0">300%</option>
                </select>
              </div>
              <div className="form-row"><label className="form-label">Customer Price</label>
                <div className="form-input" style={{ background: "#f0fdf4", fontWeight: 700, color: "var(--forest)", cursor: "default" }}>${calcCustomerPrice(pbForm.cost_per_unit, pbForm.markup_pct)}</div>
              </div>
            </div>
            <div className="form-row"><label className="form-label">Notes (optional)</label><input className="form-input" value={pbForm.notes} onChange={e => setPbForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes about this item..." /></div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={() => { setShowAddPb(false); setPbEditId(null); }}>Cancel</button><button className="btn btn-primary" onClick={savePbItem}>{pbEditId ? "Update" : "Add Item"}</button></div>
        </div>
      </div>
    )}

    {/* CONTRACT TEMPLATES TAB */}
    {activeTab === "contracts" && (
      <div onDragOver={handleCtDragOver} onDragLeave={handleCtDragLeave} onDrop={handleCtDrop} style={{ position: "relative" }}>
        {/* Drag overlay */}
        {ctDragOver && (
          <div style={{ position: "absolute", inset: 0, zIndex: 50, background: "rgba(37,99,235,.08)", border: "3px dashed #2563eb", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <div style={{ background: "white", padding: "24px 40px", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,.12)", textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#2563eb" }}>Drop PDF here</div>
              <div style={{ fontSize: 12, color: "var(--text-light)", marginTop: 4 }}>Release to upload contract template</div>
            </div>
          </div>
        )}
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
          <div className="stat-card"><div className="stat-icon">📄</div><div className="stat-label">Total Templates</div><div className="stat-value">{contractTemplates.length}</div><div className="stat-meta">Contract templates</div></div>
          <div className="stat-card"><div className="stat-icon">✅</div><div className="stat-label">Active</div><div className="stat-value">{contractTemplates.filter(t => t.is_active).length}</div><div className="stat-meta">Available for assignment</div></div>
          <div className="stat-card"><div className="stat-icon">🐎</div><div className="stat-label">Stallions Covered</div><div className="stat-value">{new Set(contractTemplates.filter(t => t.stallion_name).map(t => t.stallion_name)).size}</div><div className="stat-meta">With contracts</div></div>
        </div>

        {/* Toolbar */}
        <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "center" }}>
          <div style={{ flex: 1, fontSize: 13, color: "var(--text-mid)" }}>Upload your contract PDFs here. These templates can then be assigned to customers from their profile.</div>
          <button className="btn btn-primary" style={{ whiteSpace: "nowrap" }} onClick={() => { setCtEditId(null); setCtFile(null); setCtForm({ name: "", description: "", stallion_id: "", stallion_name: "", contract_type: "service_agreement", season: "2026" }); setShowAddTemplate(true); }}>+ Upload Template</button>
        </div>

        {/* Templates list */}
        <div className="full-panel">
          {ctLoading ? <div className="empty-state">Loading templates...</div> : contractTemplates.length === 0 ? (
            <div className="empty-state" style={{ padding: 32, textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>No contract templates yet</div>
              <div style={{ fontSize: 12, color: "var(--text-light)", marginBottom: 6 }}>Upload your stallion service agreements, ICSI contracts, and other documents your customers need to sign.</div>
              <div style={{ fontSize: 12, color: "#2563eb", fontWeight: 500, marginBottom: 16 }}>Drag and drop a PDF anywhere on this page, or click the button below.</div>
              <button className="btn btn-primary" onClick={() => { setCtEditId(null); setCtFile(null); setCtForm({ name: "", description: "", stallion_id: "", stallion_name: "", contract_type: "service_agreement", season: "2026" }); setShowAddTemplate(true); }}>+ Upload First Template</button>
            </div>
          ) : (
            <table className="data-table">
              <thead><tr><th>Contract Name</th><th style={{ width: 160 }}>Type</th><th style={{ width: 130 }}>Stallion</th><th style={{ width: 70 }}>Season</th><th style={{ width: 130 }}>File</th><th style={{ width: 130 }}>E-signing</th><th style={{ width: 70, textAlign: "center" }}>Active</th><th style={{ width: 120 }}></th></tr></thead>
              <tbody>
                {contractTemplates.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).map(t => (
                  <tr key={t.id} style={{ opacity: t.is_active ? 1 : 0.45 }}>
                    <td>
                      <div style={{ fontWeight: 600, color: "var(--forest)" }}>{t.name}</div>
                      {t.description && <div style={{ fontSize: 11, color: "var(--text-light)" }}>{t.description}</div>}
                    </td>
                    <td><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 600, background: t.contract_type === "icsi_contract" ? "#f3e8ff" : t.contract_type === "service_agreement" ? "#dcfce7" : "#e8f0e8", color: t.contract_type === "icsi_contract" ? "#7c3aed" : t.contract_type === "service_agreement" ? "#166534" : "#555" }}>{CONTRACT_TYPES[t.contract_type] || t.contract_type}</span></td>
                    <td style={{ fontSize: 12, fontWeight: 500 }}>{t.stallion_name || "—"}</td>
                    <td style={{ fontSize: 12 }}>{t.season || "—"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn btn-sm" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => setPreviewTemplate(t)}>👁 View</button>
                        <button className="btn btn-sm" style={{ fontSize: 10, padding: "2px 8px" }} onClick={() => downloadTemplate(t)}>📥 PDF</button>
                      </div>
                    </td>
                    <td>
                      <button className="btn btn-sm" style={{ fontSize: 10, padding: "2px 8px", whiteSpace: "nowrap", ...(Array.isArray(t.sign_fields) && t.sign_fields.length ? { background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" } : {}) }}
                        onClick={() => setPlacingTemplate(t)}>{Array.isArray(t.sign_fields) && t.sign_fields.length ? "✓ Set up" : "Set up signing"}</button>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button onClick={() => toggleTemplateActive(t.id, t.is_active)} style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer" }}>{t.is_active ? "✅" : "⬜"}</button>
                    </td>
                    <td style={{ display: "flex", gap: 4 }}>
                      <button className="btn btn-sm" style={{ fontSize: 11, padding: "3px 8px" }} onClick={() => { setCtEditId(t.id); setCtForm({ name: t.name, description: t.description || "", stallion_id: t.stallion_id || "", stallion_name: t.stallion_name || "", contract_type: t.contract_type, season: t.season || "" }); setCtFile(null); setShowAddTemplate(true); }}>Edit</button>
                      <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6", fontSize: 11, padding: "3px 8px" }} onClick={() => deleteTemplate(t)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ marginTop: 12, padding: "10px 14px", background: "#eff6ff", borderRadius: 8, border: "1px solid #bfdbfe", fontSize: 12, color: "#1e40af" }}>
          <strong>How contracts work:</strong> Upload your contract PDFs here as templates, then use Set up signing to mark where each signature, date and detail goes (once per template). On any customer's Contracts tab, assign a template and click Send for signature: you sign as Stallion Owner, the customer signs online as Mare Owner, and the signed copy saves to their account. You can still upload a copy signed on paper.
        </div>
      </div>
    )}

    {previewTemplate && (
      <Suspense fallback={null}>
        <PdfPreviewModal path={previewTemplate.storage_path} fileName={previewTemplate.file_name} title={previewTemplate.name} onClose={() => setPreviewTemplate(null)} />
      </Suspense>
    )}

    {placingTemplate && (
      <Suspense fallback={null}>
        <FieldPlacer
          template={placingTemplate}
          onClose={() => setPlacingTemplate(null)}
          onSaved={(fields) => { setContractTemplates(p => p.map(t => t.id === placingTemplate.id ? { ...t, sign_fields: fields } : t)); setPlacingTemplate(null); }}
        />
      </Suspense>
    )}

    {/* Upload/Edit Template Modal */}
    {showAddTemplate && (
      <div className="modal-overlay" onClick={() => { setShowAddTemplate(false); setCtEditId(null); }}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
          <div className="modal-header"><div className="modal-title">{ctEditId ? "Edit Template" : "Upload Contract Template"}</div><button className="modal-close" onClick={() => { setShowAddTemplate(false); setCtEditId(null); }}>✕</button></div>
          <div className="modal-body">
            <div className="form-row"><label className="form-label">Template Name *</label><input className="form-input" value={ctForm.name} onChange={e => setCtForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Golden Coins 2026 Stallion Service Agreement" /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="form-row"><label className="form-label">Contract Type</label>
                <select className="form-input" value={ctForm.contract_type} onChange={e => setCtForm(p => ({ ...p, contract_type: e.target.value }))}>
                  {Object.entries(CONTRACT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-row"><label className="form-label">Season / Year</label><input className="form-input" value={ctForm.season} onChange={e => setCtForm(p => ({ ...p, season: e.target.value }))} placeholder="e.g. 2026" /></div>
            </div>
            <div className="form-row"><label className="form-label">Stallion (optional)</label>
              <select className="form-input" value={ctForm.stallion_id} onChange={e => { const s = stallionsForContracts.find(x => x.id === e.target.value); setCtForm(p => ({ ...p, stallion_id: e.target.value, stallion_name: s?.name || "" })); }}>
                <option value="">— Not stallion-specific —</option>
                {stallionsForContracts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-row"><label className="form-label">Description (optional)</label><input className="form-input" value={ctForm.description} onChange={e => setCtForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description of this contract..." /></div>
            <div className="form-row">
              <label className="form-label">{ctEditId ? "Replace PDF (optional)" : "PDF File *"}</label>
              <div style={{ border: ctFile ? "2px solid var(--forest)" : "2px dashed var(--parchment-dark)", borderRadius: 8, padding: 20, textAlign: "center", background: ctFile ? "#f0fdf4" : "var(--parchment)", cursor: "pointer", position: "relative", transition: "all 0.2s" }}
                onClick={() => document.getElementById("ct-file-input").click()}
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.borderColor = "#2563eb"; e.currentTarget.style.background = "#eff6ff"; }}
                onDragLeave={e => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.borderColor = ctFile ? "var(--forest)" : "var(--parchment-dark)"; e.currentTarget.style.background = ctFile ? "#f0fdf4" : "var(--parchment)"; }}
                onDrop={e => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.borderColor = "var(--forest)"; e.currentTarget.style.background = "#f0fdf4"; const f = e.dataTransfer?.files?.[0]; if (f) { if (f.type !== "application/pdf") { alert("Only PDF files"); return; } setCtFile(f); if (!ctForm.name) { setCtForm(p => ({ ...p, name: f.name.replace(/\.pdf$/i, "").replace(/[_-]/g, " ").replace(/\b\w/g, c => c.toUpperCase()) })); } } }}>
                <input id="ct-file-input" type="file" accept=".pdf,application/pdf" style={{ display: "none" }} onChange={e => setCtFile(e.target.files[0] || null)} />
                {ctFile ? (
                  <div><span style={{ fontSize: 24 }}>📄</span><div style={{ fontSize: 13, fontWeight: 600, marginTop: 4, color: "var(--forest)" }}>{ctFile.name}</div><div style={{ fontSize: 11, color: "var(--text-light)" }}>{(ctFile.size / 1024).toFixed(0)} KB — Click or drop to replace</div></div>
                ) : (
                  <div><span style={{ fontSize: 24 }}>📤</span><div style={{ fontSize: 13, color: "var(--text-mid)", marginTop: 4, fontWeight: 500 }}>Drag & drop a PDF here, or click to browse</div><div style={{ fontSize: 11, color: "var(--text-light)", marginTop: 2 }}>PDF files only · Max 5MB</div></div>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={() => { setShowAddTemplate(false); setCtEditId(null); }}>Cancel</button><button className="btn btn-primary" onClick={uploadTemplate} disabled={ctUploading}>{ctUploading ? "Uploading..." : ctEditId ? "Update" : "Upload Template"}</button></div>
        </div>
      </div>
    )}

    {/* EMAIL TAB */}
    {activeTab === "farm" && (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 16, alignItems: "start" }}>
        <div className="full-panel" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 4 }}>🏇 Farm Details</div>
          <div style={{ fontSize: 12, color: "var(--text-light)", marginBottom: 14 }}>
            Used on your email signature, shipment notices to customers, and your dashboard.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="form-row" style={{ gridColumn: "1/-1" }}>
              <label className="form-label">Farm Name</label>
              <input className="form-input" value={farmForm?.name ?? ""}
                onChange={e => setFarmForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-row" style={{ gridColumn: "1/-1" }}>
              <label className="form-label">Street Address</label>
              <input className="form-input" value={farmForm?.address ?? ""}
                onChange={e => setFarmForm(p => ({ ...p, address: e.target.value }))} />
            </div>
            <div className="form-row">
              <label className="form-label">City</label>
              <input className="form-input" value={farmForm?.city ?? ""}
                onChange={e => setFarmForm(p => ({ ...p, city: e.target.value }))} />
            </div>
            <div className="form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <label className="form-label">State</label>
                <input className="form-input" maxLength={2} value={farmForm?.state ?? ""}
                  onChange={e => setFarmForm(p => ({ ...p, state: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <label className="form-label">ZIP</label>
                <input className="form-input" value={farmForm?.zip ?? ""}
                  onChange={e => setFarmForm(p => ({ ...p, zip: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <label className="form-label">Phone</label>
              <input className="form-input" value={farmForm?.phone ?? ""}
                onChange={e => setFarmForm(p => ({ ...p, phone: e.target.value }))} placeholder="(940) 555-0100" />
            </div>
            <div className="form-row">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={farmForm?.email ?? ""}
                onChange={e => setFarmForm(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="form-row" style={{ gridColumn: "1/-1" }}>
              <label className="form-label">Website</label>
              <input className="form-input" value={farmForm?.website ?? ""}
                onChange={e => setFarmForm(p => ({ ...p, website: e.target.value }))} placeholder="https://yourfarm.com" />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
            <button className="btn btn-primary" onClick={saveFarm} disabled={!farmDirty || farmSaving}>
              {farmSaving ? "Saving…" : "Save Farm Details"}
            </button>
            {farmSaved && <span style={{ fontSize: 12.5, color: "#166534" }}>Saved. Your signature and shipment notices will use these.</span>}
            {!farmSaved && farmDirty && <span style={{ fontSize: 12.5, color: "var(--text-light)" }}>Unsaved changes</span>}
          </div>
        </div>

        <div className="full-panel" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 4 }}>Farm Logo</div>
          <div style={{ fontSize: 12, color: "var(--text-light)", marginBottom: 12 }}>
            Shown on your dashboard and on shipment notices your customers receive. PNG or JPG, under 2 MB.
          </div>
          <div style={{ border: "1px dashed var(--parchment-dark)", borderRadius: 8, padding: 18, textAlign: "center", background: "#fff" }}>
            {orgRow?.logo_url
              ? <img src={orgRow.logo_url} alt="Farm logo" style={{ maxHeight: 80, maxWidth: "100%", display: "block", margin: "0 auto" }} />
              : <div style={{ fontSize: 34, opacity: .35 }}>🏇</div>}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <label className="btn btn-outline" style={{ flex: 1, textAlign: "center", cursor: logoBusy ? "wait" : "pointer" }}>
              {logoBusy ? "Uploading…" : orgRow?.logo_url ? "Replace" : "Upload Logo"}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: "none" }}
                disabled={logoBusy} onChange={e => { if (e.target.files[0]) uploadLogo(e.target.files[0]); e.target.value = ""; }} />
            </label>
            {orgRow?.logo_url && (
              <button className="btn btn-sm" onClick={removeLogo} disabled={logoBusy}
                style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6" }}>Remove</button>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text-light)", marginTop: 10, lineHeight: 1.5 }}>
            Upload the file rather than linking one from your website — some hosts block the
            servers Gmail and Outlook use to load images, which leaves a broken picture in
            your customer's inbox.
          </div>
        </div>
        <div className="full-panel" style={{ padding: 20, gridColumn: "1/-1" }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 4 }}>Your data</div>
          <div style={{ fontSize: 13.5, color: "var(--text-mid)", marginBottom: 12, lineHeight: 1.6, maxWidth: 760 }}>
            Download everything your farm has entered: horses, breeding and foaling records, frozen semen,
            customers, orders, shipments and contracts. You get one Excel workbook with a tab for each,
            the same data as CSV files, and every photo and document you uploaded.
          </div>
          <DownloadDataButton orgId={orgId} farmName={orgRow?.name} />
          <div style={{ fontSize: 12, color: "var(--text-light)", marginTop: 12, lineHeight: 1.55, maxWidth: 760 }}>
            If your subscription ends, your farm becomes read-only a week later. We keep your data for {RETENTION_DAYS_PAID} days
            ({RETENTION_DAYS_TRIAL} days for a free trial that was never subscribed) so you can download it, then permanently delete it.
          </div>
        </div>
      </div>
    )}

    {activeTab === "email" && (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Left: Connect Your Email */}
        <div>
          <div className="full-panel" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 24 }}>✉️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "var(--forest)" }}>Connect Your Email</div>
                <div style={{ fontSize: 12, color: "var(--text-light)" }}>Send notifications from your own email address</div>
              </div>
            </div>

            {/* Current connection status */}
            {emailInt?.is_active && (
              <div style={{ background: "#dcfce7", borderRadius: 10, padding: "14px 16px", marginBottom: 16, border: "1px solid #bbf7d0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{emailInt.provider === "gmail" ? "📧" : emailInt.provider === "outlook" ? "📬" : "✉️"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#166534" }}>{emailInt.provider === "gmail" ? "Gmail" : emailInt.provider === "outlook" ? "Outlook" : emailInt.provider} Connected</div>
                    <div style={{ fontSize: 12, color: "#15803d" }}>Sending as: <strong>{emailInt.config?.connected_email || emailInt.config?.from_email || "—"}</strong></div>
                  </div>
                  <button className="btn btn-sm" style={{ background: "#fde8e8", color: "var(--red)", border: "1px solid #f5c6c6", fontSize: 11 }} onClick={async () => {
                    await dbUpdate("org_integrations", emailInt.id, { is_active: false, sync_status: "disconnected", updated_at: new Date().toISOString() });
                    setIntegrations(p => p.map(i => i.id === emailInt.id ? { ...i, is_active: false } : i)); save();
                  }}>Disconnect</button>
                </div>
              </div>
            )}

            {/* Gmail connect */}
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "16px 18px", marginBottom: 12, background: "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: "#fff", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>📧</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Gmail / Google Workspace</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-light)" }}>Connect your Gmail or Google Workspace email</div>
                </div>
                {emailInt?.provider === "gmail" && emailInt?.is_active
                  ? <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 10, background: "#dcfce7", color: "#166534", fontWeight: 600 }}>Active</span>
                  : <button className="btn btn-primary" style={{ fontSize: 12, padding: "6px 16px" }} onClick={async () => {
                      // The server builds the consent URL and signs our farm id
                      // into `state`. The browser holds no client id or secret,
                      // and cannot name a farm that isn't its own.
                      try {
                        const { auth_url } = await callFunction("email-oauth-start", { provider: "gmail" });
                        window.open(auth_url, "_blank", "width=600,height=700");
                      } catch (e) { alert("Could not start the Gmail connection: " + e.message); }
                    }}>Connect Gmail</button>
                }
              </div>
            </div>

            {/* Outlook connect */}
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "16px 18px", marginBottom: 12, background: "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: "#fff", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>📬</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Outlook / Microsoft 365</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-light)" }}>Connect your Outlook or Microsoft 365 email</div>
                </div>
                {emailInt?.provider === "outlook" && emailInt?.is_active
                  ? <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 10, background: "#dcfce7", color: "#166534", fontWeight: 600 }}>Active</span>
                  : <button className="btn" style={{ fontSize: 12, padding: "6px 16px", background: "#0078d4", color: "#fff", border: "none", fontWeight: 600 }} onClick={async () => {
                      try {
                        const { auth_url } = await callFunction("email-oauth-start", { provider: "outlook" });
                        window.open(auth_url, "_blank", "width=600,height=700");
                      } catch (e) { alert("Could not start the Outlook connection: " + e.message); }
                    }}>Connect Outlook</button>
                }
              </div>
            </div>

            {/* SMTP / API fallback */}
            <div style={{ border: "1px dashed #d1d5db", borderRadius: 10, padding: "14px 18px", background: "#fafafa" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#9ca3af" }}>⚙️</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-mid)" }}>API Provider (Advanced)</div>
                  <div style={{ fontSize: 11, color: "var(--text-light)" }}>Use Resend, SendGrid, or other API-based providers</div>
                </div>
                <button className="btn btn-outline" style={{ fontSize: 11, padding: "4px 12px" }} onClick={() => setActiveTab("api-email")}>Configure</button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Test + How it works */}
        <div>
          <div className="full-panel" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 4 }}>✍️ Email Signature</div>
            <div style={{ fontSize: 12, color: "var(--text-light)", marginBottom: 10 }}>
              Added to emails you send to customers. Not to your own foaling or herd alerts.
            </div>
            <textarea className="form-input" rows={5}
              value={signature}
              placeholder={generatedSignature || "Your farm name and contact details"}
              onChange={e => setSignature(e.target.value)}
              onBlur={saveSignature}
              style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: 13 }} />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
              {signature.trim()
                ? <button className="btn btn-sm btn-outline" onClick={() => { setSignature(""); setTimeout(saveSignature, 0); }}>
                    Reset to farm details
                  </button>
                : <span style={{ fontSize: 12, color: "var(--text-light)", fontStyle: "italic" }}>
                    Using your farm details automatically — type here to override.
                  </span>}
              {sigStatus === "saving" && <span style={{ fontSize: 12, color: "var(--text-light)" }}>Saving…</span>}
              {sigStatus === "saved" && <span style={{ fontSize: 12, color: "#166534" }}>Saved</span>}
            </div>
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--parchment-dark)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-light)", marginBottom: 7 }}>Preview</div>
              <div style={{ borderTop: "1px solid #e3ded2", paddingTop: 12, fontFamily: "Arial, Helvetica, sans-serif", fontSize: 13, lineHeight: 1.55, color: "#666" }}>
                {orgRow?.logo_url && <img src={orgRow.logo_url} alt="" style={{ maxHeight: 52, display: "block", marginBottom: 9 }} />}
                <div style={{ fontWeight: "bold", color: "#2c2a26" }}>{profile?.full_name || "Your name"}</div>
                <div style={{ whiteSpace: "pre-line" }}>{signature.trim() || generatedSignature}</div>
              </div>
            </div>
          </div>

          <div className="full-panel" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 12 }}>🧪 Send Test Email</div>
            {!emailInt?.is_active ? (
              <div style={{ fontSize: 13, color: "var(--text-light)", fontStyle: "italic" }}>Connect your Gmail or Outlook first to send a test email.</div>
            ) : (<>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="form-input" type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="recipient@example.com" style={{ flex: 1 }} />
                <button className="btn btn-primary" onClick={sendTestEmail} disabled={testStatus === "sending"}>
                  {testStatus === "sending" ? "Sending…" : testStatus === "sent" ? "✓ Sent!" : "Send Test"}
                </button>
              </div>
              {testStatus === "sent" && <div style={{ fontSize: 12, color: "#166534", marginTop: 6 }}>Test email sent from {emailInt.config?.connected_email}! Check the recipient's inbox.</div>}
              {testStatus === "error" && <div style={{ fontSize: 12, color: "var(--red)", marginTop: 6 }}>Failed to send. Try disconnecting and reconnecting your email.</div>}
            </>)}
          </div>

          <div className="full-panel" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 10 }}>How It Works</div>
            <div style={{ fontSize: 12.5, color: "var(--text-mid)", lineHeight: 1.8 }}>
              <strong>1.</strong> Click "Connect Gmail" or "Connect Outlook" above<br/>
              <strong>2.</strong> Sign into your email account and grant permission<br/>
              <strong>3.</strong> ArcStallion can now send emails <strong>from your address</strong><br/>
              <strong>4.</strong> Emails appear in your Sent folder, just like you sent them<br/>
              <strong>5.</strong> Go to the <strong>Notifications</strong> tab to choose which events trigger emails
            </div>
          </div>

          <div className="full-panel" style={{ padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 8 }}>What Gets Sent?</div>
            <div style={{ fontSize: 12, color: "var(--text-mid)", lineHeight: 1.7 }}>
              Once connected, the system can automatically send:<br/>
              Order confirmations, shipping notifications, breeding event updates, foaling alerts, payment receipts, herd health reminders, and low inventory warnings — all from your own email address.
            </div>
          </div>
        </div>
      </div>
    )}

    {/* API EMAIL TAB (Advanced) */}
    {activeTab === "api-email" && (
      <div style={{ maxWidth: 560 }}>
        <button className="btn btn-outline" style={{ marginBottom: 16, fontSize: 12 }} onClick={() => setActiveTab("email")}>← Back to Email</button>
        <div className="full-panel" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--forest)", marginBottom: 4 }}>API Email Provider</div>
          <div style={{ fontSize: 12, color: "var(--text-light)", marginBottom: 16 }}>For advanced users who want to use a transactional email API instead of Gmail/Outlook</div>
          <div className="form-row" style={{ marginBottom: 10 }}>
            <label className="form-label">Provider</label>
            <select className="form-input" value={emailForm.provider} onChange={e => setEmailForm(p => ({ ...p, provider: e.target.value }))}>
              <option value="resend">Resend</option><option value="sendgrid">SendGrid</option>
            </select>
          </div>
          <div className="form-row" style={{ marginBottom: 10 }}>
            <label className="form-label">API Key *</label>
            <input className="form-input" type="password" value={emailForm.api_key} onChange={e => setEmailForm(p => ({ ...p, api_key: e.target.value }))} placeholder="API key from your provider" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <div className="form-row"><label className="form-label">From Email *</label><input className="form-input" type="email" value={emailForm.from_email} onChange={e => setEmailForm(p => ({ ...p, from_email: e.target.value }))} placeholder="noreply@yourdomain.com" /></div>
            <div className="form-row"><label className="form-label">From Name</label><input className="form-input" value={emailForm.from_name} onChange={e => setEmailForm(p => ({ ...p, from_name: e.target.value }))} placeholder="ArcStallion" /></div>
          </div>
          <button className="btn btn-primary" onClick={saveEmailConfig} style={{ width: "100%" }}>Save API Configuration</button>
        </div>
      </div>
    )}

    {/* QUICKBOOKS TAB */}
    {false && activeTab === "quickbooks" && (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div className="full-panel" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 24 }}>📗</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "var(--forest)" }}>QuickBooks Online</div>
              <div style={{ fontSize: 12, color: "var(--text-light)" }}>Sync customers & invoices with QuickBooks</div>
            </div>
            {qbInt?.is_active ? <span style={{ marginLeft: "auto", fontSize: 11, padding: "3px 10px", borderRadius: 10, background: "#dcfce7", color: "#166534", fontWeight: 600 }}>Connected</span>
             : qbInt ? <span style={{ marginLeft: "auto", fontSize: 11, padding: "3px 10px", borderRadius: 10, background: "#fef3c7", color: "#92400e", fontWeight: 600 }}>Not Authorized</span> : null}
          </div>
          <div className="form-row" style={{ marginBottom: 10 }}>
            <label className="form-label">Client ID *</label>
            <input className="form-input" value={qbForm.client_id} onChange={e => setQbForm(p => ({ ...p, client_id: e.target.value }))} placeholder="From Intuit Developer portal" />
          </div>
          <div className="form-row" style={{ marginBottom: 10 }}>
            <label className="form-label">Client Secret *</label>
            <input className="form-input" type="password" value={qbForm.client_secret} onChange={e => setQbForm(p => ({ ...p, client_secret: e.target.value }))} placeholder="From Intuit Developer portal" />
          </div>
          <div className="form-row" style={{ marginBottom: 14 }}>
            <label className="form-label">Company ID</label>
            <input className="form-input" value={qbForm.company_id} onChange={e => setQbForm(p => ({ ...p, company_id: e.target.value }))} placeholder="Your QuickBooks company ID (found in URL)" />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary" onClick={saveQBConfig} style={{ flex: 1 }}>Save Credentials</button>
            {qbInt && !qbInt.is_active && (
              <button className="btn" onClick={() => {
                window.open(`https://cshfvhzmvkzrvlyrnfap.supabase.co/functions/v1/qb-auth?org_id=${orgId}`, "_blank", "width=600,height=700");
              }} style={{ flex: 1, background: "#2ca01c", color: "white", border: "none", fontWeight: 600 }}>Authorize with QuickBooks</button>
            )}
          </div>
        </div>

        <div>
          <div className="full-panel" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 10 }}>Sync Settings</div>
            <div style={{ fontSize: 12.5, color: "var(--text-mid)", lineHeight: 1.7 }}>
              When connected, QuickBooks will sync:
            </div>
            <div style={{ marginTop: 10 }}>
              {[
                ["Customers → QB Customers", "Sync customer records to QuickBooks"],
                ["Orders → QB Invoices", "Create invoices from paid orders"],
                ["Payments → QB Payments", "Record payments received"],
              ].map(([title, desc]) => (
                <div key={title} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: qbInt?.is_active ? "#4caf50" : "#ccc" }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
                    <div style={{ fontSize: 11, color: "var(--text-light)" }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="full-panel" style={{ padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 10 }}>📖 Setup Guide</div>
            <div style={{ fontSize: 12.5, color: "var(--text-mid)", lineHeight: 1.7 }}>
              <strong>1.</strong> Go to <a href="https://developer.intuit.com" target="_blank" rel="noreferrer" style={{ color: "var(--blue)" }}>developer.intuit.com</a> and create an app<br/>
              <strong>2.</strong> Set redirect URI to your Edge Function URL<br/>
              <strong>3.</strong> Copy Client ID and Client Secret above<br/>
              <strong>4.</strong> Click "Authorize with QuickBooks" to connect<br/>
              <strong>5.</strong> Select your company and approve access
            </div>
          </div>
        </div>
      </div>
    )}

    {/* SHIPPING CARRIERS TAB */}
    {activeTab === "shipping" && (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div>
          {/* EasyPost API Key */}
          <div className="full-panel" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 8, background: "linear-gradient(135deg, #1a7f50, #166534)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 10 }}>EP</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>EasyPost</div>
                <div style={{ fontSize: 11, color: "var(--text-light)" }}>Multi-carrier shipping API — FedEx, UPS & Unishippers</div>
              </div>
              {easypostKey && <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 12, background: "#dcfce7", color: "#166534", fontWeight: 600 }}>✓ Connected</span>}
            </div>
            <div className="form-row" style={{ marginBottom: 12 }}>
              <label className="form-label">EasyPost API Key *</label>
              <input className="form-input" type="password" value={easypostKey} onChange={e => setEasypostKey(e.target.value)} placeholder="EZAK..." style={{ fontFamily: "monospace", fontSize: 12 }} />
            </div>
            <div style={{ padding: "10px 14px", background: "#f0fdf4", borderRadius: 8, fontSize: 12, color: "var(--text-mid)", lineHeight: 1.6 }}>
              Sign up free at <a href="https://www.easypost.com/signup" target="_blank" rel="noreferrer" style={{ color: "var(--blue)", fontWeight: 600 }}>easypost.com/signup</a>. Then connect your FedEx, UPS, and Unishippers carrier accounts in the EasyPost dashboard under Carrier Accounts. Copy your Production API key here.
            </div>
          </div>

          {/* Ship From Address (Business Origin) */}
          <div className="full-panel" style={{ padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 4 }}>Ship-From Address</div>
            <div style={{ fontSize: 11, color: "var(--text-light)", marginBottom: 14 }}>Your business origin address for all shipments</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div className="form-row"><label className="form-label">Name</label><input className="form-input" value={shipFromAddr.name} onChange={e => sfAddr("name", e.target.value)} placeholder="Business name" /></div>
              <div className="form-row"><label className="form-label">Phone</label><input className="form-input" value={shipFromAddr.phone} onChange={e => sfAddr("phone", e.target.value)} placeholder="(XXX) XXX-XXXX" /></div>
            </div>
            <div className="form-row" style={{ marginBottom: 10 }}><label className="form-label">Street Address</label><input className="form-input" value={shipFromAddr.address} onChange={e => sfAddr("address", e.target.value)} placeholder="Street address" /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 10 }}>
              <div className="form-row"><label className="form-label">City</label><input className="form-input" value={shipFromAddr.city} onChange={e => sfAddr("city", e.target.value)} /></div>
              <div className="form-row"><label className="form-label">State</label><input className="form-input" value={shipFromAddr.state} onChange={e => sfAddr("state", e.target.value)} maxLength={2} style={{ width: 60 }} /></div>
              <div className="form-row"><label className="form-label">ZIP</label><input className="form-input" value={shipFromAddr.zip} onChange={e => sfAddr("zip", e.target.value)} style={{ width: 90 }} /></div>
            </div>
          </div>
        </div>

        {/* Right column: Save + setup guide */}
        <div>
          <button className="btn btn-primary" onClick={saveShippingConfig} style={{ width: "100%", padding: "12px 20px", fontSize: 14, fontWeight: 700, marginBottom: 16 }}>💾 Save Shipping Settings</button>

          <div className="full-panel" style={{ padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--forest)", marginBottom: 10 }}>📖 Setup Guide</div>
            <div style={{ fontSize: 12.5, color: "var(--text-mid)", lineHeight: 1.8 }}>
              <strong>Step 1:</strong> Create a free account at <a href="https://www.easypost.com/signup" target="_blank" rel="noreferrer" style={{ color: "var(--blue)" }}>easypost.com</a><br/>
              <strong>Step 2:</strong> In EasyPost dashboard → Carrier Accounts, connect your FedEx, UPS, and Unishippers accounts using your existing account numbers and credentials<br/>
              <strong>Step 3:</strong> Copy your Production API key from the EasyPost dashboard and paste it here<br/>
              <strong>Step 4:</strong> Enter your ship-from address (your business location)<br/><br/>
              Once configured, you can get live rates from all carriers, compare prices, and print shipping labels directly from the Shipping page.
            </div>
          </div>
        </div>
      </div>
    )}

    {/* NOTIFICATIONS TAB */}
    {activeTab === "billing" && isAdmin && <BillingPanel billing={billing} onAddCard={onAddCard} onRefresh={onRefreshBilling} onFeedback={onFeedback} orgId={orgId} farmName={orgRow?.name} />}

    {activeTab === "notifications" && (
      <div className="full-panel" style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 24 }}>🔔</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--forest)" }}>Email Notifications</div>
            <div style={{ fontSize: 12, color: "var(--text-light)" }}>Choose which events trigger email notifications</div>
          </div>
          {/* Was "⚠️ Configure email provider first". Untrue since alerts moved to
              sendPlatformAlert (2026-09-25): they go out from ArcStallion, not the
              farm's mailbox, so a farm with no mailbox connected still gets them. */}
          <span style={{ marginLeft: "auto", fontSize: 11, padding: "4px 12px", borderRadius: 6, background: "#f0fdf4", color: "#166534", fontWeight: 500 }}>Sent by ArcStallion. No mailbox setup needed.</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--parchment-dark)" }}>
              <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "var(--text-light)", textTransform: "uppercase", letterSpacing: ".06em" }}>Event</th>
              <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "var(--text-light)", textTransform: "uppercase", letterSpacing: ".06em" }}>Description</th>
              <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "var(--text-light)", textTransform: "uppercase", letterSpacing: ".06em", width: 80 }}>Email</th>
            </tr>
          </thead>
          <tbody>
            {NOTIFICATION_EVENTS.map(ev => {
              const setting = notifSettings.find(n => n.event_type === ev.id);
              const off = !ev.live;
              return (
                <tr key={ev.id} style={{ borderBottom: "1px solid #f0f0f0", opacity: off ? 0.55 : 1 }}>
                  <td style={{ padding: "10px 12px", fontWeight: 600, fontSize: 13 }}>{ev.label}</td>
                  <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-light)" }}>
                    {ev.desc}
                    {off && <div style={{ fontSize: 11, color: "#92400e", paddingTop: 2 }}>Coming soon — {ev.note}</div>}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "center" }}>
                    {off
                      ? <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "#f0f0f0", color: "#777", fontWeight: 600 }}>soon</span>
                      : <input type="checkbox" checked={setting?.email_enabled || false} onChange={e => toggleNotification(ev.id, "email_enabled", e.target.checked)}
                          style={{ width: 18, height: 18, accentColor: "var(--forest)", cursor: "pointer" }} disabled={!emailInt?.is_active} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* The number "Low Semen Inventory" compares against. It lives on the farm
            rather than the stallion because frozen_semen.stallion is free text
            with no link to the stallions table -- a per-stallion setting would
            not reliably find the lots it was meant to watch. */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 12px", borderTop: "1px solid var(--parchment-dark)" }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Low semen alert threshold</label>
          <input type="number" min={0} value={strawThreshold}
            onChange={e => setStrawThreshold(e.target.value)}
            onBlur={saveStrawThreshold}
            style={{ width: 80, padding: "6px 8px", border: "1px solid var(--parchment-dark)", borderRadius: 6, fontSize: 13 }} />
          <span style={{ fontSize: 12, color: "var(--text-light)" }}>
            straws or fewer, counting every tank and canister for that stallion together
          </span>
        </div>
      </div>
    )}

    {/* EMAIL LOG TAB */}
    {activeTab === "logs" && (
      <div className="full-panel">
        <div style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700, color: "var(--forest)", borderBottom: "1px solid var(--parchment-dark)" }}>📋 Email Log — Recent Activity</div>
        <table className="data-table">
          <thead><tr><th>Sent</th><th>To</th><th>Subject</th><th>Event</th><th>Status</th><th>Provider</th></tr></thead>
          <tbody>
            {emailLogs.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 50).map(log => (
              <tr key={log.id}>
                <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{log.sent_at ? new Date(log.sent_at).toLocaleString() : log.created_at ? new Date(log.created_at).toLocaleString() : "—"}</td>
                <td style={{ fontSize: 12 }}>{log.to_email}</td>
                <td style={{ fontSize: 12, fontWeight: 500 }}>{log.subject}</td>
                <td style={{ fontSize: 11 }}>{log.event_type || "manual"}</td>
                <td><span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 600,
                  background: log.status === "sent" ? "#dcfce7" : log.status === "failed" ? "#fde8e8" : log.status === "bounced" ? "#fef3c7" : "#e8f0e8",
                  color: log.status === "sent" ? "#166534" : log.status === "failed" ? "#dc2626" : log.status === "bounced" ? "#92400e" : "#555" }}>
                  {log.status}</span></td>
                <td style={{ fontSize: 11, color: "var(--text-light)" }}>{log.provider || "—"}</td>
              </tr>
            ))}
            {emailLogs.length === 0 && <tr><td colSpan={6} className="empty-state">No emails sent yet. Configure your email provider and send a test.</td></tr>}
          </tbody>
        </table>
      </div>
    )}

    {/* TEAM TAB (Admin only) */}
    {activeTab === "team" && isAdmin && (
      <TeamManagement orgId={orgId} profile={profile} />
    )}

    <SaveToast show={saved} />
  </>);
}

// ─── TEAM MANAGEMENT ──────────────────────────────────────────────────────────
function TeamManagement({ orgId, profile }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("Staff");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [msgKind, setMsgKind] = useState("ok");

  const flash = useCallback((kind, text) => {
    setMsgKind(kind); setMsg(text);
    setTimeout(() => setMsg(null), 4000);
  }, []);

  const loadMembers = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("user_profiles")
      .select("id, email, full_name, role, created_at")
      .eq("org_id", orgId)
      .order("created_at", { ascending: true });
    if (error) { flash("err", "Load failed: " + error.message); }
    else { setMembers(data || []); }
    setLoading(false);
  }, [orgId, flash]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setBusy(true);
    try {
      await callFunction("invite-user", {
        email: inviteEmail.trim(),
        full_name: inviteName.trim(),
        role: inviteRole,
      });
      flash("ok", `Invite sent to ${inviteEmail}. They'll receive an email to set their password.`);
      setInviteEmail(""); setInviteName(""); setInviteRole("Staff");
      await loadMembers();
    } catch (err) {
      flash("err", "Invite failed: " + err.message);
    } finally { setBusy(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    setBusy(true);
    try {
      await callFunction("update-user-role", { user_id: userId, role: newRole });
      setMembers(prev => prev.map(m => m.id === userId ? { ...m, role: newRole } : m));
      flash("ok", "Role updated.");
    } catch (err) {
      flash("err", "Update failed: " + err.message);
    } finally { setBusy(false); }
  };

  const handleRemove = async (user) => {
    if (user.id === profile.id) { flash("err", "You cannot remove yourself."); return; }
    if (!window.confirm(`Remove ${user.email} from this organization? They will lose access immediately.`)) return;
    setBusy(true);
    try {
      await callFunction("remove-user", { user_id: user.id });
      setMembers(prev => prev.filter(m => m.id !== user.id));
      flash("ok", `${user.email} removed.`);
    } catch (err) {
      flash("err", "Remove failed: " + err.message);
    } finally { setBusy(false); }
  };

  const ROLE_DESC = {
    Admin:  "Full access. Can invite users, change roles, delete records, edit all settings.",
    Staff:  "Can create and edit records (horses, orders, customers). Cannot delete or manage users.",
    Viewer: "Read-only. Can see everything in the app but cannot change anything.",
  };

  const roleBadgeStyle = (role) => ({
    display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
    background: role === "Admin" ? "#fef3c7" : role === "Staff" ? "#e0f2fe" : "#f3f4f6",
    color:      role === "Admin" ? "#92400e" : role === "Staff" ? "#075985" : "#4b5563",
  });

  return (
    <div>
      {/* Header / intro */}
      <div className="full-panel" style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ fontSize: 13, color: "var(--text-mid)", marginBottom: 10 }}>
          Invite teammates to your ArcStallion workspace. Each person gets their own login and you choose exactly what they can do.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {["Admin", "Staff", "Viewer"].map(r => (
            <div key={r} style={{ border: "1px solid var(--parchment-dark)", borderRadius: 6, padding: 12, background: "#fff" }}>
              <div style={{ marginBottom: 4 }}><span style={roleBadgeStyle(r)}>{r}</span></div>
              <div style={{ fontSize: 12, color: "var(--text-mid)", lineHeight: 1.4 }}>{ROLE_DESC[r]}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Flash message */}
      {msg && (
        <div style={{
          padding: "10px 14px", marginBottom: 12, borderRadius: 6, fontSize: 13,
          background: msgKind === "ok" ? "#dcfce7" : "#fde8e8",
          color:      msgKind === "ok" ? "#166534" : "#991b1b",
          border: `1px solid ${msgKind === "ok" ? "#86efac" : "#fca5a5"}`,
        }}>{msg}</div>
      )}

      {/* Invite form */}
      <div className="full-panel" style={{ marginBottom: 16 }}>
        <div style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700, color: "var(--forest)", borderBottom: "1px solid var(--parchment-dark)" }}>
          ✉️ Invite a teammate
        </div>
        <form onSubmit={handleInvite} style={{ padding: 16, display: "grid", gridTemplateColumns: "2fr 2fr 1fr auto", gap: 10, alignItems: "end" }}>
          <div>
            <label style={{ fontSize: 11, color: "var(--text-light)", display: "block", marginBottom: 4 }}>Email</label>
            <input type="email" required value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              placeholder="teammate@barn.com"
              style={{ width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid var(--parchment-dark)", borderRadius: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "var(--text-light)", display: "block", marginBottom: 4 }}>Full name (optional)</label>
            <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)}
              placeholder="Jane Doe"
              style={{ width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid var(--parchment-dark)", borderRadius: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "var(--text-light)", display: "block", marginBottom: 4 }}>Role</label>
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid var(--parchment-dark)", borderRadius: 4, background: "#fff" }}>
              <option value="Admin">Admin</option>
              <option value="Staff">Staff</option>
              <option value="Viewer">Viewer</option>
            </select>
          </div>
          <button type="submit" disabled={busy || !inviteEmail.trim()} className="btn btn-primary">
            {busy ? "Sending…" : "Send invite"}
          </button>
        </form>
      </div>

      {/* Member list */}
      <div className="full-panel">
        <div style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700, color: "var(--forest)", borderBottom: "1px solid var(--parchment-dark)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>👥 Team members ({members.length})</span>
          <button className="btn btn-outline btn-sm" onClick={loadMembers} disabled={loading}>Refresh</button>
        </div>
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Added</th><th style={{ textAlign: "right" }}>Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="empty-state">Loading…</td></tr>}
            {!loading && members.length === 0 && <tr><td colSpan={5} className="empty-state">No team members yet.</td></tr>}
            {!loading && members.map(m => {
              const isSelf = m.id === profile.id;
              return (
                <tr key={m.id}>
                  <td style={{ fontWeight: 600 }}>{m.full_name || <span style={{ color: "var(--text-light)" }}>—</span>}{isSelf && <span style={{ fontSize: 10, marginLeft: 6, color: "var(--text-light)" }}>(you)</span>}</td>
                  <td><a href={`mailto:${m.email}`} style={{ color: "var(--blue)" }}>{m.email}</a></td>
                  <td>
                    <select value={m.role} disabled={busy} onChange={e => handleRoleChange(m.id, e.target.value)}
                      style={{ padding: "4px 8px", fontSize: 12, border: "1px solid var(--parchment-dark)", borderRadius: 4, background: "#fff" }}>
                      <option value="Admin">Admin</option>
                      <option value="Staff">Staff</option>
                      <option value="Viewer">Viewer</option>
                    </select>
                  </td>
                  <td style={{ fontSize: 11, color: "var(--text-light)" }}>{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    <button className="btn btn-red btn-sm" disabled={busy || isSelf}
                      title={isSelf ? "You cannot remove yourself" : "Remove from organization"}
                      onClick={() => handleRemove(m)}>
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── NAV & TITLES ─────────────────────────────────────────────────────────────
const NAV = [
  { section: "Overview", items: [{ id: "dashboard", label: "Dashboard", icon: "📊" }] },
  { section: "Herd", items: [
    { id: "herd-mgmt",   label: "Herd Management", icon: "🐴" },
    { id: "stallions",    label: "Stallion Management", icon: "🐎" },
    { id: "mares",        label: "Mare Management",  icon: "♀" },
    { id: "farrier",      label: "Farrier Schedule",  icon: "🦶" },
  ]},
  { section: "Breeding", items: [
    { id: "breeding-mgmt",label: "Breeding Protocols", icon: "🧬" },
    { id: "foaling",      label: "Foaling Records",   icon: "🐣" },
    { id: "frozen-semen", label: "Frozen Semen",     icon: "🧊" },
    { id: "embryos",      label: "Embryo Transfer",  icon: "🔬" },
  ]},
  { section: "Customers & Sales", items: [
    { id: "customers", label: "Customers",        icon: "👥" },
    { id: "orders",    label: "Orders & Payments", icon: "💳" },
    { id: "shipping",  label: "Shipping",         icon: "📦" },
  ]},
  { section: "Tools", items: [
    ...(AI_ADVISOR_ENABLED ? [{ id: "ai", label: "AI Advisor", icon: "🤖" }] : []),
    { id: "reports", label: "Reports & Export", icon: "📋" },
  ]},
  { section: "Admin", items: [
    { id: "settings", label: "Settings & Integrations", icon: "⚙️" },
  ]},
];

// ─── Page <-> address bar ─────────────────────────────────────────────────────
// There is no router: the page is one piece of state. It used to live only in
// memory, so every refresh landed on the Dashboard and the browser's Back
// button left the app entirely. Each page now has its own path (/orders,
// /customers, ...), vercel.json already serves index.html for any path, and
// Back/Forward move between pages. Unknown paths fall back to the Dashboard.
const PAGE_IDS = new Set(NAV.flatMap(s => s.items.map(i => i.id)));
function pageFromPath() {
  const id = window.location.pathname.replace(/^\/+|\/+$/g, "");
  return PAGE_IDS.has(id) ? id : "dashboard";
}

const TITLES = {
  dashboard:    { title: "Dashboard",             sub: "Season overview" },
  "herd-mgmt":  { title: "Herd Management",       sub: "All horses, health records & scheduling" },
  stallions:    { title: "Stallion Management",   sub: "Breeding sires & performance records" },
  mares:        { title: "Mare Management",       sub: "Reproductive tracking & records" },
  farrier:      { title: "Farrier Schedule",      sub: "Barn-wide farrier records & hoof care" },
  "breeding-mgmt":{ title: "Breeding Protocols",   sub: "Protocols, events & pregnancy tracking" },
  foaling:      { title: "Foaling Records",       sub: "Due dates, birth records & foal tracking" },
  "frozen-semen":{ title: "Frozen Semen",         sub: "Cryogenic storage & straw tracking" },
  embryos:      { title: "Embryo Transfer",       sub: "Flush records & cryogenic inventory" },
  customers:    { title: "Customers",             sub: "Contact records & account management" },
  orders:       { title: "Orders & Payments",     sub: "Invoices & billing" },
  shipping:     { title: "Shipping",              sub: "FedEx, UPS & carrier management" },
  ai:           { title: "AI Advisor",            sub: "Claude-powered breeding intelligence" },
  reports:      { title: "Reports & Analytics",   sub: "Season summaries & data export" },
  settings:     { title: "Settings & Integrations", sub: "Email, shipping & system config" },
};

// ─── APP SHELL ────────────────────────────────────────────────────────────────
function App() {
  const [authUser, setAuthUser] = useState(null);       // Supabase auth user
  const [profile, setProfile] = useState(null);         // user_profiles row
  const [org, setOrg] = useState(null);                 // organizations row
  const [authLoading, setAuthLoading] = useState(true); // initial auth check
  const [provisionHold, setProvisionHold] = useState(false); // account deliberately not activated yet
  const [showForgot, setShowForgot] = useState(false);       // "forgot password" screen
  // Arrived from an emailed link that must end in a password being set --
  // either a reset, or an invite from a farm Admin.
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [inviteMode] = useState(INITIAL_AUTH_TYPE === "invite");
  const [page, setPage] = useState(pageFromPath);
  // Opening one mare from another page (Herd Management calendar, Dashboard).
  const [focusMare, setFocusMare] = useState(null);
  const openMare = useCallback(id => { setFocusMare({ id, n: Date.now() }); setPage("mares"); }, []);
  // Keep the address bar on the current page. Only the path is written: the
  // one-shot query parameters (?billing=, ?data=, ?email_connected=) are
  // removed by their own handlers and must not be carried into history.
  useEffect(() => {
    const want = page === "dashboard" ? "/" : "/" + page;
    if (window.location.pathname !== want) {
      try { window.history.pushState({ page }, "", want); } catch { /* non-fatal */ }
    }
  }, [page]);
  useEffect(() => {
    const onPop = () => { setSettingsTab(null); setPage(pageFromPath()); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Billing (Admins only; the function answers "not enabled" for everyone else).
  const [billing, refreshBilling] = useBillingStatus(profile?.role === "Admin");
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [cardPromptSkipped, setCardPromptSkipped] = useState(() => {
    try { return sessionStorage.getItem(BILLING_PROMPT_KEY) === "1"; } catch { return true; }
  });
  // Back from Stripe: ?billing=success | cancelled | portal. Strip it from the
  // URL, then re-read status. The webhook usually lands within seconds, so poll
  // briefly rather than tell the farm to refresh.
  const [billingReturn] = useState(() => new URLSearchParams(window.location.search).get("billing"));
  const [billingNotice, setBillingNotice] = useState(null);
  // Has this farm's subscription or trial ended, and is it read-only yet?
  // Answered by the database (my_farm_access -> farm_access), for every role.
  // Re-read whenever billing changes, so subscribing again lifts read-only
  // without a reload. If the call fails the app behaves as before: the
  // database still enforces read-only, only the explanation is missing.
  const [access, setAccess] = useState(null);
  const refreshAccess = useCallback(async () => {
    const { data, error } = await supabase.rpc("my_farm_access");
    if (error) { console.error("[access] my_farm_access failed", error); return null; }
    const a = (Array.isArray(data) ? data[0] : data) || null;
    FARM_READ_ONLY = !!a?.read_only;
    setAccess(a);
    return a;
  }, []);
  useEffect(() => { if (profile?.org_id) refreshAccess(); },
    [profile?.org_id, billing?.status, billing?.has_subscription, refreshAccess]);
  // Links in the deletion reminder emails: ?data=export opens Farm Profile >
  // Your data, ?billing=subscribe opens Billing with the plan chooser. Admins
  // only; that is who the reminders go to.
  const [dataLink] = useState(() => new URLSearchParams(window.location.search).get("data"));
  const dataLinkHandled = useRef(false);
  useEffect(() => {
    if (dataLink !== "export" || profile?.role !== "Admin" || dataLinkHandled.current) return;
    dataLinkHandled.current = true;
    try {
      const u = new URL(window.location.href); u.searchParams.delete("data");
      window.history.replaceState(null, "", u.pathname + u.search + u.hash);
    } catch { /* cosmetic */ }
    setSettingsTab({ id: "farm", n: Date.now() }); setPage("settings");
  }, [dataLink, profile?.role]);
  // Runs ONCE, and only after the profile says Admin. It used to start on the
  // very first render, before the profile loaded, with a refresh that still
  // thought the user was not an Admin -- and that stale retry loop kept
  // resetting billing to "nothing" for ten seconds after coming back from
  // Stripe, so the Billing tab sat on "Loading billing...". Found by clicking
  // through the cancel flow in the built app, not by reading it.
  const billingReturnHandled = useRef(false);
  const billingReady = profile?.role === "Admin";
  useEffect(() => {
    if (!billingReturn || !billingReady || billingReturnHandled.current) return;
    billingReturnHandled.current = true;
    try {
      const u = new URL(window.location.href); u.searchParams.delete("billing");
      window.history.replaceState(null, "", u.pathname + u.search + u.hash);
    } catch { /* cosmetic */ }
    if (billingReturn === "cancel_done") {
      setSettingsTab({ id: "billing", n: Date.now() }); setPage("settings");
      let tries = 0;
      const tick = async () => {
        const b = await refreshBilling();
        if (b?.subscription?.ending) { setBillingNotice(`Your subscription is cancelled. You keep full access until ${formatLongDate(b.subscription.ends_at)}, and you won't be charged again.`); return; }
        if (++tries < 6) setTimeout(tick, 2000);
      };
      tick();
      setTimeout(() => setBillingNotice(null), 15000);
    }
    if (billingReturn === "card_updated") {
      setSettingsTab({ id: "billing", n: Date.now() }); setPage("settings");
      refreshBilling().then(b => setBillingNotice(b?.subscription?.card
        ? `Card updated. Payments now go to your ${b.subscription.card.brand.charAt(0).toUpperCase() + b.subscription.card.brand.slice(1)} ending ${b.subscription.card.last4}.`
        : "Card updated."));
      setTimeout(() => setBillingNotice(null), 12000);
    }
    if (billingReturn === "portal") { setSettingsTab({ id: "billing", n: Date.now() }); setPage("settings"); refreshBilling(); }
    if (billingReturn === "subscribe") { setSettingsTab({ id: "billing", n: Date.now() }); setPage("settings"); setAddCardOpen(true); }
    if (billingReturn === "success") {
      setBillingNotice("Thanks! Your card is saved. It can take a few seconds to show here.");
      let tries = 0;
      const tick = async () => {
        const b = await refreshBilling();
        if (b?.has_subscription || ++tries >= 8) { if (b?.has_subscription) setBillingNotice("You're all set. Thanks for subscribing to ArcStallion."); return; }
        setTimeout(tick, 2500);
      };
      tick();
      setTimeout(() => setBillingNotice(null), 12000);
    }
  }, [billingReturn, billingReady, refreshBilling]);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [hasNew, setHasNew] = useState(hasUnseenChanges);
  // Which Settings tab to open when arriving from the user menu. `n` changes on
  // every request so a second click remounts Settings on that tab.
  const [settingsTab, setSettingsTab] = useState(null);
  const [showSignup, setShowSignup] = useState(() => window.location.search.includes("signup") || window.location.hash === "#signup");

  // ── Coming back from an email OAuth redirect ──
  //
  // email-oauth used to answer with a styled HTML page. It cannot any more:
  // Supabase's edge gateway forces `content-type: text/plain` and a sandbox CSP
  // on every function response, so the customer saw raw markup on a supabase.co
  // URL halfway through connecting their mailbox. The function now redirects
  // back here with the outcome in the query string, and the app says what
  // happened -- in ArcStallion's own branding, on ArcStallion's own domain.
  //
  // The connect flow runs in a window.open() popup, so the usual case is: this
  // code runs inside that popup, tells the opener, and closes itself. If it was
  // opened in a tab instead (popup blocker, or a phone), there is no opener and
  // we show the banner here.
  const [oauthResult, setOauthResult] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const connected = q.get("email_connected");
    const failed = q.get("email_error");
    if (!connected && !failed) return null;
    return {
      ok: !!connected,
      provider: connected || q.get("provider") || "",
      as: q.get("as") || "",
      reason: failed || "",
    };
  });

  useEffect(() => {
    if (!oauthResult) return;
    // Take the parameters out of the address bar either way, so a refresh does
    // not replay the banner.
    try {
      window.history.replaceState({}, "", window.location.pathname);
    } catch { /* non-fatal */ }

    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage({ type: "arcstallion:email-oauth", ...oauthResult }, window.location.origin);
        window.close();
      } catch { /* fall through to the banner below */ }
    }
  }, [oauthResult]);

  // ── Listen for auth state changes (login / logout / refresh) ──
  useEffect(() => {
    // Check existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      // An invite link signs them in immediately, so without this check they
      // would sail past the password step into the app.
      if (session && INITIAL_AUTH_TYPE === "invite") { setRecoveryMode(true); setAuthLoading(false); return; }
      if (session) loadUserData(session.user);
      else setAuthLoading(false);
    });

    // Listen for future auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Someone clicking a reset link arrives with a real session, so without
      // this they would land straight in the app and never set a password.
      //
      // An INVITE also arrives with a real session, but fires SIGNED_IN rather
      // than PASSWORD_RECOVERY -- identical to a normal login as far as this
      // handler can see. That is why INITIAL_AUTH_TYPE exists: it is read from
      // the URL fragment at module load, before supabase-js strips it. Without
      // the second clause an invited teammate is signed in having never chosen
      // a password, and is locked out for good once that session expires.
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && INITIAL_AUTH_TYPE === "invite")) {
        setRecoveryMode(true);
        setAuthLoading(false);
        return;
      }
      if (session) loadUserData(session.user);
      else { setAuthUser(null); setProfile(null); setOrg(null); setAuthLoading(false); setRecoveryMode(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserData = async (user) => {
    setAuthUser(user);
    // Fetch user profile — simple select, no join (no organizations table in schema)
    let { data: prof } = await supabase
      .from("user_profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    // An account with no profile has no farm, so the app renders empty. Every
    // self-serve signup between June and August 2026 ended up in this state.
    // provision-tenant is idempotent, so it is safe to call whenever the
    // profile is missing — it creates the farm from the signup metadata and
    // returns the existing one if somehow there already is one.
    if (!prof) {
      try {
        await callFunction("provision-tenant", {});
        ({ data: prof } = await supabase
          .from("user_profiles")
          .select("*")
          .eq("id", user.id)
          .single());
      } catch (e) {
        if (e.code === "provisioning_on_hold") { setProvisionHold(true); setAuthLoading(false); return; }
        console.error("Could not set up this account's farm:", e);
      }
    }

    if (prof) {
      setProfile(prof);
      // Branding comes from the farm's own `organizations` row. It used to be a
      // hardcoded Capital Quarter Horses logo and name for every tenant, so a
      // new farm's first sight of their account was another farm's brand.
      const { data: orgRow } = await supabase
        .from("organizations")
        .select("name, logo_url")
        .eq("id", prof.org_id)
        .maybeSingle();
      setOrg({
        name: orgRow?.name || prof.org_name || "Your Farm",
        logo_url: orgRow?.logo_url || null,
      });
    }
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setPage("dashboard");
  };

  // ── Loading screen (initial auth check) ──
  if (authLoading) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading-screen">
          <ArcStallionLogo size="lg" dark={true} />
          <div className="loading-spinner" />
          <div style={{ color: "rgba(201,168,76,.4)", fontSize: 13, fontFamily: "Crimson Pro,serif" }}>Loading your account…</div>
        </div>
      </>
    );
  }

  // ── Came back from an email OAuth redirect in a tab, not a popup ──
  // (The popup case closes itself in the effect above and never reaches here.)
  if (oauthResult) {
    const REASONS = {
      missing_params:   "That link was incomplete. Start the connection again from Settings.",
      bad_state:        "That link expired or was tampered with. Start again from Settings.",
      not_configured:   "This provider is not set up on the server yet.",
      provider_refused: "The provider would not complete the connection. Please try again.",
      unreachable:      "Could not reach the provider. Please try again in a moment.",
    };
    const name = oauthResult.provider === "gmail" ? "Gmail"
               : oauthResult.provider === "outlook" ? "Outlook" : "Email";
    return (
      <>
        <style>{styles}</style>
        <div className="login-page">
          <div className="login-bg-pattern" />
          <div className="login-card">
            <div className="login-header"><ArcStallionLogo size="lg" dark={true} /></div>
            <div className="login-body">
              <div className="login-welcome">
                {oauthResult.ok ? `${name} connected` : `${name} could not be connected`}
              </div>
              <div className="login-sub" style={{ marginBottom: 18 }}>
                {oauthResult.ok
                  ? (oauthResult.as
                      ? `Your farm's email will now send from ${oauthResult.as}.`
                      : "Your farm's email is connected.")
                  : (REASONS[oauthResult.reason] || "Something went wrong. Start again from Settings.")}
              </div>
              <button className="login-btn" onClick={() => setOauthResult(null)}>
                Back to ArcStallion
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Setting a new password, from a reset email ──
  if (recoveryMode) {
    return (
      <>
        <style>{styles}</style>
        <ResetPasswordPage invite={inviteMode} onDone={async () => {
          setRecoveryMode(false);
          const { data: { session } } = await supabase.auth.getSession();
          if (session) { setAuthLoading(true); loadUserData(session.user); }
        }} />
      </>
    );
  }

  // ── Account on hold ──
  // Better than dropping them into an app with no data, which is what these
  // accounts did for months.
  if (provisionHold) {
    return (
      <>
        <style>{styles}</style>
        <div className="login-page">
          <div className="login-bg-pattern" />
          <div className="login-card" style={{ textAlign: "center" }}>
            <div className="login-header"><ArcStallionLogo size="lg" dark={true} /></div>
            <div className="login-body">
              <div style={{ fontSize: 20, fontWeight: 700, color: "var(--gold)", marginBottom: 12 }}>Almost there</div>
              <div style={{ fontSize: 14, color: "rgba(201,168,76,.6)", lineHeight: 1.6, marginBottom: 20 }}>
                Your account is being set up. We will be in touch shortly to finish getting you started.
              </div>
              <button className="btn btn-secondary" onClick={handleLogout}>Sign out</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Login / Signup gate ──
  if (!authUser) {
    return (
      <>
        <style>{styles}</style>
        {showSignup
          ? <SignupPage onSwitchToLogin={() => setShowSignup(false)} />
          : showForgot
            ? <ForgotPasswordPage onBack={() => setShowForgot(false)} />
            : <LoginPage onSwitchToSignup={() => setShowSignup(true)} onForgotPassword={() => setShowForgot(true)} />
        }
      </>
    );
  }

  const orgId = profile?.org_id;
  const orgName = org?.name || "Your Farm";
  const role = profile?.role || "Viewer";
  const isViewer = role === "Viewer";
  const isAdmin  = role === "Admin";
  CAN_DELETE = isAdmin;
  const pt = TITLES[page] || TITLES.dashboard;
  const pendingNote = false; // replace with real count if desired

  // Filter nav by role: Viewers can't reach Settings; non-Admins can still see
  // Settings but won't see the Team tab inside.
  const filteredNav = NAV.map(sec => ({
    ...sec,
    items: sec.items.filter(it => !(it.id === "settings" && isViewer)),
  })).filter(sec => sec.items.length > 0);

  // If a Viewer lands on settings somehow, bounce them back to dashboard.
  if (isViewer && page === "settings") {
    setPage("dashboard");
  }

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        {/* ── SIDEBAR ── */}
        <aside className="sidebar">
          <div className="sidebar-brand" style={{ display: "flex", justifyContent: "center" }}>
            <ArcStallionLogo size="lg" dark={true} />
          </div>
          <GlobalSearch orgId={orgId} setPage={setPage} />
          <nav className="sidebar-nav">
            {filteredNav.map(sec => (
              <div key={sec.section}>
                <div className="nav-section-label">{sec.section}</div>
                {sec.items.map(item => (
                  <div key={item.id} className={`nav-item ${page === item.id ? "active" : ""}`} onClick={() => { setSettingsTab(null); setPage(item.id); }}>
                    <span className="nav-icon">{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            ))}
          </nav>
          {/* Was a second "Software by ArcStallion" logo plus a phone number and
              address. The logo at the top already says whose software this is;
              this slot now does something useful. */}
          <div className="sidebar-footer" style={{ padding: "8px 0" }}>
            <div className="nav-item" role="button" tabIndex={0}
              onClick={() => setFeedbackOpen(true)}
              onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFeedbackOpen(true); } }}>
              <span className="nav-icon">💬</span>
              <span>Help &amp; feedback</span>
            </div>
          </div>
        </aside>

        {/* ── MAIN ── */}
        <main className="main">
          <div className="topbar">
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div>
                <div className="topbar-title">{pt.title}</div>
                <div className="topbar-subtitle">{pt.sub}</div>
              </div>
            </div>
            <div className="topbar-actions">
              <button className="btn btn-outline" onClick={() => setPage("orders")}>💳 Orders</button>
              {AI_ADVISOR_ENABLED && <button className="btn btn-ai" onClick={() => setPage("ai")}>🤖 AI Advisor</button>}
              <UserMenu user={authUser} profile={profile} onLogout={handleLogout} onFeedback={() => setFeedbackOpen(true)}
                onWhatsNew={() => { setWhatsNewOpen(true); markChangesSeen(); setHasNew(false); }} hasNew={hasNew}
                onSettings={tab => { setSettingsTab({ id: tab, n: Date.now() }); setPage("settings"); }} />
            </div>
          </div>

          {isViewer && !access?.read_only && (
            <div style={{
              background: "#fff8e1", borderBottom: "1px solid #f5e2a3",
              color: "#7a5000", padding: "8px 26px", fontSize: 12.5,
              fontFamily: "'Crimson Pro', serif", display: "flex", alignItems: "center", gap: 8
            }}>
              <span style={{ fontWeight: 700 }}>👁️ Viewer mode —</span>
              <span>you have read-only access. Contact an Admin to make changes.</span>
            </div>
          )}
          {!isAdmin && !isViewer && !access?.read_only && (
            <div style={{
              background: "#eef5ff", borderBottom: "1px solid #c7ddfa",
              color: "#1e3a8a", padding: "6px 26px", fontSize: 12,
              fontFamily: "'Crimson Pro', serif"
            }}>
              <span style={{ fontWeight: 700 }}>Staff access</span> — you can add and edit records, but deletes and team management are reserved for Admins.
            </div>
          )}
          {access?.ended_at
            ? <AccessEndedBanner access={access} isAdmin={isAdmin} canSubscribe={!!billing?.enabled}
                onSubscribe={() => { setSettingsTab({ id: "billing", n: Date.now() }); setPage("settings"); setAddCardOpen(true); }}
                onDownload={() => { setSettingsTab({ id: "farm", n: Date.now() }); setPage("settings"); }} />
            : <BillingBanner billing={billing} onAddCard={() => setAddCardOpen(true)} />}
          {billingNotice && (
            <div role="status" style={{ background: "#e8f5e9", borderBottom: "1px solid #bfe3c4", color: "#166534", padding: "7px 26px", fontSize: 13, fontFamily: "'Crimson Pro', serif" }}>{billingNotice}</div>
          )}
          <div className="content">
            {page === "dashboard"    && <Dashboard orgId={orgId} orgName={orgName} orgLogo={org?.logo_url} setPage={setPage} profile={profile} authUser={authUser} openMare={openMare} />}
            {page === "herd-mgmt"   && <HerdManagement orgId={orgId} openMare={openMare} />}
            {page === "stallions"    && <StallionManagement orgId={orgId} />}
            {page === "mares"        && <MareManagement orgId={orgId} focusMare={focusMare} onFocusHandled={() => setFocusMare(null)} />}
            {page === "farrier"     && <FarrierSchedule orgId={orgId} />}
            {page === "breeding-mgmt" && <BreedingManagement orgId={orgId} />}
            {page === "foaling"      && <FoalingRecords orgId={orgId} />}
            {page === "frozen-semen" && <FrozenSemen orgId={orgId} />}
            {page === "customers"    && <Customers orgId={orgId} />}
            {page === "orders"       && <Orders orgId={orgId} />}
            {AI_ADVISOR_ENABLED && page === "ai" && <AIAdvisor orgId={orgId} />}
            {page === "reports"      && <Reports orgId={orgId} />}
            {page === "embryos"      && <EmbryoTransfer orgId={orgId} />}
            {page === "shipping"     && <Shipping orgId={orgId} />}
            {page === "settings"     && <AdminSettings key={settingsTab?.n || 0} initialTab={settingsTab?.id} orgId={orgId} profile={profile} billing={billing} onAddCard={() => setAddCardOpen(true)} onRefreshBilling={refreshBilling} onFeedback={() => setFeedbackOpen(true)} onBrandingChange={b => setOrg(p => ({ ...(p || {}), ...b }))} />}
          </div>
        </main>
      </div>
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} page={pt.title} />}
      {/* Card at signup: shown once per browser session to an Admin whose trial
          has no card yet. "Skip for now" is always there -- nobody is stuck. */}
      {(addCardOpen || (billing?.enabled && billing.status === "trial" && !billing.has_subscription && !cardPromptSkipped && !billingReturn)) && (
        <AddCardModal billing={billing}
          onClose={() => { setAddCardOpen(false); setCardPromptSkipped(true); try { sessionStorage.setItem(BILLING_PROMPT_KEY, "1"); } catch { /* ok */ } }}
          onSkip={addCardOpen ? null : () => { setCardPromptSkipped(true); try { sessionStorage.setItem(BILLING_PROMPT_KEY, "1"); } catch { /* ok */ } }} />
      )}
      {whatsNewOpen && <WhatsNewModal onClose={() => setWhatsNewOpen(false)} onFeedback={() => setFeedbackOpen(true)} />}
    </>
  );
}

// A render error anywhere below this used to blank the whole page — white
// screen, no message, nothing for a customer to tell you on the phone. This
// catches it, says so, and offers a reload.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, stack: "", feedback: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Where a Sentry (or similar) call belongs once error tracking is wired up.
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Kept for the report form: the first few frames say which screen broke.
    this.setState({ stack: (info?.componentStack || "").trim().split("\n").slice(0, 8).join("\n") });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(160deg,#0d1f0d 0%,#1a2e1a 45%,#0a1a0a 100%)", fontFamily: "Crimson Pro,Georgia,serif", padding: 24 }}>
        <div style={{ maxWidth: 460, textAlign: "center", color: "rgba(226,201,126,.9)" }}>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, color: "#c9a84c" }}>Something went wrong</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 20, color: "rgba(201,168,76,.6)" }}>
            The page hit an unexpected error. Your data is safe — reloading usually clears it.
            If it keeps happening, let us know what you were doing at the time.
          </div>
          <button className="btn" onClick={() => window.location.reload()}
            style={{ background: "#c9a84c", color: "#0d1f0d", border: "none", padding: "10px 22px", borderRadius: 6, fontWeight: 700, cursor: "pointer" }}>
            Reload
          </button>
          <div style={{ marginTop: 16 }}>
            <button onClick={() => this.setState({ feedback: true })}
              style={{ background: "none", border: "none", color: "rgba(226,201,126,.85)", cursor: "pointer", fontSize: 14, textDecoration: "underline", fontFamily: "inherit" }}>
              Tell us what happened
            </button>
          </div>
          <div style={{ marginTop: 10, fontSize: 11.5, color: "rgba(201,168,76,.35)" }}>
            or email support@arcstallion.com
          </div>
        </div>
        {/* App's <style> is not mounted once it has crashed, so bring the
            stylesheet along or the form renders unstyled. */}
        {this.state.feedback && <>
          <style>{styles}</style>
          <FeedbackModal onClose={() => this.setState({ feedback: false })} page="Crash screen"
            errorText={`${this.state.error?.name || "Error"}: ${this.state.error?.message || ""}\n${this.state.stack}`.trim()} />
        </>}
      </div>
    );
  }
}

// /sign#t=<token> is the public e-signing page: a mare owner with no account.
// Decided before App mounts, so no session check, no page routing and no
// history rewriting ever touches it.
const IS_SIGN_PAGE = window.location.pathname.replace(/\/+$/, "") === "/sign";

export default function AppWithErrorBoundary() {
  if (IS_SIGN_PAGE) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div style={{ padding: 40, textAlign: "center", fontFamily: "Georgia, serif" }}>Opening your agreement…</div>}>
          <SignPage />
        </Suspense>
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
